import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Drawer, Button, Typography } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import { createPortal } from 'react-dom';
import './ComercialManagement.css';
import './ComercialCalendar.css'; // Import calendar CSS for sidebar and event-modal styles
import ProposalShareComercialPicker from './ProposalShareComercialPicker';
import ComercialCalendar, { type CalendarEvent } from './ComercialCalendar';
import RequestCard from './RequestCard';
import apiService from '../../services/api';
import { api } from '../../services/api';
import DEPLOYMENT_CONFIG from '../../config/deployment';
import WonderTradeLoader from '../Shared/WonderTradeLoader';
import { useApp } from '../../contexts/AppContext';
import { getClaimForPropostaId } from '../../utils/proposalClaimLookup';
import {
  allServicosHaveTerminalScd,
  anyServicoConcluidoNotCancelled,
  latestTerminalAtFromScdList,
  mergeProposalClaimsForComercial,
} from '../../utils/proposalComercialBoard';
import { canViewComercialTeamHierarchy, isGestorOrAboveComercial as isGestorOrAboveRole } from '../../utils/comercialGestaoRoles';
import {
  buildCompletedProposalsForMarcacao,
  buildSignatureAssociateOptions,
  findProposalInMarcacaoForm,
  resolveMarcacaoInStore,
  signatureAssociateOptionMatchesSearch,
  type CompletedProposalForContract,
  type SignatureAssociateOption,
} from '../../utils/proposalContractAssociation';
import {
  MOBILIDADE_FIELD_KEYS,
  MOBILIDADE_FIELD_LABELS,
  formatMobilidadeFieldValue,
} from './mobilidadeProposalFields';
import {
  applyProposalNotesImport,
  copyProposalNotesTemplate,
  downloadProposalNotesTemplate,
  parseProposalNotesTxt,
} from './proposalNotesImport';

function isCccProspeccaoMarcacao(marcacao: { marcacao_id?: string; id?: string; form_data?: Record<string, unknown> }): boolean {
  const fd = marcacao.form_data || {};
  if (String(fd.cccSource || '').toLowerCase() === 'prospeccao') return true;
  const mid = String(marcacao.marcacao_id || marcacao.id || '');
  return mid.startsWith('PROSP-');
}

function marcacaoHasSubmittedProposal(marcacao: { form_data?: Record<string, unknown>; marcacao_id?: string; id?: string }): boolean {
  const fd = marcacao.form_data || {};
  const pd = fd.proposalData;
  if (Array.isArray(pd)) return pd.length > 0;
  if (pd && typeof pd === 'object' && Object.keys(pd as object).length > 0) return true;
  const propostaIds = fd.propostaId;
  if (Array.isArray(propostaIds) && propostaIds.length > 0) return true;
  if (typeof propostaIds === 'string' && propostaIds.trim()) return true;
  return false;
}

function marcacaoHasSubmittedContract(marcacao: { form_data?: Record<string, unknown> }): boolean {
  const fd = marcacao.form_data || {};
  const contractData = fd.contractData;
  if (Array.isArray(contractData)) return contractData.length > 0;
  if (contractData && typeof contractData === 'object' && Object.keys(contractData as object).length > 0) {
    return true;
  }
  return false;
}

const { Text } = Typography;

function contractProposalMatchesSearch(
  p: CompletedProposalForContract,
  query: string
): boolean {
  const q = query.toLowerCase().trim();
  if (!q) return true;
  return (
    p.display.toLowerCase().includes(q) ||
    p.id.toLowerCase().includes(q) ||
    (p.cccSourceLabel || '').toLowerCase().includes(q)
  );
}

function SignatureAssociateItem({
  option,
  isSelected,
  onSelect,
}: {
  option: SignatureAssociateOption;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={`comercial-signature-associate-item ${isSelected ? 'selected' : ''}`}
      onClick={onSelect}
    >
      <span style={{ flex: 1, minWidth: 0 }}>{option.display}</span>
      <span className="comercial-signature-associate-tags">
        {option.isRefazer ? (
          <span className="comercial-refazer-proposal-tag">Refazer</span>
        ) : null}
        {option.cccSourceLabel ? (
          <span className="comercial-ccc-proposal-tag">{option.cccSourceLabel}</span>
        ) : null}
      </span>
    </button>
  );
}

function ContractProposalAssociateItem({
  p,
  isSelected,
  onToggle,
}: {
  p: CompletedProposalForContract;
  isSelected: boolean;
  onToggle: () => void;
}) {
  return (
    <label
      className={`comercial-signature-associate-item ${isSelected ? 'selected' : ''}`}
      style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: 0 }}
    >
      <input type="checkbox" checked={isSelected} onChange={onToggle} />
      <span
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          minWidth: 0,
        }}
      >
        <span style={{ minWidth: 0 }}>{p.display}</span>
        {p.cccSourceLabel ? (
          <span className="comercial-ccc-proposal-tag" title="Pedido originado no CCC">
            {p.cccSourceLabel}
          </span>
        ) : null}
      </span>
    </label>
  );
}

/** Mobilidade / Outro: no CPE, fatura, or print do telhado in Solicitar Proposta. */
const PROPOSAL_SERVICES_WITHOUT_ENERGY_DOCS = new Set(['mobilidade', 'outro']);
const PROPOSAL_ROADTRIP_FECHO_SERVICES = new Set(['bairro-solar', 'pap', 'transacional', 'luz', 'gas']);
const MOBILIDADE_OPTION_FIELD_PREFIXES = [
  'plataformaGestao',
  'localInstalacao',
  'quadroMobilidade',
  'tipoFixacao',
  'tipoCarregamento',
] as const;

function readStoredSessionUser(): Record<string, unknown> | null {
  try {
    const raw =
      localStorage.getItem('user') ||
      localStorage.getItem('cognito_user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** Nome comercial = logged-in user name — never empresa / marcação company name. */
function resolveContractNomeComercial(args: {
  currentUserData?: { name?: string; username?: string } | null;
  currentUserId?: number | null;
  userMap?: Record<number, string>;
  proposalEquipaInterna?: { nomeUsuario?: string } | null;
  fallback?: string;
}): string {
  const fromCurrent =
    args.currentUserData?.name?.trim() ||
    args.currentUserData?.username?.trim() ||
    '';
  if (fromCurrent) return fromCurrent;

  const uid = args.currentUserId;
  if (uid && args.userMap?.[uid]) {
    const fromMap = String(args.userMap[uid]).trim();
    if (fromMap) return fromMap;
  }

  const sessionUser = readStoredSessionUser();
  if (sessionUser) {
    const fromSession = String(
      sessionUser.name ||
        sessionUser.username ||
        sessionUser.full_name ||
        sessionUser.nome ||
        ''
    ).trim();
    if (fromSession) return fromSession;
  }

  const fromProposal = args.proposalEquipaInterna?.nomeUsuario?.trim() || '';
  if (fromProposal) return fromProposal;

  return args.fallback?.trim() || '';
}

function resolveContractCodigoComercial(args: {
  currentUserData?: { codigoComercial?: string; codigo_comercial?: string } | null;
  proposalEquipaInterna?: { codigoComercial?: string } | null;
  fallback?: string;
}): string {
  const fromCurrent =
    args.currentUserData?.codigoComercial?.trim() ||
    args.currentUserData?.codigo_comercial?.trim() ||
    '';
  if (fromCurrent) return fromCurrent;

  const sessionUser = readStoredSessionUser();
  if (sessionUser) {
    const fromSession = String(
      sessionUser.codigoComercial || sessionUser.codigo_comercial || ''
    ).trim();
    if (fromSession) return fromSession;
  }

  const fromProposal = args.proposalEquipaInterna?.codigoComercial?.trim() || '';
  if (fromProposal) return fromProposal;

  return args.fallback?.trim() || '';
}

/** Backdrop clicks must not close Solicitar Proposta / Submeter Contrato / Assinatura Digital modals. */
const swallowModalBackdropPointer = (e: React.MouseEvent) => {
  if (e.target === e.currentTarget) {
    e.stopPropagation();
  }
};

interface Marcacao {
  id: string;
  id_db?: number; // Backend numeric primary key (for association with signature requests)
  marcacao_id?: string; // Backend field
  produto: string;
  produtos?: string[]; // Array of products (for multi-product marcações)
  cliente: string;
  nome_responsavel?: string; // Backend field
  empresa?: string;
  nome_empresa?: string; // Backend field
  responsavel: string;
  data: string;
  date?: string; // Backend field (ISO format)
  hora: string;
  hora_inicio?: string; // Backend field
  hora_fim?: string; // Backend field
  morada?: string;
  contacto?: string;
  contacto_cliente?: string; // Backend field
  jaTemPaineis?: string;
  potencia?: string;
  trifasico?: string;
  tipoTelhado?: string;
  pagaMais250?: string;
  faturaPresente?: string;
  gasNatural?: string;
  comercializadora?: string;
  valorFatura?: string;
  observacoes?: string;
  marcacao_description?: string; // Backend field
  files?: Array<{ name: string; type: string; size: string; id?: string; url?: string }>;
  feedback?: string | {
    callCenterFeedback?: {
      data?: string | boolean;
      horario?: string | boolean;
      contacto?: string | boolean;
      linkGoogleMaps?: string | boolean;
      informacoesCallCenter?: string | boolean;
      outro?: string;
    };
  };
  gestao_substage?: string; // Current sub-stage from backend
  form_data?: any; // Complete backend form data
  product_details?: any; // Backend product details
  estado_proposta?: string; // Estado da Proposta (same as Minhas Propostas): '', 'pendente', 'em_progresso', 'concluido', 'cancelado' or section ids
  created_by?: number; // Backend field - user ID who created the marcação
  created_at?: string; // Backend field - creation timestamp
  creator_is_hidden?: boolean; // Backend: true when creator is hidden (for filtering in Assinatura Digital modal)
}

type Section = 'agendamentos' | 'reuniao-realizada' | 'feedback-pendencia' | 'resultado';

function findMarcacaoForMinhasProposta(proposal: any, marcacoes: any[]): any | undefined {
  if (proposal?.marcacao_db_id != null) {
    const byDbId = marcacoes.find((m) => m.id === proposal.marcacao_db_id);
    if (byDbId) return byDbId;
  }
  const propostaId = String(proposal?.id || '').trim();
  if (!propostaId) return undefined;
  return marcacoes.find((m: any) => {
    const mFormData = m.form_data || {};
    const mPropostaIds = mFormData.propostaId || [];
    const mPropostaIdsArray = Array.isArray(mPropostaIds) ? mPropostaIds : [mPropostaIds];
    if (mPropostaIdsArray.some((id: any) => String(id) === propostaId)) return true;
    const pd = mFormData.proposalData;
    const list = Array.isArray(pd) ? pd : pd && typeof pd === 'object' ? [pd] : [];
    return list.some(
      (p: any) => String(p?.propostaId || p?.proposta_id || '').trim() === propostaId
    );
  });
}

/** Enrich Minhas Propostas row from marcacao.form_data.claimedProposals (no BackOffice N+1). */
function propostaIdLookupKeys(propostaId: string): { pid: string; withPrefix: string; withoutPrefix: string } {
  const pid = String(propostaId).trim();
  const withPrefix = !pid.startsWith('PRP-') ? `PRP-${pid}` : pid;
  const withoutPrefix = pid.startsWith('PRP-') ? pid.replace('PRP-', '') : pid;
  return { pid, withPrefix, withoutPrefix };
}

function resolvePerPropostaMapEntry<T>(
  mapValue: unknown,
  propostaId: string
): T[] {
  if (typeof mapValue !== 'object' || mapValue == null || Array.isArray(mapValue)) {
    return [];
  }
  const { pid, withPrefix, withoutPrefix } = propostaIdLookupKeys(propostaId);
  const entry = (mapValue as Record<string, unknown>)[pid]
    ?? (mapValue as Record<string, unknown>)[withPrefix]
    ?? (mapValue as Record<string, unknown>)[withoutPrefix];
  return Array.isArray(entry) ? (entry as T[]) : [];
}

function resolveCardNotesFromMarcacao(marcacao: any, propostaId: string): any[] {
  if (!marcacao) return [];
  const mfd = marcacao.form_data || {};
  return resolvePerPropostaMapEntry(mfd.card_notes, propostaId);
}

function resolveCardTagsFromMarcacao(
  marcacao: any,
  propostaId: string
): { type: string; ate?: string; value?: string; motivo?: string; criada_em?: string }[] {
  if (!marcacao) return [];
  const mfd = marcacao.form_data || {};
  return resolvePerPropostaMapEntry(mfd.card_tags, propostaId);
}

function enrichMinhasPropostaFromMarcacao(proposal: any, marcacoes: any[]): any {
  try {
    const propostaId = String(proposal.id || '').trim();
    const marcacao = findMarcacaoForMinhasProposta(proposal, marcacoes);
    const claimedRaw = marcacao?.form_data?.claimedProposals;
    const claim = getClaimForPropostaId(
      typeof claimedRaw === 'object' && claimedRaw !== null ? (claimedRaw as Record<string, unknown>) : undefined,
      propostaId
    );
    const proposalClaim = mergeProposalClaimsForComercial(null, claim);

    const servicosForStatus =
      proposal?.form_data?.servicos ||
      proposal?.form_answers?.servicos ||
      proposal?.servicos ||
      [];
    const servicosList = Array.isArray(servicosForStatus) ? servicosForStatus : [];
    const scdRaw = proposalClaim?.service_completion_data;
    const scdList = Array.isArray(scdRaw) ? scdRaw : [];
    const marcacaoFormData = marcacao?.form_data || {};
    const estadoProposta = String(marcacaoFormData.estadoProposta || '').toLowerCase();
    const claimStatus = proposalClaim?.status;

    let boardStatus: 'available' | 'in_progress' | 'completed' | 'cancelled' = 'available';
    if (claimStatus === 'cancelled') {
      boardStatus = 'cancelled';
    } else if (
      servicosList.length > 0 &&
      allServicosHaveTerminalScd(servicosList, scdList) &&
      !anyServicoConcluidoNotCancelled(servicosList, scdList)
    ) {
      boardStatus = 'cancelled';
    } else if (
      claimStatus === 'completed' ||
      estadoProposta === 'concluido' ||
      estadoProposta === 'entregue' ||
      (servicosList.length > 0 &&
        allServicosHaveTerminalScd(servicosList, scdList) &&
        anyServicoConcluidoNotCancelled(servicosList, scdList))
    ) {
      boardStatus = 'completed';
    } else if (
      servicosList.length === 0 &&
      scdList.length > 0 &&
      (claimStatus === 'completed' || estadoProposta === 'concluido' || estadoProposta === 'entregue')
    ) {
      boardStatus = 'completed';
    } else if (proposalClaim?.backoffice_user_id) {
      boardStatus = 'in_progress';
    }

    type KanbanSectionId =
      | 'available'
      | 'roadtrip'
      | 'op_criada'
      | 'in_progress'
      | 'proposta_aprovacao'
      | 'exclusividade_pendente'
      | 'pendente_gestor_edp'
      | 'pendente_comercial'
      | 'completed';

    const isRoadTripRequest = (p: any): boolean => {
      const fd = p?.form_data || p?.form_answers || {};
      if (fd.equipaInterna?.tipoPedido === 'roadtrip') return true;
      const tipo = (fd.tipo || fd.type || '').toString().toLowerCase();
      if (tipo.includes('roadtrip') || tipo.includes('road trip')) return true;
      const servicos = fd.servicos || [];
      const names = (Array.isArray(servicos) ? servicos : [])
        .map((s: any) => (typeof s === 'object' ? s?.service : s) ?? '')
        .join(' ')
        .toLowerCase();
      return names.includes('roadtrip') || names.includes('road trip');
    };

    const backofficeUserId = claim?.backoffice_user_id ?? null;
    let kanbanSection: KanbanSectionId = 'available';
    if (boardStatus === 'completed' || boardStatus === 'cancelled') {
      kanbanSection = 'completed';
    } else if (boardStatus === 'available' && !backofficeUserId) {
      kanbanSection = isRoadTripRequest(proposal) ? 'roadtrip' : 'available';
    } else if (boardStatus === 'in_progress') {
      let stage = '';
      if (marcacao) {
        const mfd = marcacao.form_data || {};
        const boardStages = mfd.board_stages;
        const pid = String(propostaId);
        const withPrefix = !pid.startsWith('PRP-') ? `PRP-${pid}` : pid;
        const stageFromMap =
          typeof boardStages === 'object' && boardStages != null
            ? (boardStages[withPrefix] || boardStages[pid] || '')
            : '';
        stage = (stageFromMap || mfd.estado || mfd.board_stage || '').toString().toLowerCase().trim();
      }
      if (stage.includes('pendente_gestor_edp') || stage.includes('pendente gestor edp')) {
        kanbanSection = 'pendente_gestor_edp';
      } else if (stage.includes('pendente_comercial') || stage.includes('pendente comercial')) {
        kanbanSection = 'pendente_comercial';
      } else if (['exclusividade', 'pendente gestor', 'pendente_gestor'].some((k) => stage.includes(k))) {
        kanbanSection = 'exclusividade_pendente';
      } else if (['op_criada', 'op criada', 'opcriada'].some((k) => stage.includes(k))) {
        kanbanSection = 'op_criada';
      } else if (
        ['proposta_aprovacao', 'proposta em aprovacao', 'proposta em aprovação', 'em aprovacao', 'aprovacao', 'aprovação'].some(
          (k) => stage.includes(k)
        )
      ) {
        kanbanSection = 'proposta_aprovacao';
      } else {
        kanbanSection = 'in_progress';
      }
    }

    let completedAt = proposalClaim?.completed_at ?? null;
    if (!completedAt && boardStatus === 'completed' && scdList.length > 0) {
      completedAt = latestTerminalAtFromScdList(scdList) || completedAt;
    }

    const leadId =
      Number(
        proposal.lead_id ??
          marcacaoFormData.lead_id ??
          proposal.form_data?.lead_id ??
          proposal.form_answers?.lead_id ??
          0
      ) || null;

    let cardTags = resolveCardTagsFromMarcacao(marcacao, propostaId);
    let isRefazer =
      proposal?.form_data?.isRefazer === true || proposal?.form_answers?.isRefazer === true;
    if (!isRefazer && marcacao) {
      const rawPd = marcacaoFormData.proposalData;
      const entries = Array.isArray(rawPd) ? rawPd : rawPd && typeof rawPd === 'object' ? [rawPd] : [];
      const { pid, withPrefix, withoutPrefix } = propostaIdLookupKeys(propostaId);
      isRefazer = entries.some((entry: Record<string, unknown>) => {
        if (!entry || entry.isRefazer !== true) return false;
        const entryPid = String(entry.propostaId || entry.proposta_id || '');
        return entryPid === pid || entryPid === withPrefix || entryPid === withoutPrefix;
      });
    }
    if (isRefazer && !cardTags.some((t) => t.type === 'refazer')) {
      cardTags = [...cardTags, { type: 'refazer', value: 'Refazer' }];
    }

    const cardNotes =
      (Array.isArray(proposal?.card_notes) ? proposal.card_notes : null) ||
      (Array.isArray(proposal?.form_data?.card_notes) ? proposal.form_data.card_notes : null) ||
      (Array.isArray(proposal?.form_answers?.card_notes) ? proposal.form_answers.card_notes : null) ||
      resolveCardNotesFromMarcacao(marcacao, propostaId);

    return {
      ...proposal,
      lead_id: proposal.lead_id ?? leadId,
      boardStatus,
      kanbanSection,
      proposal_claim: proposalClaim,
      backoffice_user_id: backofficeUserId,
      backoffice_user: proposalClaim?.backoffice_user ?? null,
      cancelled_by_username: proposalClaim?.cancelled_by_username ?? null,
      cancelled_at: proposalClaim?.cancelled_at ?? null,
      cancellation_feedback: proposalClaim?.cancellation_feedback ?? null,
      completed_at: completedAt,
      completion_notes: proposalClaim?.completion_notes ?? null,
      card_tags: cardTags,
      is_refazer: isRefazer,
      card_notes: cardNotes,
    };
  } catch (error) {
    console.error('Error enriching minhas proposta from marcacao:', proposal?.id, error);
    return { ...proposal, boardStatus: 'available' as const };
  }
}

function resolveMarcacaoDbId(marcacao: any): string | null {
  const raw = marcacao?.id ?? marcacao?.marcacao_db_id ?? null;
  if (raw == null || String(raw).trim() === '') return null;
  return String(raw).trim();
}

function resolveContractSubmitterId(marcacao: any, contract: any): number | null {
  const submittedBy = contract?.submittedBy;
  if (submittedBy != null) {
    if (typeof submittedBy === 'number' && Number.isFinite(submittedBy)) return submittedBy;
    if (typeof submittedBy === 'string') {
      const n = Number(submittedBy);
      if (Number.isFinite(n)) return n;
    }
    if (typeof submittedBy === 'object') {
      const obj = submittedBy as Record<string, unknown>;
      const v = obj.id ?? obj.userId ?? obj.user_id;
      const n = typeof v === 'number' ? v : Number(v);
      if (Number.isFinite(n)) return n;
    }
  }
  if (contract?.comercial_user_id != null) {
    const n = Number(contract.comercial_user_id);
    if (Number.isFinite(n)) return n;
  }
  if (marcacao?.created_by != null) {
    const n = Number(marcacao.created_by);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** Resolve contractClaim_/contractStatus_ keys (db id or marcacao_id string). */
function resolveContractClaimAndStatus(
  formData: Record<string, unknown>,
  marcacao: any,
  contractIdx: number
): { contractStatus: string; claimInfo: Record<string, unknown> } {
  const fd = formData || {};
  const keyIds: string[] = [];
  const dbId = resolveMarcacaoDbId(marcacao);
  if (dbId) keyIds.push(dbId);
  const stringId = String(marcacao?.marcacao_id || '').trim();
  if (stringId && !keyIds.includes(stringId)) keyIds.push(stringId);

  for (const idPart of keyIds) {
    const claimKey = `contractClaim_${idPart}_${contractIdx}`;
    const statusKey = `contractStatus_${idPart}_${contractIdx}`;
    const claimRaw = fd[claimKey];
    const statusRaw = fd[statusKey];
    if (claimRaw && typeof claimRaw === 'object') {
      return {
        contractStatus: String(statusRaw || 'available'),
        claimInfo: claimRaw as Record<string, unknown>,
      };
    }
    if (statusRaw && statusRaw !== 'available') {
      return {
        contractStatus: String(statusRaw),
        claimInfo: {},
      };
    }
  }
  return { contractStatus: 'available', claimInfo: {} };
}

function resolveSolarContractDisplayStatus(
  contractStatus: string,
  claimInfo: Record<string, unknown>
): string {
  const cs = contractStatus || 'available';
  if (cs === 'cancelled' || cs === 'vt_not_ok') return 'cancelled';
  if (cs === 'installed') return 'installed';
  if (
    [
      'in_progress',
      'aguarda_agendamento_vt',
      'aguardo_confirmacao',
      'reformulacao',
      'reformulado_ok',
      'vt_realizada_ok',
      'aceite',
      'waiting_installation',
      'vt_ok',
    ].includes(cs)
  ) {
    return cs === 'vt_ok' || cs === 'waiting_installation' ? 'waiting_installation' : cs;
  }
  if (claimInfo?.backoffice_user_id) {
    if (claimInfo.installed_at) return 'installed';
    if (claimInfo.objected) return 'reformulacao';
    if (claimInfo.vt_status === 'ok') return 'waiting_installation';
    if (claimInfo.vt_status === 'not_ok') return 'cancelled';
    return 'in_progress';
  }
  return 'available';
}

function resolveEeGasContractDisplayStatus(
  contractStatus: string,
  claimInfo: Record<string, unknown>
): string {
  const cs = contractStatus || 'available';
  if (cs === 'cancelled') return 'cancelled';
  if (cs === 'completed' || claimInfo?.completed) return 'completed';
  if (cs === 'objected' || claimInfo?.objected) return 'objected';
  if (claimInfo?.backoffice_user_id) {
    if (claimInfo.submitted_to_bpo || cs === 'waiting_installation') return 'pending';
    return 'pending';
  }
  return 'available';
}

function enrichContractSubmissionFromMarcacao(submission: any, marcacao: any): any {
  const formData = marcacao?.form_data || {};
  const contractData = submission?.contract_data || {};
  const assignedRaw = contractData.assignedProposalIds;
  const assignedIds = Array.isArray(assignedRaw)
    ? assignedRaw.map((id: unknown) => String(id).trim()).filter(Boolean)
    : [];
  const claimedRaw = formData.claimedProposals;
  const claimedMap =
    typeof claimedRaw === 'object' && claimedRaw !== null && !Array.isArray(claimedRaw)
      ? (claimedRaw as Record<string, unknown>)
      : undefined;

  const linkedProposalCompletions = assignedIds
    .map((propostaId: string) => {
      const claim = getClaimForPropostaId(claimedMap, propostaId);
      const merged = mergeProposalClaimsForComercial(null, claim);
      const scdRaw = merged?.service_completion_data;
      const scdList = Array.isArray(scdRaw) ? scdRaw : [];
      if (!merged || (scdList.length === 0 && merged.status !== 'completed' && merged.status !== 'cancelled')) {
        return null;
      }
      return {
        propostaId,
        status: merged.status,
        completed_at: merged.completed_at ?? null,
        completion_notes: merged.completion_notes ?? null,
        service_completion_data: scdList,
      };
    })
    .filter(Boolean);

  return {
    ...submission,
    linked_proposal_completions: linkedProposalCompletions,
  };
}

function formatContractDisplayDate(iso?: string | null): string | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(iso);
  }
}

function mergeMarcacoesForGestaoContracts(primary: any[], secondary: any[]): any[] {
  const byKey = new Map<string, any>();
  const upsert = (marcacao: any) => {
    if (!marcacao || typeof marcacao !== 'object') return;
    const key = String(marcacao.id ?? marcacao.marcacao_id ?? '').trim();
    if (!key) return;
    const prev = byKey.get(key);
    if (!prev) {
      byKey.set(key, marcacao);
      return;
    }
    const prevFd = prev.form_data || {};
    const nextFd = marcacao.form_data || {};
    byKey.set(key, {
      ...prev,
      ...marcacao,
      form_data: { ...prevFd, ...nextFd },
    });
  };
  primary.forEach(upsert);
  secondary.forEach(upsert);
  return Array.from(byKey.values());
}

/** Read auth token (CRM + CCC session). */
function readAuthToken(): string | null {
  return (
    localStorage.getItem('jwt_token') ||
    localStorage.getItem('access_token') ||
    localStorage.getItem('cognito_access_token') ||
    null
  );
}

/** Main CRM API (:5000). Empty on CCC app — same-origin /api via Vite or Nginx proxy. */
function resolveMainApiBaseUrl(): string {
  if (typeof window !== 'undefined') {
    const { port, hostname } = window.location;
    if (port === '3001' || hostname.toLowerCase().includes('ccc.')) {
      return '';
    }
  }
  const raw = DEPLOYMENT_CONFIG.getBackendUrl();
  if (!raw) return '';
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
}

/** Read CRM user id from CCC / CRM session storage (sync, for gestão-only modals). */
function readGestaoUserIdFromStorage(): number | null {
  if (typeof window === 'undefined') return null;
  const tryId = (raw: unknown): number | null => {
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  try {
    const userRaw = localStorage.getItem('user');
    if (userRaw) {
      const user = JSON.parse(userRaw);
      const fromUser = tryId(user?.id ?? user?.user_id);
      if (fromUser) return fromUser;
    }
    const cognitoRaw = localStorage.getItem('cognito_user');
    if (cognitoRaw) {
      const cognito = JSON.parse(cognitoRaw);
      const fromCognito = tryId(cognito?.id ?? cognito?.user_id);
      if (fromCognito) return fromCognito;
    }
  } catch {
    /* ignore parse errors */
  }
  return null;
}

/** CCC API (:5001). Empty on CCC app (Vite proxy); set VITE_CCC_API_URL for direct calls. */
function resolveCccApiBaseUrl(): string {
  if (typeof window !== 'undefined') {
    const { port, hostname } = window.location;
    if (port === '3001' || hostname.toLowerCase().includes('ccc.')) {
      return '';
    }
  }
  try {
    const vite = (import.meta as { env?: { VITE_CCC_API_URL?: string } }).env?.VITE_CCC_API_URL;
    if (vite) return vite.replace(/\/$/, '');
  } catch {
    /* CRA main app */
  }
  const react = process.env.REACT_APP_CCC_API_URL || '';
  if (react) return react.replace(/\/$/, '');
  if (process.env.NODE_ENV === 'development') return 'http://127.0.0.1:5001';
  return '';
}

/** Auth token for embedded CCC flows (Prospeção / Recuperação hosts). */
function readEmbeddedCccAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  return (
    localStorage.getItem('jwt_token') ||
    localStorage.getItem('access_token') ||
    localStorage.getItem('cognito_access_token') ||
    localStorage.getItem('token')
  );
}

async function fetchMarcacaoFromCallcenter(marcacaoId: string): Promise<Record<string, unknown>> {
  const token = readEmbeddedCccAuthToken();
  const backendUrl = resolveMainApiBaseUrl();
  const getUrl = `${backendUrl}/api/callcenter/calendar/marcacoes/${marcacaoId}`;
  const getResponse = await fetch(getUrl, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!getResponse.ok) {
    const error = await getResponse.json().catch(() => ({}));
    throw new Error(
      (error as { message?: string }).message || 'Erro ao buscar dados da marcação'
    );
  }
  const getData = await getResponse.json();
  if (!getData.success || !getData.marcacao) {
    throw new Error(getData.message || 'Erro ao buscar dados da marcação');
  }
  return getData.marcacao;
}

async function uploadEmbeddedProposalFile(
  marcacaoId: string,
  formData: FormData,
  prospeccaoLeadId: string | null | undefined,
  cccMarcacaoWorkspace?: 'recuperacao' | 'retencao' | null,
  retencaoClientId?: string | null
): Promise<{ success: boolean; file?: any; error?: string; message?: string }> {
  const token = readEmbeddedCccAuthToken();
  const cccBase = resolveCccApiBaseUrl();
  if (retencaoClientId) {
    if (!token) {
      return { success: false, error: 'Sessão expirada. Volte a iniciar sessão no CCC.' };
    }
    const url = `${cccBase}/api/ccc/retencao/clients/${encodeURIComponent(retencaoClientId)}/proposal/files`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        success: false,
        error: (json as { message?: string; error?: string }).message || (json as { error?: string }).error,
        message: (json as { message?: string }).message,
      };
    }
    return json;
  }
  if (prospeccaoLeadId) {
    if (!token) {
      return { success: false, error: 'Sessão expirada. Volte a iniciar sessão no CCC.' };
    }
    const url = `${cccBase}/api/ccc/prospeccao/leads/${encodeURIComponent(prospeccaoLeadId)}/proposal/files`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        success: false,
        error: (json as { message?: string; error?: string }).message || (json as { error?: string }).error,
        message: (json as { message?: string }).message,
      };
    }
    return json;
  }
  if (cccMarcacaoWorkspace === 'recuperacao' || cccMarcacaoWorkspace === 'retencao') {
    if (!token) {
      return { success: false, error: 'Sessão expirada. Volte a iniciar sessão no CCC.' };
    }
    const url = `${cccBase}/api/ccc/${cccMarcacaoWorkspace}/marcacoes/${encodeURIComponent(marcacaoId)}/proposal/files`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        success: false,
        error: (json as { message?: string; error?: string }).message || (json as { error?: string }).error,
        message: (json as { message?: string }).message,
      };
    }
    return json;
  }
  return apiService.uploadProposalFile(marcacaoId, formData);
}

async function uploadEmbeddedContractFile(
  marcacaoId: string,
  formData: FormData,
  prospeccaoLeadId: string | null | undefined,
  useCccRecuperacaoContract?: boolean
): Promise<{ success: boolean; file?: any; error?: string; message?: string }> {
  const token = readEmbeddedCccAuthToken();
  const cccBase = resolveCccApiBaseUrl();
  if (prospeccaoLeadId) {
    if (!token) {
      return { success: false, error: 'Sessão expirada. Volte a iniciar sessão no CCC.' };
    }
    const url = `${cccBase}/api/ccc/prospeccao/leads/${encodeURIComponent(prospeccaoLeadId)}/contract/files`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        success: false,
        error: (json as { message?: string; error?: string }).message || (json as { error?: string }).error,
        message: (json as { message?: string }).message,
      };
    }
    return json;
  }
  if (useCccRecuperacaoContract) {
    if (!token) {
      return { success: false, error: 'Sessão expirada. Volte a iniciar sessão no CCC.' };
    }
    const url = `${cccBase}/api/ccc/recuperacao/marcacoes/${encodeURIComponent(marcacaoId)}/contract/files`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        success: false,
        error: (json as { message?: string; error?: string }).message || (json as { error?: string }).error,
        message: (json as { message?: string }).message,
      };
    }
    return json;
  }
  return apiService.uploadProposalFile(marcacaoId, formData);
}

function embeddedContractSubmissionMeta(
  embeddedContractOnly: boolean,
  embeddedProspeccaoLeadId: string | null | undefined
): { submittedFromCcc: true; submittedBySource: 'ccc_prospeccao' | 'ccc_recuperacao' } | Record<string, never> {
  if (embeddedProspeccaoLeadId) {
    return { submittedFromCcc: true, submittedBySource: 'ccc_prospeccao' };
  }
  if (embeddedContractOnly) {
    return { submittedFromCcc: true, submittedBySource: 'ccc_recuperacao' };
  }
  return {};
}

async function persistEmbeddedContractData(
  marcacaoId: string,
  contractData: Record<string, unknown>,
  embeddedContractOnly: boolean,
  embeddedProspeccaoLeadId: string | null | undefined
): Promise<void> {
  const token = readEmbeddedCccAuthToken();
  const cccBase = resolveCccApiBaseUrl();

  if ((embeddedProspeccaoLeadId || embeddedContractOnly) && !token) {
    throw new Error('Sessão expirada. Volte a iniciar sessão no CCC.');
  }

  if (embeddedProspeccaoLeadId) {
    const res = await fetch(
      `${cccBase}/api/ccc/prospeccao/leads/${encodeURIComponent(embeddedProspeccaoLeadId)}/contract`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ contractData }),
      }
    );
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error((error as { message?: string }).message || 'Erro ao salvar dados do contrato');
    }
    const json = await res.json();
    if (!json.success) {
      throw new Error(json.message || 'Erro ao salvar dados do contrato');
    }
    return;
  }

  if (embeddedContractOnly) {
    const res = await fetch(
      `${cccBase}/api/ccc/recuperacao/marcacoes/${encodeURIComponent(marcacaoId)}/contract`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ contractData }),
      }
    );
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error((error as { message?: string }).message || 'Erro ao salvar dados do contrato');
    }
    const json = await res.json();
    if (!json.success) {
      throw new Error(json.message || 'Erro ao salvar dados do contrato');
    }
    return;
  }

  const backendUrl = resolveMainApiBaseUrl();
  const patchResponse = await fetch(`${backendUrl}/api/callcenter/calendar/marcacoes/${marcacaoId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      form_data: { contractData: [contractData] },
    }),
  });

  if (!patchResponse.ok) {
    throw new Error('Erro ao salvar dados do contrato');
  }
}

export interface ComercialManagementProps {
  /** CCC Recuperação: same Gestão UI, data from /api/ccc/recuperacao/marcacoes */
  recuperacaoMode?: boolean;
  /** CCC lead panel: only Solicitar Proposta modal (no Gestão board). */
  embeddedProposalOnly?: boolean;
  /** When embeddedProposalOnly: solicitar (default) or refazer completed request. */
  embeddedProposalMode?: 'solicitar' | 'refazer';
  embeddedProposalMarcacaoId?: string | null;
  /** CCC Prospeção: submit proposal to /api/ccc/prospeccao/leads/:id/proposal instead of CallCenter marcação. */
  embeddedProspeccaoLeadId?: string | null;
  /** CCC Reposição clients board: submit to /api/ccc/retencao/clients/:id/proposal. */
  embeddedRetencaoClientId?: string | null;
  /**
   * When embeddedProposalOnly without prospeccao lead: which CCC marcação workspace API to use.
   * Defaults to recuperacao for backward compatibility.
   */
  embeddedCccWorkspace?: 'recuperacao' | 'retencao';
  embeddedMarcacaoSeed?: Record<string, Marcacao>;
  onEmbeddedProposalClose?: () => void;
  onEmbeddedProposalSuccess?: () => void;
  /** CCC Prospeção: Submeter Contrato flow only (Gestão das Marcações contract modals). */
  embeddedContractOnly?: boolean;
  embeddedContractMarcacaoId?: string | null;
  /** When set, contract file upload + persist use CCC API (same auth as cccApi). */
  embeddedContractApi?: {
    uploadFile: (
      formData: FormData
    ) => Promise<{ success: boolean; file?: Record<string, unknown>; error?: string; message?: string }>;
    persist: (contractData: Record<string, unknown>) => Promise<void>;
  };
  onEmbeddedContractClose?: () => void;
  onEmbeddedContractSuccess?: () => void;
  /** CCC Recuperação: only Minhas Propostas / Meus Contratos modals (Gestão Comercial APIs). */
  gestaoModalsOnly?: boolean;
  /** When true with gestaoModalsOnly, fetch from /api/ccc/recuperacao/gestao/* (claimed Recuperação clients). */
  gestaoCccRecuperacao?: boolean;
  autoOpenModal?: 'minhas-propostas' | 'meus-contratos' | null;
  onGestaoModalsClose?: () => void;
  /** CCC Prospeção / Recuperação: filter modals by workspace-selected team member. */
  gestaoFilterUserId?: number | null;
  gestaoFilterUserLabel?: string;
  /** CCC Prospeção / embedded hosts: Pedir Assinatura Digital modal only. */
  embeddedSignatureOnly?: boolean;
  onEmbeddedSignatureClose?: () => void;
}

const ComercialManagement: React.FC<ComercialManagementProps> = ({
  recuperacaoMode = false,
  embeddedProposalOnly = false,
  embeddedProposalMode = 'solicitar',
  embeddedProposalMarcacaoId = null,
  embeddedProspeccaoLeadId = null,
  embeddedRetencaoClientId = null,
  embeddedCccWorkspace = 'recuperacao',
  embeddedMarcacaoSeed,
  onEmbeddedProposalClose,
  onEmbeddedProposalSuccess,
  embeddedContractOnly = false,
  embeddedContractMarcacaoId = null,
  embeddedContractApi,
  onEmbeddedContractClose,
  onEmbeddedContractSuccess,
  gestaoModalsOnly = false,
  gestaoCccRecuperacao = false,
  autoOpenModal = null,
  onGestaoModalsClose,
  gestaoFilterUserId = null,
  gestaoFilterUserLabel,
  embeddedSignatureOnly = false,
  onEmbeddedSignatureClose,
}) => {
  const { refreshCalendarData } = useApp();
  
  const [currentSection, setCurrentSection] = useState<Section>('agendamentos');
  const [currentMarcacaoId, setCurrentMarcacaoId] = useState<string | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isCallCenterInfoCollapsed, setIsCallCenterInfoCollapsed] = useState(false);
  const [isComercialCurrentCollapsed, setIsComercialCurrentCollapsed] = useState(false);
  const [isComercialInfoCollapsed, setIsComercialInfoCollapsed] = useState(false);
  const [editComercialInfo, setEditComercialInfo] = useState<Record<string, any>>({});
  const [savingComercialInfo, setSavingComercialInfo] = useState(false);
  const [isRescheduleReasonCollapsed, setIsRescheduleReasonCollapsed] = useState(false);
  const [isProposalInfoCollapsed, setIsProposalInfoCollapsed] = useState(true); // Collapsed by default
  const [showEditModal, setShowEditModal] = useState(false);
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [proposalModalMode, setProposalModalMode] = useState<'solicitar' | 'refazer'>('solicitar');
  const [selectedRefazerProposalId, setSelectedRefazerProposalId] = useState<string | null>(null);
  const [sharedComercialUserIds, setSharedComercialUserIds] = useState<number[]>([]);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [searchInProgress, setSearchInProgress] = useState(false);
  const [highlightedMarcacaoId, setHighlightedMarcacaoId] = useState<string | null>(null);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [marcacaoToRevert, setMarcacaoToRevert] = useState<string | null>(null);
  const [revertTarget, setRevertTarget] = useState<'agendamentos' | 'reuniao-realizada'>('agendamentos');
  // Change assigned comercial (Admin/Gerente only) - select mode
  const [changeComercialSelectMode, setChangeComercialSelectMode] = useState(false);
  const [selectedMarcacoesForChange, setSelectedMarcacoesForChange] = useState<Set<string>>(new Set());
  const [showChangeComercialModal, setShowChangeComercialModal] = useState(false);
  const [changeComercialSelectedUserId, setChangeComercialSelectedUserId] = useState<string>('');
  const [savingChangeComercial, setSavingChangeComercial] = useState(false);
  const [showFeedbackValidationModal, setShowFeedbackValidationModal] = useState(false);
  const [marcacoesWithoutFeedback, setMarcacoesWithoutFeedback] = useState<string[]>([]);
  const [highlightedSubsections, setHighlightedSubsections] = useState<Record<string, boolean>>({});
  const [highlightedMarcacoesForFeedback, setHighlightedMarcacoesForFeedback] = useState<Set<string>>(new Set());
  const [showNotOkCallCenterFeedbackModal, setShowNotOkCallCenterFeedbackModal] = useState(false);
  const [notOkCallCenterFeedbackMarcacaoId, setNotOkCallCenterFeedbackMarcacaoId] = useState<string | null>(null);
  const [notOkCallCenterFeedbackOptions, setNotOkCallCenterFeedbackOptions] = useState<Record<string, { selected: boolean; text: string }>>({
    data: { selected: false, text: '' },
    horario: { selected: false, text: '' },
    contacto: { selected: false, text: '' },
    linkGoogleMaps: { selected: false, text: '' },
    informacoesCallCenter: { selected: false, text: '' },
    outro: { selected: false, text: '' }
  });
  const [showSubmitContractModal, setShowSubmitContractModal] = useState(false);
  const [marcacaoToSubmitContract, setMarcacaoToSubmitContract] = useState<string | null>(null);
  const [showSolarContractForm, setShowSolarContractForm] = useState(false);
  const [isSubmittingSolarContract, setIsSubmittingSolarContract] = useState(false);
  const [showEnergyGasContractForm, setShowEnergyGasContractForm] = useState(false);
  const [isSubmittingEnergyGasContract, setIsSubmittingEnergyGasContract] = useState(false);
  const [isSubmittingProposal, setIsSubmittingProposal] = useState(false);
  // Instant Marcação states
  const [showInstantMarcacaoModal, setShowInstantMarcacaoModal] = useState(false);
  const [currentInstantMarcacaoStep, setCurrentInstantMarcacaoStep] = useState(1);
  const [instantMarcacaoFormData, setInstantMarcacaoFormData] = useState<any>({});
  const [instantMarcacaoValidationErrors, setInstantMarcacaoValidationErrors] = useState<any>({});
  const [instantMarcacaoNotification, setInstantMarcacaoNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [savingInstantMarcacao, setSavingInstantMarcacao] = useState(false);
  const [isInstantMarcacao, setIsInstantMarcacao] = useState(false); // Flag to track if this is an instant marcação
  // Minhas Propostas modal states
  const [showMinhasPropostasModal, setShowMinhasPropostasModal] = useState(false);
  const [proposalRequests, setProposalRequests] = useState<any[]>([]);
  const [loadingProposalRequests, setLoadingProposalRequests] = useState(false);
  const [currentProposalPage, setCurrentProposalPage] = useState(1);
  const [expandedProposalCardId, setExpandedProposalCardId] = useState<string | null>(null);
  const proposalsPerPage = 3;

  // Gestão das Marcações sub-sections: 10 marcações per page
  const MARCADOES_PER_PAGE = 10;
  const [subsectionCurrentPage, setSubsectionCurrentPage] = useState<Record<string, number>>({
    'agendamentos-agendamentos': 1,
    'agendamentos-remarcacao': 1,
    'reuniao-realizada-reuniao': 1,
    'feedback-pendencia-concluido-sem-proposta': 1,
    'feedback-pendencia-concluido-com-proposta': 1,
    'feedback-pendencia-not-ok-cliente': 1,
    'feedback-pendencia-not-ok-callcenter': 1,
    'feedback-pendencia-pendente': 1,
    'resultado-fecho-ok': 1,
    'resultado-fecho-not-ok': 1,
    'resultado-aguardar': 1,
    'resultado-cancelado': 1,
  });
  
  // Search and filter states for Minhas Propostas modal
  const [proposalSearchTerm, setProposalSearchTerm] = useState('');
  const [filterDay, setFilterDay] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterServiceType, setFilterServiceType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSection, setFilterSection] = useState<string>(''); // Backoffice sections: available | roadtrip | op_criada | in_progress | proposta_aprovacao | exclusividade_pendente | pendente_gestor_edp | pendente_comercial | completed
  const [filteredProposalRequests, setFilteredProposalRequests] = useState<any[]>([]);
  
  // Meus Contratos modal states
  const [showMeusContratosModal, setShowMeusContratosModal] = useState(false);
  const [contractSubmissions, setContractSubmissions] = useState<any[]>([]);
  const [loadingContractSubmissions, setLoadingContractSubmissions] = useState(false);
  const [currentContractPage, setCurrentContractPage] = useState(1);
  const [expandedContractCardId, setExpandedContractCardId] = useState<string | null>(null);
  const contractsPerPage = 3;
  
  // Search and filter states for Meus Contratos modal
  const [contractTypeTab, setContractTypeTab] = useState<'energia_gas' | 'solar'>('energia_gas');
  const [contractSearchTerm, setContractSearchTerm] = useState('');
  const [contractFilterDay, setContractFilterDay] = useState('');
  const [contractFilterMonth, setContractFilterMonth] = useState('');
  const [contractFilterYear, setContractFilterYear] = useState('');
  const [contractFilterServiceType, setContractFilterServiceType] = useState('');
  const [filteredContractSubmissions, setFilteredContractSubmissions] = useState<any[]>([]);

  useEffect(() => {
    setExpandedProposalCardId(null);
  }, [currentProposalPage, showMinhasPropostasModal]);

  useEffect(() => {
    setExpandedContractCardId(null);
  }, [currentContractPage, showMeusContratosModal]);
  
  // Pedir Assinatura Digital (Comercial) modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureRequests, setSignatureRequests] = useState<any[]>([]);
  const [loadingSignatureRequests, setLoadingSignatureRequests] = useState(false);
  const [signatureFilter, setSignatureFilter] = useState<'pending' | 'all'>('pending');
  const [signatureForm, setSignatureForm] = useState({
    company_name: '',
    nif: '',
    iban: '',
    bic: '',
    representativeName: '',
    representativeEmail: '',
    representativeContact: '',
    expectedDate: '',
    expectedTime: '',
    associatedMarcacaoId: '',
  });
  const [submittingSignature, setSubmittingSignature] = useState(false);
  const [signatureNotification, setSignatureNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [associateSearchQuery, setAssociateSearchQuery] = useState('');
  
  // Submeter Contrato: assigned proposal requests (multi-select, similar to Assinatura Digital)
  const [contractSelectedProposalIds, setContractSelectedProposalIds] = useState<string[]>([]);
  const [contractProposalSearchQuery, setContractProposalSearchQuery] = useState('');
  
  const [solarContractData, setSolarContractData] = useState({
    urgente: false,
    tipoServico: '',
    numeroPaineis: '',
    nomeEmpresa: '',
    moradaInstalacao: '',
    distrito: '',
    nifEmpresa: '',
    emailEmpresa: '',
    nomeRepresentante: '',
    nifRepresentanteLegal: '',
    contactoResponsavel: '',
    emailRepresentanteLegal: '',
    cpe: '',
    iban: '',
    potenciaContratada: '',
    idPropostaS2C: '',
    valorProposta: '',
    codigoCertidaoPermanente: '',
    nomeComercial: '',
    codigoComercial: '',
    vyn: '',
    documentos: {
      contratoAssinado: [] as File[],
      contratoAssinadoDigital: false,
      teaserS2C: [] as File[],
      cc: [] as File[],
      crcOuInicioAtividade: [] as File[],
      fatura: [] as File[],
      documentosAdicionais: [] as File[]
    }
  });
  const [solarContractFormErrors, setSolarContractFormErrors] = useState<Record<string, string>>({});
  const createEmptyEnergyGasContractData = () => ({
    urgente: false,
    angariacao: {
      validaAteDia: '',
      idProposta: '',
      iban: ''
    },
    // Product-specific blocks (CPE/CUI lists + datas). Empty block is omitted from the BO contract.
    ee: {
      cpes: [''],
      dataInicioFornecimento: '',
      dataFimFornecimento: ''
    },
    gas: {
      cuis: [''],
      dataInicioFornecimento: '',
      dataFimFornecimento: ''
    },
    documentos: {
      contratoAssinado: [] as File[],
      contratoAssinadoDigital: false,
      cartaoCidadao: [] as File[],
      faturaAtual: [] as File[],
      ibanComprovativo: [] as File[],
      comprovativoTitularidadeConta: [] as File[],
      inicioAtividade: '',
      declaracaoInicioAtividade: [] as File[],
      documentosAdicionais: [] as File[]
    }
  });
  const [energyGasContractData, setEnergyGasContractData] = useState(createEmptyEnergyGasContractData);
  const [energyGasEeExpanded, setEnergyGasEeExpanded] = useState(true);
  const [energyGasGasExpanded, setEnergyGasGasExpanded] = useState(true);
  const [contractFormErrors, setContractFormErrors] = useState<Record<string, string>>({});
  const [lastUpdateTimestamp, setLastUpdateTimestamp] = useState<Date | null>(null);
  const [dateFilter, setDateFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState<string | null>(null);
  const [recuperacaoAssigneeFilter, setRecuperacaoAssigneeFilter] = useState('');
  const [recuperacaoStats, setRecuperacaoStats] = useState<{
    total: number;
    with_deleted_or_suspended: number;
    with_hidden_assignee?: number;
  } | null>(null);
  const [showRecentMarcacoes, setShowRecentMarcacoes] = useState(!recuperacaoMode);
  const [expandedSubsections, setExpandedSubsections] = useState<Record<string, boolean>>({
    'agendamentos-agendamentos': false,
    'agendamentos-remarcacao': false,
    'reuniao-realizada-reuniao': false,
    'feedback-pendencia-concluido-sem-proposta': false,
    'feedback-pendencia-concluido-com-proposta': false,
    'feedback-pendencia-not-ok-cliente': false,
    'feedback-pendencia-not-ok-callcenter': false,
    'feedback-pendencia-pendente': false,
    'resultado-fecho-ok': false,
    'resultado-fecho-not-ok': false,
    'resultado-aguardar': false,
    'resultado-cancelado': false,
  });
  const [currentProposalStep, setCurrentProposalStep] = useState(1);
  const [proposalNotesPasteText, setProposalNotesPasteText] = useState('');
  const [proposalNotesImportErrors, setProposalNotesImportErrors] = useState<string[]>([]);
  const [proposalNotesImportWarnings, setProposalNotesImportWarnings] = useState<string[]>([]);
  const [proposalNotesImportBanner, setProposalNotesImportBanner] = useState(false);
  const [proposalNotesPasteOpen, setProposalNotesPasteOpen] = useState(false);
  const proposalNotesFileInputRef = useRef<HTMLInputElement | null>(null);
  const [proposalData, setProposalData] = useState<any>({
    equipaInterna: {
      nomeUsuario: '',
      codigoComercial: '',
      equipa: '',
      emailUsuario: '',
      propostaMarcacao: '',
      assinaturaContrato: '',
      tipoPedido: 'normal'
    },
    empresa: {
      nomeEmpresa: '',
      nifEmpresa: '',
      moradaFornecimento: '',
      moradaCorrespondencia: '',
      regiao: '',
      cae: '',
      nomeRepresentante: '',
      contactoRepresentante: '',
      emailRepresentante: '',
      emailEmpresa: ''
    },
    servico: {},
    resumo: {},
    servicos: [], // Array of service instances: [{ service: 'bairro-solar', instanceNumber: 1 }, ...]
    serviceQuantities: { // Track quantities for each service type: { 'bairro-solar': 2, 'pap': 1 }
      'bairro-solar': 0,
      'pap': 0,
      'transacional': 0,
      'luz': 0,
      'gas': 0,
      'mobilidade': 0,
      'outro': 0
    },
    serviceDetails: {}
  });
  const [expandedServiceSegments, setExpandedServiceSegments] = useState<Record<string, boolean>>({});

  const [marcacoesData, setMarcacoesData] = useState<Record<string, Marcacao>>({});
  const [isLoadingMarcacoes, setIsLoadingMarcacoes] = useState(false);
  const [proposalsData, setProposalsData] = useState<Record<string, any[]>>({}); // Store proposals by marcação ID
  const [currentUserId, setCurrentUserId] = useState<number | null>(() =>
    gestaoModalsOnly ? readGestaoUserIdFromStorage() : null
  );
  const [userMap, setUserMap] = useState<Record<number, string>>({}); // Map user ID to username
  const [currentUserData, setCurrentUserData] = useState<any>(null); // Store full current user data
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const fetchMarcacoesRef = useRef<(() => Promise<void>) | null>(null); // Store latest fetchMarcacoes function
  const fetchMarcacoesAbortRef = useRef<AbortController | null>(null);
  const fetchMarcacoesGenRef = useRef(0);
  const embeddedProposalOpenRef = useRef(false);
  
  // Proposals available for the marcação submitting the contract (filtered to this marcação only)
  // Only "Concluído" proposals (completed by BackOffice) are shown; merged with BackOffice service_completion_data
  const contractProposalsForMarcacao = useMemo(() => {
    if (!marcacaoToSubmitContract || (!showSolarContractForm && !showEnergyGasContractForm)) return [];
    const marcacao = resolveMarcacaoInStore(marcacoesData, marcacaoToSubmitContract);
    return buildCompletedProposalsForMarcacao(marcacao);
  }, [marcacaoToSubmitContract, marcacoesData, showSolarContractForm, showEnergyGasContractForm]);

  const refazerCompletedProposals = useMemo(() => {
    if (!showProposalModal || proposalModalMode !== 'refazer' || !currentMarcacaoId) return [];
    return buildCompletedProposalsForMarcacao(
      resolveMarcacaoInStore(marcacoesData, currentMarcacaoId)
    );
  }, [showProposalModal, proposalModalMode, currentMarcacaoId, marcacoesData]);

  const signatureAssociateOptions = useMemo(() => {
    const marcacoes = Object.values(marcacoesData).filter((m: any) => {
      if (m.creator_is_hidden) return false;
      if (embeddedSignatureOnly && !marcacaoHasSubmittedProposal(m)) return false;
      return true;
    });
    return buildSignatureAssociateOptions(marcacoes);
  }, [marcacoesData, embeddedSignatureOnly]);
  
  // Initialize contractSelectedProposalIds when opening Solar or EE/Gas contract form (only when none selected yet)
  useEffect(() => {
    if (!showSolarContractForm && !showEnergyGasContractForm) {
      setContractSelectedProposalIds([]);
      setContractProposalSearchQuery('');
    } else if ((showSolarContractForm || showEnergyGasContractForm) && marcacaoToSubmitContract && contractProposalsForMarcacao.length > 0 && contractSelectedProposalIds.length === 0) {
      const cccIds = contractProposalsForMarcacao.filter((p) => p.isCcc).map((p) => p.id);
      const defaultIds =
        embeddedContractOnly && cccIds.length > 0
          ? cccIds
          : contractProposalsForMarcacao.map((p) => p.id);
      setContractSelectedProposalIds(defaultIds);
      setContractProposalSearchQuery('');
    }
  }, [
    showSolarContractForm,
    showEnergyGasContractForm,
    marcacaoToSubmitContract,
    contractProposalsForMarcacao,
    embeddedContractOnly,
  ]);
  
  // Auto-fill Solar Contract form from selected proposals
  useEffect(() => {
    if (showSolarContractForm && marcacaoToSubmitContract) {
      const marcacao = resolveMarcacaoInStore(marcacoesData, marcacaoToSubmitContract);
      if (!marcacao) return;
      const proposalsToUse = contractSelectedProposalIds.length > 0
        ? contractProposalsForMarcacao.filter((p) => contractSelectedProposalIds.includes(p.id)).map((p) => p.proposal)
        : contractProposalsForMarcacao.map((p) => p.proposal);
      if (proposalsToUse.length === 0 && contractProposalsForMarcacao.length > 0) return; // User deselected all
      let servicos: any[] = [];
      let serviceDetails: any = {};
      let empresa: any = {};
      let proposalData: any = proposalsToUse[proposalsToUse.length - 1] || {};
      proposalsToUse.forEach((proposal: any) => {
        if (proposal?.servicos && Array.isArray(proposal.servicos)) servicos.push(...proposal.servicos);
        if (proposal?.serviceDetails) serviceDetails = { ...serviceDetails, ...proposal.serviceDetails };
      });
      const lastProposal = proposalsToUse[proposalsToUse.length - 1] || {};
      empresa = lastProposal.empresa || {};
      let solarInstance: any = null;
      let solarInstanceDetails: any = null;
      for (const instance of servicos) {
        if (instance.service === 'bairro-solar' || instance.service === 'pap' || instance.service === 'transacional') {
          solarInstance = instance;
          solarInstanceDetails = serviceDetails[instance.instanceId] || {};
          break;
        }
      }
      const initialTipoServico: string = solarInstance?.service === 'bairro-solar' ? 'BS' :
        solarInstance?.service === 'pap' ? 'PAP' :
        solarInstance?.service === 'transacional' ? 'PP' : '';
      let initialCpe = '';
      if (solarInstance) {
        const instanceDetails = serviceDetails[solarInstance.instanceId] || {};
        initialCpe = instanceDetails.cpe || instanceDetails.instalacao?.cpe || '';
      }
      setSolarContractData((prev) => ({
        ...prev,
        tipoServico: initialTipoServico || prev.tipoServico,
        // Prefer user's current value over proposal data to avoid overwriting edits (e.g. user types 10 but proposal had 9)
        numeroPaineis: prev.numeroPaineis || solarInstanceDetails?.numeroPaineis || solarInstanceDetails?.instalacao?.numeroPaineis,
        nomeEmpresa: empresa.nomeEmpresa || marcacao.nome_empresa || marcacao.empresa || prev.nomeEmpresa,
        moradaInstalacao: empresa.moradaFornecimento || marcacao.morada || prev.moradaInstalacao,
        distrito: empresa.regiao || prev.distrito,
        nifEmpresa: empresa.nifEmpresa || prev.nifEmpresa,
        emailEmpresa: empresa.emailEmpresa || prev.emailEmpresa,
        nomeRepresentante: empresa.nomeRepresentante || marcacao.nome_responsavel || marcacao.responsavel || prev.nomeRepresentante,
        nifRepresentanteLegal: empresa.nifRepresentanteLegal || empresa.nifEmpresa || prev.nifRepresentanteLegal,
        contactoResponsavel: empresa.contactoRepresentante || marcacao.contacto || marcacao.contacto_cliente || prev.contactoResponsavel,
        emailRepresentanteLegal: empresa.emailRepresentante || prev.emailRepresentanteLegal,
        cpe: initialCpe || prev.cpe,
        iban: solarInstanceDetails?.iban || empresa?.iban || prev.iban,
        potenciaContratada: prev.potenciaContratada,
        idPropostaS2C: solarInstanceDetails?.idPropostaS2C || solarInstanceDetails?.propostaId || prev.idPropostaS2C,
        valorProposta: solarInstanceDetails?.valorProposta || solarInstanceDetails?.investimento || marcacao.valorFatura || prev.valorProposta,
        codigoCertidaoPermanente: solarInstanceDetails?.codigoCertidaoPermanente || empresa.cae || prev.codigoCertidaoPermanente,
        nomeComercial: resolveContractNomeComercial({
          currentUserData,
          currentUserId,
          userMap,
          proposalEquipaInterna: proposalData?.equipaInterna,
          fallback: prev.nomeComercial,
        }),
        codigoComercial: resolveContractCodigoComercial({
          currentUserData,
          proposalEquipaInterna: proposalData?.equipaInterna,
          fallback: prev.codigoComercial,
        }),
        vyn: prev.vyn
      }));
    }
  }, [showSolarContractForm, marcacaoToSubmitContract, marcacoesData, currentUserData, currentUserId, userMap, contractSelectedProposalIds, contractProposalsForMarcacao]);
  
  // Auto-fill EE/Gas Contract form from selected proposals (shared + CPE/CUI per product)
  useEffect(() => {
    if (showEnergyGasContractForm && marcacaoToSubmitContract) {
      const marcacao = resolveMarcacaoInStore(marcacoesData, marcacaoToSubmitContract);
      if (!marcacao) return;
      const proposalsToUse = contractSelectedProposalIds.length > 0
        ? contractProposalsForMarcacao.filter((p) => contractSelectedProposalIds.includes(p.id)).map((p) => p.proposal)
        : contractProposalsForMarcacao.map((p) => p.proposal);
      if (proposalsToUse.length === 0 && contractProposalsForMarcacao.length > 0) return;
      let empresa: any = {};
      let idProposta = '';
      let iban = '';
      let dataValidade = '';
      const cpeList: string[] = [];
      const cuiList: string[] = [];
      let hasEeService = false;
      let hasGasService = false;
      proposalsToUse.forEach((proposal: any) => {
        const emp = proposal?.empresa || {};
        empresa = { ...empresa, ...emp };
        const servicos = proposal?.servicos || [];
        const serviceDetails = proposal?.serviceDetails || {};
        for (const s of servicos) {
          const serviceType = String(s?.service || '').toLowerCase();
          if (!['luz', 'gas', 'eletricidade'].includes(serviceType)) continue;
          const details = serviceDetails[s.instanceId] || serviceDetails[String(s.instanceId)] || {};
          if (details.idProposta) idProposta = idProposta || details.idProposta;
          if (details.iban) iban = iban || details.iban;
          const dv = details.dataValidade || details.validade;
          if (dv) dataValidade = dataValidade || dv;
          if (serviceType === 'luz' || serviceType === 'eletricidade') {
            hasEeService = true;
            const cpeVal = String(details.cpe || details.CPE || details.instalacao?.cpe || '').trim();
            if (cpeVal && !cpeList.includes(cpeVal)) cpeList.push(cpeVal);
          }
          if (serviceType === 'gas') {
            hasGasService = true;
            const cuiVal = String(details.cui || details.CUI || details.instalacao?.cui || '').trim();
            if (cuiVal && !cuiList.includes(cuiVal)) cuiList.push(cuiVal);
          }
        }
      });
      // Convert YYYY-MM-DD to DD/MM/YYYY for consistency with other date fields
      const toDDMMYYYY = (s: string) => {
        if (!s) return s;
        if (s.includes('-')) {
          const [y, m, d] = s.split('-');
          return `${d}/${m}/${y}`;
        }
        return s;
      };
      const lastProposal = proposalsToUse[proposalsToUse.length - 1] || {};
      empresa = lastProposal.empresa || empresa;
      const cpeJoined = cpeList.join('_');
      const cuiJoined = cuiList.join('_');
      if (hasEeService || cpeList.length > 0) setEnergyGasEeExpanded(true);
      if (hasGasService || cuiList.length > 0) setEnergyGasGasExpanded(true);
      setEnergyGasContractData((prev: any) => ({
        ...prev,
        angariacao: {
          ...prev.angariacao,
          idProposta: idProposta || prev.angariacao.idProposta,
          iban: iban || empresa?.iban || prev.angariacao.iban,
          validaAteDia: toDDMMYYYY(dataValidade) || prev.angariacao.validaAteDia
        },
        ee: {
          ...prev.ee,
          // Keep every CPE from associated request(s); allow empty slot if none yet
          cpes: cpeList.length > 0 ? cpeList : (Array.isArray(prev.ee?.cpes) && prev.ee.cpes.length ? prev.ee.cpes : [''])
        },
        gas: {
          ...prev.gas,
          cuis: cuiList.length > 0 ? cuiList : (Array.isArray(prev.gas?.cuis) && prev.gas.cuis.length ? prev.gas.cuis : [''])
        }
      }));
    }
  }, [showEnergyGasContractForm, marcacaoToSubmitContract, marcacoesData, contractSelectedProposalIds, contractProposalsForMarcacao]);
  
  // Sidebar states for Admin and Gerente
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Touch-capable devices (iPad/tablets) should behave like mobile layouts.
  const [isMobileOrTablet] = useState(() => {
    if (typeof window === 'undefined') return false;
    const hasTouch =
      (typeof navigator !== 'undefined' && (navigator.maxTouchPoints || 0) > 0)
      || 'ontouchstart' in window;
    // iPads/tablets can be wider than 768px, so allow a larger cutoff.
    const isTabletWidth = window.innerWidth <= 1100;
    const isSmallScreen = window.matchMedia('(max-width: 768px)').matches;
    return hasTouch && (isTabletWidth || isSmallScreen);
  });
  const [sidebarSearchTerm, setSidebarSearchTerm] = useState('');
  const [selectedSidebarUser, setSelectedSidebarUser] = useState<number | null>(null);
  const [comercialUsers, setComercialUsers] = useState<any[]>([]);

  const shareableComercialUserOptions = useMemo(() => {
    const selfId = Number(currentUserId || 0);
    return (comercialUsers || [])
      .filter(
        (u: any) =>
          u?.id != null && Number(u.id) > 0 && Number(u.id) !== selfId
      )
      .slice()
      .sort((a: any, b: any) =>
        String(a.username || a.name || '').localeCompare(
          String(b.username || b.name || ''),
          'pt',
          { sensitivity: 'base' }
        )
      )
      .map((u: any) => {
        const username = u.username || u.name || u.email || `ID ${u.id}`;
        const email = u.email || '';
        const team = u.team_name || u.team || u.equipa || '';
        return {
          value: Number(u.id),
          username,
          label: `${username}${email ? ` — ${email}` : ''}${team ? ` (${team})` : ''}`,
        };
      });
  }, [comercialUsers, currentUserId]);

  // Fetch current user ID and comercial users
  useEffect(() => {
    if (embeddedContractOnly || embeddedProposalOnly || gestaoModalsOnly || embeddedSignatureOnly) {
      const sessionUser = readStoredSessionUser();
      if (sessionUser) {
        const sessionId = Number(sessionUser.id || sessionUser.user_id || 0);
        if (sessionId > 0) setCurrentUserId(sessionId);
        setCurrentUserData((prev: any) => ({
          ...prev,
          name: prev?.name || String(sessionUser.name || sessionUser.username || ''),
          username: prev?.username || String(sessionUser.username || sessionUser.name || ''),
          email: prev?.email || String(sessionUser.email || ''),
          codigoComercial:
            prev?.codigoComercial ||
            String(sessionUser.codigoComercial || sessionUser.codigo_comercial || ''),
          team:
            prev?.team ||
            String(sessionUser.team || sessionUser.team_name || sessionUser.equipa || ''),
          role: prev?.role || String(sessionUser.role || ''),
        }));
      }
    }

    const fetchCurrentUser = async () => {
      try {
        const user = await apiService.getCurrentUser();
        if (user && user.id) {
          setCurrentUserId(user.id);
          const userRecord = user as unknown as Record<string, unknown>;
          const displayName = String(
            userRecord.display_name || userRecord.name || user.username || ''
          ).trim();
          setCurrentUserData((prev: any) => ({
            ...prev,
            name: displayName || prev?.name || '',
            username: user.username || displayName || prev?.username || '',
            email: user.email || prev?.email || '',
            codigoComercial:
              String(userRecord.codigoComercial || userRecord.codigo_comercial || '').trim() ||
              prev?.codigoComercial ||
              '',
            role: user.role || prev?.role || '',
            team:
              String(userRecord.team || userRecord.team_name || '').trim() || prev?.team || '',
            team_name:
              String(userRecord.team_name || userRecord.team || '').trim() ||
              prev?.team_name ||
              '',
            tenant:
              String(userRecord.tenant || '').trim() ||
              prev?.tenant ||
              localStorage.getItem('user_tenant') ||
              'comercial',
            is_hidden: Boolean(userRecord.is_hidden ?? prev?.is_hidden ?? false),
          }));
          return user.id;
        }
      } catch (error) {
      }
      return null;
    };

    const fetchComercialUsers = async (userId: number | null) => {
      try {
        const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
        const baseUrl = resolveMainApiBaseUrl();
        const usersQuery = recuperacaoMode ? '?include_hidden=1' : '';
        const url = `${baseUrl}/api/callcenter/comercial-users${usersQuery}`;
        
        const res = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          }
        });

        if (!res.ok) {
          return;
        }

        // Check if response is JSON
        const contentType = res.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await res.text();
          // Don't return early - try to parse anyway in case content-type header is wrong
          try {
            const json = JSON.parse(text);
            // If parsing succeeds, continue
          } catch (e) {
            return;
          }
        }

        const json = await res.json();
        const users = Array.isArray(json?.comercial_users) ? json.comercial_users : [];
        
        // Create map of user ID to username
        const map: Record<number, string> = {};
        users.forEach((u: any) => {
          if (u.id && (u.username || u.name)) {
            map[u.id] = u.username || u.name || 'Unknown';
          }
        });
        
        setUserMap(map);
        
        // Store current user's full data
        if (userId) {
          const currentUser = users.find((u: any) => u.id === userId);
          if (currentUser) {
            setCurrentUserData({
              name: currentUser.name || currentUser.username || '',
              email: currentUser.email || '',
              codigoComercial: currentUser.codigoComercial || currentUser.codigo_comercial || '',
              team: currentUser.team || currentUser.team_name || currentUser.equipa || '',
              team_name: currentUser.team_name || currentUser.team || currentUser.equipa || '',
              role: currentUser.role || '',
              tenant: currentUser.tenant || localStorage.getItem('user_tenant') || 'comercial',
              is_hidden: !!currentUser.is_hidden,
            });
          }
        }
        // Store all comercial users for sidebar
        setComercialUsers(users);
      } catch (error) {
      }
    };

    const init = async () => {
      const userId = await fetchCurrentUser();
      await fetchComercialUsers(userId);
    };

    init();
  }, [recuperacaoMode, embeddedContractOnly, embeddedProposalOnly, gestaoModalsOnly, embeddedSignatureOnly]);

  // Map backend marcação to frontend format
  const mapBackendMarcacao = (backendMarcacao: any, userMap: Record<number, string>): Marcacao => {
    const formData = backendMarcacao.form_data || {};
    const productDetails = backendMarcacao.product_details || {};
    
    // Format date from ISO to display format
    const formatDate = (dateStr?: string) => {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        const day = date.getDate();
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
      } catch {
        return dateStr;
      }
    };

    // Format time range
    const formatTimeRange = () => {
      if (backendMarcacao.hora_inicio && backendMarcacao.hora_fim) {
        return `${backendMarcacao.hora_inicio} - ${backendMarcacao.hora_fim}`;
      }
      return backendMarcacao.hora_inicio || backendMarcacao.hora || '-';
    };

    // Map produto from backend format to display format
    const mapProduto = (produto: string) => {
      const produtoMap: Record<string, string> = {
        'bairro-solar': 'Bairro Solar',
        'pap': 'PAP',
        'gas': 'Gás',
        'eletricidade': 'Eletricidade'
      };
      return produtoMap[produto] || produto;
    };

    // Handle multiple products from formData.produtos array
    const produtosArray = formData.produtos || (backendMarcacao.produto ? [backendMarcacao.produto] : []);
    const produtos = Array.isArray(produtosArray) ? produtosArray : [produtosArray].filter(Boolean);
    
    return {
      id: backendMarcacao.marcacao_id || backendMarcacao.id?.toString() || '',
      id_db: backendMarcacao.id,
      marcacao_id: backendMarcacao.marcacao_id,
      produto: mapProduto(backendMarcacao.produto || ''),
      produtos: produtos.map((p: string) => mapProduto(p)), // Array of mapped product names
      cliente: backendMarcacao.nome_responsavel || formData.nomeResponsavel || '',
      nome_responsavel: backendMarcacao.nome_responsavel,
      empresa: backendMarcacao.nome_empresa || formData.nomeEmpresa || '',
      nome_empresa: backendMarcacao.nome_empresa,
      responsavel: backendMarcacao.nome_responsavel || formData.nomeResponsavel || '-',
      data: formatDate(backendMarcacao.date || backendMarcacao.data),
      date: backendMarcacao.date,
      hora: formatTimeRange(),
      hora_inicio: backendMarcacao.hora_inicio,
      created_by: backendMarcacao.created_by,
      created_at: backendMarcacao.created_at,
      creator_is_hidden: !!backendMarcacao.creator_is_hidden,
      hora_fim: backendMarcacao.hora_fim,
      morada: backendMarcacao.morada || formData.morada || '',
      contacto: backendMarcacao.contacto_cliente || formData.contactoCliente || '',
      contacto_cliente: backendMarcacao.contacto_cliente,
      observacoes: backendMarcacao.marcacao_description || formData.marcacaoDescription || '',
      marcacao_description: backendMarcacao.marcacao_description,
      feedback: formData.feedback || '',
      gestao_substage: backendMarcacao.gestao_substage || 'agendamentos-agendamentos',
      form_data: backendMarcacao.form_data,
      product_details: backendMarcacao.product_details,
      estado_proposta: formData.estadoProposta || backendMarcacao.form_data?.estadoProposta || '',
      // Product-specific fields
      jaTemPaineis: productDetails.jaTemPaineis || formData.jaTemPaineis || '',
      potencia: productDetails.potencia || formData.potencia1035 || formData.potencia69 || '',
      trifasico: productDetails.trifasico || formData.trifasico || '',
      tipoTelhado: productDetails.tipoTelhado || formData.tipoTelhado || '',
      pagaMais250: productDetails.pagaMais250 || formData.pagaMais250 || '',
      faturaPresente: productDetails.faturaPresente || formData.faturaPresenteBairro || formData.faturaPresentePAP || formData.faturaPresenteGas || formData.faturaPresenteEletricidade || '',
      gasNatural: productDetails.gasNatural || formData.gasNatural || '',
      comercializadora: productDetails.comercializadora || formData.comercializadoraGas || formData.comercializadoraEletricidade || '',
      valorFatura: productDetails.valorFatura || formData.valorFaturaGas || formData.valorFaturaEletricidade || '',
      files: formData.files || []
    };
  };

  // Platform / tenant admin (incl. business_area admin, Cognito attributes)
  const isAdmin = () => {
    try {
      if (currentUserData) {
        const role = String(currentUserData.role || '').toLowerCase();
        const tenant = String((currentUserData as { tenant?: string }).tenant || '').toLowerCase();
        const businessArea = String((currentUserData as { business_area?: string }).business_area || '').toLowerCase();
        if (role.includes('admin') || tenant === 'admin' || businessArea === 'admin') {
          return true;
        }
      }

      const userRole = localStorage.getItem('userRole') || '';
      const tenant = localStorage.getItem('user_tenant') || '';
      const roleLower = userRole.toLowerCase();
      const tenantLower = tenant.toLowerCase();

      if (roleLower.includes('admin') || tenantLower === 'admin') {
        return true;
      }

      const cognitoUserRaw = localStorage.getItem('cognito_user');
      if (cognitoUserRaw) {
        const user = JSON.parse(cognitoUserRaw);
        const role = String(user?.role || '').toLowerCase();
        const cognitoTenant = String(user?.tenant || '').toLowerCase();

        if (role.includes('admin') || cognitoTenant === 'admin') {
          return true;
        }

        if (Array.isArray(user?.UserAttributes)) {
          const attrs = user.UserAttributes.reduce((acc: Record<string, string>, a: { Name: string; Value: string }) => {
            acc[a.Name] = a.Value;
            return acc;
          }, {});
          const attrTenant = String(attrs['custom:tenant'] || '').toLowerCase();
          const attrRole = String(attrs['custom:role'] || '').toLowerCase();

          if (attrTenant === 'admin' || attrRole.includes('admin')) {
            return true;
          }
        }
      }
    } catch (e) {
      console.error('Error checking if admin:', e);
    }

    return false;
  };

  // Admin, Gerente, Gestor, Assistente de Gestor, Instrutor (Gestão hierarchy)
  const isAdminOrGerenteComercial = () => {
    if (isAdmin()) return true;
    try {
      const tenant =
        (currentUserData as any)?.tenant ||
        localStorage.getItem('user_tenant') ||
        '';
      const role = (currentUserData as any)?.role || localStorage.getItem('userRole') || '';
      if (canViewComercialTeamHierarchy({ role, tenant, isAdmin: false })) {
        return true;
      }
      const cognitoUserRaw = localStorage.getItem('cognito_user');
      if (cognitoUserRaw) {
        const user = JSON.parse(cognitoUserRaw);
        if (
          canViewComercialTeamHierarchy({
            role: user?.role || '',
            tenant: user?.tenant || '',
            isAdmin: false,
          })
        ) {
          return true;
        }
        if (Array.isArray(user?.UserAttributes)) {
          const attrs = user.UserAttributes.reduce((acc: any, a: any) => {
            acc[a.Name] = a.Value;
            return acc;
          }, {});
          if (
            canViewComercialTeamHierarchy({
              role: attrs['custom:role'] || '',
              tenant: attrs['custom:tenant'] || '',
              isAdmin: false,
            })
          ) {
            return true;
          }
        }
      }
    } catch (e) {
      console.error('Error checking user role:', e);
    }
    return false;
  };

  const isGestorOrAboveComercial = () => {
    if (isAdmin()) return true;
    try {
      const tenant =
        (currentUserData as any)?.tenant ||
        localStorage.getItem('user_tenant') ||
        '';
      const role = (currentUserData as any)?.role || localStorage.getItem('userRole') || '';
      if (isGestorOrAboveRole({ role, tenant, isAdmin: false })) {
        return true;
      }
      const cognitoUserRaw = localStorage.getItem('cognito_user');
      if (cognitoUserRaw) {
        const user = JSON.parse(cognitoUserRaw);
        if (isGestorOrAboveRole({ role: user?.role || '', tenant: user?.tenant || '', isAdmin: false })) {
          return true;
        }
      }
    } catch (e) {
      console.error('Error checking gestor role:', e);
    }
    return false;
  };

  const AGUARDAR_SUBSTAGE = 'resultado-aguardar';

  const canChangeAguardarSubstage = () => isGestorOrAboveComercial();

  /** Admin or Gestão hierarchy — filter marcações / Minhas Propostas by sidebar user */
  const canFilterGestaoBySidebarUser = () => isAdmin() || isAdminOrGerenteComercial();

  const resolveGestaoFilterUserId = (): number | null => {
    if (
      gestaoModalsOnly &&
      gestaoFilterUserId != null &&
      Number.isFinite(Number(gestaoFilterUserId))
    ) {
      return Number(gestaoFilterUserId);
    }
    if (canFilterGestaoBySidebarUser() && selectedSidebarUser) {
      return selectedSidebarUser;
    }
    return null;
  };

  const buildGestaoApiUrl = (path: string): string => {
    const baseUrl = gestaoCccRecuperacao ? resolveCccApiBaseUrl() : resolveMainApiBaseUrl();
    const apiPath = gestaoCccRecuperacao
      ? path.replace('/api/comercial/gestao/', '/api/ccc/recuperacao/gestao/')
      : path;
    let url = `${baseUrl}${apiPath}`;
    const filterId = resolveGestaoFilterUserId();
    const selfId = currentUserId ?? readGestaoUserIdFromStorage();
    const shouldAppendUserFilter =
      filterId != null &&
      selfId != null &&
      filterId !== selfId &&
      (canFilterGestaoBySidebarUser() || (gestaoModalsOnly && gestaoFilterUserId != null));
    if (!shouldAppendUserFilter) {
      return url;
    }
    return `${url}${url.includes('?') ? '&' : '?'}userId=${encodeURIComponent(String(filterId))}`;
  };

  /** Align with backend `can_set_shared_comercial_proposal_users` (Gestão, Instrutor, admin). */
  const canShareProposalWithHierarchy = () => {
    if (isAdmin()) {
      return true;
    }

    const tenant = ((currentUserData as any)?.tenant || localStorage.getItem('user_tenant') || '').toLowerCase();
    if (tenant === 'admin') {
      return true;
    }
    if (tenant && tenant !== 'comercial') {
      return false;
    }

    const rawRole = (
      ((currentUserData as any)?.role as string | undefined) ||
      localStorage.getItem('userRole') ||
      ''
    ).trim();
    const rl = rawRole.toLowerCase();

    if (['Commercial Director', 'Sales Team Lead', 'Regional Manager'].includes(rawRole)) {
      return true;
    }

    if (rl.includes('instrutor') || rl.includes('trainer')) {
      return true;
    }

    if (rl.includes('assistente de gestor')) return true;
    if (rl === 'gestor') return true;

    if (
      rl.includes('gerente') ||
      rl.includes('director') ||
      rl.includes('commercial director') ||
      rl.includes('sales team lead') ||
      rl.includes('regional manager')
    ) {
      return true;
    }

    return false;
  };

  // CCC embeds must not expose or submit proposal sharing.
  const allowProposalSharing = !embeddedProposalOnly && !recuperacaoMode && canShareProposalWithHierarchy();

  const normalizedSharedComercialIdsFromProposal = (src: Record<string, unknown> | undefined | null): number[] => {
    if (!src || typeof src !== 'object') return [];
    const raw = src.shared_commercial_user_ids;
    const nums: number[] = [];
    const seen = new Set<number>();
    if (Array.isArray(raw)) {
      for (const x of raw) {
        const n = Number(x);
        if (Number.isFinite(n) && n > 0 && !seen.has(n)) {
          seen.add(n);
          nums.push(n);
        }
      }
    }
    return nums;
  };

  const proposalSubmitterId = (proposal: Record<string, unknown> | undefined | null): number | null => {
    if (!proposal || typeof proposal !== 'object') return null;
    const sb = proposal.submittedBy;
    if (sb == null) return null;
    if (typeof sb === 'number' && Number.isFinite(sb)) return sb;
    if (typeof sb === 'string') {
      const n = Number(sb);
      return Number.isFinite(n) ? n : null;
    }
    if (typeof sb === 'object' && sb !== null) {
      const obj = sb as Record<string, unknown>;
      const v = obj.id ?? obj.userId ?? obj.user_id;
      const n = typeof v === 'number' ? v : Number(v);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  };

  // Get filtered users for sidebar (hierarchy from backend; search filter only)
  const getFilteredSidebarUsers = () => {
    if (!isAdminOrGerenteComercial()) {
      return [];
    }
    
    // Exclude hidden users (for non-hidden viewers; backend also filters, this is defense-in-depth)
    const currentUserHidden = (currentUserData as any)?.is_hidden ?? false;
    let filtered = currentUserHidden ? comercialUsers : comercialUsers.filter((u: any) => !u.is_hidden);
    
    // Hierarchy is enforced by backend (api/callcenter/comercial-users). No team filter.
    // Filter by search term only
    if (sidebarSearchTerm.trim()) {
      const searchLower = sidebarSearchTerm.toLowerCase();
      filtered = filtered.filter((u: any) => {
        const name = (u.name || u.username || '').toLowerCase();
        const email = (u.email || '').toLowerCase();
        return name.includes(searchLower) || email.includes(searchLower);
      });
    }
    
    return filtered;
  };

  // Fetch proposals from database for marcações
  const fetchProposalsForMarcacoes = useCallback(async (marcacoes: Record<string, Marcacao>) => {
    try {
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
      
      // Fetch all proposal requests from database
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      const url = `${backendUrl}/api/comercial/proposal-requests`;
      const res = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });

      if (!res.ok) {
        console.error('Failed to fetch proposals:', res.status);
        return;
      }

      const json = await res.json();
      const allProposals = Array.isArray(json?.proposal_requests) ? json.proposal_requests : [];
      
      // Map proposals by marcação ID
      // We need to match proposals with marcações based on propostaId in form_data
      const proposalsByMarcacao: Record<string, any[]> = {};
      
      Object.values(marcacoes).forEach(marcacao => {
        const marcacaoId = marcacao.marcacao_id || marcacao.id;
        const formData = marcacao.form_data || {};
        
        // Get propostaIds from form_data
        const propostaIds: string[] = [];
        const propostaIdDirect = formData.propostaId || formData.proposta_id || formData.propostaID;
        if (propostaIdDirect) {
          if (Array.isArray(propostaIdDirect)) {
            propostaIds.push(...propostaIdDirect.map(id => id.toString()));
          } else {
            propostaIds.push(propostaIdDirect.toString());
          }
        }
        
        // Also check proposalData array
        const proposalData = formData.proposalData || formData.proposal_data || formData.proposals;
        if (proposalData) {
          if (Array.isArray(proposalData)) {
            proposalData.forEach((data: any) => {
              const propostaId = data?.propostaId || data?.proposta_id || data?.propostaID || data?.id || data?.proposalId || data?.proposal_id;
              if (propostaId && !propostaIds.includes(propostaId.toString())) {
                propostaIds.push(propostaId.toString());
              }
            });
          } else if (typeof proposalData === 'object' && proposalData !== null) {
            const propostaId = proposalData.propostaId || proposalData.proposta_id || proposalData.propostaID || proposalData.id || proposalData.proposalId || proposalData.proposal_id;
            if (propostaId && !propostaIds.includes(propostaId.toString())) {
              propostaIds.push(propostaId.toString());
            }
          }
        }
        
        // Match proposals with marcação based on propostaId
        const matchingProposals = allProposals.filter((proposal: any) => {
          // Check if proposal's form_answers contains propostaId that matches
          const formAnswers = proposal.form_answers || {};
          const proposalPropostaId = formAnswers.propostaId || formAnswers.proposta_id || formAnswers.propostaID || 
                                     proposal.id?.toString() || proposal.propostaId?.toString() || proposal.proposta_id?.toString();
          
          return propostaIds.some(id => {
            const normalizedId = id.toString().toUpperCase().trim();
            const normalizedProposalId = proposalPropostaId?.toString().toUpperCase().trim();
            return normalizedId === normalizedProposalId || 
                   normalizedId.includes(normalizedProposalId || '') || 
                   (normalizedProposalId || '').includes(normalizedId);
          });
        });
        
        if (matchingProposals.length > 0) {
          proposalsByMarcacao[marcacaoId] = matchingProposals;
        }
      });
      
      setProposalsData(proposalsByMarcacao);
    } catch (error) {
      console.error('Error fetching proposals:', error);
    }
  }, []);

  // Fetch proposal requests for "Minhas Propostas" modal
  // Similar to BackOffice - extract proposals from marcações with proposalData
  const fetchProposalRequests = useCallback(async () => {
    setLoadingProposalRequests(true);
    try {
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');

      const userIdToFilter =
        resolveGestaoFilterUserId() ?? currentUserId ?? readGestaoUserIdFromStorage();

      if (!userIdToFilter) {
        setProposalRequests([]);
        setFilteredProposalRequests([]);
        setLoadingProposalRequests(false);
        return;
      }

      const url = buildGestaoApiUrl('/api/comercial/gestao/minhas-propostas');
      const filterIdForApi = resolveGestaoFilterUserId();

      let res = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });

      if (!res.ok) {
        if (res.status === 403 && filterIdForApi && !(gestaoModalsOnly && gestaoFilterUserId != null)) {
          console.warn('403 when filtering minhas propostas by userId, retrying for current user');
          const retryUrl = `${resolveMainApiBaseUrl()}/api/comercial/gestao/minhas-propostas`;
          res = await fetch(retryUrl, {
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            }
          });
        }
        if (!res.ok) {
          const errorText = await res.text();
          console.error('Failed to fetch minhas propostas:', res.status, errorText);
          setProposalRequests([]);
          setFilteredProposalRequests([]);
          return;
        }
      }

      const json = await res.json();
      const marcacoes = Array.isArray(json?.marcacoes) ? json.marcacoes : [];
      const apiProposals = Array.isArray(json?.proposals) ? json.proposals : [];

      const proposalsMap = new Map<string, any>();

      if (apiProposals.length > 0) {
        for (const row of apiProposals) {
          const propostaId = String(row.proposta_id || row.id || '').trim();
          if (!propostaId || proposalsMap.has(propostaId)) continue;
          const proposalFormData = row.form_data || row.form_answers || {};
          proposalsMap.set(propostaId, {
            id: propostaId,
            board_proposal_id: row.board_proposal_id || `marcacao-${row.marcacao_db_id}-${propostaId}`,
            marcacao_db_id: row.marcacao_db_id,
            marcacao_id: row.marcacao_id,
            is_shared_with_me: Boolean(row.is_shared_with_me),
            lead_id: row.lead_id ?? null,
            comercial_user_id: userIdToFilter,
            form_answers: proposalFormData,
            form_data: proposalFormData,
            status: row.status || 'submitted',
            tenant: row.tenant || 'comercial',
            created_at: row.created_at || row.submitted_at,
            updated_at: row.updated_at || row.submitted_at,
            submitted_at: row.submitted_at || row.updated_at || row.created_at,
            estado_proposta: row.estado_proposta,
          });
        }
      } else {
      marcacoes.forEach((marcacao: any) => {
        const formData = marcacao.form_data || {};
        const proposalData = formData.proposalData;
        
        if (!proposalData) {
          return;
        }
        
        // Handle both array and single object
        const proposalsList: any[] = [];
        if (Array.isArray(proposalData)) {
          proposalsList.push(...proposalData);
        } else if (typeof proposalData === 'object' && proposalData !== null) {
          proposalsList.push(proposalData);
        }
        
        // Extract propostaId array
        const propostaIds = formData.propostaId || [];
        const propostaIdsArray = Array.isArray(propostaIds) ? propostaIds : [propostaIds];
        
        // Create proposal request objects from proposalData
        proposalsList.forEach((proposal: any, idx: number) => {
          const propostaId =
            proposal.propostaId ||
            proposal.proposta_id ||
            propostaIdsArray[idx] ||
            propostaIdsArray[0] ||
            `PRP-${marcacao.id}-${idx}`;
          const uniqueId = String(propostaId);

          const proposalFormData = proposal || {};

          const viewerNorm =
            userIdToFilter !== null && userIdToFilter !== undefined ? Number(userIdToFilter) : NaN;
          if (Number.isFinite(viewerNorm)) {
            const sid = proposalSubmitterId(proposalFormData as Record<string, unknown>);
            const cid =
              sid != null && Number.isFinite(Number(sid))
                ? Number(sid)
                : marcacao.created_by != null && Number.isFinite(Number(marcacao.created_by))
                  ? Number(marcacao.created_by)
                  : null;
            const sharedList = normalizedSharedComercialIdsFromProposal(
              proposalFormData as Record<string, unknown>
            );
            const viewerIsPrimary = cid != null && cid === viewerNorm;
            const viewerIsShared = sharedList.some((sh) => sh === viewerNorm);
            if (!viewerIsPrimary && !viewerIsShared) {
              return;
            }
          }

          // Skip if we've already seen this proposal ID
          if (proposalsMap.has(uniqueId)) {
            console.warn('Duplicate proposal ID found:', uniqueId, 'skipping...');
            return;
          }

          // Use proposalData as the form_data (this contains empresa, equipaInterna, servicos, serviceDetails, etc.)
          // proposalData is the actual proposal request data from "Solicitar Proposta" form
          
          // Create a proposal request object with proposalData as form_data
          // submitted_at: when the proposal was requested (from proposalData.submittedAt if set when user submitted)
          const proposalSubmittedAt = proposalFormData.submittedAt || proposalFormData.submitted_at;
          const proposalLeadId = Number(
            marcacao.lead_id ??
            formData.lead_id ??
            proposalFormData.lead_id ??
            proposalFormData.leadId ??
            0
          ) || null;
          const proposalRequest = {
            id: uniqueId,
            lead_id: proposalLeadId,
            comercial_user_id: userIdToFilter,
            form_answers: proposalFormData, // Use proposalData (the actual proposal request data)
            form_data: proposalFormData, // Use proposalData (the actual proposal request data)
            status: formData.estadoProposta === 'entregue' ? 'completed' : 
                   formData.estadoProposta === 'pendente' ? 'submitted' : 'submitted',
            tenant: marcacao.tenant || 'comercial',
            created_at: marcacao.created_at || marcacao.date || new Date().toISOString(),
            updated_at: marcacao.updated_at || marcacao.date || new Date().toISOString(),
            submitted_at: proposalSubmittedAt || marcacao.updated_at || marcacao.created_at || marcacao.date || new Date().toISOString(),
            lead: marcacao.lead || null
          };
          
          proposalsMap.set(uniqueId, proposalRequest);
        });
      });
      }

      const allProposals = Array.from(proposalsMap.values());

      // CCC gestão-only: enrich from marcacao.form_data (no N+1 BackOffice API).
      if (gestaoModalsOnly) {
        const enriched = allProposals.map((proposal) =>
          enrichMinhasPropostaFromMarcacao(proposal, marcacoes)
        );
        setProposalRequests(enriched);
        setFilteredProposalRequests(enriched);
        return;
      }
      
      // Store original proposals for filtering
      const originalProposals = allProposals;
      
      // Fetch proposal board status and full proposal data for each proposal
      const proposalsWithStatus = await Promise.all(
        allProposals.map(async (proposal) => {
          try {
            // Extract propostaId to find the proposal board entry
            const propostaId = proposal.id;
            const boardLookupId =
              (proposal as { board_proposal_id?: string }).board_proposal_id ||
              (proposal.marcacao_db_id != null
                ? `marcacao-${proposal.marcacao_db_id}-${propostaId}`
                : propostaId);

            // Try to fetch full proposal board data from API
            let proposalBoardData = null;
            try {
              const response = await api.get(`/api/backoffice/proposal-board/${boardLookupId}`);
              if (response.data && response.data.success && response.data.proposal) {
                proposalBoardData = response.data.proposal;
              }
            } catch (apiError) {
              // If API call fails, fall back to marcação data
            }
            
            // Try to get proposal board status from the marcação's claim_info
            // The claim_info is stored in form_data when BackOffice claims/completes
            const marcacao = marcacoes.find((m: any) => {
              const mFormData = m.form_data || {};
              const mPropostaIds = mFormData.propostaId || [];
              const mPropostaIdsArray = Array.isArray(mPropostaIds) ? mPropostaIds : [mPropostaIds];
              return mPropostaIdsArray.some((id: any) => String(id) === String(propostaId));
            });
            const claimedRaw = marcacao?.form_data?.claimedProposals;
            const claim = getClaimForPropostaId(
              typeof claimedRaw === 'object' && claimedRaw !== null ? (claimedRaw as Record<string, unknown>) : undefined,
              propostaId
            );

            const apiClaim = proposalBoardData?.proposal_claim;
            const proposalClaim = mergeProposalClaimsForComercial(apiClaim, claim);

            const servicosForStatus =
              proposalBoardData?.proposal_request?.form_data?.servicos ||
              proposalBoardData?.proposal_request?.form_answers?.servicos ||
              proposal?.servicos ||
              proposal?.form_data?.servicos ||
              proposal?.form_answers?.servicos ||
              [];
            const servicosList = Array.isArray(servicosForStatus) ? servicosForStatus : [];
            const scdRaw = proposalClaim?.service_completion_data;
            const scdList = Array.isArray(scdRaw) ? scdRaw : [];
            const marcacaoFormData = marcacao?.form_data || {};
            const estadoProposta = String(marcacaoFormData.estadoProposta || '').toLowerCase();
            const claimStatus = proposalClaim?.status;

            let boardStatus: 'available' | 'in_progress' | 'completed' | 'cancelled' = 'available';
            if (claimStatus === 'cancelled') {
              boardStatus = 'cancelled';
            } else if (
              servicosList.length > 0 &&
              allServicosHaveTerminalScd(servicosList, scdList) &&
              !anyServicoConcluidoNotCancelled(servicosList, scdList)
            ) {
              boardStatus = 'cancelled';
            } else if (
              claimStatus === 'completed' ||
              estadoProposta === 'concluido' ||
              estadoProposta === 'entregue' ||
              (servicosList.length > 0 &&
                allServicosHaveTerminalScd(servicosList, scdList) &&
                anyServicoConcluidoNotCancelled(servicosList, scdList))
            ) {
              boardStatus = 'completed';
            } else if (
              servicosList.length === 0 &&
              scdList.length > 0 &&
              (claimStatus === 'completed' || estadoProposta === 'concluido' || estadoProposta === 'entregue')
            ) {
              boardStatus = 'completed';
            } else if (proposalClaim?.backoffice_user_id || proposalBoardData?.backoffice_user_id) {
              boardStatus = 'in_progress';
            } else {
              boardStatus = 'available';
            }

            const backofficeUser =
              proposalBoardData?.backoffice_user ?? proposalClaim?.backoffice_user ?? null;
            const cancelledByUsername =
              proposalBoardData?.cancelled_by_username ?? proposalClaim?.cancelled_by_username ?? null;
            const cancelledAt = proposalBoardData?.cancelled_at ?? proposalClaim?.cancelled_at ?? null;
            const cancellationFeedback =
              proposalBoardData?.cancellation_feedback ?? proposalClaim?.cancellation_feedback ?? null;
            let completedAt = proposalBoardData?.completed_at ?? proposalClaim?.completed_at ?? null;
            if (!completedAt && boardStatus === 'completed' && scdList.length > 0) {
              completedAt = latestTerminalAtFromScdList(scdList) || completedAt;
            }
            const completionNotes =
              proposalBoardData?.completion_notes ?? proposalClaim?.completion_notes ?? null;

            // Backoffice section (same as BackOffice proposal board)
            type KanbanSectionId = 'available' | 'roadtrip' | 'op_criada' | 'in_progress' | 'proposta_aprovacao' | 'exclusividade_pendente' | 'pendente_gestor_edp' | 'pendente_comercial' | 'completed';
            const isRoadTripRequest = (p: any): boolean => {
              const fd = p?.form_data || p?.form_answers || {};
              if (fd.equipaInterna?.tipoPedido === 'roadtrip') return true;
              const tipo = (fd.tipo || fd.type || '').toString().toLowerCase();
              if (tipo.includes('roadtrip') || tipo.includes('road trip')) return true;
              const servicos = fd.servicos || [];
              const names = (Array.isArray(servicos) ? servicos : []).map((s: any) => (typeof s === 'object' ? s?.service : s) ?? '').join(' ').toLowerCase();
              if (names.includes('roadtrip') || names.includes('road trip')) return true;
              return false;
            };
            const backofficeUserId = proposalBoardData?.backoffice_user_id ?? claim?.backoffice_user_id ?? null;
            let kanbanSection: KanbanSectionId = 'available';
            if (boardStatus === 'completed' || boardStatus === 'cancelled') {
              kanbanSection = 'completed';
            } else if (boardStatus === 'available' && !backofficeUserId) {
              kanbanSection = isRoadTripRequest(proposal) ? 'roadtrip' : 'available';
            } else if (boardStatus === 'in_progress') {
              let stage = '';
              if (proposalBoardData?.proposal_claim?.board_stage) {
                stage = String(proposalBoardData.proposal_claim.board_stage).toLowerCase().trim();
              } else if (marcacao) {
                const mfd = marcacao.form_data || {};
                const boardStages = mfd.board_stages;
                const pid = String(propostaId);
                const withPrefix = !pid.startsWith('PRP-') ? `PRP-${pid}` : pid;
                const stageFromMap = typeof boardStages === 'object' && boardStages != null
                  ? (boardStages[withPrefix] || boardStages[pid] || '')
                  : '';
                stage = (stageFromMap || mfd.estado || mfd.board_stage || '').toString().toLowerCase().trim();
              }
              if (stage.includes('pendente_gestor_edp') || stage.includes('pendente gestor edp')) kanbanSection = 'pendente_gestor_edp';
              else if (stage.includes('pendente_comercial') || stage.includes('pendente comercial')) kanbanSection = 'pendente_comercial';
              else if (['exclusividade', 'pendente gestor', 'pendente_gestor'].some(k => stage.includes(k))) kanbanSection = 'exclusividade_pendente';
              else if (['op_criada', 'op criada', 'opcriada'].some(k => stage.includes(k))) kanbanSection = 'op_criada';
              else if (['proposta_aprovacao', 'proposta em aprovacao', 'proposta em aprovação', 'em aprovacao', 'aprovacao', 'aprovação'].some(k => stage.includes(k))) kanbanSection = 'proposta_aprovacao';
              else kanbanSection = 'in_progress';
            }

            // Card tags (Exclusividade, etc.) from quadro de propostas – for display in Minhas Propostas
            let cardTags: { type: string; ate?: string; value?: string; motivo?: string; criada_em?: string }[] = [];
            if (proposalBoardData?.proposal_request?.form_data?.card_tags && Array.isArray(proposalBoardData.proposal_request.form_data.card_tags)) {
              cardTags = proposalBoardData.proposal_request.form_data.card_tags;
            }
            if (cardTags.length === 0 && marcacao) {
              const mfd = marcacao.form_data || {};
              const cardTagsAll = mfd.card_tags;
              const pid = String(propostaId);
              const withPrefix = !pid.startsWith('PRP-') ? `PRP-${pid}` : pid;
              const withoutPrefix = pid.startsWith('PRP-') ? pid.replace('PRP-', '') : pid;
              const propostaTags = (typeof cardTagsAll === 'object' && cardTagsAll != null)
                ? (cardTagsAll[pid] || cardTagsAll[withPrefix] || cardTagsAll[withoutPrefix] || [])
                : [];
              cardTags = Array.isArray(propostaTags) ? propostaTags : [];
            }

            const proposalFormData =
              proposalBoardData?.proposal_request?.form_data ||
              proposalBoardData?.proposal_request?.form_answers ||
              proposal?.form_data ||
              proposal?.form_answers ||
              {};
            let isRefazer =
              proposalBoardData?.is_refazer === true ||
              proposalFormData?.isRefazer === true;
            if (!isRefazer && marcacao) {
              const rawPd = marcacaoFormData.proposalData;
              const entries = Array.isArray(rawPd) ? rawPd : rawPd && typeof rawPd === 'object' ? [rawPd] : [];
              const pid = String(propostaId);
              const withPrefix = !pid.startsWith('PRP-') ? `PRP-${pid}` : pid;
              const withoutPrefix = pid.startsWith('PRP-') ? pid.replace('PRP-', '') : pid;
              isRefazer = entries.some((entry: Record<string, unknown>) => {
                if (!entry || entry.isRefazer !== true) return false;
                const entryPid = String(entry.propostaId || '');
                return entryPid === pid || entryPid === withPrefix || entryPid === withoutPrefix;
              });
            }
            if (isRefazer && !cardTags.some((t) => t.type === 'refazer')) {
              cardTags = [...cardTags, { type: 'refazer', value: 'Refazer' }];
            }

            // BackOffice notes attached to the proposal card
            const proposalNotesFromMarcacao = (() => {
              if (!marcacao) return [];
              const mfd = marcacao.form_data || {};
              const cardNotesAll = mfd.card_notes;
              const pid = String(propostaId);
              const withPrefix = !pid.startsWith('PRP-') ? `PRP-${pid}` : pid;
              const withoutPrefix = pid.startsWith('PRP-') ? pid.replace('PRP-', '') : pid;
              const notes = (typeof cardNotesAll === 'object' && cardNotesAll != null)
                ? (cardNotesAll[pid] || cardNotesAll[withPrefix] || cardNotesAll[withoutPrefix] || [])
                : [];
              return Array.isArray(notes) ? notes : [];
            })();

            const cardNotes =
              (Array.isArray(proposalBoardData?.proposal_request?.form_data?.card_notes)
                ? proposalBoardData.proposal_request.form_data.card_notes
                : null) ||
              (Array.isArray(proposalBoardData?.proposal_request?.form_answers?.card_notes)
                ? proposalBoardData.proposal_request.form_answers.card_notes
                : null) ||
              (Array.isArray(proposal?.card_notes) ? proposal.card_notes : null) ||
              (Array.isArray(proposal?.form_data?.card_notes) ? proposal.form_data.card_notes : null) ||
              (Array.isArray(proposal?.form_answers?.card_notes) ? proposal.form_answers.card_notes : null) ||
              proposalNotesFromMarcacao;

            return {
              ...proposal,
              boardStatus: boardStatus,
              backoffice_user_id: backofficeUserId,
              kanbanSection,
              card_tags: cardTags,
              is_refazer: isRefazer,
              card_notes: cardNotes,
              proposal_claim: proposalClaim,
              backoffice_user: backofficeUser,
              cancelled_by_username: cancelledByUsername,
              cancelled_at: cancelledAt,
              cancellation_feedback: cancellationFeedback,
              completed_at: completedAt,
              completion_notes: completionNotes
            };
          } catch (error) {
            console.error('Error fetching board status for proposal:', proposal.id, error);
            return {
              ...proposal,
              boardStatus: 'available' // Default to available if error
            };
          }
        })
      );
      
      if (allProposals.length > 0) {
        // proposals ready for board
      }
      setProposalRequests(proposalsWithStatus);
      setFilteredProposalRequests(proposalsWithStatus);
    } catch (error) {
      console.error('Error fetching proposal requests:', error);
      setProposalRequests([]);
      setFilteredProposalRequests([]);
    } finally {
      setLoadingProposalRequests(false);
    }
  }, [selectedSidebarUser, currentUserId, currentUserData, gestaoModalsOnly, gestaoCccRecuperacao, gestaoFilterUserId]);

  // Fetch contract submissions for Meus Contratos modal
  const fetchContractSubmissions = useCallback(async () => {
    setLoadingContractSubmissions(true);
    try {
      const userIdToFilter =
        resolveGestaoFilterUserId() ??
        (isAdminOrGerenteComercial() && selectedSidebarUser
          ? selectedSidebarUser
          : currentUserId) ??
        readGestaoUserIdFromStorage();

      const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
      const baseUrl = resolveMainApiBaseUrl();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const filterIdForApi = resolveGestaoFilterUserId();

      const fetchMarcacoesFrom = async (path: string): Promise<any[]> => {
        const url = buildGestaoApiUrl(path);
        const res = await fetch(url, { headers });
        if (!res.ok) {
          if (res.status === 403 && filterIdForApi && !(gestaoModalsOnly && gestaoFilterUserId != null)) {
            const retryRes = await fetch(`${baseUrl}${path}`, { headers });
            if (!retryRes.ok) return [];
            const retryJson = await retryRes.json();
            return Array.isArray(retryJson?.marcacoes) ? retryJson.marcacoes : [];
          }
          return [];
        }
        const json = await res.json();
        return Array.isArray(json?.marcacoes) ? json.marcacoes : [];
      };

      let marcacoes: any[] = [];
      if (gestaoModalsOnly) {
        if (gestaoCccRecuperacao) {
          const [fromPropostas, fromContracts] = await Promise.all([
            fetchMarcacoesFrom('/api/comercial/gestao/minhas-propostas'),
            fetchMarcacoesFrom('/api/comercial/gestao/marcacoes?contracts_only=1'),
          ]);
          marcacoes = mergeMarcacoesForGestaoContracts(fromPropostas, fromContracts);
        } else {
          const [fromPropostas, fromGestao] = await Promise.all([
            fetchMarcacoesFrom('/api/comercial/gestao/minhas-propostas'),
            fetchMarcacoesFrom('/api/comercial/gestao/marcacoes'),
          ]);
          marcacoes = mergeMarcacoesForGestaoContracts(fromPropostas, fromGestao);
        }
      } else {
        marcacoes = await fetchMarcacoesFrom('/api/comercial/gestao/marcacoes');
      }

      if (userIdToFilter && !gestaoModalsOnly) {
        marcacoes = marcacoes.filter((m: any) => {
          const formData = m.form_data || {};
          const marcacaoUsers = formData.marcacaoUsers || [];
          if (!Array.isArray(marcacaoUsers)) return false;
          return marcacaoUsers.map(String).includes(String(userIdToFilter));
        });
      }
      
      // Extract contracts from marcações
      const contractsMap = new Map<string, any>();
      
      marcacoes.forEach((marcacao: any) => {
        const formData = marcacao.form_data || {};
        const contractDataArray = formData.contractData;
        
        if (!contractDataArray || !Array.isArray(contractDataArray) || contractDataArray.length === 0) {
          return;
        }
        
        // Process each contract in the array
        contractDataArray.forEach((contract: any, idx: number) => {
          const submitterId = resolveContractSubmitterId(marcacao, contract);
          if (
            !gestaoModalsOnly &&
            userIdToFilter &&
            submitterId != null &&
            Number(submitterId) !== Number(userIdToFilter)
          ) {
            return;
          }
          if (
            gestaoModalsOnly &&
            userIdToFilter &&
            submitterId != null &&
            Number(submitterId) !== Number(userIdToFilter)
          ) {
            const formDataUsers = formData.marcacaoUsers || [];
            const inMarcacaoUsers =
              Array.isArray(formDataUsers) &&
              formDataUsers.map(String).includes(String(userIdToFilter));
            if (!inMarcacaoUsers) return;
          }

          // Create unique ID for each contract
          const marcacaoKey = resolveMarcacaoDbId(marcacao) || marcacao.marcacao_id || 'unknown';
          const contractId = `${marcacaoKey}-contract-${idx}`;
          const uniqueId = String(contractId);
          
          // Skip if we've already seen this contract
          if (contractsMap.has(uniqueId)) {
            return;
          }
          
          const { contractStatus, claimInfo } = resolveContractClaimAndStatus(formData, marcacao, idx);
          
          const contractTipo = contract.tipo || contract.tipoServico || '';
          const status =
            contractTipo === 'energia_gas'
              ? resolveEeGasContractDisplayStatus(contractStatus, claimInfo)
              : resolveSolarContractDisplayStatus(contractStatus, claimInfo);
          
          // Create contract submission object
          // submitted_at: when the contract was submitted (from contract_data.submittedAt set on submit)
          const contractSubmittedAt = contract.submittedAt || contract.submitted_at;
          const contractLeadId = Number(
            marcacao.lead_id ??
            formData.lead_id ??
            contract?.lead_id ??
            contract?.leadId ??
            0
          ) || null;
          const contractSubmission = enrichContractSubmissionFromMarcacao(
            {
              id: uniqueId,
              marcacao_id: marcacao.marcacao_id || marcacao.id,
              marcacao_db_id: resolveMarcacaoDbId(marcacao),
              lead_id: contractLeadId,
              comercial_user_id: submitterId ?? userIdToFilter,
              contract_data: contract,
              contract_status: status,
              contract_claim: claimInfo,
              tenant: marcacao.tenant || 'comercial',
              created_at: contractSubmittedAt || marcacao.created_at || marcacao.date || new Date().toISOString(),
              updated_at: marcacao.updated_at || marcacao.date || new Date().toISOString(),
              submitted_at: contractSubmittedAt || marcacao.updated_at || marcacao.created_at || marcacao.date || new Date().toISOString(),
              lead: marcacao.lead || null,
              marcacao,
            },
            marcacao
          );
          
          contractsMap.set(uniqueId, contractSubmission);
        });
      });
      
      const allContracts = Array.from(contractsMap.values());
      
      // Sort by submission date (most recent first)
      allContracts.sort((a, b) => {
        const dateA = new Date(a.submitted_at || a.created_at || a.updated_at || 0).getTime();
        const dateB = new Date(b.submitted_at || b.created_at || b.updated_at || 0).getTime();
        return dateB - dateA; // Descending order (most recent first)
      });
      
      setContractSubmissions(allContracts);
      setFilteredContractSubmissions(allContracts);
    } catch (error) {
      console.error('Error fetching contract submissions:', error);
      setContractSubmissions([]);
    } finally {
      setLoadingContractSubmissions(false);
    }
  }, [selectedSidebarUser, currentUserId, currentUserData, gestaoModalsOnly, gestaoCccRecuperacao, gestaoFilterUserId]);

  // Search function for Minhas Propostas modal
  const handleProposalSearch = useCallback(() => {
    if (!proposalSearchTerm.trim()) {
      applyFilters();
      return;
    }

    const searchLower = proposalSearchTerm.toLowerCase().trim();
    const filtered = proposalRequests.filter((request) => {
      const formData = request.form_answers || request.form_data || {};
      const empresa = formData.empresa || {};
      
      // Search in Empresa name
      const empresaName = (empresa.nomeEmpresa || '').toLowerCase();
      if (empresaName.includes(searchLower)) return true;
      
      // Search in NIF
      const nif = (empresa.nifEmpresa || '').toLowerCase();
      if (nif.includes(searchLower)) return true;
      
      // Search in CPE
      const cpe = (empresa.cpe || '').toLowerCase();
      if (cpe.includes(searchLower)) return true;
      
      // Search in Contacto Representante
      const contacto = (empresa.contactoRepresentante || empresa.contactoResponsavel || '').toLowerCase();
      if (contacto.includes(searchLower)) return true;
      
      // Search in ID
      const id = String(request.id || '').toLowerCase();
      const idDisplay = String(request.id || '').startsWith('PRP-') 
        ? String(request.id).toLowerCase() 
        : `prp-${String(request.id).toLowerCase()}`;
      if (id.includes(searchLower) || idDisplay.includes(searchLower)) return true;
      
      return false;
    });

    // Sort by submission date (most recent first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.submitted_at || a.created_at || a.updated_at || 0).getTime();
      const dateB = new Date(b.submitted_at || b.created_at || b.updated_at || 0).getTime();
      return dateB - dateA; // Descending order (most recent first)
    });
    
    setFilteredProposalRequests(filtered);
    setCurrentProposalPage(1);
    
    // Navigate to first result if found
    if (filtered.length > 0) {
      setTimeout(() => {
        const firstResultId = filtered[0].id;
        const element = document.getElementById(`proposal-${firstResultId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Highlight the result
          element.style.outline = '3px solid #3b82f6';
          element.style.outlineOffset = '4px';
          setTimeout(() => {
            element.style.outline = '';
            element.style.outlineOffset = '';
          }, 2000);
        } else {
          // If not on current page, navigate to the page containing it
          const resultIndex = filtered.findIndex(r => r.id === firstResultId);
          const targetPage = Math.floor(resultIndex / proposalsPerPage) + 1;
          setCurrentProposalPage(targetPage);
          setTimeout(() => {
            const elementAfterPageChange = document.getElementById(`proposal-${firstResultId}`);
            if (elementAfterPageChange) {
              elementAfterPageChange.scrollIntoView({ behavior: 'smooth', block: 'center' });
              elementAfterPageChange.style.outline = '3px solid #3b82f6';
              elementAfterPageChange.style.outlineOffset = '4px';
              setTimeout(() => {
                elementAfterPageChange.style.outline = '';
                elementAfterPageChange.style.outlineOffset = '';
              }, 2000);
            }
          }, 100);
        }
      }, 100);
    }
  }, [proposalSearchTerm, proposalRequests, proposalsPerPage]);

  // Apply filters function
  const applyFilters = useCallback(() => {
    let filtered = [...proposalRequests];

    // Date filter
    if (filterDay || filterMonth || filterYear) {
      filtered = filtered.filter((request) => {
        const submittedDate = request.submitted_at || request.created_at || request.updated_at;
        if (!submittedDate) return false;
        
        const date = new Date(submittedDate);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear());
        
        if (filterDay && day !== filterDay) return false;
        if (filterMonth && month !== filterMonth) return false;
        if (filterYear && year !== filterYear) return false;
        
        return true;
      });
    }

    // Service type filter
    if (filterServiceType) {
      filtered = filtered.filter((request) => {
        const formData = request.form_answers || request.form_data || {};
        const servicos = formData.servicos || [];
        if (Array.isArray(servicos)) {
          return servicos.some((s: any) => {
            const serviceType = typeof s === 'object' ? s.service : s;
            return String(serviceType).toLowerCase() === filterServiceType.toLowerCase();
          });
        }
        return false;
      });
    }

    // Status filter
    if (filterStatus) {
      filtered = filtered.filter((request) => {
        const boardStatus = request.boardStatus || 'available';
        if (filterStatus === 'claimed' || filterStatus === 'in_progress') {
          return boardStatus === 'claimed' || boardStatus === 'in_progress';
        }
        return boardStatus === filterStatus;
      });
    }

    // Section filter (Backoffice section ids; Comercial shows Por Iniciar for `available`)
    if (filterSection) {
      filtered = filtered.filter((request) => (request.kanbanSection || 'available') === filterSection);
    }

    // Apply search if there's a search term
    if (proposalSearchTerm.trim()) {
      const searchLower = proposalSearchTerm.toLowerCase().trim();
      filtered = filtered.filter((request) => {
        const formData = request.form_answers || request.form_data || {};
        const empresa = formData.empresa || {};
        
        const empresaName = (empresa.nomeEmpresa || '').toLowerCase();
        const nif = (empresa.nifEmpresa || '').toLowerCase();
        const cpe = (empresa.cpe || '').toLowerCase();
        const contacto = (empresa.contactoRepresentante || empresa.contactoResponsavel || '').toLowerCase();
        const id = String(request.id || '').toLowerCase();
        const idDisplay = String(request.id || '').startsWith('PRP-') 
          ? String(request.id).toLowerCase() 
          : `prp-${String(request.id).toLowerCase()}`;
        
        return empresaName.includes(searchLower) ||
               nif.includes(searchLower) ||
               cpe.includes(searchLower) ||
               contacto.includes(searchLower) ||
               id.includes(searchLower) ||
               idDisplay.includes(searchLower);
      });
    }

    // Sort by submission date (most recent first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.submitted_at || a.created_at || a.updated_at || 0).getTime();
      const dateB = new Date(b.submitted_at || b.created_at || b.updated_at || 0).getTime();
      return dateB - dateA; // Descending order (most recent first)
    });
    
    setFilteredProposalRequests(filtered);
    setCurrentProposalPage(1);
  }, [filterDay, filterMonth, filterYear, filterServiceType, filterStatus, filterSection, proposalSearchTerm, proposalRequests]);

  // Apply filters when filter values change (but not search - that's handled by handleProposalSearch)
  useEffect(() => {
    if (proposalRequests.length > 0 && !proposalSearchTerm.trim()) {
      applyFilters();
    }
  }, [filterDay, filterMonth, filterYear, filterServiceType, filterStatus, filterSection, applyFilters, proposalSearchTerm, proposalRequests]);

  // Section counts for Minhas Propostas (same as BackOffice proposal board)
  const MINHAS_PROPOSTAS_SECTION_LABELS: Record<string, string> = {
    available: 'Por Iniciar',
    roadtrip: 'RoadTrip',
    op_criada: 'OP Criada',
    in_progress: 'Em Progresso',
    proposta_aprovacao: 'Proposta em Aprovação',
    exclusividade_pendente: 'Exclusividade',
    pendente_gestor_edp: 'Pendente Gestor EDP (Erro Sistema)',
    pendente_comercial: 'Pendente Comercial',
    completed: 'Concluído / Cancelado',
  };
  const minhasPropostasSectionCounts = useMemo(() => {
    const counts: Record<string, number> = {
      available: 0, roadtrip: 0, op_criada: 0, in_progress: 0,
      proposta_aprovacao: 0, exclusividade_pendente: 0, pendente_gestor_edp: 0, pendente_comercial: 0, completed: 0,
    };
    proposalRequests.forEach((r) => {
      const s = r.kanbanSection || 'available';
      if (counts[s] !== undefined) counts[s]++;
      else counts.available++;
    });
    return counts;
  }, [proposalRequests]);

  // Update filtered proposals when proposalRequests changes (initial load or refresh)
  useEffect(() => {
    if (proposalRequests.length === 0) {
      setFilteredProposalRequests([]);
      return;
    }
    if (proposalSearchTerm.trim() || filterDay || filterMonth || filterYear || filterServiceType || filterStatus || filterSection) {
      applyFilters();
    } else {
      const sorted = [...proposalRequests].sort((a, b) => {
        const dateA = new Date(a.submitted_at || a.created_at || a.updated_at || 0).getTime();
        const dateB = new Date(b.submitted_at || b.created_at || b.updated_at || 0).getTime();
        return dateB - dateA;
      });
      setFilteredProposalRequests(sorted);
    }
  }, [proposalRequests, proposalSearchTerm, filterDay, filterMonth, filterYear, filterServiceType, filterStatus, filterSection, applyFilters]);

  // Apply filters for Meus Contratos modal
  const applyContractFilters = useCallback(() => {
    let filtered = [...contractSubmissions];

    // Filter by contract type tab (Energia/Gás or Solar)
    filtered = filtered.filter((contract) => {
      const contractData = contract.contract_data || {};
      const tipoServico = contractData.tipoServico || contractData.tipo || '';
      
      if (contractTypeTab === 'energia_gas') {
        return tipoServico === 'energia_gas';
      } else {
        // Solar - all non-energia_gas contracts
        return tipoServico !== 'energia_gas' && tipoServico !== '';
      }
    });

    // Filter by date
    if (contractFilterDay || contractFilterMonth || contractFilterYear) {
      filtered = filtered.filter((contract) => {
        const submittedDate = new Date(contract.submitted_at || contract.created_at || contract.updated_at);
        const day = contractFilterDay ? String(submittedDate.getDate()).padStart(2, '0') : null;
        const month = contractFilterMonth ? String(submittedDate.getMonth() + 1).padStart(2, '0') : null;
        const year = contractFilterYear ? String(submittedDate.getFullYear()) : null;
        
        if (contractFilterDay && day !== contractFilterDay) return false;
        if (contractFilterMonth && month !== contractFilterMonth) return false;
        if (contractFilterYear && year !== contractFilterYear) return false;
        return true;
      });
    }

    // Filter by service type
    if (contractFilterServiceType) {
      filtered = filtered.filter((contract) => {
        const contractData = contract.contract_data || {};
        const tipoServico = contractData.tipoServico || contractData.tipo || '';
        return tipoServico === contractFilterServiceType;
      });
    }

    // Sort by submission date (most recent first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.submitted_at || a.created_at || a.updated_at || 0).getTime();
      const dateB = new Date(b.submitted_at || b.created_at || b.updated_at || 0).getTime();
      return dateB - dateA; // Descending order (most recent first)
    });

    setFilteredContractSubmissions(filtered);
    setCurrentContractPage(1);
  }, [contractSubmissions, contractTypeTab, contractFilterDay, contractFilterMonth, contractFilterYear, contractFilterServiceType]);

  // Apply contract filters when filter values change
  useEffect(() => {
    if (contractSubmissions.length > 0 && !contractSearchTerm.trim()) {
      applyContractFilters();
    }
  }, [contractTypeTab, contractFilterDay, contractFilterMonth, contractFilterYear, contractFilterServiceType, applyContractFilters, contractSearchTerm, contractSubmissions]);

  // Search function for Meus Contratos modal
  const handleContractSearch = useCallback(() => {
    if (!contractSearchTerm.trim()) {
      applyContractFilters();
      return;
    }

    const searchLower = contractSearchTerm.toLowerCase().trim();
    
    // First filter by contract type tab
    let filtered = contractSubmissions.filter((contract) => {
      const contractData = contract.contract_data || {};
      const tipoServico = contractData.tipoServico || contractData.tipo || '';
      
      if (contractTypeTab === 'energia_gas') {
        return tipoServico === 'energia_gas';
      } else {
        // Solar - all non-energia_gas contracts
        return tipoServico !== 'energia_gas' && tipoServico !== '';
      }
    });
    
    // Then apply search term
    filtered = filtered.filter((contract) => {
      const contractData = contract.contract_data || {};
      
      // Search in Empresa name
      const empresaName = (contractData.nomeEmpresa || '').toLowerCase();
      if (empresaName.includes(searchLower)) return true;
      
      // Search in NIF
      const nif = (contractData.nifEmpresa || '').toLowerCase();
      if (nif.includes(searchLower)) return true;
      
      // Search in CPE
      const cpe = (contractData.cpe || '').toLowerCase();
      if (cpe.includes(searchLower)) return true;
      
      // Search in Contacto
      const contacto = (contractData.contactoResponsavel || contractData.contactoRepresentante || '').toLowerCase();
      if (contacto.includes(searchLower)) return true;
      
      // Search in ID
      const id = String(contract.id || '').toLowerCase();
      if (id.includes(searchLower)) return true;
      
      return false;
    });

    // Sort by submission date (most recent first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.submitted_at || a.created_at || a.updated_at || 0).getTime();
      const dateB = new Date(b.submitted_at || b.created_at || b.updated_at || 0).getTime();
      return dateB - dateA; // Descending order (most recent first)
    });
    
    setFilteredContractSubmissions(filtered);
    setCurrentContractPage(1);
    
    // Navigate to first result if found
    if (filtered.length > 0) {
      setTimeout(() => {
        const firstResultId = filtered[0].id;
        const element = document.getElementById(`contract-${firstResultId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.style.outline = '3px solid #10b981';
          element.style.outlineOffset = '4px';
          setTimeout(() => {
            element.style.outline = '';
            element.style.outlineOffset = '';
          }, 2000);
        } else {
          const resultIndex = filtered.findIndex(r => r.id === firstResultId);
          const targetPage = Math.floor(resultIndex / contractsPerPage) + 1;
          setCurrentContractPage(targetPage);
          setTimeout(() => {
            const elementAfterPageChange = document.getElementById(`contract-${firstResultId}`);
            if (elementAfterPageChange) {
              elementAfterPageChange.scrollIntoView({ behavior: 'smooth', block: 'center' });
              elementAfterPageChange.style.outline = '3px solid #10b981';
              elementAfterPageChange.style.outlineOffset = '4px';
              setTimeout(() => {
                elementAfterPageChange.style.outline = '';
                elementAfterPageChange.style.outlineOffset = '';
              }, 2000);
            }
          }, 100);
        }
      }, 100);
    }
  }, [contractSearchTerm, contractSubmissions, contractTypeTab, applyContractFilters, contractsPerPage]);

  // Fetch marcações from backend
  const closeMinhasPropostasModal = () => {
    setShowMinhasPropostasModal(false);
    if (gestaoModalsOnly) onGestaoModalsClose?.();
  };

  const closeMeusContratosModal = () => {
    setShowMeusContratosModal(false);
    if (gestaoModalsOnly) onGestaoModalsClose?.();
  };

  const fetchMarcacoes = useCallback(async () => {
    if (embeddedProposalOnly || embeddedContractOnly || gestaoModalsOnly || embeddedSignatureOnly) return;
    if (!recuperacaoMode && !currentUserId) return;

    fetchMarcacoesRef.current = fetchMarcacoes;

    // Cancel any in-flight list request so side-panel clicks cannot race/wipe the board.
    fetchMarcacoesAbortRef.current?.abort();
    const controller = new AbortController();
    fetchMarcacoesAbortRef.current = controller;
    const requestGen = ++fetchMarcacoesGenRef.current;
    const isCurrentRequest = () => requestGen === fetchMarcacoesGenRef.current;

    setIsLoadingMarcacoes(true);

    try {
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
      if (!token) {
        console.error('[ComercialManagement] fetchMarcacoes: No token found');
        if (isCurrentRequest()) setIsLoadingMarcacoes(false);
        return;
      }
      
      const baseUrl = recuperacaoMode ? resolveCccApiBaseUrl() : resolveMainApiBaseUrl();
      if (!recuperacaoMode && !baseUrl) {
        console.error('[ComercialManagement] fetchMarcacoes: No backendUrl found');
        if (isCurrentRequest()) setIsLoadingMarcacoes(false);
        return;
      }

      let url: string;
      if (recuperacaoMode) {
        const params = new URLSearchParams();
        const q = (activeSearchTerm ?? searchTerm).trim();
        if (q) params.set('search', q);
        if (recuperacaoAssigneeFilter) params.set('assignee_status', recuperacaoAssigneeFilter);
        url = `${baseUrl}/api/ccc/recuperacao/marcacoes${params.toString() ? `?${params}` : ''}`;
      } else {
        url = `${baseUrl}/api/comercial/gestao/marcacoes`;
        try {
          const isAdminOrGerente = isAdminOrGerenteComercial();
          if (isAdminOrGerente && selectedSidebarUser) {
            url += `?userId=${encodeURIComponent(String(selectedSidebarUser))}`;
          }
        } catch (error) {
          console.error('[ComercialManagement] fetchMarcacoes: Error checking isAdminOrGerenteComercial:', error);
        }
      }
      
      const res = await fetch(url, {
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });

      if (!isCurrentRequest()) return;

      if (!res.ok) {
        const text = await res.text();
        console.error('Failed to fetch marcações:', res.status, text.substring(0, 200));
        // Keep previous board data on failure (avoid empty wipe from races/timeouts).
        return;
      }

      // Check if response is JSON
      const contentType = res.headers.get('content-type');
      let json: any;
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        console.error('[ComercialManagement] Response is not JSON:', contentType, 'URL:', url, 'Status:', res.status, 'Response:', text.substring(0, 500));
        try {
          json = JSON.parse(text);
        } catch (e) {
          console.error('[ComercialManagement] Failed to parse response as JSON, returning early');
          return;
        }
      } else {
        json = await res.json();
      }
      if (!isCurrentRequest()) return;

      if (recuperacaoMode && json?.stats) {
        setRecuperacaoStats({
          total: json.stats.total ?? 0,
          with_deleted_or_suspended: json.stats.with_deleted_or_suspended ?? 0,
          with_hidden_assignee: json.stats.with_hidden_assignee ?? 0,
        });
      }
      const marcacoes = Array.isArray(json?.marcacoes) ? json.marcacoes : [];

      // Map to frontend format
      const mapped: Marcacao[] = [];
      marcacoes.forEach((m: any) => {
        const mappedMarcacao = mapBackendMarcacao(m, userMap);
        if (mappedMarcacao.marcacao_id || mappedMarcacao.id) {
          mapped.push(mappedMarcacao);
        }
      });

      // Group marcações that share the same date, time, and client (same logic as calendars)
      const groupedMap = new Map<string, Marcacao[]>();
      mapped.forEach((marcacao) => {
        // Normalize date to ISO format (YYYY-MM-DD) for grouping
        let dateKey = '';
        if (marcacao.date) {
          // If date is already in ISO format, use it directly
          if (typeof marcacao.date === 'string' && marcacao.date.includes('T')) {
            dateKey = marcacao.date.split('T')[0];
          } else if (typeof marcacao.date === 'string' && marcacao.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
            dateKey = marcacao.date;
          } else {
            // Try to parse and format
            try {
              const date = new Date(marcacao.date);
              if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                dateKey = `${year}-${month}-${day}`;
              }
            } catch (e) {
              // Use as-is if parsing fails
              dateKey = String(marcacao.date);
            }
          }
        }
        
        // Normalize hora_inicio (extract just the start time if it's a range)
        let horaKey = '';
        if (marcacao.hora_inicio) {
          horaKey = marcacao.hora_inicio.split(' - ')[0].split(' ')[0]; // Get first part if range
        } else if (marcacao.hora) {
          horaKey = marcacao.hora.split(' - ')[0].split(' ')[0]; // Get first part if range
        }
        
        const empresaKey = (marcacao.nome_empresa || marcacao.empresa || '').trim();
        const key = `${dateKey}_${horaKey}_${empresaKey}`;
        
        if (!groupedMap.has(key)) {
          groupedMap.set(key, []);
        }
        groupedMap.get(key)!.push(marcacao);
      });

      // Process grouped marcações: combine into single marcações with multiple products
      const finalMarcacoes: Record<string, Marcacao> = {};
      groupedMap.forEach((group, key) => {
        if (group.length === 1) {
          // Single product - keep as is, but ensure produtos array is set
          const marcacao = group[0];
          if (!marcacao.produtos || marcacao.produtos.length === 0) {
            marcacao.produtos = marcacao.produto ? [marcacao.produto] : [];
          }
          // Use marcacao_id or id as the key
          const recordKey = marcacao.marcacao_id || marcacao.id;
          if (recordKey) {
            finalMarcacoes[recordKey] = marcacao;
          }
        } else {
          // Multiple products - combine into one marcação
          const firstMarcacao = group[0];
          const produtos = group.map(m => m.produto).filter(Boolean);
          
          // Combine all unique produtos from the group
          const allProdutos = new Set<string>();
          group.forEach(m => {
            if (m.produtos && m.produtos.length > 0) {
              m.produtos.forEach((p: string) => allProdutos.add(p));
            } else if (m.produto) {
              allProdutos.add(m.produto);
            }
          });
          const combinedProdutos = Array.from(allProdutos);
          
          // Merge form_data from all marcações so each product's fields (comercializadoraGas, comercializadoraEletricidade, etc.) are present
          const mergedFormData = group.reduce<Record<string, any>>((acc, m) => ({ ...acc, ...(m.form_data || {}) }), {});
          const mergedOriginalCallcenter = group.reduce<Record<string, any>>((acc, m) => ({
            ...acc,
            ...((m.form_data?.original_callcenter) || {})
          }), {});
          const mergedComercialOverrides = group.reduce<Record<string, any>>((acc, m) => ({
            ...acc,
            ...((m.form_data?.comercial_overrides) || {})
          }), {});
          const mergedProductDetails = group.reduce<Record<string, any>>((acc, m) => ({
            ...acc,
            ...(m.product_details || {})
          }), {});
          const combinedFormData = {
            ...mergedFormData,
            original_callcenter: Object.keys(mergedOriginalCallcenter).length > 0 ? mergedOriginalCallcenter : mergedFormData.original_callcenter,
            comercial_overrides: Object.keys(mergedComercialOverrides).length > 0 ? mergedComercialOverrides : mergedFormData.comercial_overrides,
            groupedMarcacoes: group.map(m => m.marcacao_id || m.id).filter(Boolean),
            produtos: combinedProdutos
          };
          // Create combined marcação
          const combinedMarcacao: Marcacao = {
            ...firstMarcacao,
            // Keep original ID and marcacao_id for compatibility
            id: firstMarcacao.id,
            marcacao_id: firstMarcacao.marcacao_id || firstMarcacao.id,
            produto: produtos[0] || firstMarcacao.produto, // Use first product for backward compatibility
            produtos: combinedProdutos, // Store all products
            form_data: combinedFormData,
            // Merge product_details from all events so detail modal has per-product data
            product_details: Object.keys(mergedProductDetails).length > 0 ? mergedProductDetails : firstMarcacao.product_details
          };
          
          // Use first marcação's ID as the key
          const recordKey = firstMarcacao.marcacao_id || firstMarcacao.id;
          if (recordKey) {
            finalMarcacoes[recordKey] = combinedMarcacao;
          }
        }
      });

      if (!isCurrentRequest()) return;
      setMarcacoesData(finalMarcacoes);
      
      // Fetch proposals from database for all marcações
      await fetchProposalsForMarcacoes(finalMarcacoes);
    } catch (error) {
      const aborted =
        (error instanceof DOMException && error.name === 'AbortError') ||
        (error instanceof Error && error.name === 'AbortError');
      if (aborted) return;
      console.error('Error fetching marcações:', error);
      // Keep previous board data on unexpected errors.
    } finally {
      if (isCurrentRequest()) {
        setIsLoadingMarcacoes(false);
      }
    }
  }, [
    currentUserId,
    userMap,
    selectedSidebarUser,
    recuperacaoMode,
    recuperacaoAssigneeFilter,
    searchTerm,
    activeSearchTerm,
  ]);
  
  // Update ref whenever fetchMarcacoes changes
  useEffect(() => {
    fetchMarcacoesRef.current = fetchMarcacoes;
  }, [fetchMarcacoes]);

  // Abort in-flight list fetch on unmount
  useEffect(() => {
    return () => {
      fetchMarcacoesAbortRef.current?.abort();
    };
  }, []);

  // Auto-select current user if regular Comercial user (not Gerente/Admin)
  useEffect(() => {
    if (recuperacaoMode) return;
    if (currentUserId && !isAdminOrGerenteComercial()) {
      setSelectedSidebarUser(currentUserId);
    }
  }, [currentUserId, recuperacaoMode]);

  // For Gestor/Gerente: on first load set selected user to themselves
  useEffect(() => {
    if (recuperacaoMode) return;
    if (currentUserId && isAdminOrGerenteComercial() && selectedSidebarUser === null) {
      setSelectedSidebarUser(currentUserId);
    }
  }, [currentUserId, selectedSidebarUser, recuperacaoMode]);

  // Recuperação: fetch eligible marcações (server-side rules)
  useEffect(() => {
    if (!recuperacaoMode) return;
    fetchMarcacoesRef.current?.();
  }, [recuperacaoMode, recuperacaoAssigneeFilter, activeSearchTerm, fetchMarcacoes]);

  // Gestão Comercial: fetch when user + userMap ready
  useEffect(() => {
    if (recuperacaoMode) return;
    if (!currentUserId) return;
    if (Object.keys(userMap).length === 0) return;
    fetchMarcacoesRef.current?.();
  }, [currentUserId, userMap, selectedSidebarUser, recuperacaoMode]);

  // Reset subsection pagination when filters change
  useEffect(() => {
    setSubsectionCurrentPage({
      'agendamentos-agendamentos': 1,
      'agendamentos-remarcacao': 1,
      'reuniao-realizada-reuniao': 1,
      'feedback-pendencia-concluido-sem-proposta': 1,
      'feedback-pendencia-concluido-com-proposta': 1,
      'feedback-pendencia-not-ok-cliente': 1,
      'feedback-pendencia-not-ok-callcenter': 1,
      'feedback-pendencia-pendente': 1,
      'resultado-fecho-ok': 1,
      'resultado-fecho-not-ok': 1,
      'resultado-aguardar': 1,
      'resultado-cancelado': 1,
    });
  }, [dateFilter, searchTerm, activeSearchTerm, showRecentMarcacoes]);

  // Auto-fill proposal form when modal opens with marcação data (Solicitar Proposta only)
  useEffect(() => {
    if (proposalModalMode === 'refazer') return;
    if (showProposalModal && currentMarcacaoId && marcacoesData[currentMarcacaoId]) {
      const marcacao = marcacoesData[currentMarcacaoId];
      
      // Extract empresa data from marcação
      const formData = marcacao.form_data || {};
      const existingProposalData = formData.proposalData || {};
      
      // Auto-fill empresa fields from marcação
      setProposalData((prev: any) => ({
        ...prev,
        empresa: {
          nomeEmpresa: prev.empresa?.nomeEmpresa ?? '',
          nifEmpresa: existingProposalData.empresa?.nifEmpresa || formData.nifEmpresa || prev.empresa.nifEmpresa || '',
          moradaFornecimento: existingProposalData.empresa?.moradaFornecimento || marcacao.morada || formData.morada || prev.empresa.moradaFornecimento || '',
          // Never hydrate from marcação or saved proposal — only keep what the user typed this session (prev)
          moradaCorrespondencia: prev.empresa?.moradaCorrespondencia ?? '',
          regiao: existingProposalData.empresa?.regiao || formData.regiao || formData.localidade || prev.empresa.regiao || '',
          cae: existingProposalData.empresa?.cae || formData.cae || prev.empresa.cae || '',
          nomeRepresentante: existingProposalData.empresa?.nomeRepresentante || marcacao.nome_responsavel || marcacao.responsavel || prev.empresa.nomeRepresentante || '',
          contactoRepresentante: existingProposalData.empresa?.contactoRepresentante || marcacao.contacto_cliente || marcacao.contacto || prev.empresa.contactoRepresentante || '',
          emailRepresentante: existingProposalData.empresa?.emailRepresentante || formData.emailRepresentante || formData.email || prev.empresa.emailRepresentante || '',
          emailEmpresa: existingProposalData.empresa?.emailEmpresa || formData.emailEmpresa || prev.empresa.emailEmpresa || ''
        },
        // Auto-fill servicos if there's existing proposal data
        servicos: existingProposalData.servicos || prev.servicos || [],
        serviceQuantities: existingProposalData.serviceQuantities || prev.serviceQuantities || {
          'bairro-solar': 0,
          'pap': 0,
          'transacional': 0,
          'luz': 0,
          'gas': 0,
          'mobilidade': 0,
          'outro': 0
        },
        serviceDetails: { ...(existingProposalData.serviceDetails || prev.serviceDetails || {}) }
      }));
    }
  }, [showProposalModal, proposalModalMode, currentMarcacaoId, marcacoesData]);

  useEffect(() => {
    if (!embeddedProposalOnly || !embeddedProposalMarcacaoId) return;
    if (!embeddedMarcacaoSeed || Object.keys(embeddedMarcacaoSeed).length === 0) return;
    if (embeddedProposalOpenRef.current) return;

    embeddedProposalOpenRef.current = true;

    if (embeddedMarcacaoSeed && Object.keys(embeddedMarcacaoSeed).length > 0) {
      setMarcacoesData((prev) => ({ ...prev, ...embeddedMarcacaoSeed }));
    }

    const rawSeed =
      embeddedMarcacaoSeed?.[embeddedProposalMarcacaoId] ||
      Object.values(embeddedMarcacaoSeed || {}).find(
        (m) => String(m.marcacao_id || '').trim() === embeddedProposalMarcacaoId
      );
    const seedMarcacao = rawSeed
      ? {
          ...rawSeed,
          form_data:
            rawSeed.form_data && typeof rawSeed.form_data === 'object'
              ? (rawSeed.form_data as Record<string, unknown>)
              : {},
        }
      : null;

    if (embeddedProposalMode === 'refazer') {
      const completed = buildCompletedProposalsForMarcacao(seedMarcacao);
      if (completed.length === 0) {
        showNotification('Não existem pedidos concluídos para refazer nesta marcação.', 'info');
        onEmbeddedProposalClose?.();
        return;
      }
      setProposalModalMode('refazer');
      setSelectedRefazerProposalId(null);
      setSharedComercialUserIds([]);
      setCurrentMarcacaoId(embeddedProposalMarcacaoId);
      setShowProposalModal(true);
      setCurrentProposalStep(0);
      setProposalData(getEmptyProposalFormState());
      setExpandedServiceSegments({});
      setFieldErrors({});
      return;
    }

    setProposalModalMode('solicitar');
    setSelectedRefazerProposalId(null);
    setSharedComercialUserIds([]);
    setCurrentMarcacaoId(embeddedProposalMarcacaoId);
    setShowProposalModal(true);
    setCurrentProposalStep(1);
    setProposalData(getEmptyProposalFormState());
    setExpandedServiceSegments({});
    setFieldErrors({});
  }, [
    embeddedProposalOnly,
    embeddedProposalMode,
    embeddedProposalMarcacaoId,
    embeddedMarcacaoSeed,
  ]);

  useEffect(() => {
    if (!embeddedProposalOnly) {
      embeddedProposalOpenRef.current = false;
    }
  }, [embeddedProposalOnly]);

  useEffect(() => {
    if (!embeddedContractOnly || !embeddedContractMarcacaoId) return;
    const seedKey = (() => {
      if (!embeddedMarcacaoSeed) return embeddedContractMarcacaoId;
      const keys = Object.keys(embeddedMarcacaoSeed);
      if (keys.includes(embeddedContractMarcacaoId)) return embeddedContractMarcacaoId;
      const match = keys.find(
        (k) =>
          k === embeddedContractMarcacaoId ||
          String(embeddedMarcacaoSeed[k]?.marcacao_id || '') === embeddedContractMarcacaoId
      );
      return match || keys[0] || embeddedContractMarcacaoId;
    })();
    if (embeddedMarcacaoSeed && Object.keys(embeddedMarcacaoSeed).length > 0) {
      setMarcacoesData((prev) => ({ ...prev, ...embeddedMarcacaoSeed }));
    }
    setMarcacaoToSubmitContract(seedKey);
    setShowSubmitContractModal(true);
    setContractSelectedProposalIds([]);
    setContractProposalSearchQuery('');
  }, [embeddedContractOnly, embeddedContractMarcacaoId, embeddedMarcacaoSeed]);

  useEffect(() => {
    if (!embeddedSignatureOnly) return;
    let cancelled = false;
    void (async () => {
      setShowSignatureModal(true);
      setSignatureNotification(null);
      setSignatureForm({
        company_name: '',
        nif: '',
        iban: '',
        bic: '',
        representativeName: '',
        representativeEmail: '',
        representativeContact: '',
        expectedDate: '',
        expectedTime: '',
        associatedMarcacaoId: '',
      });
      setAssociateSearchQuery('');
      await fetchMarcacoesForSignatureAssociate();
      if (!cancelled) await fetchMySignatureRequests('pending');
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally run once when the embedded host mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embeddedSignatureOnly]);

  useEffect(() => {
    if (!gestaoModalsOnly || !autoOpenModal) return;

    if (autoOpenModal === 'minhas-propostas') {
      setShowMinhasPropostasModal(true);
      setCurrentProposalPage(1);
      setProposalSearchTerm('');
      setFilterDay('');
      setFilterMonth('');
      setFilterYear('');
      setFilterServiceType('');
      setFilterStatus('');
      setFilterSection('');
      fetchProposalRequests();
      return;
    }
    if (autoOpenModal === 'meus-contratos') {
      setContractTypeTab('energia_gas');
      setShowMeusContratosModal(true);
      setCurrentContractPage(1);
      setContractSearchTerm('');
      setContractFilterDay('');
      setContractFilterMonth('');
      setContractFilterYear('');
      setContractFilterServiceType('');
      fetchContractSubmissions();
    }
  }, [
    gestaoModalsOnly,
    autoOpenModal,
    currentUserId,
    selectedSidebarUser,
    gestaoFilterUserId,
    fetchProposalRequests,
    fetchContractSubmissions,
  ]);

  // Check if a marcação matches the search term in Gestão das Marcações
  // Search scope: Nome da Empresa, NIF Empresa and Nome do Responsável
  const isMarcacaoMatch = (marcacao: Marcacao, queryRaw: string): boolean => {
    const q = queryRaw.trim().toLowerCase();
    if (!q) return false;

    const marcacaoIdToUse = String(marcacao.marcacao_id || marcacao.id || '');
    const empresa = String(marcacao.empresa || marcacao.nome_empresa || '').toLowerCase();
    const nifEmpresa = String(
      getNifFromProposals(marcacaoIdToUse) ||
      marcacao.form_data?.nifEmpresa ||
      marcacao.form_data?.nif_empresa ||
      ''
    ).toLowerCase();
    const responsavel = String(marcacao.responsavel || marcacao.nome_responsavel || marcacao.cliente || '').toLowerCase();

    return empresa.includes(q) || nifEmpresa.includes(q) || responsavel.includes(q);
  };

  // Handle marcação search and navigate to matching marcação
  const handleMarcacaoSearch = (searchTerm: string) => {
    if (!searchTerm || !searchTerm.trim()) {
      setActiveSearchTerm(null);
      setHighlightedMarcacaoId(null);
      return;
    }

    const trimmedTerm = searchTerm.trim();

    // Find matching marcação in current data
    const allMarcacoes = Object.values(marcacoesData);
    let matchingMarcacao: Marcacao | null = null;

    for (const marcacao of allMarcacoes) {
      if (isMarcacaoMatch(marcacao, trimmedTerm)) {
        matchingMarcacao = marcacao;
        break;
      }
    }

    if (matchingMarcacao) {
      // Determine which section and subsection the marcação belongs to
      const substage = matchingMarcacao.gestao_substage || 'agendamentos-agendamentos';
      let targetSection: Section = 'agendamentos';
      let targetSubsection = substage;

      if (substage.startsWith('agendamentos-')) {
        targetSection = 'agendamentos';
      } else if (substage.startsWith('reuniao-realizada-')) {
        targetSection = 'reuniao-realizada';
      } else if (substage.startsWith('feedback-pendencia-')) {
        targetSection = 'feedback-pendencia';
      } else if (substage.startsWith('resultado-')) {
        targetSection = 'resultado';
      }

      // Navigate to the section
      setCurrentSection(targetSection);

      // Expand the relevant subsection
      setExpandedSubsections(prev => ({
        ...prev,
        [targetSubsection]: true
      }));

      // Highlight the matching marcação
      const marcacaoId = matchingMarcacao.marcacao_id || matchingMarcacao.id;
      setHighlightedMarcacaoId(marcacaoId);
      setActiveSearchTerm(trimmedTerm);

      // Scroll to the subsection after a short delay
      setTimeout(() => {
        const subsectionElement = document.querySelector(`[data-subsection="${targetSubsection}"]`);
        if (subsectionElement) {
          subsectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    } else {
      setActiveSearchTerm(null);
      setHighlightedMarcacaoId(null);
      showNotification(
        'Nenhuma marcação encontrada. Pesquise por Nome da Empresa, NIF Empresa ou Nome do Responsável.',
        'info'
      );
    }
  };

  const runMarcacaoSearchWithNotification = async (term: string) => {
    const trimmedTerm = term?.trim();
    if (!trimmedTerm) {
      setActiveSearchTerm(null);
      setHighlightedMarcacaoId(null);
      return;
    }

    setSearchInProgress(true);
    try {
      // Let React paint the "search in progress" popup before heavy filtering/navigation work.
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      handleMarcacaoSearch(trimmedTerm);
    } finally {
      setSearchInProgress(false);
    }
  };

  // Filter marcações by search term (uses activeSearchTerm, not searchTerm)
  const filterMarcacoesBySearch = (marcacoes: Marcacao[]): Marcacao[] => {
    if (!activeSearchTerm || !activeSearchTerm.trim()) {
      return marcacoes;
    }
    return marcacoes.filter(m => isMarcacaoMatch(m, activeSearchTerm));
  };

  // Filter marcações by date (from selected date forward)
  const filterMarcacoesByDate = (marcacoes: Marcacao[]): Marcacao[] => {
    if (!dateFilter || !dateFilter.trim()) {
      return marcacoes;
    }
    
    const filterDate = new Date(dateFilter);
    filterDate.setHours(0, 0, 0, 0); // Start of the selected date
    
    return marcacoes.filter(m => {
      // Get date from marcação (can be in date or data field)
      const marcacaoDateStr = m.date || m.data || '';
      if (!marcacaoDateStr) return false;
      
      try {
        const marcacaoDate = new Date(marcacaoDateStr);
        marcacaoDate.setHours(0, 0, 0, 0);
        // Include marcações from the selected date forward (>=)
        return marcacaoDate >= filterDate;
      } catch (e) {
        return false;
      }
    });
  };

  /** Start of calendar day for a marcação (ISO `date` or pt `data` DD/MM/AAAA). */
  const getMarcacaoDayStartForFilter = (m: Marcacao): Date | null => {
    try {
      if (m.date) {
        const d = new Date(m.date);
        if (Number.isNaN(d.getTime())) return null;
        d.setHours(0, 0, 0, 0);
        return d;
      }
      if (m.data && typeof m.data === 'string') {
        const parts = m.data.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const mon = parseInt(parts[1], 10) - 1;
          const y = parseInt(parts[2], 10);
          if (!Number.isNaN(day) && !Number.isNaN(mon) && !Number.isNaN(y)) {
            const d = new Date(y, mon, day);
            if (Number.isNaN(d.getTime())) return null;
            d.setHours(0, 0, 0, 0);
            return d;
          }
        }
      }
      const raw = m.date || m.data;
      if (!raw || typeof raw !== 'string') return null;
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return null;
      d.setHours(0, 0, 0, 0);
      return d;
    } catch {
      return null;
    }
  };

  /** End of current calendar week (Sunday 23:59:59.999); week = Monday–Sunday. */
  const getEndOfCurrentWeek = (ref: Date): Date => {
    const today = new Date(ref);
    today.setHours(0, 0, 0, 0);
    const dow = today.getDay(); // 0 Sun … 6 Sat
    const daysUntilSunday = dow === 0 ? 0 : 7 - dow;
    const end = new Date(today);
    end.setDate(today.getDate() + daysUntilSunday);
    end.setHours(23, 59, 59, 999);
    return end;
  };

  // Filter marcações: hide older than 7 days in the past, and hide dates after end of current week
  const filterMarcacoesByRecent = (marcacoes: Marcacao[]): Marcacao[] => {
    if (!showRecentMarcacoes) {
      return marcacoes;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const cutoffDate = new Date(today);
    cutoffDate.setDate(today.getDate() - 7);

    const weekEnd = getEndOfCurrentWeek(today);

    return marcacoes.filter((m) => {
      const marcacaoDay = getMarcacaoDayStartForFilter(m);
      if (!marcacaoDay) return false;
      return marcacaoDay >= cutoffDate && marcacaoDay <= weekEnd;
    });
  };

  /** Gestão: recent + date + search. Recuperação: date only (API already applies CCC eligibility rules). */
  const applyMarcacaoListFilters = (marcacoes: Marcacao[]): Marcacao[] => {
    let filtered = marcacoes;
    if (!recuperacaoMode) {
      filtered = filterMarcacoesByRecent(filtered);
    }
    filtered = filterMarcacoesByDate(filtered);
    return recuperacaoMode ? filtered : filterMarcacoesBySearch(filtered);
  };

  // Get marcações by section based on gestao_substage
  const getMarcacoesBySection = (section: Section): Marcacao[] => {
    const allMarcacoes = Object.values(marcacoesData);
    let filtered: Marcacao[] = [];
    
    switch (section) {
      case 'agendamentos':
        filtered = allMarcacoes.filter(m => {
          const substage = m.gestao_substage || 'agendamentos-agendamentos';
          return substage === 'agendamentos-agendamentos' || substage === 'agendamentos-remarcacao';
        });
        break;
      case 'reuniao-realizada':
        filtered = allMarcacoes.filter(m => {
          const substage = m.gestao_substage || 'agendamentos-agendamentos';
          return substage === 'reuniao-realizada-reuniao';
        });
        break;
      case 'feedback-pendencia':
        filtered = allMarcacoes.filter(m => {
          const substage = m.gestao_substage || 'agendamentos-agendamentos';
          return substage.startsWith('feedback-pendencia-');
        });
        break;
      case 'resultado':
        filtered = allMarcacoes.filter(m => {
          const substage = m.gestao_substage || 'agendamentos-agendamentos';
          return substage.startsWith('resultado-');
        });
        break;
      default:
        return [];
    }

    return applyMarcacaoListFilters(filtered);
  };

  // Get occurrence date as timestamp for sorting (data da marcação)
  const getOccurrenceTime = (m: Marcacao): number => {
    if (m.date) return new Date(m.date).getTime();
    if (m.data && typeof m.data === 'string') {
      const parts = m.data.split('/');
      if (parts.length === 3) {
        const d = parseInt(parts[0], 10), mon = parseInt(parts[1], 10) - 1, y = parseInt(parts[2], 10);
        if (!isNaN(d) && !isNaN(mon) && !isNaN(y)) return new Date(y, mon, d).getTime();
      }
    }
    return 0;
  };

  /** Timestamp for oldest-first sort; missing/unparseable date → MAX so those rows sort last (ka - kb). */
  const getOccurrenceSortKeyAsc = (m: Marcacao): number => {
    const t = getOccurrenceTime(m);
    if (!Number.isFinite(t) || t === 0) return Number.MAX_SAFE_INTEGER;
    return t;
  };

  // Sort and paginate subsection list: data da marcação ascending (oldest first), then created_at ascending
  const getPaginatedSubsection = (subsectionKey: string, fullList: Marcacao[]): { items: Marcacao[]; total: number; currentPage: number; totalPages: number } => {
    const page = subsectionCurrentPage[subsectionKey] ?? 1; // key selects pagination bucket only; sort is uniform across subsections
    const sorted = [...fullList].sort((a, b) => {
      const ka = getOccurrenceSortKeyAsc(a);
      const kb = getOccurrenceSortKeyAsc(b);
      if (ka !== kb) return ka - kb;
      const cA = new Date(a.created_at || 0).getTime();
      const cB = new Date(b.created_at || 0).getTime();
      return cA - cB;
    });
    const total = sorted.length;
    const totalPages = Math.max(1, Math.ceil(total / MARCADOES_PER_PAGE));
    const start = (page - 1) * MARCADOES_PER_PAGE;
    const items = sorted.slice(start, start + MARCADOES_PER_PAGE);
    return { items, total, currentPage: page, totalPages };
  };

  const setSubsectionPage = (subsectionKey: string, nextPage: number) => {
    setSubsectionCurrentPage(prev => ({ ...prev, [subsectionKey]: nextPage }));
  };

  // Helper functions for Agendamentos subsections
  const getAgendamentosSubsection = () => {
    const filtered = Object.values(marcacoesData).filter(m => {
      const substage = m.gestao_substage || 'agendamentos-agendamentos';
      return substage === 'agendamentos-agendamentos';
    });
    return applyMarcacaoListFilters(filtered);
  };
  
  const getRemarcacoesSubsection = () => {
    const filtered = Object.values(marcacoesData).filter(m => {
      const substage = m.gestao_substage || 'agendamentos-agendamentos';
      return substage === 'agendamentos-remarcacao';
    });
    return applyMarcacaoListFilters(filtered);
  };

  // Helper functions for Feedback/Pendencia subsections
  const getConcluidoSemProposta = () => {
    const filtered = Object.values(marcacoesData).filter(m => {
      const substage = m.gestao_substage || 'agendamentos-agendamentos';
      return substage === 'feedback-pendencia-concluido-sem-proposta';
    });
    return applyMarcacaoListFilters(filtered);
  };
  
  const getConcluidoComProposta = () => {
    const filtered = Object.values(marcacoesData).filter(m => {
      const substage = m.gestao_substage || 'agendamentos-agendamentos';
      return substage === 'feedback-pendencia-concluido-com-proposta';
    });
    return applyMarcacaoListFilters(filtered);
  };
  
  const getNotOkCliente = () => {
    const filtered = Object.values(marcacoesData).filter(m => {
      const substage = m.gestao_substage || 'agendamentos-agendamentos';
      return substage === 'feedback-pendencia-not-ok-cliente';
    });
    return applyMarcacaoListFilters(filtered);
  };
  
  const getNotOkCallCenter = () => {
    const filtered = Object.values(marcacoesData).filter(m => {
      const substage = m.gestao_substage || 'agendamentos-agendamentos';
      return substage === 'feedback-pendencia-not-ok-callcenter';
    });
    return applyMarcacaoListFilters(filtered);
  };
  
  const getPendente = () => {
    const filtered = Object.values(marcacoesData).filter(m => {
      const substage = m.gestao_substage || 'agendamentos-agendamentos';
      return substage === 'feedback-pendencia-pendente';
    });
    return applyMarcacaoListFilters(filtered);
  };

  const getMarcacaoPageInSubsectionList = (fullList: Marcacao[], marcacaoId: string): number => {
    const sorted = [...fullList].sort((a, b) => {
      const ka = getOccurrenceSortKeyAsc(a);
      const kb = getOccurrenceSortKeyAsc(b);
      if (ka !== kb) return ka - kb;
      const cA = new Date(a.created_at || 0).getTime();
      const cB = new Date(b.created_at || 0).getTime();
      return cA - cB;
    });
    const index = sorted.findIndex((m) => (m.marcacao_id || m.id) === marcacaoId);
    if (index < 0) return 1;
    return Math.floor(index / MARCADOES_PER_PAGE) + 1;
  };

  const getSubsectionListForMandatoryFeedback = (subsection: string): Marcacao[] => {
    switch (subsection) {
      case 'feedback-pendencia-not-ok-cliente':
        return getNotOkCliente();
      case 'feedback-pendencia-not-ok-callcenter':
        return getNotOkCallCenter();
      case 'feedback-pendencia-pendente':
        return getPendente();
      default:
        return [];
    }
  };

  const focusSubsectionPagesForMissingFeedback = (
    subsectionsWithEmptyFeedback: Record<string, boolean>,
    marcacoesToHighlight: Set<string>
  ) => {
    const pageUpdates: Record<string, number> = {};
    for (const subsection of Object.keys(subsectionsWithEmptyFeedback)) {
      const fullList = getSubsectionListForMandatoryFeedback(subsection);
      for (const marcacaoId of Array.from(marcacoesToHighlight)) {
        if (fullList.some((m) => (m.marcacao_id || m.id) === marcacaoId)) {
          pageUpdates[subsection] = getMarcacaoPageInSubsectionList(fullList, marcacaoId);
          break;
        }
      }
    }
    if (Object.keys(pageUpdates).length > 0) {
      setSubsectionCurrentPage((prev) => ({ ...prev, ...pageUpdates }));
    }
  };

  // Helper functions for Resultado subsections
  const getFechoOk = () => {
    const filtered = Object.values(marcacoesData).filter(m => {
      const substage = m.gestao_substage || 'agendamentos-agendamentos';
      return substage === 'resultado-fecho-ok';
    });
    return applyMarcacaoListFilters(filtered);
  };
  
  const getFechoNotOk = () => {
    const filtered = Object.values(marcacoesData).filter(m => {
      const substage = m.gestao_substage || 'agendamentos-agendamentos';
      return substage === 'resultado-fecho-not-ok';
    });
    return applyMarcacaoListFilters(filtered);
  };
  
  const getAguardar = () => {
    const filtered = Object.values(marcacoesData).filter(m => {
      const substage = m.gestao_substage || 'agendamentos-agendamentos';
      return substage === 'resultado-aguardar';
    });
    return applyMarcacaoListFilters(filtered);
  };
  
  const getCancelado = () => {
    const filtered = Object.values(marcacoesData).filter(m => {
      const substage = m.gestao_substage || 'agendamentos-agendamentos';
      return substage === 'resultado-cancelado';
    });
    return applyMarcacaoListFilters(filtered);
  };

  // Helper: for combined marcações (multiple products), return all marcacao IDs to update; otherwise just the one.
  const getMarcacaoIdsToUpdateForSubstage = (marcacaoId: string): string[] => {
    const marcacao = marcacoesData[marcacaoId];
    if (!marcacao) return [marcacaoId];
    const grouped = marcacao.form_data?.groupedMarcacoes;
    if (Array.isArray(grouped) && grouped.length > 0) return grouped;
    const id = marcacao.marcacao_id || marcacao.id || marcacaoId;
    return [id];
  };

  // Function to update sub-stage (single marcacao_id). Use getMarcacaoIdsToUpdateForSubstage + loop for combined marcações.
  const updateMarcacaoSubstage = async (marcacaoId: string, newSubstage: string, options?: { silent?: boolean }) => {
    try {
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
      const baseUrl = recuperacaoMode ? resolveCccApiBaseUrl() : resolveMainApiBaseUrl();
      const url = recuperacaoMode
        ? `${baseUrl}/api/ccc/recuperacao/marcacoes/${marcacaoId}/substage`
        : `${baseUrl}/api/comercial/gestao/marcacoes/${marcacaoId}/substage`;
      
      const res = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ substage: newSubstage })
      });

      if (!res.ok) {
        const error = await res.json();
        if (!options?.silent) showNotification(`Erro ao atualizar sub-stage: ${error.message || 'Erro desconhecido'}`, 'error');
        return false;
      }

      const json = await res.json();
      if (json.success) {
        // Update local state
        setMarcacoesData(prev => {
          const updated = { ...prev };
          if (updated[marcacaoId]) {
            updated[marcacaoId] = {
              ...updated[marcacaoId],
              gestao_substage: newSubstage
            };
          }
          return updated;
        });
        if (!options?.silent) showNotification('Sub-stage atualizado com sucesso!', 'success');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error updating substage:', error);
      if (!options?.silent) showNotification('Erro ao atualizar sub-stage', 'error');
      return false;
    }
  };

  // Save Informações Comerciais (commercial user edits, kept separate from CallCenter info)
  const saveComercialInfo = async () => {
    if (!currentMarcacaoId) return;
    const record = marcacoesData[currentMarcacaoId];
    const marcacaoId = record?.marcacao_id || record?.id || currentMarcacaoId;
    const apiMarcacaoId = typeof marcacaoId === 'string' ? marcacaoId : String(marcacaoId);
    if (!apiMarcacaoId) return;
    setSavingComercialInfo(true);
    try {
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      const url = `${backendUrl}/api/comercial/gestao/marcacoes/${apiMarcacaoId}/informacoes-comerciais`;
      const res = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ informacoesComerciais: editComercialInfo })
      });
      if (!res.ok) {
        const err = await res.json();
        showNotification(err.message || 'Erro ao guardar informações comerciais', 'error');
        return;
      }
      const json = await res.json();
      if (json.success && json.marcacao) {
        setMarcacoesData(prev => {
          const updated = { ...prev };
          const key = currentMarcacaoId;
          if (updated[key]) {
            updated[key] = {
              ...updated[key],
              form_data: { ...(updated[key].form_data || {}), informacoesComerciais: json.marcacao.form_data?.informacoesComerciais || editComercialInfo }
            };
          }
          return updated;
        });
        showNotification('Informações comerciais guardadas com sucesso.', 'success');
      }
    } catch (error) {
      console.error('Error saving informacoes comerciais:', error);
      showNotification('Erro ao guardar informações comerciais', 'error');
    } finally {
      setSavingComercialInfo(false);
    }
  };

  // Update sub-stage for a marcação (handles combined multi-product: updates all grouped IDs).
  const updateMarcacaoSubstageForMarcacao = async (marcacaoId: string, newSubstage: string): Promise<boolean> => {
    const ids = getMarcacaoIdsToUpdateForSubstage(marcacaoId);
    for (let i = 0; i < ids.length; i++) {
      const ok = await updateMarcacaoSubstage(ids[i], newSubstage, { silent: i > 0 });
      if (!ok) return false;
    }
    return true;
  };

  const switchSection = (section: Section) => {
    // Validate feedback fields before navigating to any other section
    // Feedback is optional for Concluído Sem Proposta and Concluído Com Proposta
    const subsectionsToCheck = [
      'feedback-pendencia-not-ok-cliente',
      'feedback-pendencia-not-ok-callcenter',
      'feedback-pendencia-pendente'
    ];

    const marcacoesWithoutFeedbackList: string[] = [];
    const subsectionsWithEmptyFeedback: Record<string, boolean> = {};
    const marcacoesToHighlight: Set<string> = new Set();

    subsectionsToCheck.forEach(subsection => {
      let hasEmptyFeedback = false;
      const marcacoes = Object.values(marcacoesData).filter(m => {
        const substage = m.gestao_substage || 'agendamentos-agendamentos';
        return substage === subsection;
      });

      marcacoes.forEach(marcacao => {
        // For Not Ok CallCenter, check using hasNotOkCallCenterFeedback
        if (subsection === 'feedback-pendencia-not-ok-callcenter') {
          if (!hasNotOkCallCenterFeedback(marcacao)) {
            hasEmptyFeedback = true;
            const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
            marcacoesWithoutFeedbackList.push(`#${marcacaoIdToUse.toUpperCase()}`);
            marcacoesToHighlight.add(marcacaoIdToUse);
          }
        } else {
          // For other subsections, check text feedback
          const feedback = marcacao.feedback;
          const feedbackText = typeof feedback === 'string' ? feedback : '';
          if (!feedbackText.trim()) {
            hasEmptyFeedback = true;
            const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
            marcacoesWithoutFeedbackList.push(`#${marcacaoIdToUse.toUpperCase()}`);
            marcacoesToHighlight.add(marcacaoIdToUse);
          }
        }
      });

      if (hasEmptyFeedback) {
        subsectionsWithEmptyFeedback[subsection] = true;
      }
    });

    if (marcacoesWithoutFeedbackList.length > 0) {
      setMarcacoesWithoutFeedback(marcacoesWithoutFeedbackList);
      setHighlightedSubsections(subsectionsWithEmptyFeedback);
      setHighlightedMarcacoesForFeedback(marcacoesToHighlight);
      focusSubsectionPagesForMissingFeedback(subsectionsWithEmptyFeedback, marcacoesToHighlight);
      setShowFeedbackValidationModal(true);
      
      // Switch to feedback-pendencia section and expand relevant subsections
      setCurrentSection('feedback-pendencia');
      setExpandedSubsections(prev => ({
        ...prev,
        ...subsectionsWithEmptyFeedback
      }));

      // Scroll to feedback section after a short delay
      setTimeout(() => {
        const feedbackSection = document.querySelector('.section-content.active');
        if (feedbackSection) {
          feedbackSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);

      return; // Don't navigate to the requested section
    }

    // If no validation issues, proceed with navigation
    setCurrentSection(section);
  };

  const toggleSubsection = (subsectionId: string) => {
    setExpandedSubsections(prev => ({
      ...prev,
      [subsectionId]: !prev[subsectionId]
    }));
  };

  const collapseAllSubsections = (section: Section) => {
    const subsectionIds = Object.keys(expandedSubsections).filter(id => 
      id.startsWith(section.replace('-', '-'))
    );
    const newState = { ...expandedSubsections };
    subsectionIds.forEach(id => {
      newState[id] = false;
    });
    setExpandedSubsections(newState);
  };

  const openUpdateModal = (marcacaoId: string) => {
    setCurrentMarcacaoId(marcacaoId);
    setShowUpdateModal(true);
  };

  const closeUpdateModal = () => {
    setShowUpdateModal(false);
    setCurrentMarcacaoId(null);
  };

  const openRescheduleModal = (marcacaoId: string) => {
    setCurrentMarcacaoId(marcacaoId);
    setShowRescheduleModal(true);
    
    // Pre-fill the form with current marcação values
    setTimeout(() => {
      const marcacao = marcacoesData[marcacaoId];
      if (marcacao) {
        const dateInput = document.getElementById('rescheduleDate') as HTMLInputElement;
        const horaInicioInput = document.getElementById('rescheduleHoraInicio') as HTMLInputElement;
        const horaFimInput = document.getElementById('rescheduleHoraFim') as HTMLInputElement;
        
        if (dateInput && marcacao.date) {
          // Convert date to YYYY-MM-DD format if needed
          const dateValue = marcacao.date.includes('T') 
            ? marcacao.date.split('T')[0] 
            : marcacao.date.split(' ')[0];
          dateInput.value = dateValue;
        }
        
        if (horaInicioInput && marcacao.hora_inicio) {
          // Format time to HH:MM for input type="time"
          const horaInicio = marcacao.hora_inicio.length === 5 
            ? marcacao.hora_inicio 
            : marcacao.hora_inicio.substring(0, 5);
          horaInicioInput.value = horaInicio;
        }
        
        if (horaFimInput && marcacao.hora_fim) {
          // Format time to HH:MM for input type="time"
          const horaFim = marcacao.hora_fim.length === 5 
            ? marcacao.hora_fim 
            : marcacao.hora_fim.substring(0, 5);
          horaFimInput.value = horaFim;
        }
      }
    }, 100);
  };

  const closeRescheduleModal = () => {
    setShowRescheduleModal(false);
    setCurrentMarcacaoId(null);
  };

  const openDetailsModal = (marcacaoId: string) => {
    setCurrentMarcacaoId(marcacaoId);
    setShowDetailsModal(true);
    setEditComercialInfo(marcacoesData[marcacaoId]?.form_data?.informacoesComerciais || {});
    setIsCallCenterInfoCollapsed(false);
    setIsComercialInfoCollapsed(false);
    setIsRescheduleReasonCollapsed(false);
    setIsProposalInfoCollapsed(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setCurrentMarcacaoId(null);
    setEditComercialInfo({});
    // Reset collapsed states when closing modal
    setIsCallCenterInfoCollapsed(false);
    setIsComercialCurrentCollapsed(false);
    setIsComercialInfoCollapsed(false);
    setIsRescheduleReasonCollapsed(false);
    setIsProposalInfoCollapsed(true); // Reset to collapsed
  };


  const openEditModal = (marcacaoId: string) => {
    setCurrentMarcacaoId(marcacaoId);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setCurrentMarcacaoId(null);
  };

  const getEmptyProposalFormState = () => ({
    equipaInterna: {
      nomeUsuario: currentUserData?.name || userMap[currentUserId || 0] || '',
      codigoComercial: currentUserData?.codigoComercial || '',
      equipa: currentUserData?.team || '',
      emailUsuario: currentUserData?.email || '',
      propostaMarcacao: '',
      assinaturaContrato: '',
      tipoPedido: 'normal'
    },
    empresa: {
      nomeEmpresa: '',
      nifEmpresa: '',
      moradaFornecimento: '',
      moradaCorrespondencia: '',
      regiao: '',
      cae: '',
      nomeRepresentante: '',
      contactoRepresentante: '',
      emailRepresentante: '',
      emailEmpresa: ''
    },
    servico: {},
    resumo: {},
    servicos: [] as any[],
    serviceQuantities: {
      'bairro-solar': 0,
      'pap': 0,
      'transacional': 0,
      'luz': 0,
      'gas': 0,
      'mobilidade': 0,
      'outro': 0
    },
    serviceDetails: {} as Record<string, any>
  });

  const applyRefazerSourceProposal = (proposalId: string) => {
    const completedList = currentMarcacaoId
      ? buildCompletedProposalsForMarcacao(resolveMarcacaoInStore(marcacoesData, currentMarcacaoId))
      : refazerCompletedProposals;
    const entry = completedList.find((p) => p.id === proposalId);
    if (!entry) return;
    const source = (entry.proposal || {}) as Record<string, any>;
    const defaultQuantities = {
      'bairro-solar': 0,
      'pap': 0,
      'transacional': 0,
      'luz': 0,
      'gas': 0,
      'mobilidade': 0,
      'outro': 0
    };
    const serviceDetailsClone: Record<string, any> = {};
    Object.entries(source.serviceDetails || {}).forEach(([instanceId, details]) => {
      if (!details || typeof details !== 'object') return;
      const clean = { ...(details as Record<string, any>) };
      delete clean.fatura;
      delete clean.printTelhado;
      serviceDetailsClone[instanceId] = clean;
    });
    const servicos = Array.isArray(source.servicos)
      ? source.servicos.map((instance: any) => ({ ...instance }))
      : [];
    setProposalData({
      equipaInterna: {
        ...getEmptyProposalFormState().equipaInterna,
        ...(source.equipaInterna || {}),
        nomeUsuario: currentUserData?.name || userMap[currentUserId || 0] || source.equipaInterna?.nomeUsuario || '',
        codigoComercial: currentUserData?.codigoComercial || source.equipaInterna?.codigoComercial || '',
        equipa: currentUserData?.team || source.equipaInterna?.equipa || '',
        emailUsuario: currentUserData?.email || source.equipaInterna?.emailUsuario || ''
      },
      empresa: { ...getEmptyProposalFormState().empresa, ...(source.empresa || {}) },
      servico: source.servico || {},
      resumo: source.resumo || {},
      servicos,
      serviceQuantities: { ...defaultQuantities, ...(source.serviceQuantities || {}) },
      serviceDetails: serviceDetailsClone
    });
    const expanded: Record<string, boolean> = {};
    servicos.forEach((instance: any) => {
      const instanceId = instance.instanceId || `${instance.service}-${instance.instanceNumber}`;
      expanded[instanceId] = true;
      if (instance.service) expanded[instance.service] = true;
    });
    setExpandedServiceSegments(expanded);
    setSharedComercialUserIds(normalizedSharedComercialIdsFromProposal(source as Record<string, unknown>));
  };

  const openProposalModal = (marcacaoId: string) => {
    setProposalModalMode('solicitar');
    setSelectedRefazerProposalId(null);
    setSharedComercialUserIds([]);
    setCurrentMarcacaoId(marcacaoId);
    setShowProposalModal(true);
    setCurrentProposalStep(1);
    setProposalData(getEmptyProposalFormState());
    setExpandedServiceSegments({});
    setProposalNotesPasteText('');
    setProposalNotesImportErrors([]);
    setProposalNotesImportWarnings([]);
    setProposalNotesImportBanner(false);
    setProposalNotesPasteOpen(false);
  };

  const importProposalNotesText = (raw: string) => {
    const parsed = parseProposalNotesTxt(raw);
    if (!parsed.ok) {
      setProposalNotesImportErrors(parsed.errors);
      setProposalNotesImportWarnings(parsed.warnings);
      setProposalNotesImportBanner(false);
      showNotification(parsed.errors[0] || 'Não foi possível importar as notas.', 'error');
      return;
    }

    const sessionIdentity = {
      nomeUsuario: currentUserData?.name || userMap[currentUserId || 0] || '',
      codigoComercial: currentUserData?.codigoComercial || '',
      equipa: currentUserData?.team || '',
      emailUsuario: currentUserData?.email || '',
    };

    setProposalData((prev: any) =>
      applyProposalNotesImport(parsed.data, prev, sessionIdentity)
    );

    const expanded: Record<string, boolean> = {};
    parsed.data.services.forEach((svc) => {
      const instanceId = `${svc.service}-${svc.instanceNumber}`;
      expanded[instanceId] = true;
      expanded[svc.service] = true;
    });
    setExpandedServiceSegments(expanded);
    setProposalNotesImportErrors([]);
    setProposalNotesImportWarnings(parsed.warnings);
    setProposalNotesImportBanner(true);

    const svcCount = parsed.data.services.length;
    showNotification(
      svcCount > 0
        ? `Notas importadas (${svcCount} serviço${svcCount === 1 ? '' : 's'}). Anexe ainda a Fatura / Print do Telhado.`
        : 'Notas importadas. Anexe ainda a Fatura / Print do Telhado nos serviços.',
      parsed.warnings.length ? 'info' : 'success'
    );

    if (svcCount > 0) {
      setCurrentProposalStep(3);
    } else {
      setCurrentProposalStep(2);
    }
  };

  const handleProposalNotesFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      importProposalNotesText(text);
    };
    reader.onerror = () => {
      showNotification('Erro ao ler o ficheiro .txt.', 'error');
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleCopyProposalNotesTemplate = async () => {
    try {
      await copyProposalNotesTemplate();
      showNotification('Modelo copiado para a área de transferência.', 'success');
    } catch {
      showNotification('Não foi possível copiar o modelo.', 'error');
    }
  };

  const openRefazerProposalModal = (marcacaoId: string) => {
    const completed = buildCompletedProposalsForMarcacao(
      resolveMarcacaoInStore(marcacoesData, marcacaoId)
    );
    if (completed.length === 0) {
      showNotification('Não existem pedidos concluídos para refazer nesta marcação.', 'info');
      return;
    }
    setProposalModalMode('refazer');
    setSelectedRefazerProposalId(null);
    setSharedComercialUserIds([]);
    setCurrentMarcacaoId(marcacaoId);
    setShowProposalModal(true);
    setCurrentProposalStep(0);
    setProposalData(getEmptyProposalFormState());
    setExpandedServiceSegments({});
    setFieldErrors({});
  };

  const closeProposalModal = () => {
    setShowProposalModal(false);
    setProposalModalMode('solicitar');
    setSelectedRefazerProposalId(null);
    setCurrentMarcacaoId(null);
    setCurrentProposalStep(1);
    setIsInstantMarcacao(false); // Reset flag when closing proposal modal
    setProposalData(getEmptyProposalFormState());
    setExpandedServiceSegments({});
    setSharedComercialUserIds([]);
    setFieldErrors({});
    setProposalNotesPasteText('');
    setProposalNotesImportErrors([]);
    setProposalNotesImportWarnings([]);
    setProposalNotesImportBanner(false);
    setProposalNotesPasteOpen(false);
    if (embeddedProposalOnly) {
      onEmbeddedProposalClose?.();
    }
  };

  const validateStep = (step: number): boolean => {
    const errors: Record<string, string> = {};
    
    if (proposalModalMode === 'refazer' && step === 0) {
      if (!selectedRefazerProposalId) {
        errors['refazerSelection'] = 'Selecione um pedido concluído para continuar.';
      }
      setFieldErrors(errors);
      return Object.keys(errors).length === 0;
    }
    
    if (step === 1) {
      // Step 1: Equipa Interna
      if (!proposalData.equipaInterna?.propostaMarcacao) {
        errors['propostaMarcacao'] = 'Por favor, selecione se é uma Proposta de Marcação.';
      }
      if (!proposalData.equipaInterna?.assinaturaContrato) {
        errors['assinaturaContrato'] = 'Por favor, selecione o tipo de Assinatura do Contrato.';
      }
      if (!proposalData.equipaInterna?.tipoPedido) {
        errors['tipoPedido'] = 'Por favor, selecione o Tipo de Pedido.';
      }
    } else if (step === 2) {
      // Step 2: Empresa
      const empresa = proposalData?.empresa || {};
      
      // Check required fields
      if (!empresa.nomeEmpresa || (typeof empresa.nomeEmpresa === 'string' && empresa.nomeEmpresa.trim() === '')) {
        errors['nomeEmpresa'] = 'Este campo é obrigatório.';
      }
      
      if (!empresa.nifEmpresa || (typeof empresa.nifEmpresa === 'string' && empresa.nifEmpresa.trim() === '')) {
        errors['nifEmpresa'] = 'Este campo é obrigatório.';
      } else if (empresa.nifEmpresa.length !== 9) {
        errors['nifEmpresa'] = 'O NIF deve ter exatamente 9 dígitos.';
      }
      
      
      if (!empresa.regiao || (typeof empresa.regiao === 'string' && empresa.regiao.trim() === '')) {
        errors['regiao'] = 'Este campo é obrigatório.';
      }
      
      if (!empresa.cae || (typeof empresa.cae === 'string' && empresa.cae.trim() === '')) {
        errors['cae'] = 'Este campo é obrigatório.';
      } else {
        // Check if CAE is exactly 5 digits
        const caeValue = empresa.cae.toString().trim();
        if (caeValue.length !== 5) {
          errors['cae'] = 'O CAE deve ter exatamente 5 dígitos.';
        } else if (!/^\d+$/.test(caeValue)) {
          errors['cae'] = 'O CAE deve conter apenas números.';
        }
      }
      
      if (!empresa.nomeRepresentante || (typeof empresa.nomeRepresentante === 'string' && empresa.nomeRepresentante.trim() === '')) {
        errors['nomeRepresentante'] = 'Este campo é obrigatório.';
      }
      
      if (!empresa.contactoRepresentante || (typeof empresa.contactoRepresentante === 'string' && empresa.contactoRepresentante.trim() === '')) {
        errors['contactoRepresentante'] = 'Este campo é obrigatório.';
      }
      
      if (!empresa.emailRepresentante || (typeof empresa.emailRepresentante === 'string' && empresa.emailRepresentante.trim() === '')) {
        errors['emailRepresentante'] = 'Este campo é obrigatório.';
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(empresa.emailRepresentante)) {
          errors['emailRepresentante'] = 'Por favor, insira um email válido.';
        }
      }
      
      // Validate optional email if it exists
      if (empresa.emailEmpresa && empresa.emailEmpresa.trim() !== '') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(empresa.emailEmpresa)) {
          errors['emailEmpresa'] = 'Por favor, insira um email válido.';
        }
      }
    } else if (step === 3) {
      // Step 3: Serviço
      const totalServices = Object.values(proposalData.serviceQuantities || {}).reduce((sum: number, qty: any) => sum + (qty || 0), 0);
      if (totalServices === 0) {
        errors['servicos'] = 'Por favor, selecione pelo menos um serviço.';
      }
      
      // Validate CPE for all service instances that require it
      for (const instance of proposalData.servicos || []) {
        const instanceId = instance.instanceId;
        const instanceDetails = proposalData.serviceDetails[instanceId] || {};
        const serviceType = instance.service;
        
        // CPE is required for bairro-solar, pap, luz, and transacional
        if (serviceType === 'bairro-solar' || serviceType === 'pap' || serviceType === 'luz' || serviceType === 'transacional') {
          const cpe = instanceDetails.cpe || '';
          if (!cpe || cpe.trim() === '') {
            errors[`cpe-${instanceId}`] = 'Este campo é obrigatório.';
          } else {
            const cpeValue = cpe.toString().trim().toUpperCase();
            // CPE must start with "PT" and have 16 digits + 2 letters (PT + 16 digits + 2 letters = 20 characters)
            if (!cpeValue.startsWith('PT')) {
              errors[`cpe-${instanceId}`] = 'O CPE deve começar com "PT".';
            } else if (cpeValue.length !== 20) {
              errors[`cpe-${instanceId}`] = 'O CPE deve ter exatamente 20 caracteres (PT + 16 dígitos + 2 letras).';
            } else {
              // Check if after "PT" there are exactly 16 digits followed by 2 letters
              const afterPT = cpeValue.substring(2);
              if (!/^\d{16}[A-Z]{2}$/.test(afterPT)) {
                errors[`cpe-${instanceId}`] = 'O CPE deve ter "PT" seguido de exatamente 16 dígitos e 2 letras.';
              }
            }
          }
        }
        
        // CUI is required for gas and must start with "PT"
        if (serviceType === 'gas') {
          const cui = (instanceDetails.cui || '').toString().trim().toUpperCase();
          if (!cui) {
            errors[`cui-${instanceId}`] = 'Este campo é obrigatório.';
          } else if (!cui.startsWith('PT')) {
            errors[`cui-${instanceId}`] = 'O CUI deve começar com "PT".';
          }
        }
        
        // Fatura is required for energy/solar services (not Mobilidade nor Outro)
        if (!PROPOSAL_SERVICES_WITHOUT_ENERGY_DOCS.has(serviceType)) {
          if (!instanceDetails.fatura && !instanceDetails.fatura_name) {
            errors[`fatura-${instanceId}`] = 'Este campo é obrigatório.';
          }
        }

        if (serviceType === 'outro') {
          const observacoes = (instanceDetails.observacoes || '').trim();
          if (!observacoes) {
            errors[`observacoes-${instanceId}`] = 'Descreva o serviço em Observações.';
          }
        }

        if (serviceType === 'mobilidade') {
          for (const fieldKey of MOBILIDADE_FIELD_KEYS) {
            const value = String(instanceDetails[fieldKey] || '').trim();
            if (!value) {
              errors[`${fieldKey}-${instanceId}`] = 'Este campo é obrigatório.';
            }
          }
        }
        
        // Print do Telhado is required for bairro-solar, pap, and transacional, but NOT for luz
        if (serviceType !== 'luz' && (serviceType === 'bairro-solar' || serviceType === 'pap' || serviceType === 'transacional')) {
          if (!instanceDetails.printTelhado && !instanceDetails.printTelhado_name) {
            errors[`printTelhado-${instanceId}`] = 'Este campo é obrigatório.';
          }
        }
        
        // Data prevista para o Fecho (RoadTrip) only for services that expose the field
        if (
          proposalData.equipaInterna?.tipoPedido === 'roadtrip' &&
          PROPOSAL_ROADTRIP_FECHO_SERVICES.has(serviceType)
        ) {
          const dataFecho = instanceDetails.dataFecho || '';
          if (!dataFecho || dataFecho.trim() === '') {
            errors[`dataFecho-${instanceId}`] = 'Este campo é obrigatório.';
          }
        }
        
        // Coordenadas Geográficas is required only for bairro-solar
        if (serviceType === 'bairro-solar') {
          const coordenadasGeograficas = instanceDetails.coordenadasGeograficas || '';
          if (!coordenadasGeograficas || coordenadasGeograficas.trim() === '') {
            errors[`coordenadasGeograficas-${instanceId}`] = 'Este campo é obrigatório.';
          }
        }
        
        // Morada de Fornecimento is required for all services
        const moradaFornecimento = instanceDetails.moradaFornecimento || '';
        if (!moradaFornecimento || moradaFornecimento.trim() === '') {
          errors[`moradaFornecimento-${instanceId}`] = 'Este campo é obrigatório.';
        }
      }
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getProposalNextStep = (step: number): number | null => {
    if (proposalModalMode === 'refazer') {
      if (step === 0) return 2;
      if (step === 2) return 3;
      if (step === 3) return 4;
      return null;
    }
    if (step < 4) return step + 1;
    return null;
  };

  const getProposalPrevStep = (step: number): number | null => {
    if (proposalModalMode === 'refazer') {
      if (step === 2) return 0;
      if (step === 3) return 2;
      if (step === 4) return 3;
      return null;
    }
    if (step > 1) return step - 1;
    return null;
  };

  const getProposalMinStep = () => (proposalModalMode === 'refazer' ? 0 : 1);

  const changeProposalStep = (direction: 'next' | 'prev') => {
    const scrollModalTop = () => {
      const modalBody = document.querySelector('.proposal-modal .modal-body');
      if (modalBody) modalBody.scrollTop = 0;
    };

    if (direction === 'next') {
      const nextStep = getProposalNextStep(currentProposalStep);
      if (nextStep == null) return;
      setFieldErrors({});
      if (!validateStep(currentProposalStep)) return;
      if (proposalModalMode === 'refazer' && currentProposalStep === 0 && selectedRefazerProposalId) {
        applyRefazerSourceProposal(selectedRefazerProposalId);
      }
      setCurrentProposalStep(nextStep);
      scrollModalTop();
      return;
    }

    if (direction === 'prev') {
      const prevStep = getProposalPrevStep(currentProposalStep);
      if (prevStep == null) return;
      setCurrentProposalStep(prevStep);
      scrollModalTop();
    }
  };

  const toggleServiceSegment = (serviceType: string) => {
    setExpandedServiceSegments(prev => ({
      ...prev,
      [serviceType]: !prev[serviceType]
    }));
  };

  const handleServiceQuantityChange = (serviceType: string, newQuantity: number) => {
    setProposalData((prev: any) => {
      const currentQuantity = prev.serviceQuantities[serviceType] || 0;
      const newQuantityValid = Math.max(0, Math.min(99, newQuantity)); // Limit between 0 and 99
      
      // Update quantity
      const newServiceQuantities = {
        ...prev.serviceQuantities,
        [serviceType]: newQuantityValid
      };
      
      // Update service instances
      let newServicos = [...prev.servicos];
      
      let newServiceDetails = { ...prev.serviceDetails };
      if (newQuantityValid > currentQuantity) {
        for (let i = currentQuantity + 1; i <= newQuantityValid; i++) {
          const instanceId = `${serviceType}-${i}`;
          newServicos.push({
            service: serviceType,
            instanceNumber: i,
            instanceId: instanceId
          });
          // Auto-expand newly added instances
          setExpandedServiceSegments((prev: any) => ({
            ...prev,
            [instanceId]: true
          }));
        }
      } else if (newQuantityValid < currentQuantity) {
        // Remove instances (remove from end)
        const instancesToRemove: string[] = [];
        newServicos = newServicos.filter((instance: any) => {
          if (instance.service === serviceType && instance.instanceNumber > newQuantityValid) {
            // Also remove service details for removed instances
            const instanceId = instance.instanceId || `${serviceType}-${instance.instanceNumber}`;
            instancesToRemove.push(instanceId);
            if (prev.serviceDetails[instanceId]) {
              delete prev.serviceDetails[instanceId];
            }
            return false;
          }
          return true;
        });
        // Remove expanded state for removed instances
        if (instancesToRemove.length > 0) {
          setExpandedServiceSegments((prev: any) => {
            const newExpanded = { ...prev };
            instancesToRemove.forEach(id => delete newExpanded[id]);
            return newExpanded;
          });
        }
      }
      
      return {
        ...prev,
        servicos: newServicos,
        serviceQuantities: newServiceQuantities,
        serviceDetails: newServiceDetails
      };
    });
    
    // Clear error when services are updated
    if (fieldErrors.servicos) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.servicos;
        return newErrors;
      });
    }
  };

  const handleBtnOptionClick = (field: string, value: string) => {
    setProposalData((prev: any) => {
      if (field === 'propostaMarcacao' || field === 'assinaturaContrato' || field === 'tipoPedido') {
        // For selection fields, always set the value (don't toggle)
        return {
          ...prev,
          equipaInterna: {
            ...prev.equipaInterna,
            [field]: value
          }
        };
      } else if (
        field.startsWith('potencia-') ||
        field.startsWith('escalao-') ||
        field.startsWith('formaPagamento-') ||
        field.startsWith('sistema-') ||
        MOBILIDADE_OPTION_FIELD_PREFIXES.some((prefix) => field.startsWith(`${prefix}-`))
      ) {
        // Field format: "potencia-bairro-solar-1", "plataformaGestao-mobilidade-1", etc.
        const optionPrefix = (
          ['potencia', 'escalao', 'formaPagamento', 'sistema', ...MOBILIDADE_OPTION_FIELD_PREFIXES] as const
        ).find((prefix) => field.startsWith(`${prefix}-`));
        if (!optionPrefix) return prev;
        const fieldName = optionPrefix;
        const instanceId = field.slice(optionPrefix.length + 1);
        // For selection fields, always set the value (don't toggle)
        return {
          ...prev,
          serviceDetails: {
            ...prev.serviceDetails,
            [instanceId]: {
              ...prev.serviceDetails[instanceId],
              [fieldName]: value
            }
          }
        };
      }
      return prev;
    });
    // Clear error when field is updated
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleEmpresaFieldChange = (field: string, value: string) => {
    setProposalData((prev: any) => ({
      ...prev,
      empresa: {
        ...prev.empresa,
        [field]: value
      }
    }));
    // Clear error when field is updated
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleNifInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length > 9) {
      value = value.substring(0, 9);
    }
    handleEmpresaFieldChange('nifEmpresa', value);
  };

  const handleNifPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text');
    const digitsOnly = paste.replace(/[^0-9]/g, '').substring(0, 9);
    handleEmpresaFieldChange('nifEmpresa', digitsOnly);
  };

  const handleCaeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length > 5) {
      value = value.substring(0, 5);
    }
    handleEmpresaFieldChange('cae', value);
  };

  const handleCaePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text');
    const digitsOnly = paste.replace(/[^0-9]/g, '').substring(0, 5);
    handleEmpresaFieldChange('cae', digitsOnly);
  };

  // Handler for CPE input (must start with PT or pt and have 16 digits + 2 letters, case-insensitive)
  const handleCpeInput = (instanceId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    
    // Allow user to delete and type freely, but format it correctly
    if (value === '') {
      // Allow empty value
      handleServiceInstanceFieldChange(instanceId, 'cpe', '');
      return;
    }
    
    // Normalize to uppercase for processing, but accept both pt and PT
    const upperValue = value.toUpperCase();
    
    // If it starts with PT or pt (case-insensitive), keep PT and allow 16 digits + 2 letters after
    if (upperValue.startsWith('PT')) {
      const afterPT = upperValue.substring(2);
      // Extract digits first (up to 16), then letters (up to 2)
      let digits = '';
      let letters = '';
      let digitCount = 0;
      let letterCount = 0;
      
      for (let i = 0; i < afterPT.length; i++) {
        const char = afterPT[i];
        if (/[0-9]/.test(char) && digitCount < 16) {
          digits += char;
          digitCount++;
        } else if (/[A-Z]/.test(char) && digitCount === 16 && letterCount < 2) {
          letters += char;
          letterCount++;
        }
      }
      
      value = 'PT' + digits + letters;
    } else if (upperValue.startsWith('P')) {
      // Allow typing "P", "p", "PT", or "pt"
      if (upperValue === 'P' || upperValue === 'PT') {
        value = upperValue; // Normalize to uppercase
      } else {
        // If P/p is followed by T/t, format correctly
        if (upperValue[1] === 'T') {
          const afterPT = upperValue.substring(2);
          let digits = '';
          let letters = '';
          let digitCount = 0;
          let letterCount = 0;
          
          for (let i = 0; i < afterPT.length; i++) {
            const char = afterPT[i];
            if (/[0-9]/.test(char) && digitCount < 16) {
              digits += char;
              digitCount++;
            } else if (/[A-Z]/.test(char) && digitCount === 16 && letterCount < 2) {
              letters += char;
              letterCount++;
            }
          }
          
          value = 'PT' + digits + letters;
        } else {
          // If P/p is followed by non-T/t, extract digits and add PT
          const digitsOnly = upperValue.replace(/[^0-9]/g, '');
          value = digitsOnly.length > 0 ? 'PT' + digitsOnly.substring(0, 16) : 'P';
        }
      }
    } else {
      // If it doesn't start with P/p, extract digits and add PT prefix
      const digitsOnly = upperValue.replace(/[^0-9]/g, '');
      value = digitsOnly.length > 0 ? 'PT' + digitsOnly.substring(0, 16) : '';
    }
    
    handleServiceInstanceFieldChange(instanceId, 'cpe', value);
  };

  const handleCpePaste = (instanceId: string, e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    let paste = e.clipboardData.getData('text');
    const upperPaste = paste.toUpperCase(); // Normalize to uppercase for processing
    
    // Format: PT + 16 digits + 2 letters
    if (upperPaste.startsWith('PT')) {
      const afterPT = upperPaste.substring(2);
      const digits = afterPT.replace(/[^0-9]/g, '').substring(0, 16);
      const letters = afterPT.replace(/[^A-Z]/g, '').substring(0, 2);
      paste = 'PT' + digits + letters; // Always use uppercase PT and letters
    } else {
      // If it doesn't start with PT/pt, extract digits and add PT prefix
      const digitsOnly = upperPaste.replace(/[^0-9]/g, '');
      paste = 'PT' + digitsOnly.substring(0, 16);
    }
    
    handleServiceInstanceFieldChange(instanceId, 'cpe', paste);
  };

  // Handler for CUI input (auto-prefixes with PT but no extra format rules)
  const handleCuiInput = (instanceId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.toUpperCase();

    // Ensure value starts with PT if there is any content
    if (value && !value.startsWith('PT')) {
      value = `PT${value.replace(/^PT/i, '')}`;
    } else if (!value) {
      // Allow clearing the field completely
      value = '';
    }

    handleServiceInstanceFieldChange(instanceId, 'cui', value);
  };

  const handleCuiPaste = (instanceId: string, e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    let paste = e.clipboardData.getData('text').toUpperCase().trim();

    if (!paste.startsWith('PT')) {
      paste = `PT${paste.replace(/^PT/i, '')}`;
    }

    handleServiceInstanceFieldChange(instanceId, 'cui', paste);
  };

  // Helper function to copy text to clipboard
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showNotification('Texto copiado para a área de transferência!', 'success');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        showNotification('Texto copiado para a área de transferência!', 'success');
      } catch (err) {
        showNotification('Erro ao copiar texto.', 'error');
      }
      document.body.removeChild(textArea);
    }
  };

  // --- Assinatura Digital (Comercial): fetch my requests
  const fetchMySignatureRequests = useCallback(async (statusOverride?: 'pending' | 'all') => {
    const mode = statusOverride ?? signatureFilter;
    try {
      setLoadingSignatureRequests(true);
      const res = await api.get<{ success: boolean; signature_requests?: any[] }>('/api/comercial/signature-requests', {
        params: { status: mode === 'all' ? 'all' : 'pending' }
      });
      if (res.data?.success && Array.isArray(res.data.signature_requests)) {
        setSignatureRequests(res.data.signature_requests);
      } else {
        setSignatureRequests([]);
      }
    } catch (err: any) {
      setSignatureRequests([]);
      setSignatureNotification({ message: err?.response?.data?.message || 'Erro ao carregar pedidos.', type: 'error' });
    } finally {
      setLoadingSignatureRequests(false);
    }
  }, [signatureFilter]);

  const fetchMarcacoesForSignatureAssociate = useCallback(async () => {
    const mergeRawMarcacoes = (rawList: Record<string, unknown>[]) => {
      if (!Array.isArray(rawList) || rawList.length === 0) return;
      const next: Record<string, Marcacao> = {};
      rawList.forEach((m) => {
        const mapped = mapBackendMarcacao(m, userMap);
        const key = String(mapped.marcacao_id || mapped.id || m.id || '');
        if (key) next[key] = mapped;
      });
      if (Object.keys(next).length > 0) {
        setMarcacoesData((prev) => ({ ...prev, ...next }));
      }
    };

    const token = readAuthToken();
    if (!token) return;

    const baseUrl = resolveMainApiBaseUrl();
    const authHeaders = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };

    const fetchJsonMarcacoes = async (url: string) => {
      const res = await fetch(url, { headers: authHeaders });
      if (!res.ok) return [];
      const json = await res.json();
      return Array.isArray(json?.marcacoes) ? json.marcacoes : [];
    };

    const cccBase = resolveCccApiBaseUrl();
    const fetchProspeccaoAssociateRows = async () => {
      if (!embeddedSignatureOnly && !embeddedProspeccaoLeadId) return [];
      const mainRows = await fetchJsonMarcacoes(
        `${baseUrl}/api/comercial/gestao/signature-associate-marcacoes`
      );
      if (mainRows.length > 0) return mainRows;
      return fetchJsonMarcacoes(`${cccBase}/api/ccc/prospeccao/signature-associate-marcacoes`);
    };

    try {
      if (gestaoCccRecuperacao) {
        const recRows = await fetchJsonMarcacoes(
          `${cccBase}/api/ccc/recuperacao/gestao/signature-associate-marcacoes`
        );
        mergeRawMarcacoes(recRows);
        return;
      }

      const [gestaoRows, propostasRows, prospeccaoRows] = await Promise.all([
        fetchJsonMarcacoes(`${baseUrl}/api/comercial/gestao/marcacoes`),
        fetchJsonMarcacoes(`${baseUrl}/api/comercial/gestao/minhas-propostas`),
        fetchProspeccaoAssociateRows(),
      ]);
      mergeRawMarcacoes([...gestaoRows, ...propostasRows, ...prospeccaoRows]);
    } catch {
      /* associate list is optional */
    }
  }, [userMap, embeddedSignatureOnly, embeddedProspeccaoLeadId, gestaoCccRecuperacao]);

  const openSignatureModal = useCallback(async () => {
    setShowSignatureModal(true);
    setSignatureNotification(null);
    setSignatureForm({ company_name: '', nif: '', iban: '', bic: '', representativeName: '', representativeEmail: '', representativeContact: '', expectedDate: '', expectedTime: '', associatedMarcacaoId: '' });
    setAssociateSearchQuery('');
    await fetchMarcacoesForSignatureAssociate();
    await fetchMySignatureRequests('pending');
  }, [fetchMySignatureRequests, fetchMarcacoesForSignatureAssociate]);

  const closeSignatureModal = useCallback(() => {
    setShowSignatureModal(false);
    setSignatureNotification(null);
    if (embeddedSignatureOnly) onEmbeddedSignatureClose?.();
  }, [embeddedSignatureOnly, onEmbeddedSignatureClose]);

  const handleSignatureFilterChange = useCallback((mode: 'pending' | 'all') => {
    setSignatureFilter(mode);
    fetchMySignatureRequests(mode);
  }, [fetchMySignatureRequests]);

  const handleSignatureHoje = useCallback(() => {
    const now = new Date();
    const date = now.toISOString().slice(0, 10);
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    setSignatureForm(prev => ({ ...prev, expectedDate: date, expectedTime: `${hours}:${minutes}` }));
  }, []);

  // IBAN input/paste for signature form (same rules as Submeter Contrato: PT50 + 21 digits)
  const handleSignatureIbanInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (value === '') {
      setSignatureForm(prev => ({ ...prev, iban: '' }));
      return;
    }
    const upperValue = value.toUpperCase();
    if (upperValue.startsWith('PT50')) {
      const digitsOnly = upperValue.substring(4).replace(/[^0-9]/g, '').substring(0, 21);
      value = 'PT50' + digitsOnly;
    } else if (upperValue.startsWith('PT5')) {
      if (upperValue.length === 4 && upperValue[3] === '0') value = 'PT50';
      else if (upperValue.length < 4) value = upperValue;
      else {
        const digitsOnly = upperValue.replace(/[^0-9]/g, '').substring(0, 21);
        value = digitsOnly.length > 0 ? 'PT50' + digitsOnly : upperValue.substring(0, 4);
      }
    } else if (upperValue.startsWith('PT')) {
      if (upperValue.length <= 2) value = upperValue;
      else {
        const digitsOnly = upperValue.replace(/[^0-9]/g, '').substring(0, 21);
        value = digitsOnly.length > 0 ? 'PT50' + digitsOnly : upperValue.substring(0, 2);
      }
    } else if (upperValue.startsWith('P')) {
      if (upperValue.length === 1) value = 'P';
      else if (upperValue[1] === 'T') value = upperValue;
      else {
        const digitsOnly = upperValue.replace(/[^0-9]/g, '').substring(0, 21);
        value = digitsOnly.length > 0 ? 'PT50' + digitsOnly : upperValue;
      }
    } else {
      const digitsOnly = upperValue.replace(/[^0-9]/g, '').substring(0, 21);
      value = digitsOnly.length > 0 ? 'PT50' + digitsOnly : '';
    }
    setSignatureForm(prev => ({ ...prev, iban: value }));
  }, []);

  const handleSignatureIbanPaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').trim().toUpperCase();
    if (pastedText.includes('PT50')) {
      const pt50Index = pastedText.indexOf('PT50');
      const afterPT50 = pastedText.substring(pt50Index + 4);
      const digitsOnly = afterPT50.replace(/[^0-9]/g, '').substring(0, 21);
      setSignatureForm(prev => ({ ...prev, iban: 'PT50' + digitsOnly }));
    } else if (pastedText.startsWith('PT')) {
      const afterPT = pastedText.substring(2);
      const digitsOnly = afterPT.replace(/[^0-9]/g, '').substring(0, 21);
      setSignatureForm(prev => ({ ...prev, iban: digitsOnly.length > 0 ? 'PT50' + digitsOnly : 'PT' }));
    } else {
      const digitsOnly = pastedText.replace(/[^0-9]/g, '').substring(0, 21);
      setSignatureForm(prev => ({ ...prev, iban: digitsOnly.length > 0 ? 'PT50' + digitsOnly : '' }));
    }
  }, []);

  const getNifFromMarcacao = useCallback((m: any): string => {
    const formData = m?.form_data || {};
    const empresa = formData.empresa && typeof formData.empresa === 'object' ? formData.empresa : {};
    const proposalData = formData.proposalData;
    let proposalEmpresa: Record<string, unknown> = {};
    if (Array.isArray(proposalData) && proposalData.length > 0 && proposalData[0]?.empresa) {
      proposalEmpresa = proposalData[0].empresa;
    } else if (proposalData?.empresa && typeof proposalData.empresa === 'object') {
      proposalEmpresa = proposalData.empresa;
    }
    return (formData.nifEmpresa || empresa.nifEmpresa || (proposalEmpresa.nifEmpresa as string) || '').toString().trim();
  }, []);

  const handleAssociateMarcacao = useCallback((associateId: string) => {
    if (!associateId) {
      setSignatureForm(prev => ({ ...prev, associatedMarcacaoId: '', company_name: '', nif: '', representativeName: '', representativeEmail: '', representativeContact: '' }));
      return;
    }
    const match = associateId.match(/^marcacao-(\d+)(?:-(.+))?$/);
    if (!match) return;

    const dbId = match[1];
    const propostaSuffix = match[2] || '';
    const marcacao = Object.values(marcacoesData).find((m: any) => String(m.id_db ?? m.id) === dbId);
    if (!marcacao) return;

    const m = marcacao as any;
    const formData = (m.form_data && typeof m.form_data === 'object') ? m.form_data : {};
    const linkedProposal = propostaSuffix
      ? findProposalInMarcacaoForm(formData, propostaSuffix)
      : null;
    const proposalEmpresa =
      linkedProposal?.empresa && typeof linkedProposal.empresa === 'object'
        ? (linkedProposal.empresa as Record<string, unknown>)
        : {};
    const empresaInfo = (formData.empresa && typeof formData.empresa === 'object') ? formData.empresa : {};

    const name =
      String(proposalEmpresa.nomeEmpresa || m.nome_empresa || m.empresa || m.cliente || '').trim();
    const nif =
      String(proposalEmpresa.nifEmpresa || getNifFromMarcacao(marcacao) || '').trim();
    const repName =
      String(proposalEmpresa.nomeRepresentante || empresaInfo.nomeRepresentante || m.nome_responsavel || m.responsavel || '').trim();
    const repContact =
      String(proposalEmpresa.contactoRepresentante || empresaInfo.contactoRepresentante || m.contacto_cliente || m.contacto || '').trim();
    const repEmail =
      String(proposalEmpresa.emailRepresentante || empresaInfo.emailRepresentante || formData.emailRepresentante || m.email_cliente || m.email || '').trim();

    setSignatureForm(prev => ({
      ...prev,
      associatedMarcacaoId: associateId,
      company_name: name,
      nif,
      representativeName: repName,
      representativeEmail: repEmail,
      representativeContact: repContact,
    }));
  }, [marcacoesData, getNifFromMarcacao]);

  const handleSubmitSignatureRequest = useCallback(async () => {
    const { company_name, nif, iban, bic, representativeName, representativeEmail, representativeContact, expectedDate, expectedTime, associatedMarcacaoId } = signatureForm;
    if (!iban?.trim()) { setSignatureNotification({ message: 'IBAN é obrigatório.', type: 'error' }); return; }
    const ibanValue = iban.trim().toUpperCase();
    if (!ibanValue.startsWith('PT50')) {
      setSignatureNotification({ message: 'O IBAN deve começar com "PT50".', type: 'error' });
      return;
    }
    if (ibanValue.length !== 25) {
      setSignatureNotification({ message: 'O IBAN deve ter exatamente 25 caracteres (PT50 + 21 dígitos).', type: 'error' });
      return;
    }
    if (!/^PT50\d{21}$/.test(ibanValue)) {
      setSignatureNotification({ message: 'O IBAN deve ter "PT50" seguido de exatamente 21 dígitos.', type: 'error' });
      return;
    }
    if (!bic?.trim()) { setSignatureNotification({ message: 'BIC / SWIFT é obrigatório.', type: 'error' }); return; }
    let expected_signature_at: string | undefined;
    if (expectedDate?.trim() && expectedTime?.trim()) {
      const expected = new Date(`${expectedDate}T${expectedTime}:00`);
      if (isNaN(expected.getTime())) {
        setSignatureNotification({ message: 'Data/hora inválida.', type: 'error' });
        return;
      }
      expected_signature_at = expected.toISOString();
    }
    setSubmittingSignature(true);
    setSignatureNotification(null);
    try {
      const payload: any = {
        iban: iban.trim(),
        bic: bic.trim(),
        company_name: company_name?.trim() || undefined,
        nif: nif?.trim() || undefined,
        representative_name: representativeName?.trim() || undefined,
        representative_email: representativeEmail?.trim() || undefined,
        representative_contact: representativeContact?.trim() || undefined,
        associated_proposal_id: associatedMarcacaoId?.trim() || undefined
      };
      if (expected_signature_at) payload.expected_signature_at = expected_signature_at;
      const res = await api.post<{ success: boolean; signature_request?: any }>('/api/comercial/signature-requests', payload);
      if (res.data?.success) {
        setSignatureNotification({ message: 'Pedido de assinatura digital criado com sucesso.', type: 'success' });
        setSignatureForm(prev => ({ ...prev, company_name: '', nif: '', iban: '', bic: '', representativeName: '', representativeEmail: '', representativeContact: '', expectedDate: '', expectedTime: '', associatedMarcacaoId: '' }));
        setAssociateSearchQuery('');
        await fetchMySignatureRequests(signatureFilter);
      } else {
        setSignatureNotification({ message: (res.data as any)?.message || 'Erro ao criar pedido.', type: 'error' });
      }
    } catch (err: any) {
      setSignatureNotification({ message: err?.response?.data?.message || 'Erro ao criar pedido de assinatura.', type: 'error' });
    } finally {
      setSubmittingSignature(false);
    }
  }, [signatureForm, signatureFilter, fetchMySignatureRequests]);

  // Helper function to render copy buttons for a field
  const renderCopyButtons = (instanceId: string, field: string, fieldName: string) => {
    const currentValue = proposalData.serviceDetails[instanceId]?.[field] || '';
    return (
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
        <button
          type="button"
          onClick={() => copyToClipboard(currentValue)}
          style={{
            padding: '6px',
            backgroundColor: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px'
          }}
          title={`Copiar ${fieldName}`}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 2C4 0.895431 4.89543 0 6 0H10C11.1046 0 12 0.895431 12 2V4H14C15.1046 4 16 4.89543 16 6V14C16 15.1046 15.1046 16 14 16H8C6.89543 16 6 15.1046 6 14V12H4C2.89543 12 2 11.1046 2 10V4C2 2.89543 2.89543 2 4 2Z" fill="#6B7280" stroke="#6B7280" strokeWidth="0.5"/>
            <path d="M8 4H14C15.1046 4 16 4.89543 16 6V14C16 15.1046 15.1046 16 14 16H8C6.89543 16 6 15.1046 6 14V6C6 4.89543 6.89543 4 8 4Z" fill="white" stroke="#6B7280" strokeWidth="0.5"/>
          </svg>
        </button>
      </div>
    );
  };

  // Handler for service instance text/date/textarea fields
  const handleServiceInstanceFieldChange = (instanceId: string, field: string, value: string) => {
    setProposalData((prev: any) => ({
      ...prev,
      serviceDetails: {
        ...prev.serviceDetails,
        [instanceId]: {
          ...prev.serviceDetails[instanceId] || {},
          [field]: value
        }
      }
    }));
    const errorKey = `${field}-${instanceId}`;
    if (fieldErrors[errorKey]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // Handler for service instance file uploads
  const handleServiceInstanceFileChange = async (instanceId: string, field: string, file: File | null) => {
    if (!file) {
      // Clear file reference
      setProposalData((prev: any) => ({
        ...prev,
        serviceDetails: {
          ...prev.serviceDetails,
          [instanceId]: {
            ...prev.serviceDetails[instanceId] || {},
            [field]: null,
            [`${field}_name`]: null,
            [`${field}_id`]: null
          }
        }
      }));
      return;
    }

    // Store file metadata immediately
    setProposalData((prev: any) => ({
      ...prev,
      serviceDetails: {
        ...prev.serviceDetails,
        [instanceId]: {
          ...prev.serviceDetails[instanceId] || {},
          [field]: file,
          [`${field}_name`]: file.name,
          [`${field}_size`]: file.size,
          [`${field}_type`]: file.type
        }
      }
    }));
  };

  const updateProposalSummary = () => {
    // Summary is rendered dynamically in JSX based on proposalData state
  };

  const validateProposalForm = (): boolean => {
    const requiredFields = [
      'nomeEmpresa',
      'nifEmpresa',
      'regiao',
      'cae',
      'nomeRepresentante',
      'contactoRepresentante',
      'emailRepresentante'
    ];

    let missingCount = 0;
    const missingFields: string[] = [];
    requiredFields.forEach(field => {
      if (!proposalData.empresa[field] || !proposalData.empresa[field].trim()) {
        missingCount++;
        missingFields.push(field);
      }
    });

    if (missingCount > 0) {
      showNotification(`Por favor, preencha todos os campos obrigatórios (${missingCount} em falta).`, 'error');
      return false;
    }

    // Validate CAE format (exactly 5 digits)
    if (proposalData.empresa.cae) {
      const caeValue = proposalData.empresa.cae.toString().trim();
      if (caeValue.length !== 5) {
        showNotification('O CAE deve ter exatamente 5 dígitos.', 'error');
        return false;
      }
      if (!/^\d+$/.test(caeValue)) {
        showNotification('O CAE deve conter apenas números.', 'error');
        return false;
      }
    }

    const totalServices = Object.values(proposalData.serviceQuantities || {}).reduce((sum: number, qty: any) => sum + (qty || 0), 0);
    if (totalServices === 0) {
      showNotification('Por favor, selecione pelo menos um serviço.', 'error');
      return false;
    }

    if (!proposalData.equipaInterna.propostaMarcacao) {
      showNotification('Por favor, selecione se é uma Proposta de Marcação.', 'error');
      return false;
    }

    if (!proposalData.equipaInterna.assinaturaContrato) {
      showNotification('Por favor, selecione o tipo de Assinatura do Contrato.', 'error');
      return false;
    }

    if (!proposalData.equipaInterna.tipoPedido) {
      showNotification('Por favor, selecione o Tipo de Pedido.', 'error');
      return false;
    }

    // Validate CPE for all service instances that require it
    for (const instance of proposalData.servicos || []) {
      const instanceId = instance.instanceId;
      const instanceDetails = proposalData.serviceDetails[instanceId] || {};
      const serviceType = instance.service;
      
      // CPE is required for bairro-solar, pap, luz, and transacional
      if (serviceType === 'bairro-solar' || serviceType === 'pap' || serviceType === 'luz' || serviceType === 'transacional') {
        const cpe = instanceDetails.cpe || '';
        if (!cpe || cpe.trim() === '') {
          showNotification('O CPE é obrigatório para todos os serviços selecionados.', 'error');
          return false;
        }
        const cpeValue = cpe.toString().trim().toUpperCase();
        if (!cpeValue.startsWith('PT')) {
          showNotification('O CPE deve começar com "PT".', 'error');
          return false;
        }
        if (cpeValue.length !== 20) {
          showNotification('O CPE deve ter exatamente 20 caracteres (PT + 16 dígitos + 2 letras).', 'error');
          return false;
        }
        const afterPT = cpeValue.substring(2);
        if (!/^\d{16}[A-Z]{2}$/.test(afterPT)) {
          showNotification('O CPE deve ter "PT" seguido de exatamente 16 dígitos e 2 letras.', 'error');
          return false;
        }
      }

      // Sistema is required for Luz (Trifásico or Monofásico)
      if (serviceType === 'luz') {
        const sistema = (instanceDetails.sistema || '').toString().trim().toLowerCase();
        if (sistema !== 'trifasico' && sistema !== 'monofasico') {
          showNotification('Para o serviço Luz, selecione o Sistema (Trifásico ou Monofásico).', 'error');
          return false;
        }
      }
      
      // CUI is required for gas and must start with "PT"
      if (serviceType === 'gas') {
        const cui = (instanceDetails.cui || '').toString().trim().toUpperCase();
        if (!cui) {
          showNotification('O CUI é obrigatório para todos os serviços selecionados.', 'error');
          return false;
        }
        if (!cui.startsWith('PT')) {
          showNotification('O CUI deve começar com "PT".', 'error');
          return false;
        }
      }
      
      // Coordenadas Geográficas is required only for bairro-solar
      if (serviceType === 'bairro-solar') {
        const coordenadasGeograficas = instanceDetails.coordenadasGeograficas || '';
        if (!coordenadasGeograficas || coordenadasGeograficas.trim() === '') {
          showNotification('O campo "Coordenadas Geográficas" é obrigatório para o serviço Bairro Solar.', 'error');
          return false;
        }
      }

      const moradaFornecimento = (instanceDetails.moradaFornecimento || '').trim();
      if (!moradaFornecimento) {
        showNotification('A Morada de Fornecimento é obrigatória para todos os serviços selecionados.', 'error');
        return false;
      }

      if (serviceType === 'outro') {
        const observacoes = (instanceDetails.observacoes || '').trim();
        if (!observacoes) {
          showNotification('Para o serviço Outro, preencha o campo Observações com a descrição do pedido.', 'error');
          return false;
        }
      }

      if (serviceType === 'mobilidade') {
        for (const fieldKey of MOBILIDADE_FIELD_KEYS) {
          const value = String(instanceDetails[fieldKey] || '').trim();
          if (!value) {
            showNotification(
              `Para Mobilidade Elétrica, preencha: ${MOBILIDADE_FIELD_LABELS[fieldKey]}.`,
              'error'
            );
            return false;
          }
        }
      }
    }

    return true;
  };

  // Handler for Solar Contract form fields
  // Handler for CPE input in Contratação Solar form (must start with PT or pt and have 16 digits + 2 letters, case-insensitive)
  const handleSolarCpeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    
    // Allow user to delete and type freely, but format it correctly
    if (value === '') {
      // Allow empty value
      handleSolarContractFieldChange('cpe', '');
      return;
    }
    
    // Normalize to uppercase for processing, but accept both pt and PT
    const upperValue = value.toUpperCase();
    
    // If it starts with PT or pt (case-insensitive), keep PT and allow 16 digits + 2 letters after
    if (upperValue.startsWith('PT')) {
      const afterPT = upperValue.substring(2);
      // Extract digits first (up to 16), then letters (up to 2)
      let digits = '';
      let letters = '';
      let digitCount = 0;
      let letterCount = 0;
      
      for (let i = 0; i < afterPT.length; i++) {
        const char = afterPT[i];
        if (/[0-9]/.test(char) && digitCount < 16) {
          digits += char;
          digitCount++;
        } else if (/[A-Z]/.test(char) && digitCount === 16 && letterCount < 2) {
          letters += char;
          letterCount++;
        }
      }
      
      value = 'PT' + digits + letters;
    } else if (upperValue.startsWith('P')) {
      // Allow typing "P", "p", "PT", or "pt"
      if (upperValue === 'P' || upperValue === 'PT') {
        value = upperValue; // Normalize to uppercase
      } else {
        // If P/p is followed by T/t, format correctly
        if (upperValue[1] === 'T') {
          const afterPT = upperValue.substring(2);
          let digits = '';
          let letters = '';
          let digitCount = 0;
          let letterCount = 0;
          
          for (let i = 0; i < afterPT.length; i++) {
            const char = afterPT[i];
            if (/[0-9]/.test(char) && digitCount < 16) {
              digits += char;
              digitCount++;
            } else if (/[A-Z]/.test(char) && digitCount === 16 && letterCount < 2) {
              letters += char;
              letterCount++;
            }
          }
          
          value = 'PT' + digits + letters;
        } else {
          // If P/p is followed by non-T/t, extract digits and add PT
          const digitsOnly = upperValue.replace(/[^0-9]/g, '');
          value = digitsOnly.length > 0 ? 'PT' + digitsOnly.substring(0, 16) : 'P';
        }
      }
    } else {
      // If it doesn't start with P/p, extract digits and add PT prefix
      const digitsOnly = upperValue.replace(/[^0-9]/g, '');
      value = digitsOnly.length > 0 ? 'PT' + digitsOnly.substring(0, 16) : '';
    }
    
    handleSolarContractFieldChange('cpe', value);
  };

  const handleSolarCpePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    let paste = e.clipboardData.getData('text');
    const upperPaste = paste.toUpperCase(); // Normalize to uppercase for processing
    
    // Format: PT + 16 digits + 2 letters
    if (upperPaste.startsWith('PT')) {
      const afterPT = upperPaste.substring(2);
      const digits = afterPT.replace(/[^0-9]/g, '').substring(0, 16);
      const letters = afterPT.replace(/[^A-Z]/g, '').substring(0, 2);
      paste = 'PT' + digits + letters; // Always use uppercase PT and letters
    } else {
      // If it doesn't start with PT/pt, extract digits and add PT prefix
      const digitsOnly = upperPaste.replace(/[^0-9]/g, '');
      paste = 'PT' + digitsOnly.substring(0, 16);
    }
    
    handleSolarContractFieldChange('cpe', paste);
  };

  const handleSolarContractFieldChange = (field: string, value: string) => {
    setSolarContractData((prev: any) => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // If Tipo de Serviço changes, update CPE based on selection
      if (field === 'tipoServico' && marcacaoToSubmitContract) {
        const marcacao = marcacoesData[marcacaoToSubmitContract];
        if (marcacao) {
          const formData = marcacao.form_data || {};
          let proposalData: any = formData.proposalData || {};
          
          // Handle proposalData as both array and object
          let servicos: any[] = [];
          let serviceDetails: any = {};
          
          if (Array.isArray(proposalData) && proposalData.length > 0) {
            // If it's an array, collect serviços from all elements
            proposalData.forEach((proposal: any) => {
              if (proposal && proposal.servicos && Array.isArray(proposal.servicos)) {
                servicos.push(...proposal.servicos);
              }
              if (proposal && proposal.serviceDetails) {
                serviceDetails = { ...serviceDetails, ...proposal.serviceDetails };
              }
            });
          } else if (proposalData && typeof proposalData === 'object') {
            // If it's an object, use it directly
            servicos = proposalData.servicos || [];
            serviceDetails = proposalData.serviceDetails || {};
          }
          
          // Map Tipo de Serviço to service type
          // BS → bairro-solar
          // PAP → pap
          // VP (Venda a Prestações) → transacional
          // PP (Pronto Pagamento) → transacional
          let targetService: string | null = null;
          if (value === 'BS') {
            targetService = 'bairro-solar';
          } else if (value === 'PAP') {
            targetService = 'pap';
          } else if (value === 'VP' || value === 'PP') {
            targetService = 'transacional';
          }
          
          // Find first matching service instance and get its CPE
          if (targetService && servicos.length > 0) {
            for (const instance of servicos) {
              if (instance.service === targetService) {
                const instanceDetails = serviceDetails[instance.instanceId] || {};
                const cpe = instanceDetails.cpe || instanceDetails.instalacao?.cpe || '';
                if (cpe) {
                  updated.cpe = cpe;
                }
                break;
              }
            }
          }
        }
      }
      
      return updated;
    });
    
    // Clear error for this field
    if (solarContractFormErrors[field]) {
      setSolarContractFormErrors((prev: any) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handler for Solar Contract file uploads (supports multiple files)
  const handleSolarContractFileChange = (field: string, files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }

    // Validate all files
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const allowedExtensions = ['pdf', 'png', 'jpg', 'jpeg', 'doc', 'docx'];
    
    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      
      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension || '')) {
        showNotification(`Tipo de ficheiro não permitido para "${file.name}". Apenas PDF, PNG, JPG, DOC e DOCX são permitidos.`, 'error');
        continue;
      }
      
      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      return;
    }

    // Add new files to existing array
    setSolarContractData((prev: any) => {
      const currentFiles = prev.documentos[field] || [];
      const newFiles = [...currentFiles, ...validFiles];
      
      return {
        ...prev,
        documentos: {
          ...prev.documentos,
          [field]: newFiles
        }
      };
    });

    // Clear error for this field
    if (solarContractFormErrors[`documentos.${field}`]) {
      setSolarContractFormErrors((prev: any) => {
        const newErrors = { ...prev };
        delete newErrors[`documentos.${field}`];
        return newErrors;
      });
    }
  };

  // Handler to remove a specific file from Solar Contract form
  const handleRemoveSolarFile = (field: string, index: number) => {
    setSolarContractData((prev: any) => {
      const currentFiles = prev.documentos[field] || [];
      const newFiles = currentFiles.filter((_: File, i: number) => i !== index);
      return {
        ...prev,
        documentos: {
          ...prev.documentos,
          [field]: newFiles
        }
      };
    });
  };

  // Validate Solar Contract form
  const validateSolarContractForm = (): boolean => {
    const errors: Record<string, string> = {};
    const missingFields: string[] = [];

    // Validate all required fields
    if (!solarContractData.tipoServico.trim()) {
      errors['tipoServico'] = 'Campo obrigatório';
      missingFields.push('Tipo de Serviço');
    }
    if (!solarContractData.numeroPaineis.trim()) {
      errors['numeroPaineis'] = 'Campo obrigatório';
      missingFields.push('Número de Painéis');
    }
    if (!solarContractData.nomeEmpresa.trim()) {
      errors['nomeEmpresa'] = 'Campo obrigatório';
      missingFields.push('Nome Empresa');
    }
    if (!solarContractData.moradaInstalacao.trim()) {
      errors['moradaInstalacao'] = 'Campo obrigatório';
      missingFields.push('Morada da instalação');
    }
    if (!solarContractData.distrito.trim()) {
      errors['distrito'] = 'Campo obrigatório';
      missingFields.push('Distrito');
    }
    if (!solarContractData.nifEmpresa.trim()) {
      errors['nifEmpresa'] = 'Campo obrigatório';
      missingFields.push('NIF empresa');
    }
    if (!solarContractData.emailEmpresa.trim()) {
      errors['emailEmpresa'] = 'Campo obrigatório';
      missingFields.push('Email da empresa');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(solarContractData.emailEmpresa)) {
      errors['emailEmpresa'] = 'Email inválido';
    }
    if (!solarContractData.nomeRepresentante.trim()) {
      errors['nomeRepresentante'] = 'Campo obrigatório';
      missingFields.push('Nome Representante');
    }
    if (!solarContractData.nifRepresentanteLegal.trim()) {
      errors['nifRepresentanteLegal'] = 'Campo obrigatório';
      missingFields.push('NIF Representante Legal');
    }
    if (!solarContractData.contactoResponsavel.trim()) {
      errors['contactoResponsavel'] = 'Campo obrigatório';
      missingFields.push('Contacto Responsável');
    }
    if (!solarContractData.emailRepresentanteLegal.trim()) {
      errors['emailRepresentanteLegal'] = 'Campo obrigatório';
      missingFields.push('Email Representante legal');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(solarContractData.emailRepresentanteLegal)) {
      errors['emailRepresentanteLegal'] = 'Email inválido';
    }
    if (!solarContractData.cpe || solarContractData.cpe.trim() === '') {
      errors['cpe'] = 'Campo obrigatório';
      missingFields.push('CPE');
    } else {
      const cpeValue = solarContractData.cpe.toString().trim().toUpperCase();
      if (!cpeValue.startsWith('PT')) {
        errors['cpe'] = 'O CPE deve começar com "PT".';
      } else if (cpeValue.length !== 20) {
        errors['cpe'] = 'O CPE deve ter exatamente 20 caracteres (PT + 16 dígitos + 2 letras).';
      } else {
        const afterPT = cpeValue.substring(2);
        if (!/^\d{16}[A-Z]{2}$/.test(afterPT)) {
          errors['cpe'] = 'O CPE deve ter "PT" seguido de exatamente 16 dígitos e 2 letras.';
        }
      }
    }
    if (!solarContractData.potenciaContratada.trim()) {
      errors['potenciaContratada'] = 'Campo obrigatório';
      missingFields.push('Potência Contratada');
    }
    if (!solarContractData.idPropostaS2C.trim()) {
      errors['idPropostaS2C'] = 'Campo obrigatório';
      missingFields.push('ID Proposta S2C');
    }
    if (!solarContractData.valorProposta.trim()) {
      errors['valorProposta'] = 'Campo obrigatório';
      missingFields.push('Valor da proposta');
    }
    if (!solarContractData.codigoCertidaoPermanente.trim()) {
      errors['codigoCertidaoPermanente'] = 'Campo obrigatório';
      missingFields.push('Código Certidão Permanente');
    }
    // Nome Comercial and Código Comercial are hidden fields auto-filled from current user
    const resolvedNomeComercialForValidation = resolveContractNomeComercial({
      currentUserData,
      currentUserId,
      userMap,
      fallback: solarContractData.nomeComercial,
    });
    if (!resolvedNomeComercialForValidation.trim()) {
      errors['nomeComercial'] = 'Campo obrigatório';
      missingFields.push('Nome comercial');
    }
    // Código Comercial is optional - don't validate it
    if (!solarContractData.vyn.trim()) {
      errors['vyn'] = 'Campo obrigatório';
      missingFields.push('VYN');
    }

    // Validate required documents (Contrato assinado not required when Digital is checked)
    if (!solarContractData.documentos.contratoAssinadoDigital &&
        (!solarContractData.documentos.contratoAssinado || 
        !Array.isArray(solarContractData.documentos.contratoAssinado) || 
        solarContractData.documentos.contratoAssinado.length === 0)) {
      errors['documentos.contratoAssinado'] = 'Campo obrigatório';
      missingFields.push('Contrato assinado');
    }
    if (!solarContractData.documentos.teaserS2C || 
        !Array.isArray(solarContractData.documentos.teaserS2C) || 
        solarContractData.documentos.teaserS2C.length === 0) {
      errors['documentos.teaserS2C'] = 'Campo obrigatório';
      missingFields.push('Teaser S2C');
    }
    if (!solarContractData.documentos.cc || 
        !Array.isArray(solarContractData.documentos.cc) || 
        solarContractData.documentos.cc.length === 0) {
      errors['documentos.cc'] = 'Campo obrigatório';
      missingFields.push('CC');
    }
    if (!solarContractData.documentos.crcOuInicioAtividade || 
        !Array.isArray(solarContractData.documentos.crcOuInicioAtividade) || 
        solarContractData.documentos.crcOuInicioAtividade.length === 0) {
      errors['documentos.crcOuInicioAtividade'] = 'Campo obrigatório';
      missingFields.push('CRC OU Inicio de atividade');
    }
    if (!solarContractData.documentos.fatura || 
        !Array.isArray(solarContractData.documentos.fatura) || 
        solarContractData.documentos.fatura.length === 0) {
      errors['documentos.fatura'] = 'Campo obrigatório';
      missingFields.push('Fatura');
    }

    setSolarContractFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      // Create detailed error message
      const errorMessage = missingFields.length > 0 
        ? `Por favor, preencha todos os campos obrigatórios:\n${missingFields.join(', ')}`
        : 'Por favor, corrija os erros no formulário.';
      
      showNotification(errorMessage, 'error');
      
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.querySelector(`[data-field="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (errorElement as HTMLElement).focus();
      }
      
      return false;
    }

    return true;
  };

  // Submit Solar Contract form
  const submitSolarContract = async (e?: React.MouseEvent) => {
    // Prevent default form submission if called from a form
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Prevent double submission
    if (isSubmittingSolarContract) {
      return;
    }

    if (!marcacaoToSubmitContract) {
      showNotification('Erro: Marcação não encontrada.', 'error');
      return;
    }

    // Validate form
    if (!validateSolarContractForm()) {
      return;
    }

    setIsSubmittingSolarContract(true);

    try {
      if (!embeddedContractOnly) {
        await fetchMarcacaoFromCallcenter(marcacaoToSubmitContract);
      } else if (!resolveMarcacaoInStore(marcacoesData, marcacaoToSubmitContract)) {
        throw new Error(
          'Dados da marcação não disponíveis. Feche e volte a abrir Submeter Contrato.'
        );
      }

      // Map of document categories to field_name, stage, and display label (for backoffice)
      const documentCategoryMap: { [key: string]: { fieldName: string; stage: string; fieldLabel: string } } = {
        'contratoAssinado': { fieldName: 'contrato_assinado', stage: 'contract', fieldLabel: 'Contrato assinado' },
        'teaserS2C': { fieldName: 'teaser_s2c', stage: 'contract', fieldLabel: 'Teaser S2C' },
        'cc': { fieldName: 'cartao_cidadao', stage: 'contract', fieldLabel: 'CC' },
        'crcOuInicioAtividade': { fieldName: 'crc_inicio_atividade', stage: 'contract', fieldLabel: 'CRC ou Início de Atividade' },
        'fatura': { fieldName: 'fatura_atual', stage: 'contract', fieldLabel: 'Fatura' },
        'documentosAdicionais': { fieldName: 'documentos_adicionais', stage: 'contract', fieldLabel: 'Documentos Adicionais' }
      };

      // Upload all files via proposal endpoint (backend creates/finds lead from marcação; no lead_id required)
      const uploadedFiles: { [category: string]: any[] } = {};
      
      for (const [category, config] of Object.entries(documentCategoryMap)) {
        const files = (solarContractData.documentos as any)[category];
        if (Array.isArray(files) && files.length > 0) {
          uploadedFiles[category] = [];
          
          for (const file of files) {
            const fileMeta = (obj: any) => ({ ...obj, field: category, fieldLabel: config.fieldLabel });
            // Skip if already uploaded (has lead_file_id)
            if (file.lead_file_id || file.id) {
              uploadedFiles[category].push(fileMeta({
                name: file.name,
                size: file.size,
                type: file.type,
                lead_file_id: file.lead_file_id || file.id,
                lead_id: file.lead_id || file.leadId || null
              }));
              continue;
            }
            
            // Upload file if it's a File object
            if (file instanceof File) {
              try {
                const uploadFormData = new FormData();
                uploadFormData.append('file', file);
                uploadFormData.append('business_area', 'energy_solutions');
                uploadFormData.append('stage', config.stage);
                uploadFormData.append('field_name', config.fieldName);
                
                const uploadResponse = embeddedContractApi
                  ? await embeddedContractApi.uploadFile(uploadFormData)
                  : await uploadEmbeddedContractFile(
                      marcacaoToSubmitContract!,
                      uploadFormData,
                      embeddedProspeccaoLeadId,
                      embeddedContractOnly && !embeddedProspeccaoLeadId
                    );
                
                if (uploadResponse.success && uploadResponse.file) {
                  uploadedFiles[category].push(fileMeta({
                    name: uploadResponse.file.name || uploadResponse.file.file_name || file.name,
                    size: uploadResponse.file.size ?? uploadResponse.file.file_size ?? file.size,
                    type: uploadResponse.file.type || uploadResponse.file.file_type || file.type,
                    lead_file_id: uploadResponse.file.lead_file_id ?? uploadResponse.file.id,
                    lead_id: uploadResponse.file.lead_id ?? null
                  }));
                } else if (embeddedContractOnly || embeddedContractApi) {
                  throw new Error(
                    uploadResponse.error ||
                      uploadResponse.message ||
                      `Falha ao enviar ficheiro ${file.name}`
                  );
                } else {
                  console.error(`Failed to upload file ${file.name} for category ${category}:`, uploadResponse);
                  uploadedFiles[category].push(fileMeta({
                    name: file.name,
                    size: file.size,
                    type: file.type
                  }));
                }
              } catch (uploadError) {
                if (embeddedContractOnly || embeddedContractApi) {
                  throw uploadError;
                }
                console.error(`Error uploading file ${file.name} for category ${category}:`, uploadError);
                uploadedFiles[category].push(fileMeta({
                  name: file.name,
                  size: file.size,
                  type: file.type
                }));
              }
            } else {
              uploadedFiles[category].push(fileMeta({
                name: file.name || file.file_name,
                size: file.size || file.file_size,
                type: file.type || file.file_type,
                lead_file_id: file.lead_file_id || file.id,
                lead_id: file.lead_id || file.leadId || null
              }));
            }
          }
        } else {
          uploadedFiles[category] = [];
        }
      }

      // Prepare contract data for submission with uploaded file references
      const lastProposalForSubmit =
        contractProposalsForMarcacao
          .filter((p) => contractSelectedProposalIds.includes(p.id))
          .slice(-1)[0] || contractProposalsForMarcacao.slice(-1)[0];
      const proposalEquipaInterna = (lastProposalForSubmit?.proposal?.equipaInterna || {}) as {
        nomeUsuario?: string;
        codigoComercial?: string;
      };
      const resolvedNomeComercial = resolveContractNomeComercial({
        currentUserData,
        currentUserId,
        userMap,
        proposalEquipaInterna,
        fallback: solarContractData.nomeComercial,
      });
      const resolvedCodigoComercial = resolveContractCodigoComercial({
        currentUserData,
        proposalEquipaInterna,
        fallback: solarContractData.codigoComercial,
      });
      const contractData = {
        assignedProposalIds: contractSelectedProposalIds,
        urgente: !!solarContractData.urgente,
        tipoServico: solarContractData.tipoServico,
        numeroPaineis: solarContractData.numeroPaineis,
        nomeEmpresa: solarContractData.nomeEmpresa,
        moradaInstalacao: solarContractData.moradaInstalacao,
        distrito: solarContractData.distrito,
        nifEmpresa: solarContractData.nifEmpresa,
        emailEmpresa: solarContractData.emailEmpresa,
        nomeRepresentante: solarContractData.nomeRepresentante,
        nifRepresentanteLegal: solarContractData.nifRepresentanteLegal,
        contactoResponsavel: solarContractData.contactoResponsavel,
        emailRepresentanteLegal: solarContractData.emailRepresentanteLegal,
        cpe: solarContractData.cpe,
        iban: solarContractData.iban || '',
        potenciaContratada: solarContractData.potenciaContratada,
        idPropostaS2C: solarContractData.idPropostaS2C,
        valorProposta: solarContractData.valorProposta,
        codigoCertidaoPermanente: solarContractData.codigoCertidaoPermanente,
        nomeComercial: resolvedNomeComercial,
        codigoComercial: resolvedCodigoComercial,
        vyn: solarContractData.vyn,
        // User and team information for audit
        submittedBy: {
          userId: currentUserId,
          username: resolvedNomeComercial,
          email: currentUserData?.email || '',
          team: currentUserData?.team || currentUserData?.team_name || '',
          codigoComercial: resolvedCodigoComercial,
        },
        documentos: {
          contratoAssinado: uploadedFiles.contratoAssinado || [],
          contratoAssinadoDigital: !!solarContractData.documentos.contratoAssinadoDigital,
          teaserS2C: uploadedFiles.teaserS2C || [],
          cc: uploadedFiles.cc || [],
          crcOuInicioAtividade: uploadedFiles.crcOuInicioAtividade || [],
          fatura: uploadedFiles.fatura || [],
          documentosAdicionais: uploadedFiles.documentosAdicionais || []
        },
        submittedAt: new Date().toISOString(),
        ...embeddedContractSubmissionMeta(embeddedContractOnly, embeddedProspeccaoLeadId),
      };

      if (embeddedContractApi) {
        await embeddedContractApi.persist(contractData);
      } else {
        await persistEmbeddedContractData(
          marcacaoToSubmitContract,
          contractData,
          embeddedContractOnly,
          embeddedProspeccaoLeadId
        );
      }

      showNotification('Contrato de Contratação Solar submetido com sucesso!', 'success');
      setShowSolarContractForm(false);
      setMarcacaoToSubmitContract(null);
      
      // Reset form
      setSolarContractData({
        urgente: false,
        tipoServico: '',
        numeroPaineis: '',
        nomeEmpresa: '',
        moradaInstalacao: '',
        distrito: '',
        nifEmpresa: '',
        emailEmpresa: '',
        nomeRepresentante: '',
        nifRepresentanteLegal: '',
        contactoResponsavel: '',
        emailRepresentanteLegal: '',
        cpe: '',
        iban: '',
        potenciaContratada: '',
        idPropostaS2C: '',
        valorProposta: '',
        codigoCertidaoPermanente: '',
        nomeComercial: '',
        codigoComercial: '',
        vyn: '',
        documentos: {
          contratoAssinado: [],
          contratoAssinadoDigital: false,
          teaserS2C: [],
          cc: [],
          crcOuInicioAtividade: [],
          fatura: [],
          documentosAdicionais: []
        }
      });
      setSolarContractFormErrors({});

      if (embeddedContractOnly) {
        onEmbeddedContractSuccess?.();
      } else {
        fetchMarcacoes();
      }
    } catch (error: any) {
      console.error('Erro ao submeter contrato solar:', error);
      showNotification(error.message || 'Erro ao submeter contrato solar. Por favor, tente novamente.', 'error');
    } finally {
      setIsSubmittingSolarContract(false);
    }
  };

  // Handler for Energy/Gas Contract form fields
  const handleEnergyGasContractFieldChange = (
    section: 'angariacao' | 'documentos' | 'ee' | 'gas',
    field: string,
    value: string | boolean
  ) => {
    setEnergyGasContractData((prev: any) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    // Clear error for this field when user types or pastes
    if (contractFormErrors[`${section}.${field}`]) {
      setContractFormErrors((prev: any) => {
        const newErrors = { ...prev };
        delete newErrors[`${section}.${field}`];
        return newErrors;
      });
    }
  };

  const normalizeEnergyGasIdList = (block: any, arrayKey: 'cpes' | 'cuis', scalarKey: 'cpe' | 'cui'): string[] => {
    if (!block || typeof block !== 'object') return [];
    const arr = block[arrayKey];
    if (Array.isArray(arr)) {
      return arr.map((v) => String(v || '').trim()).filter(Boolean);
    }
    const scalar = String(block[scalarKey] || '').trim();
    if (!scalar) return [];
    // Legacy joined string (same convention as BackOffice assunto): split on "_"
    if (scalar.includes('_')) {
      return scalar.split('_').map((v) => v.trim()).filter(Boolean);
    }
    return [scalar];
  };
  const energyGasEeHasAnyValue = (ee: any) =>
    !!(
      normalizeEnergyGasIdList(ee, 'cpes', 'cpe').length > 0 ||
      String(ee?.dataInicioFornecimento || '').trim() ||
      String(ee?.dataFimFornecimento || '').trim()
    );
  const energyGasGasHasAnyValue = (gas: any) =>
    !!(
      normalizeEnergyGasIdList(gas, 'cuis', 'cui').length > 0 ||
      String(gas?.dataInicioFornecimento || '').trim() ||
      String(gas?.dataFimFornecimento || '').trim()
    );
  /** EE block is included in the contract only when CPE(s) + both dates are filled. */
  const energyGasEeIsComplete = (ee: any) =>
    !!(
      normalizeEnergyGasIdList(ee, 'cpes', 'cpe').length > 0 &&
      String(ee?.dataInicioFornecimento || '').trim() &&
      String(ee?.dataFimFornecimento || '').trim()
    );
  /** Gás block is included only when CUI(s) + both dates are filled. */
  const energyGasGasIsComplete = (gas: any) =>
    !!(
      normalizeEnergyGasIdList(gas, 'cuis', 'cui').length > 0 &&
      String(gas?.dataInicioFornecimento || '').trim() &&
      String(gas?.dataFimFornecimento || '').trim()
    );

  const handleEnergyGasCpeChange = (index: number, value: string) => {
    setEnergyGasContractData((prev: any) => {
      const current = Array.isArray(prev.ee?.cpes) ? [...prev.ee.cpes] : [''];
      while (current.length <= index) current.push('');
      current[index] = value;
      return { ...prev, ee: { ...prev.ee, cpes: current } };
    });
    if (contractFormErrors['ee.cpe'] || contractFormErrors['ee.cpes']) {
      setContractFormErrors((prev: any) => {
        const next = { ...prev };
        delete next['ee.cpe'];
        delete next['ee.cpes'];
        return next;
      });
    }
  };
  const handleEnergyGasAddCpe = () => {
    setEnergyGasContractData((prev: any) => {
      const current = Array.isArray(prev.ee?.cpes) ? [...prev.ee.cpes] : [''];
      return { ...prev, ee: { ...prev.ee, cpes: [...current, ''] } };
    });
  };
  const handleEnergyGasRemoveCpe = (index: number) => {
    setEnergyGasContractData((prev: any) => {
      const current = Array.isArray(prev.ee?.cpes) ? [...prev.ee.cpes] : [''];
      const next = current.filter((_: string, i: number) => i !== index);
      return { ...prev, ee: { ...prev.ee, cpes: next.length ? next : [''] } };
    });
  };
  const handleEnergyGasCuiChange = (index: number, value: string) => {
    setEnergyGasContractData((prev: any) => {
      const current = Array.isArray(prev.gas?.cuis) ? [...prev.gas.cuis] : [''];
      while (current.length <= index) current.push('');
      current[index] = value;
      return { ...prev, gas: { ...prev.gas, cuis: current } };
    });
    if (contractFormErrors['gas.cui'] || contractFormErrors['gas.cuis']) {
      setContractFormErrors((prev: any) => {
        const next = { ...prev };
        delete next['gas.cui'];
        delete next['gas.cuis'];
        return next;
      });
    }
  };
  const handleEnergyGasAddCui = () => {
    setEnergyGasContractData((prev: any) => {
      const current = Array.isArray(prev.gas?.cuis) ? [...prev.gas.cuis] : [''];
      return { ...prev, gas: { ...prev.gas, cuis: [...current, ''] } };
    });
  };
  const handleEnergyGasRemoveCui = (index: number) => {
    setEnergyGasContractData((prev: any) => {
      const current = Array.isArray(prev.gas?.cuis) ? [...prev.gas.cuis] : [''];
      const next = current.filter((_: string, i: number) => i !== index);
      return { ...prev, gas: { ...prev.gas, cuis: next.length ? next : [''] } };
    });
  };

  // Handler for IBAN input (PT50 + 21 digits)
  const handleIbanInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    
    // Allow user to delete and type freely, but format it correctly
    if (value === '') {
      // Allow empty value
      handleEnergyGasContractFieldChange('angariacao', 'iban', '');
      return;
    }
    
    // Normalize to uppercase for processing
    const upperValue = value.toUpperCase();
    
    // If it starts with PT50, keep PT50 and allow 21 digits after
    if (upperValue.startsWith('PT50')) {
      const afterPT50 = upperValue.substring(4);
      // Extract only digits (up to 21)
      const digitsOnly = afterPT50.replace(/[^0-9]/g, '').substring(0, 21);
      value = 'PT50' + digitsOnly;
    } else if (upperValue.startsWith('PT5')) {
      // If it starts with PT5, allow user to continue typing or delete
      if (upperValue.length === 4 && upperValue[3] === '0') {
        value = 'PT50';
      } else if (upperValue.length < 4) {
        // User is deleting, allow partial values like PT, PT5
        value = upperValue;
      } else {
        // Extract digits and add PT50 prefix only if there are digits
        const digitsOnly = upperValue.replace(/[^0-9]/g, '').substring(0, 21);
        value = digitsOnly.length > 0 ? 'PT50' + digitsOnly : upperValue.substring(0, 4);
      }
    } else if (upperValue.startsWith('PT')) {
      // If it starts with PT but not PT5, allow user to continue typing or delete
      if (upperValue.length <= 2) {
        // User is typing PT or deleting, allow it
        value = upperValue;
      } else {
        // Extract digits and add PT50 prefix only if there are digits
        const digitsOnly = upperValue.replace(/[^0-9]/g, '').substring(0, 21);
        value = digitsOnly.length > 0 ? 'PT50' + digitsOnly : upperValue.substring(0, 2);
      }
    } else if (upperValue.startsWith('P')) {
      // If it starts with P, allow user to continue typing or delete
      if (upperValue.length === 1) {
        value = 'P';
      } else if (upperValue[1] === 'T') {
        // User is typing PT, allow it
        value = upperValue;
      } else {
        // Extract digits and add PT50 prefix only if there are digits
        const digitsOnly = upperValue.replace(/[^0-9]/g, '').substring(0, 21);
        value = digitsOnly.length > 0 ? 'PT50' + digitsOnly : upperValue;
      }
    } else {
      // If it doesn't start with P/p, extract digits and add PT50 prefix
      const digitsOnly = upperValue.replace(/[^0-9]/g, '').substring(0, 21);
      value = digitsOnly.length > 0 ? 'PT50' + digitsOnly : '';
    }
    
    handleEnergyGasContractFieldChange('angariacao', 'iban', value);
  };

  // Handler for IBAN paste
  const handleIbanPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').trim().toUpperCase();
    
    // Check if the pasted text already contains PT50
    if (pastedText.includes('PT50')) {
      // Extract PT50 and the digits after it
      const pt50Index = pastedText.indexOf('PT50');
      const afterPT50 = pastedText.substring(pt50Index + 4);
      // Extract only digits (up to 21)
      const digitsOnly = afterPT50.replace(/[^0-9]/g, '').substring(0, 21);
      const formattedValue = 'PT50' + digitsOnly;
      handleEnergyGasContractFieldChange('angariacao', 'iban', formattedValue);
    } else if (pastedText.startsWith('PT')) {
      // If it starts with PT but not PT50, extract digits and add PT50 prefix
      const afterPT = pastedText.substring(2);
      const digitsOnly = afterPT.replace(/[^0-9]/g, '').substring(0, 21);
      const formattedValue = digitsOnly.length > 0 ? 'PT50' + digitsOnly : 'PT';
      handleEnergyGasContractFieldChange('angariacao', 'iban', formattedValue);
    } else {
      // Extract only digits and format as PT50 + 21 digits
      const digitsOnly = pastedText.replace(/[^0-9]/g, '').substring(0, 21);
      const formattedValue = digitsOnly.length > 0 ? 'PT50' + digitsOnly : '';
      handleEnergyGasContractFieldChange('angariacao', 'iban', formattedValue);
    }
  };

  // Handler for Energy/Gas Contract file uploads (supports multiple files)
  const handleEnergyGasContractFileChange = (field: string, files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }

    // Validate all files
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const allowedExtensions = ['pdf', 'png', 'jpg', 'jpeg', 'doc', 'docx'];
    
    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      
      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension || '')) {
        showNotification(`Tipo de ficheiro não permitido para "${file.name}". Apenas PDF, PNG, JPG, DOC e DOCX são permitidos.`, 'error');
        continue;
      }
      
      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      return;
    }

    // Add new files to existing array
    setEnergyGasContractData((prev: any) => {
      const currentFiles = prev.documentos[field] || [];
      const newFiles = [...currentFiles, ...validFiles];
      
      return {
        ...prev,
        documentos: {
          ...prev.documentos,
          [field]: newFiles
        }
      };
    });

    // Clear error for this field
    if (contractFormErrors[`documentos.${field}`]) {
      setContractFormErrors((prev: any) => {
        const newErrors = { ...prev };
        delete newErrors[`documentos.${field}`];
        return newErrors;
      });
    }
  };

  // Handler to remove a specific file from a field
  const handleRemoveFile = (field: string, index: number) => {
    setEnergyGasContractData((prev: any) => {
      const currentFiles = prev.documentos[field] || [];
      const newFiles = currentFiles.filter((_: File, i: number) => i !== index);
      
      return {
        ...prev,
        documentos: {
          ...prev.documentos,
          [field]: newFiles
        }
      };
    });
  };

  // Get NIF from proposals associated with marcação
  const getNifFromProposals = (marcacaoId: string | null): string | null => {
    if (!marcacaoId) return null;
    
    // Get marcação from state
    const marcacao = marcacoesData[marcacaoId];
    if (!marcacao) return null;
    
    // Get proposal data from marcação's form_data
    const formData = marcacao.form_data || {};
    const proposalData = formData.proposalData;
    
    if (!proposalData) return null;
    
    // Handle array of proposals (current format)
    if (Array.isArray(proposalData)) {
      for (const proposal of proposalData) {
        // Check empresa data in proposal
        const empresa = proposal.empresa || {};
        const nif = empresa.nifEmpresa || empresa.nif || empresa.nif_empresa || empresa.nifEmpresa || empresa.nif_empresa;
        if (nif) {
          return String(nif).trim();
        }
      }
    } else if (typeof proposalData === 'object' && proposalData !== null) {
      // Single proposal object (backward compatibility)
      const empresa = proposalData.empresa || {};
      const nif = empresa.nifEmpresa || empresa.nif || empresa.nif_empresa || empresa.nifEmpresa || empresa.nif_empresa;
      if (nif) {
        return String(nif).trim();
      }
    }
    
    // Fallback: check proposalsData (from ProposalRequest table, if any)
    const proposals = proposalsData[marcacaoId] || [];
    for (const proposal of proposals) {
      const formAnswers = proposal.form_answers || {};
      const empresa = formAnswers.empresa || {};
      const nif = empresa.nifEmpresa || empresa.nif || empresa.nif_empresa;
      if (nif) {
        return String(nif).trim();
      }
    }
    
    return null;
  };

  // Check if NIF starts with "5"
  const shouldShowDeclaracaoInicioAtividade = (): boolean => {
    if (!marcacaoToSubmitContract) return false;
    const nif = getNifFromProposals(marcacaoToSubmitContract);
    if (!nif) return false;
    return !nif.startsWith('5');
  };

  // Check if Marcação has a proposal with Luz/Eletricidade and Potência BTE or BTN → IBAN Comprovativo (and Comprovativo de Titularidade) mandatory
  const shouldRequireIbanComprovativo = (): boolean => {
    if (!marcacaoToSubmitContract) return false;
    const marcacao = marcacoesData[marcacaoToSubmitContract];
    if (!marcacao) return false;
    const formData = marcacao.form_data || {};
    const proposalData = formData.proposalData;
    if (!proposalData) return false;
    let servicos: any[] = [];
    let serviceDetails: any = {};
    if (Array.isArray(proposalData)) {
      proposalData.forEach((p: any) => {
        if (p && p.servicos && Array.isArray(p.servicos)) servicos.push(...p.servicos);
        if (p && p.serviceDetails) serviceDetails = { ...serviceDetails, ...p.serviceDetails };
      });
    } else if (typeof proposalData === 'object' && proposalData.servicos) {
      servicos = proposalData.servicos || [];
      serviceDetails = proposalData.serviceDetails || {};
    }
    for (const s of servicos) {
      const service = (s.service || '').toLowerCase();
      if (service !== 'luz' && service !== 'eletricidade') continue;
      const instanceId = s.instanceId;
      const details = serviceDetails[instanceId] || {};
      const potencia = (details.potencia || '').toLowerCase();
      if (potencia === 'bte' || potencia === 'btn') return true;
    }
    return false;
  };

  // Validate Energy/Gas Contract form
  const validateEnergyGasContractForm = (): boolean => {
    const errors: Record<string, string> = {};
    const missingFields: string[] = [];

    // Shared Angariação fields
    if (!energyGasContractData.angariacao.validaAteDia.trim()) {
      errors['angariacao.validaAteDia'] = 'Campo obrigatório';
      missingFields.push('Válida até dia');
    }
    if (!energyGasContractData.angariacao.idProposta.trim()) {
      errors['angariacao.idProposta'] = 'Campo obrigatório';
      missingFields.push('ID da Proposta');
    }

    const ee = (energyGasContractData as any).ee || {};
    const gas = (energyGasContractData as any).gas || {};
    const eeCpes = normalizeEnergyGasIdList(ee, 'cpes', 'cpe');
    const gasCuis = normalizeEnergyGasIdList(gas, 'cuis', 'cui');

    // Datas início/fim are mandatory only when the corresponding CPE / CUI is filled
    if (eeCpes.length > 0) {
      if (!String(ee.dataInicioFornecimento || '').trim()) {
        errors['ee.dataInicioFornecimento'] = 'Campo obrigatório';
        missingFields.push('Data início EE');
      }
      if (!String(ee.dataFimFornecimento || '').trim()) {
        errors['ee.dataFimFornecimento'] = 'Campo obrigatório';
        missingFields.push('Data fim EE');
      }
    }
    if (gasCuis.length > 0) {
      if (!String(gas.dataInicioFornecimento || '').trim()) {
        errors['gas.dataInicioFornecimento'] = 'Campo obrigatório';
        missingFields.push('Data início Gás');
      }
      if (!String(gas.dataFimFornecimento || '').trim()) {
        errors['gas.dataFimFornecimento'] = 'Campo obrigatório';
        missingFields.push('Data fim Gás');
      }
    }
    if (!energyGasEeIsComplete(ee) && !energyGasGasIsComplete(gas)) {
      missingFields.push('Pelo menos um bloco completo (EE ou Gás)');
      if (eeCpes.length === 0 && gasCuis.length === 0) {
        errors['ee.cpes'] = 'Preencha EE e/ou Gás';
        errors['gas.cuis'] = 'Preencha EE e/ou Gás';
      }
    }

    // Validate IBAN (optional field, but if filled must be valid)
    if (energyGasContractData.angariacao.iban && energyGasContractData.angariacao.iban.trim() !== '') {
      const ibanValue = energyGasContractData.angariacao.iban.trim().toUpperCase();
      // IBAN must start with "PT50" and have 21 digits after (total 25 characters)
      if (!ibanValue.startsWith('PT50')) {
        errors['angariacao.iban'] = 'O IBAN deve começar com "PT50".';
      } else if (ibanValue.length < 4) {
        // Allow partial input (PT50 is being typed)
        // Don't show error if user is still typing
      } else if (ibanValue.length !== 25) {
        errors['angariacao.iban'] = 'O IBAN deve ter exatamente 25 caracteres (PT50 + 21 dígitos).';
      } else {
        // Check if after "PT50" there are exactly 21 digits
        const digitsPart = ibanValue.substring(4);
        if (!/^\d{21}$/.test(digitsPart)) {
          errors['angariacao.iban'] = 'O IBAN deve ter "PT50" seguido de exatamente 21 dígitos.';
        }
      }
    }

    // Validate Documentos section
    const energyGasDigital = energyGasContractData.documentos.contratoAssinadoDigital === true;
    const showDeclaracao = shouldShowDeclaracaoInicioAtividade();
    if (showDeclaracao) {
      // If NIF doesn't start with "5", validate "CRC ou Início de Atividade" (file upload)
      if (!energyGasContractData.documentos.declaracaoInicioAtividade || 
          !Array.isArray(energyGasContractData.documentos.declaracaoInicioAtividade) || 
          energyGasContractData.documentos.declaracaoInicioAtividade.length === 0) {
        errors['documentos.declaracaoInicioAtividade'] = 'Campo obrigatório';
        missingFields.push('CRC ou Início de Atividade');
      }
    } else {
      // If NIF starts with "5", validate "Inicio de Atividade" (text field)
      if (!energyGasContractData.documentos.inicioAtividade.trim()) {
        errors['documentos.inicioAtividade'] = 'Campo obrigatório';
        missingFields.push('Inicio de Atividade');
      }
    }
    if (!energyGasDigital &&
        (!energyGasContractData.documentos.contratoAssinado || 
        !Array.isArray(energyGasContractData.documentos.contratoAssinado) || 
        energyGasContractData.documentos.contratoAssinado.length === 0)) {
      errors['documentos.contratoAssinado'] = 'Campo obrigatório';
      missingFields.push('Contrato assinado');
    }
    if (!energyGasContractData.documentos.cartaoCidadao || 
        !Array.isArray(energyGasContractData.documentos.cartaoCidadao) || 
        energyGasContractData.documentos.cartaoCidadao.length === 0) {
      errors['documentos.cartaoCidadao'] = 'Campo obrigatório';
      missingFields.push('Cartão Cidadão');
    }
    if (!energyGasContractData.documentos.faturaAtual || 
        !Array.isArray(energyGasContractData.documentos.faturaAtual) || 
        energyGasContractData.documentos.faturaAtual.length === 0) {
      errors['documentos.faturaAtual'] = 'Campo obrigatório';
      missingFields.push('Fatura Atual');
    }

    const requireIbanComprovativo = shouldRequireIbanComprovativo();
    if (requireIbanComprovativo) {
      if (!energyGasContractData.documentos.ibanComprovativo || 
          !Array.isArray(energyGasContractData.documentos.ibanComprovativo) || 
          energyGasContractData.documentos.ibanComprovativo.length === 0) {
        errors['documentos.ibanComprovativo'] = 'Campo obrigatório (proposta com Luz/Eletricidade e Potência BTE ou BTN)';
        missingFields.push('Comprovativo de IBAN');
      }
      if (!energyGasDigital &&
          (!energyGasContractData.documentos.comprovativoTitularidadeConta || 
          !Array.isArray(energyGasContractData.documentos.comprovativoTitularidadeConta) || 
          energyGasContractData.documentos.comprovativoTitularidadeConta.length === 0)) {
        errors['documentos.comprovativoTitularidadeConta'] = 'Campo obrigatório (proposta com Luz/Eletricidade e Potência BTE ou BTN)';
        missingFields.push('SEPA');
      }
    }

    setContractFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      // Create detailed error message
      const errorMessage = missingFields.length > 0 
        ? `Por favor, preencha todos os campos obrigatórios:\n${missingFields.join(', ')}`
        : 'Por favor, preencha todos os campos obrigatórios.';
      
      showNotification(errorMessage, 'error');
      
      // Scroll to first error field after a short delay to allow the modal to render
      setTimeout(() => {
        const firstErrorField = document.querySelector('.form-input.error');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Focus the first input field (if it's an input)
          if (firstErrorField instanceof HTMLInputElement || firstErrorField instanceof HTMLTextAreaElement) {
            firstErrorField.focus();
          }
        }
      }, 100);
      
      return false;
    }

    return true;
  };

  // Submit Energy/Gas Contract form
  const submitEnergyGasContract = async () => {
    if (!validateEnergyGasContractForm()) {
      return;
    }

    if (!marcacaoToSubmitContract) {
      showNotification('Erro: Marcação não encontrada.', 'error');
      return;
    }

    if (isSubmittingEnergyGasContract) return;
    setIsSubmittingEnergyGasContract(true);

    try {
      if (!embeddedContractOnly) {
        await fetchMarcacaoFromCallcenter(marcacaoToSubmitContract);
      } else if (!resolveMarcacaoInStore(marcacoesData, marcacaoToSubmitContract)) {
        throw new Error(
          'Dados da marcação não disponíveis. Feche e volte a abrir Submeter Contrato.'
        );
      }

      // Map of document categories to field_name, stage, and display label (for backoffice)
      const documentCategoryMap: { [key: string]: { fieldName: string; stage: string; fieldLabel: string } } = {
        'contratoAssinado': { fieldName: 'contrato_assinado', stage: 'contract', fieldLabel: 'Contrato assinado' },
        'cartaoCidadao': { fieldName: 'cartao_cidadao', stage: 'contract', fieldLabel: 'Cartão Cidadão' },
        'faturaAtual': { fieldName: 'fatura_atual', stage: 'contract', fieldLabel: 'Fatura Atual' },
        'ibanComprovativo': { fieldName: 'iban_comprovativo', stage: 'contract', fieldLabel: 'Comprovativo de IBAN' },
        'comprovativoTitularidadeConta': { fieldName: 'comprovativo_titularidade_conta', stage: 'contract', fieldLabel: 'Comprovativo de Titularidade da Conta' },
        'declaracaoInicioAtividade': { fieldName: 'declaracao_inicio_atividade', stage: 'contract', fieldLabel: 'CRC ou Início de Atividade' },
        'documentosAdicionais': { fieldName: 'documentos_adicionais', stage: 'contract', fieldLabel: 'Documentos Adicionais' }
      };

      // Upload all files first and collect lead_file_ids; each file includes field + fieldLabel for backoffice
      const uploadedFiles: { [category: string]: any } = {};
      
      for (const [category, config] of Object.entries(documentCategoryMap)) {
        const files = (energyGasContractData.documentos as any)[category];
        if (Array.isArray(files) && files.length > 0) {
          uploadedFiles[category] = [];
          
          for (const file of files) {
            const fileMeta = (obj: any) => ({ ...obj, field: category, fieldLabel: config.fieldLabel });
            // Skip if already uploaded (has lead_file_id)
            if (file.lead_file_id || file.id) {
              uploadedFiles[category].push(fileMeta({
                name: file.name,
                size: file.size,
                type: file.type,
                lead_file_id: file.lead_file_id || file.id,
                lead_id: file.lead_id || file.leadId || null
              }));
              continue;
            }
            
            // Upload file if it's a File object
            if (file instanceof File) {
              try {
                const uploadFormData = new FormData();
                uploadFormData.append('file', file);
                uploadFormData.append('business_area', 'energy_solutions');
                uploadFormData.append('stage', config.stage);
                uploadFormData.append('field_name', config.fieldName);
                
                const uploadResponse = embeddedContractApi
                  ? await embeddedContractApi.uploadFile(uploadFormData)
                  : await uploadEmbeddedContractFile(
                      marcacaoToSubmitContract!,
                      uploadFormData,
                      embeddedProspeccaoLeadId,
                      embeddedContractOnly && !embeddedProspeccaoLeadId
                    );
                
                if (uploadResponse.success && uploadResponse.file) {
                  uploadedFiles[category].push(fileMeta({
                    name: uploadResponse.file.name || uploadResponse.file.file_name || file.name,
                    size: uploadResponse.file.size ?? uploadResponse.file.file_size ?? file.size,
                    type: uploadResponse.file.type || uploadResponse.file.file_type || file.type,
                    lead_file_id: uploadResponse.file.lead_file_id ?? uploadResponse.file.id,
                    lead_id: uploadResponse.file.lead_id ?? null
                  }));
                } else if (embeddedContractOnly || embeddedContractApi) {
                  throw new Error(
                    uploadResponse.error ||
                      uploadResponse.message ||
                      `Falha ao enviar ficheiro ${file.name}`
                  );
                } else {
                  console.error(`Failed to upload file ${file.name} for category ${category}:`, uploadResponse);
                  uploadedFiles[category].push(fileMeta({
                    name: file.name,
                    size: file.size,
                    type: file.type
                  }));
                }
              } catch (uploadError) {
                if (embeddedContractOnly || embeddedContractApi) {
                  throw uploadError;
                }
                console.error(`Error uploading file ${file.name} for category ${category}:`, uploadError);
                uploadedFiles[category].push(fileMeta({
                  name: file.name,
                  size: file.size,
                  type: file.type
                }));
              }
            } else {
              uploadedFiles[category].push(fileMeta({
                name: file.name || file.file_name,
                size: file.size || file.file_size,
                type: file.type || file.file_type,
                lead_file_id: file.lead_file_id || file.id,
                lead_id: file.lead_id || file.leadId || null
              }));
            }
          }
        } else {
          uploadedFiles[category] = [];
        }
      }

      // Handle inicioAtividade (can be string or array)
      const inicioAtividade = (energyGasContractData.documentos as any).inicioAtividade;
      if (typeof inicioAtividade === 'string') {
        uploadedFiles.inicioAtividade = inicioAtividade;
      } else if (Array.isArray(inicioAtividade)) {
        uploadedFiles.inicioAtividade = inicioAtividade.map((file: any) => {
          if (file.lead_file_id || file.id) {
            return {
              name: file.name,
              size: file.size,
              type: file.type,
              lead_file_id: file.lead_file_id || file.id,
              lead_id: file.lead_id || file.leadId || null
            };
          }
          return {
            name: file.name || file.file_name,
            size: file.size || file.file_size,
            type: file.type || file.file_type
          };
        });
      } else {
        uploadedFiles.inicioAtividade = '';
      }

      const lastProposalForSubmit =
        contractProposalsForMarcacao
          .filter((p) => contractSelectedProposalIds.includes(p.id))
          .slice(-1)[0] || contractProposalsForMarcacao.slice(-1)[0];
      const proposalEquipaInterna = (lastProposalForSubmit?.proposal?.equipaInterna || {}) as {
        nomeUsuario?: string;
        codigoComercial?: string;
      };
      const resolvedNomeComercial = resolveContractNomeComercial({
        currentUserData,
        currentUserId,
        userMap,
        proposalEquipaInterna,
      });
      const resolvedCodigoComercial = resolveContractCodigoComercial({
        currentUserData,
        proposalEquipaInterna,
      });

      // Prepare contract data with uploaded file references
      const eeBlock = (energyGasContractData as any).ee || {};
      const gasBlock = (energyGasContractData as any).gas || {};
      const eeComplete = energyGasEeIsComplete(eeBlock);
      const gasComplete = energyGasGasIsComplete(gasBlock);
      // Persist only completed product blocks so empty EE/Gás do not appear on the BackOffice card
      const eeCpes = normalizeEnergyGasIdList(eeBlock, 'cpes', 'cpe');
      const gasCuis = normalizeEnergyGasIdList(gasBlock, 'cuis', 'cui');
      const eeCpeJoined = eeCpes.join('_');
      const gasCuiJoined = gasCuis.join('_');
      const eeToSave = eeComplete
        ? {
            cpes: eeCpes,
            cpe: eeCpeJoined, // joined for Assunto / legacy readers (same as before)
            dataInicioFornecimento: String(eeBlock.dataInicioFornecimento || '').trim(),
            dataFimFornecimento: String(eeBlock.dataFimFornecimento || '').trim(),
          }
        : null;
      const gasToSave = gasComplete
        ? {
            cuis: gasCuis,
            cui: gasCuiJoined,
            dataInicioFornecimento: String(gasBlock.dataInicioFornecimento || '').trim(),
            dataFimFornecimento: String(gasBlock.dataFimFornecimento || '').trim(),
          }
        : null;
      const contractDataToSave: any = {
        assignedProposalIds: contractSelectedProposalIds,
        urgente: !!energyGasContractData.urgente,
        tipo: 'energia_gas',
        angariacao: {
          // Shared fields; mirror first product dates for legacy BO readers
          dataInicioFornecimento:
            (eeToSave && eeToSave.dataInicioFornecimento) ||
            (gasToSave && gasToSave.dataInicioFornecimento) ||
            '',
          dataFimFornecimento:
            (eeToSave && eeToSave.dataFimFornecimento) ||
            (gasToSave && gasToSave.dataFimFornecimento) ||
            '',
          validaAteDia: energyGasContractData.angariacao.validaAteDia,
          idProposta: energyGasContractData.angariacao.idProposta,
          iban: energyGasContractData.angariacao.iban || ''
        },
        ee: eeToSave,
        gas: gasToSave,
        cpe: eeCpeJoined,
        cui: gasCuiJoined,
        nomeComercial: resolvedNomeComercial,
        codigoComercial: resolvedCodigoComercial,
        // User and team information for audit
        submittedBy: {
          userId: currentUserId,
          username: resolvedNomeComercial,
          email: currentUserData?.email || '',
          team: currentUserData?.team || currentUserData?.team_name || '',
          codigoComercial: resolvedCodigoComercial,
        },
        documentos: {
          contratoAssinado: uploadedFiles.contratoAssinado || [],
          contratoAssinadoDigital: !!energyGasContractData.documentos.contratoAssinadoDigital,
          cartaoCidadao: uploadedFiles.cartaoCidadao || [],
          faturaAtual: uploadedFiles.faturaAtual || [],
          ibanComprovativo: uploadedFiles.ibanComprovativo || [],
          comprovativoTitularidadeConta: uploadedFiles.comprovativoTitularidadeConta || [],
          inicioAtividade: uploadedFiles.inicioAtividade || '',
          declaracaoInicioAtividade: uploadedFiles.declaracaoInicioAtividade || [],
          documentosAdicionais: uploadedFiles.documentosAdicionais || []
        },
        submittedAt: new Date().toISOString(),
        ...embeddedContractSubmissionMeta(embeddedContractOnly, embeddedProspeccaoLeadId),
      };

      if (embeddedContractApi) {
        await embeddedContractApi.persist(contractDataToSave);
      } else {
        await persistEmbeddedContractData(
          marcacaoToSubmitContract,
          contractDataToSave,
          embeddedContractOnly,
          embeddedProspeccaoLeadId
        );
      }

      showNotification('Contrato de Contratação de Energia e Gás Natural submetido com sucesso!', 'success');
      setShowEnergyGasContractForm(false);
      setMarcacaoToSubmitContract(null);
      
      // Reset form
      setEnergyGasContractData(createEmptyEnergyGasContractData());
      setEnergyGasEeExpanded(true);
      setEnergyGasGasExpanded(true);
      setContractFormErrors({});

      if (embeddedContractOnly) {
        onEmbeddedContractSuccess?.();
      } else {
        fetchMarcacoes();
      }
    } catch (error: any) {
      console.error('Error submitting energy/gas contract:', error);
      showNotification(error.response?.data?.message || error?.message || 'Erro ao submeter contrato. Por favor, tente novamente.', 'error');
    } finally {
      setIsSubmittingEnergyGasContract(false);
    }
  };


  const submitProposal = async () => {
    if (!validateProposalForm()) {
      return;
    }

    if (!currentMarcacaoId) {
      showNotification('Erro: Marcação não encontrada.', 'error');
      return;
    }

    if (isSubmittingProposal) return;
    setIsSubmittingProposal(true);
    
    try {
      // First, upload all files and store their references
      // IMPORTANT: If ANY file upload fails, the entire proposal submission will fail
      const fileReferences: Record<string, Record<string, any>> = {};
      
      // Upload files for each service instance
      // If any upload fails, an error will be thrown and proposal submission will stop
      for (const instance of (proposalData.servicos || [])) {
        const instanceId = instance.instanceId;
        const instanceDetails = proposalData.serviceDetails[instanceId] || {};
        
        fileReferences[instanceId] = {};
        
        // Upload fatura if exists - MUST succeed or proposal submission fails
        if (instanceDetails.fatura instanceof File) {
          const uploadFormData = new FormData();
          uploadFormData.append('file', instanceDetails.fatura);
          uploadFormData.append('business_area', 'energy_solutions');
          uploadFormData.append('stage', 'proposta');
          uploadFormData.append('field_name', 'fatura');
          uploadFormData.append('instance_id', instanceId);
          
          const uploadResponse = await uploadEmbeddedProposalFile(
            currentMarcacaoId!,
            uploadFormData,
            embeddedProspeccaoLeadId,
            embeddedProposalOnly && !embeddedProspeccaoLeadId && !embeddedRetencaoClientId
              ? embeddedCccWorkspace
              : null,
            embeddedRetencaoClientId
          );
          
          if (!uploadResponse.success || !uploadResponse.file) {
            const errorMsg = uploadResponse.error || uploadResponse.message || 'Falha ao enviar ficheiro fatura';
            console.error(`File fatura upload failed for ${instanceId}:`, errorMsg);
            showNotification(`Erro ao enviar ficheiro fatura: ${errorMsg}`, 'error');
            throw new Error(`Falha ao enviar ficheiro fatura: ${errorMsg}`);
          }
          
            fileReferences[instanceId]['fatura'] = {
            name: uploadResponse.file.name,
            size: uploadResponse.file.size,
            type: uploadResponse.file.type,
            data: uploadResponse.file.data, // Base64 data
            id: uploadResponse.file.id,
            lead_file_id: uploadResponse.file.lead_file_id, // Database file ID
            field: 'fatura',
            uploaded: true,
            uploaded_at: uploadResponse.file.uploaded_at
          };
        }
        
        // Upload printTelhado if exists - MUST succeed or proposal submission fails
        if (instanceDetails.printTelhado instanceof File) {
          const uploadFormData = new FormData();
          uploadFormData.append('file', instanceDetails.printTelhado);
          uploadFormData.append('business_area', 'energy_solutions');
          uploadFormData.append('stage', 'proposta');
          uploadFormData.append('field_name', 'printTelhado');
          uploadFormData.append('instance_id', instanceId);
          
          const uploadResponse = await uploadEmbeddedProposalFile(
            currentMarcacaoId!,
            uploadFormData,
            embeddedProspeccaoLeadId,
            embeddedProposalOnly && !embeddedProspeccaoLeadId && !embeddedRetencaoClientId
              ? embeddedCccWorkspace
              : null,
            embeddedRetencaoClientId
          );
          
          if (!uploadResponse.success || !uploadResponse.file) {
            const errorMsg = uploadResponse.error || uploadResponse.message || 'Falha ao enviar ficheiro printTelhado';
            console.error(`File printTelhado upload failed for ${instanceId}:`, errorMsg);
            showNotification(`Erro ao enviar ficheiro printTelhado: ${errorMsg}`, 'error');
            throw new Error(`Falha ao enviar ficheiro printTelhado: ${errorMsg}`);
          }
          
            fileReferences[instanceId]['printTelhado'] = {
            name: uploadResponse.file.name,
            size: uploadResponse.file.size,
            type: uploadResponse.file.type,
            data: uploadResponse.file.data, // Base64 data
            id: uploadResponse.file.id,
            lead_file_id: uploadResponse.file.lead_file_id, // Database file ID
            field: 'printTelhado',
            uploaded: true,
            uploaded_at: uploadResponse.file.uploaded_at
            };
        }
      }

      // Prepare proposal data structure (excluding File objects, storing metadata instead)
      // Note: propostaId will be generated automatically by the backend
      const proposalDataToSave: any = {
        equipaInterna: {
          ...proposalData.equipaInterna,
          tipoPedido: proposalData.equipaInterna?.tipoPedido || 'normal' // Default to 'normal' if not set
        },
        empresa: {
          ...proposalData.empresa
        },
        servicos: (proposalData.servicos || []).map((instance: any) => {
          return {
          service: instance.service,
          instanceNumber: instance.instanceNumber,
          instanceId: instance.instanceId
          };
        }),
        serviceQuantities: proposalData.serviceQuantities,
        serviceDetails: {} as Record<string, any>,
        fileReferences: fileReferences,
        submittedAt: new Date().toISOString(),
        submittedBy: currentUserId,
        // propostaId will be generated automatically by the backend
      };

      if (embeddedProposalOnly) {
        proposalDataToSave.submittedFromCcc = true;
        proposalDataToSave.submittedBySource = embeddedProspeccaoLeadId
          ? 'ccc_prospeccao'
          : embeddedRetencaoClientId || embeddedCccWorkspace === 'retencao'
            ? 'ccc_retencao'
            : 'ccc_recuperacao';
      }

      if (proposalModalMode === 'refazer') {
        proposalDataToSave.isRefazer = true;
        if (selectedRefazerProposalId) {
          proposalDataToSave.refazerSourcePropostaId = selectedRefazerProposalId;
        }
      }

      if (allowProposalSharing && currentUserId) {
        const seen = new Set<number>();
        const cleaned: number[] = [];
        for (const sid of sharedComercialUserIds) {
          const n = Number(sid);
          if (!Number.isFinite(n) || n <= 0 || n === Number(currentUserId)) continue;
          if (!seen.has(n)) {
            seen.add(n);
            cleaned.push(n);
          }
        }
        proposalDataToSave.shared_comercial_user_ids = cleaned;
      }

      // Mirror proposal company data into marcação commercial fields so they appear under
      // "Informação Comercial" in the marcação details.
      const proposalEmpresa = proposalDataToSave.empresa || {};
      const firstServiceWithAddress = (proposalData.servicos || [])
        .map((instance: any) => proposalData.serviceDetails?.[instance.instanceId])
        .find((details: any) => details?.moradaFornecimento && String(details.moradaFornecimento).trim() !== '');

      const proposalMarcacaoOverrides: Record<string, string> = {};
      const setOverrideIfValue = (field: string, value: any) => {
        const normalizedValue = typeof value === 'string' ? value.trim() : value;
        if (normalizedValue) {
          proposalMarcacaoOverrides[field] = String(normalizedValue);
        }
      };

      setOverrideIfValue('nomeEmpresa', proposalEmpresa.nomeEmpresa);
      setOverrideIfValue('nomeResponsavel', proposalEmpresa.nomeRepresentante);
      setOverrideIfValue('contactoCliente', proposalEmpresa.contactoRepresentante);
      setOverrideIfValue(
        'morada',
        proposalEmpresa.moradaFornecimento || proposalEmpresa.moradaCorrespondencia || firstServiceWithAddress?.moradaFornecimento
      );
      setOverrideIfValue('localidade', proposalEmpresa.regiao);
      setOverrideIfValue('emailEmpresa', proposalEmpresa.emailEmpresa);

      // Store service instance details (excluding File objects)
      // CRITICAL: Ensure we iterate over ALL services in servicos array
      for (const instance of (proposalData.servicos || [])) {
        const instanceId = instance.instanceId;
        const instanceDetails = proposalData.serviceDetails[instanceId] || {};
        
        proposalDataToSave.serviceDetails[instanceId] = {
          moradaFornecimento: instanceDetails.moradaFornecimento || '',
          cpe: instanceDetails.cpe || '',
          potencia: instanceDetails.potencia || '',
          potenciaOutra: instanceDetails.potenciaOutra || '',
          sistema: instanceDetails.sistema || '', // Luz: Trifásico or Monofásico
          formaPagamento: instanceDetails.formaPagamento || '', // Only for transacional
          cui: instanceDetails.cui || '', // Only for gas
          escalao: instanceDetails.escalao || '', // Only for gas
          dataFecho: instanceDetails.dataFecho || '',
          coordenadasGeograficas: instanceDetails.coordenadasGeograficas || '',
          observacoes: instanceDetails.observacoes || '',
          // Mobilidade Elétrica questionnaire
          plataformaGestao: instanceDetails.plataformaGestao || '',
          localInstalacao: instanceDetails.localInstalacao || '',
          quadroMobilidade: instanceDetails.quadroMobilidade || '',
          tipoFixacao: instanceDetails.tipoFixacao || '',
          tipoCarregamento: instanceDetails.tipoCarregamento || '',
          distanciaQveProximo: instanceDetails.distanciaQveProximo || '',
          distanciaQveDistante: instanceDetails.distanciaQveDistante || '',
          // File metadata from new upload or reused from previous request (Refazer)
          ...(fileReferences[instanceId]?.fatura ? {
            fatura_name: fileReferences[instanceId].fatura.name || '',
            fatura_size: fileReferences[instanceId].fatura.size || 0,
            fatura_type: fileReferences[instanceId].fatura.type || '',
            fatura_lead_file_id: fileReferences[instanceId].fatura.lead_file_id || null
          } : (instanceDetails.fatura_name ? {
            fatura_name: instanceDetails.fatura_name || '',
            fatura_size: instanceDetails.fatura_size || 0,
            fatura_type: instanceDetails.fatura_type || '',
            fatura_lead_file_id: instanceDetails.fatura_lead_file_id ?? instanceDetails.faturaLeadFileId ?? null
          } : {})),
          ...(fileReferences[instanceId]?.printTelhado ? {
            printTelhado_name: fileReferences[instanceId].printTelhado.name || '',
            printTelhado_size: fileReferences[instanceId].printTelhado.size || 0,
            printTelhado_type: fileReferences[instanceId].printTelhado.type || '',
            printTelhado_lead_file_id: fileReferences[instanceId].printTelhado.lead_file_id || null
          } : (instanceDetails.printTelhado_name ? {
            printTelhado_name: instanceDetails.printTelhado_name || '',
            printTelhado_size: instanceDetails.printTelhado_size || 0,
            printTelhado_type: instanceDetails.printTelhado_type || '',
            printTelhado_lead_file_id: instanceDetails.printTelhado_lead_file_id ?? instanceDetails.printTelhadoLeadFileId ?? null
          } : {}))
        };
      }

      const marcacaoIdToUse = currentMarcacaoId;
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');

      if (embeddedProposalOnly && embeddedProspeccaoLeadId) {
        const cccBase = resolveCccApiBaseUrl();
        const url = `${cccBase}/api/ccc/prospeccao/leads/${encodeURIComponent(embeddedProspeccaoLeadId)}/proposal`;
        const res = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            proposalData: proposalDataToSave,
            ...proposalMarcacaoOverrides,
          }),
        });
        if (!res.ok) {
          const error = await res.json().catch(() => ({}));
          showNotification(
            `Erro ao submeter proposta: ${(error as { message?: string }).message || 'Erro desconhecido'}`,
            'error'
          );
          return;
        }
        const json = await res.json();
        if (json.success) {
          showNotification('Proposta submetida com sucesso!', 'success');
          closeProposalModal();
          setIsInstantMarcacao(false);
          onEmbeddedProposalSuccess?.();
        } else {
          showNotification(json.message || 'Erro ao submeter proposta.', 'error');
        }
        return;
      }

      if (embeddedProposalOnly && embeddedRetencaoClientId) {
        const cccBase = resolveCccApiBaseUrl();
        const url = `${cccBase}/api/ccc/retencao/clients/${encodeURIComponent(embeddedRetencaoClientId)}/proposal`;
        const res = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            proposalData: proposalDataToSave,
            ...proposalMarcacaoOverrides,
          }),
        });
        if (!res.ok) {
          const error = await res.json().catch(() => ({}));
          showNotification(
            `Erro ao submeter proposta: ${(error as { message?: string }).message || 'Erro desconhecido'}`,
            'error'
          );
          return;
        }
        const json = await res.json();
        if (json.success) {
          showNotification('Proposta submetida com sucesso!', 'success');
          closeProposalModal();
          setIsInstantMarcacao(false);
          onEmbeddedProposalSuccess?.();
        } else {
          showNotification(json.message || 'Erro ao submeter proposta.', 'error');
        }
        return;
      }

      if (embeddedProposalOnly && !embeddedProspeccaoLeadId && !embeddedRetencaoClientId) {
        const cccBase = resolveCccApiBaseUrl();
        const workspace = embeddedCccWorkspace === 'retencao' ? 'retencao' : 'recuperacao';
        const url = `${cccBase}/api/ccc/${workspace}/marcacoes/${encodeURIComponent(marcacaoIdToUse)}/proposal`;
        const res = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            proposalData: proposalDataToSave,
            ...proposalMarcacaoOverrides,
          }),
        });
        if (!res.ok) {
          const error = await res.json().catch(() => ({}));
          showNotification(
            `Erro ao submeter proposta: ${(error as { message?: string }).message || 'Erro desconhecido'}`,
            'error'
          );
          return;
        }
        const json = await res.json();
        if (json.success) {
          showNotification('Proposta submetida com sucesso!', 'success');
          closeProposalModal();
          setIsInstantMarcacao(false);
          onEmbeddedProposalSuccess?.();
        } else {
          showNotification(json.message || 'Erro ao submeter proposta.', 'error');
        }
        return;
      }

      // Save proposal data to marcação's form_data
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      const url = `${backendUrl}/api/callcenter/calendar/marcacoes/${marcacaoIdToUse}`;

      const res = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          proposalData: proposalDataToSave,
          ...proposalMarcacaoOverrides
        })
      });

      if (!res.ok) {
        const error = await res.json();
        showNotification(`Erro ao submeter proposta: ${error.message || 'Erro desconhecido'}`, 'error');
        return;
      }

      const json = await res.json();
      if (json.success) {
        showNotification('Proposta submetida com sucesso!', 'success');

        if (embeddedProposalOnly) {
          closeProposalModal();
          setIsInstantMarcacao(false);
          onEmbeddedProposalSuccess?.();
          return;
        }
        
        // Update local state immediately to show estadoProposta change
        if (currentMarcacaoId) {
          setMarcacoesData(prev => ({
            ...prev,
            [currentMarcacaoId]: {
              ...prev[currentMarcacaoId],
              estado_proposta: 'pendente'
            }
          }));
        }
        
        // If this is an instant marcação, update sub-stage to "Concluido Com Proposta"
        if (isInstantMarcacao && currentMarcacaoId) {
          const success = await updateMarcacaoSubstageForMarcacao(currentMarcacaoId, 'feedback-pendencia-concluido-com-proposta');
          if (success) {
            // Navigate to Feedback Pendência section and expand Concluido Com Proposta subsection
            setCurrentSection('feedback-pendencia');
            setExpandedSubsections(prev => ({
              ...prev,
              'feedback-pendencia-concluido-com-proposta': true
            }));
          }
        }
        
        closeProposalModal();
        setIsInstantMarcacao(false); // Reset flag
        
        // Refresh marcações data to show updated proposal (includes estadoProposta update)
        await fetchMarcacoes();
      } else {
        showNotification(json.message || 'Erro ao submeter proposta.', 'error');
      }
    } catch (error: any) {
      console.error('Error submitting proposal:', error);
      showNotification(error.response?.data?.message || error.message || 'Erro ao submeter proposta.', 'error');
    } finally {
      setIsSubmittingProposal(false);
    }
  };

  // Validation functions for instant marcação form
  const validateInstantMarcacaoStep = (step: number) => {
    const errors: any = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (step === 1) {
      // Agendamento step
      if (!instantMarcacaoFormData.marcacaoData) errors.marcacaoData = 'Data é obrigatória';
      if (!instantMarcacaoFormData.horaInicio) errors.horaInicio = 'Hora de início é obrigatória';
      if (!instantMarcacaoFormData.horaFim) errors.horaFim = 'Hora de fim é obrigatória';
    } else if (step === 2) {
      // Produto step
      if (!instantMarcacaoFormData.produto) errors.produto = 'Produto é obrigatório';
    } else if (step === 3) {
      // Cliente step
      if (!instantMarcacaoFormData.nomeEmpresa) errors.nomeEmpresa = 'Nome da empresa é obrigatório';
      if (!instantMarcacaoFormData.nomeResponsavel) errors.nomeResponsavel = 'Nome do responsável é obrigatório';
      if (!instantMarcacaoFormData.morada) errors.morada = 'Morada é obrigatória';
      if (!instantMarcacaoFormData.contactoCliente) errors.contactoCliente = 'Contacto do cliente é obrigatório';
      if (instantMarcacaoFormData.emailEmpresa && !emailRegex.test(instantMarcacaoFormData.emailEmpresa.trim())) {
        errors.emailEmpresa = 'Por favor, insira um email válido';
      }
    }
    
    setInstantMarcacaoValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInstantMarcacaoNextStep = () => {
    if (validateInstantMarcacaoStep(currentInstantMarcacaoStep)) {
      setCurrentInstantMarcacaoStep(currentInstantMarcacaoStep + 1);
      setInstantMarcacaoValidationErrors({});
    }
  };

  const handleInstantMarcacaoPrevStep = () => {
    setCurrentInstantMarcacaoStep(Math.max(1, currentInstantMarcacaoStep - 1));
    setInstantMarcacaoValidationErrors({});
  };

  // Handle instant marcação form submission
  const handleInstantMarcacaoSubmit = async () => {
    setSavingInstantMarcacao(true);
    setInstantMarcacaoNotification(null);
    
    try {
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
      if (!token) {
        setInstantMarcacaoNotification({ message: 'Erro: Token de autenticação não encontrado', type: 'error' });
        setTimeout(() => setInstantMarcacaoNotification(null), 4000);
        setSavingInstantMarcacao(false);
        return;
      }

      // Validate all steps before submitting
      const allErrors: any = {};
      for (let step = 1; step <= 3; step++) {
        const stepErrors = validateInstantMarcacaoStep(step);
        if (!stepErrors) {
          Object.assign(allErrors, instantMarcacaoValidationErrors);
        }
      }
      
      if (Object.keys(allErrors).length > 0) {
        setSavingInstantMarcacao(false);
        return;
      }

      // Prepare data for backend API - convert date format if needed
      let marcacaoData = instantMarcacaoFormData.marcacaoData;
      
      // Convert DD/MM/YYYY to YYYY-MM-DD if needed
      if (marcacaoData && marcacaoData.includes('/')) {
        const parts = marcacaoData.split('/');
        if (parts.length === 3) {
          marcacaoData = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        }
      }

      // Ensure marcacaoUsers is set based on user role
      let userIds = instantMarcacaoFormData.marcacaoUsers || [];
      if (!userIds || userIds.length === 0) {
        if (isAdminOrGerenteComercial() && selectedSidebarUser) {
          userIds = [selectedSidebarUser];
        } else if (currentUserId) {
          userIds = [currentUserId];
        }
      }
      
      // Prepare submission data
      const normalizedUserIds = userIds.map((uid: any) => String(uid));
      const submissionData = {
        // Step 1: Agendamento
        marcacaoData: marcacaoData,
        horaInicio: instantMarcacaoFormData.horaInicio,
        horaFim: instantMarcacaoFormData.horaFim,
        marcacaoUsers: normalizedUserIds,
        marcacaoDescription: instantMarcacaoFormData.marcacaoDescription || '',
        
        // Step 2: Produto
        produto: instantMarcacaoFormData.produto,
        
        // Step 3: Cliente
        nomeEmpresa: instantMarcacaoFormData.nomeEmpresa,
        nomeResponsavel: instantMarcacaoFormData.nomeResponsavel,
        morada: instantMarcacaoFormData.morada,
        contactoCliente: instantMarcacaoFormData.contactoCliente,
        emailEmpresa: instantMarcacaoFormData.emailEmpresa?.trim() || '',
        codigoPostal1: instantMarcacaoFormData.codigoPostal1 || '',
        codigoPostal2: instantMarcacaoFormData.codigoPostal2 || '',
        localidade: instantMarcacaoFormData.localidade || '',
        linkGoogleMaps: instantMarcacaoFormData.linkGoogleMaps || '',
        
        // Source: indicate this is from Comercial Management
        source: 'comercial'
      };

      // Call backend API to create marcação event
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      const apiUrl = `${backendUrl}/api/callcenter/calendar/marcacoes`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submissionData)
      });

      const result = await response.json().catch(() => ({} as any));

      if (!response.ok || !result.success) {
        setInstantMarcacaoNotification({ 
          message: `Erro ao criar marcação: ${result.message || result.error || 'Tente novamente.'}`, 
          type: 'error' 
        });
        setTimeout(() => setInstantMarcacaoNotification(null), 4000);
        setSavingInstantMarcacao(false);
        return;
      }

      // Get the created marcação ID from the response
      // Backend returns: { success: true, marcacao: { marcacao_id: 'MAR-...', id: 123, ... } }
      const createdMarcacaoId = result.marcacao?.marcacao_id || result.marcacao_id || result.marcacao?.id?.toString();
      
      if (!createdMarcacaoId) {
        setInstantMarcacaoNotification({ 
          message: 'Erro: ID da marcação não retornado', 
          type: 'error' 
        });
        setTimeout(() => setInstantMarcacaoNotification(null), 4000);
        setSavingInstantMarcacao(false);
        return;
      }

      // Close instant marcação modal
      setShowInstantMarcacaoModal(false);
      setCurrentInstantMarcacaoStep(1);
      setInstantMarcacaoFormData({});
      setInstantMarcacaoValidationErrors({});
      setSavingInstantMarcacao(false);

      // Set flag to indicate this is an instant marcação
      setIsInstantMarcacao(true);

      // Update sub-stage to "Concluido Com Proposta" immediately after creation
      const substageSuccess = await updateMarcacaoSubstage(createdMarcacaoId, 'feedback-pendencia-concluido-com-proposta');
      if (substageSuccess) {
        // Navigate to Feedback Pendência section and expand Concluido Com Proposta subsection
        setCurrentSection('feedback-pendencia');
        setExpandedSubsections(prev => ({
          ...prev,
          'feedback-pendencia-concluido-com-proposta': true
        }));
      }

      // Refresh marcações to get the newly created one with updated sub-stage
      await fetchMarcacoes();

      // Trigger calendar refresh so the new marcação appears on the calendar
      refreshCalendarData();

      // Open proposal modal automatically for the newly created marcação
      openProposalModal(createdMarcacaoId);

      showNotification('Marcação criada com sucesso! Abrindo Solicitar Proposta...', 'success');
    } catch (error: any) {
      console.error('Error creating instant marcação:', error);
      setInstantMarcacaoNotification({ 
        message: `Erro ao criar marcação: ${error.message || 'Erro desconhecido'}`, 
        type: 'error' 
      });
      setTimeout(() => setInstantMarcacaoNotification(null), 4000);
      setSavingInstantMarcacao(false);
    }
  };

  // Create service segment JSX for a specific instance
  const createServiceSegment = (instance: { service: string; instanceNumber: number; instanceId: string }) => {
    const serviceType = instance.service;
    const instanceId = instance.instanceId;
    const instanceNumber = instance.instanceNumber;
    
    const serviceNames: Record<string, string> = {
      'bairro-solar': 'Bairro Solar',
      'pap': 'Pay AS Produced (PAP)',
      'transacional': 'Transacional',
      'luz': 'Luz',
      'gas': 'Gás',
      'mobilidade': 'Mobilidade Elétrica',
      'outro': 'Outro'
    };

    const segmentTitle = `${serviceNames[serviceType] || serviceType} - ${instanceNumber}`;
    const isExpanded = expandedServiceSegments[instanceId] || false;

    let segmentContent: JSX.Element;

    const moradaFornecimentoField = (
      <div className="form-group full-width">
        <label className="form-label" htmlFor={`moradaFornecimento-${instanceId}`}>
          <span className="required">*</span> Morada de Fornecimento
        </label>
        <input 
          type="text" 
          id={`moradaFornecimento-${instanceId}`} 
          className={`form-input ${fieldErrors[`moradaFornecimento-${instanceId}`] ? 'input-error' : ''}`}
          placeholder="Morada completa de fornecimento" 
          value={proposalData.serviceDetails[instanceId]?.moradaFornecimento || ''}
          onChange={(e) => handleServiceInstanceFieldChange(instanceId, 'moradaFornecimento', e.target.value)}
          required 
        />
        {fieldErrors[`moradaFornecimento-${instanceId}`] && (
          <span className="field-error">{fieldErrors[`moradaFornecimento-${instanceId}`]}</span>
        )}
      </div>
    );

    switch(serviceType) {
      case 'bairro-solar':
        segmentContent = (
          <>
            {moradaFornecimentoField}
            <div className="form-group">
              <label className="form-label" htmlFor={`cpe-${instanceId}`}>
                <span className="required">*</span> CPE
              </label>
              {renderCopyButtons(instanceId, 'cpe', 'CPE')}
              <input 
                type="text" 
                id={`cpe-${instanceId}`} 
                className={`form-input ${fieldErrors[`cpe-${instanceId}`] ? 'input-error' : ''}`}
                placeholder="PT + 16 dígitos + 2 letras (ex: PT1234567890123456AB)" 
                maxLength={20}
                pattern="[Pp][Tt]\d{16}[A-Za-z]{2}"
                value={proposalData.serviceDetails[instanceId]?.cpe || ''}
                onChange={(e) => handleCpeInput(instanceId, e)}
                onPaste={(e) => handleCpePaste(instanceId, e)}
                required 
              />
              {fieldErrors[`cpe-${instanceId}`] && (
                <span className="field-error">{fieldErrors[`cpe-${instanceId}`]}</span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">
                <span className="required">*</span> Potência
              </label>
              <div className="button-group">
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.potencia === 'btn' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`potencia-${instanceId}`, 'btn')}>BTN</button>
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.potencia === 'bte' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`potencia-${instanceId}`, 'bte')}>BTE</button>
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.potencia === 'mt' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`potencia-${instanceId}`, 'mt')}>MT</button>
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.potencia === 'outra' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`potencia-${instanceId}`, 'outra')}>Outra</button>
              </div>
              {proposalData.serviceDetails[instanceId]?.potencia === 'outra' && (
                <>
                  <div style={{ marginTop: '10px' }}>
                    {renderCopyButtons(instanceId, 'potenciaOutra', 'Potência')}
                  </div>
                  <input 
                    type="text" 
                    id={`potenciaOutra-${instanceId}`} 
                    className="form-input" 
                    placeholder="Especifique outra potência" 
                    value={proposalData.serviceDetails[instanceId]?.potenciaOutra || ''}
                    onChange={(e) => handleServiceInstanceFieldChange(instanceId, 'potenciaOutra', e.target.value)}
                    style={{ marginTop: '10px' }} 
                  />
                </>
              )}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor={`fatura-${instanceId}`}>
                <span className="required">*</span> Fatura
              </label>
              <input 
                type="file" 
                id={`fatura-${instanceId}`} 
                className="form-input" 
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" 
                onChange={(e) => handleServiceInstanceFileChange(instanceId, 'fatura', e.target.files?.[0] || null)}
                required 
              />
              {proposalData.serviceDetails[instanceId]?.fatura_name && (
                <span className="file-name" style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#666' }}>
                  {proposalData.serviceDetails[instanceId]?.fatura_name}
                </span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor={`printTelhado-${instanceId}`}>
                <span className="required">*</span> Print do Telhado
              </label>
              <input 
                type="file" 
                id={`printTelhado-${instanceId}`} 
                className="form-input" 
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" 
                onChange={(e) => handleServiceInstanceFileChange(instanceId, 'printTelhado', e.target.files?.[0] || null)}
                required 
              />
              {proposalData.serviceDetails[instanceId]?.printTelhado_name && (
                <span className="file-name" style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#666' }}>
                  {proposalData.serviceDetails[instanceId]?.printTelhado_name}
                </span>
              )}
            </div>
            {proposalData.equipaInterna?.tipoPedido === 'roadtrip' && (
              <div className="form-group">
                <label className="form-label" htmlFor={`dataFecho-${instanceId}`}>
                  <span className="required">*</span> Data prevista para o Fecho
                </label>
                <input 
                  type="date" 
                  id={`dataFecho-${instanceId}`} 
                  className="form-input" 
                  value={proposalData.serviceDetails[instanceId]?.dataFecho || ''}
                  onChange={(e) => handleServiceInstanceFieldChange(instanceId, 'dataFecho', e.target.value)}
                  required 
                />
              </div>
            )}
            <div className="form-group">
              <label className="form-label" htmlFor={`coordenadasGeograficas-${instanceId}`}>
                <span className="required">*</span> Coordenadas Geográficas
              </label>
              {renderCopyButtons(instanceId, 'coordenadasGeograficas', 'Coordenadas Geográficas')}
              <textarea 
                id={`coordenadasGeograficas-${instanceId}`} 
                className={`form-input ${fieldErrors[`coordenadasGeograficas-${instanceId}`] ? 'error' : ''}`}
                placeholder="Coordenadas geográficas" 
                rows={3}
                value={proposalData.serviceDetails[instanceId]?.coordenadasGeograficas || ''}
                onChange={(e) => handleServiceInstanceFieldChange(instanceId, 'coordenadasGeograficas', e.target.value)}
                required
              ></textarea>
              {fieldErrors[`coordenadasGeograficas-${instanceId}`] && (
                <span className="error-message">{fieldErrors[`coordenadasGeograficas-${instanceId}`]}</span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor={`observacoes-${instanceId}`}>
                Observações
              </label>
              {renderCopyButtons(instanceId, 'observacoes', 'Observações')}
              <textarea 
                id={`observacoes-${instanceId}`} 
                className="form-input" 
                placeholder="Observações adicionais" 
                rows={3}
                value={proposalData.serviceDetails[instanceId]?.observacoes || ''}
                onChange={(e) => handleServiceInstanceFieldChange(instanceId, 'observacoes', e.target.value)}
              ></textarea>
            </div>
          </>
        );
        break;
      case 'transacional':
        segmentContent = (
          <>
            {moradaFornecimentoField}
            <div className="form-group">
              <label className="form-label" htmlFor={`cpe-${instanceId}`}>
                <span className="required">*</span> CPE
              </label>
              {renderCopyButtons(instanceId, 'cpe', 'CPE')}
              <input 
                type="text" 
                id={`cpe-${instanceId}`} 
                className={`form-input ${fieldErrors[`cpe-${instanceId}`] ? 'input-error' : ''}`}
                placeholder="PT + 16 dígitos + 2 letras (ex: PT1234567890123456AB)" 
                maxLength={20}
                pattern="[Pp][Tt]\d{16}[A-Za-z]{2}"
                value={proposalData.serviceDetails[instanceId]?.cpe || ''}
                onChange={(e) => handleCpeInput(instanceId, e)}
                onPaste={(e) => handleCpePaste(instanceId, e)}
                required 
              />
              {fieldErrors[`cpe-${instanceId}`] && (
                <span className="field-error">{fieldErrors[`cpe-${instanceId}`]}</span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">
                <span className="required">*</span> Potência
              </label>
              <div className="button-group">
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.potencia === 'btn' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`potencia-${instanceId}`, 'btn')}>BTN</button>
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.potencia === 'bte' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`potencia-${instanceId}`, 'bte')}>BTE</button>
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.potencia === 'mt' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`potencia-${instanceId}`, 'mt')}>MT</button>
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.potencia === 'outra' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`potencia-${instanceId}`, 'outra')}>Outra</button>
              </div>
              {proposalData.serviceDetails[instanceId]?.potencia === 'outra' && (
                <>
                  <div style={{ marginTop: '10px' }}>
                    {renderCopyButtons(instanceId, 'potenciaOutra', 'Potência')}
                  </div>
                  <input 
                    type="text" 
                    id={`potenciaOutra-${instanceId}`} 
                    className="form-input" 
                    placeholder="Especifique outra potência" 
                    value={proposalData.serviceDetails[instanceId]?.potenciaOutra || ''}
                    onChange={(e) => handleServiceInstanceFieldChange(instanceId, 'potenciaOutra', e.target.value)}
                    style={{ marginTop: '10px' }} 
                  />
                </>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">
                Forma de Pagamento <span className="required">*</span>
              </label>
              <div className="button-group">
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.formaPagamento === 'pronto' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`formaPagamento-${instanceId}`, 'pronto')}>Pronto Pagamento</button>
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.formaPagamento === 'prestacoes' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`formaPagamento-${instanceId}`, 'prestacoes')}>Prestações</button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor={`fatura-${instanceId}`}>
                <span className="required">*</span> Fatura
              </label>
              <input 
                type="file" 
                id={`fatura-${instanceId}`} 
                className="form-input" 
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" 
                onChange={(e) => handleServiceInstanceFileChange(instanceId, 'fatura', e.target.files?.[0] || null)}
                required 
              />
              {proposalData.serviceDetails[instanceId]?.fatura_name && (
                <span className="file-name" style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#666' }}>
                  {proposalData.serviceDetails[instanceId]?.fatura_name}
                </span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor={`printTelhado-${instanceId}`}>
                <span className="required">*</span> Print do Telhado
              </label>
              <input 
                type="file" 
                id={`printTelhado-${instanceId}`} 
                className="form-input" 
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" 
                onChange={(e) => handleServiceInstanceFileChange(instanceId, 'printTelhado', e.target.files?.[0] || null)}
                required 
              />
              {proposalData.serviceDetails[instanceId]?.printTelhado_name && (
                <span className="file-name" style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#666' }}>
                  {proposalData.serviceDetails[instanceId]?.printTelhado_name}
                </span>
              )}
            </div>
            {proposalData.equipaInterna?.tipoPedido === 'roadtrip' && (
              <div className="form-group">
                <label className="form-label" htmlFor={`dataFecho-${instanceId}`}>
                  <span className="required">*</span> Data prevista para o Fecho
                </label>
                <input 
                  type="date" 
                  id={`dataFecho-${instanceId}`} 
                  className="form-input" 
                  value={proposalData.serviceDetails[instanceId]?.dataFecho || ''}
                  onChange={(e) => handleServiceInstanceFieldChange(instanceId, 'dataFecho', e.target.value)}
                  required 
                />
              </div>
            )}
            <div className="form-group">
              <label className="form-label" htmlFor={`observacoes-${instanceId}`}>
                Observações
              </label>
              {renderCopyButtons(instanceId, 'observacoes', 'Observações')}
              <textarea 
                id={`observacoes-${instanceId}`} 
                className="form-input" 
                placeholder="Observações adicionais" 
                rows={3}
                value={proposalData.serviceDetails[instanceId]?.observacoes || ''}
                onChange={(e) => handleServiceInstanceFieldChange(instanceId, 'observacoes', e.target.value)}
              ></textarea>
            </div>
          </>
        );
        break;
      case 'pap':
        segmentContent = (
          <>
            {moradaFornecimentoField}
            <div className="form-group">
              <label className="form-label" htmlFor={`cpe-${instanceId}`}>
                <span className="required">*</span> CPE
              </label>
              {renderCopyButtons(instanceId, 'cpe', 'CPE')}
              <input 
                type="text" 
                id={`cpe-${instanceId}`} 
                className={`form-input ${fieldErrors[`cpe-${instanceId}`] ? 'input-error' : ''}`}
                placeholder="PT + 16 dígitos + 2 letras (ex: PT1234567890123456AB)" 
                maxLength={20}
                pattern="[Pp][Tt]\d{16}[A-Za-z]{2}"
                value={proposalData.serviceDetails[instanceId]?.cpe || ''}
                onChange={(e) => handleCpeInput(instanceId, e)}
                onPaste={(e) => handleCpePaste(instanceId, e)}
                required 
              />
              {fieldErrors[`cpe-${instanceId}`] && (
                <span className="field-error">{fieldErrors[`cpe-${instanceId}`]}</span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">
                <span className="required">*</span> Potência
              </label>
              <div className="button-group">
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.potencia === 'btn' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`potencia-${instanceId}`, 'btn')}>BTN</button>
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.potencia === 'bte' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`potencia-${instanceId}`, 'bte')}>BTE</button>
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.potencia === 'mt' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`potencia-${instanceId}`, 'mt')}>MT</button>
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.potencia === 'outra' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`potencia-${instanceId}`, 'outra')}>Outra</button>
              </div>
              {proposalData.serviceDetails[instanceId]?.potencia === 'outra' && (
                <>
                  <div style={{ marginTop: '10px' }}>
                    {renderCopyButtons(instanceId, 'potenciaOutra', 'Potência')}
                  </div>
                  <input 
                    type="text" 
                    id={`potenciaOutra-${instanceId}`} 
                    className="form-input" 
                    placeholder="Especifique outra potência" 
                    value={proposalData.serviceDetails[instanceId]?.potenciaOutra || ''}
                    onChange={(e) => handleServiceInstanceFieldChange(instanceId, 'potenciaOutra', e.target.value)}
                    style={{ marginTop: '10px' }} 
                  />
                </>
              )}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor={`fatura-${instanceId}`}>
                <span className="required">*</span> Fatura
              </label>
              <input 
                type="file" 
                id={`fatura-${instanceId}`} 
                className="form-input" 
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" 
                onChange={(e) => handleServiceInstanceFileChange(instanceId, 'fatura', e.target.files?.[0] || null)}
                required 
              />
              {proposalData.serviceDetails[instanceId]?.fatura_name && (
                <span className="file-name" style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#666' }}>
                  {proposalData.serviceDetails[instanceId]?.fatura_name}
                </span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor={`printTelhado-${instanceId}`}>
                <span className="required">*</span> Print do Telhado
              </label>
              <input 
                type="file" 
                id={`printTelhado-${instanceId}`} 
                className="form-input" 
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" 
                onChange={(e) => handleServiceInstanceFileChange(instanceId, 'printTelhado', e.target.files?.[0] || null)}
                required 
              />
              {proposalData.serviceDetails[instanceId]?.printTelhado_name && (
                <span className="file-name" style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#666' }}>
                  {proposalData.serviceDetails[instanceId]?.printTelhado_name}
                </span>
              )}
            </div>
            {proposalData.equipaInterna?.tipoPedido === 'roadtrip' && (
              <div className="form-group">
                <label className="form-label" htmlFor={`dataFecho-${instanceId}`}>
                  <span className="required">*</span> Data prevista para o Fecho
                </label>
                <input 
                  type="date" 
                  id={`dataFecho-${instanceId}`} 
                  className="form-input" 
                  value={proposalData.serviceDetails[instanceId]?.dataFecho || ''}
                  onChange={(e) => handleServiceInstanceFieldChange(instanceId, 'dataFecho', e.target.value)}
                  required 
                />
              </div>
            )}
            <div className="form-group">
              <label className="form-label" htmlFor={`observacoes-${instanceId}`}>
                Observações
              </label>
              {renderCopyButtons(instanceId, 'observacoes', 'Observações')}
              <textarea 
                id={`observacoes-${instanceId}`} 
                className="form-input" 
                placeholder="Observações adicionais" 
                rows={3}
                value={proposalData.serviceDetails[instanceId]?.observacoes || ''}
                onChange={(e) => handleServiceInstanceFieldChange(instanceId, 'observacoes', e.target.value)}
              ></textarea>
            </div>
          </>
        );
        break;
      case 'luz':
        segmentContent = (
          <>
            {moradaFornecimentoField}
            <div className="form-group">
              <label className="form-label" htmlFor={`cpe-${instanceId}`}>
                <span className="required">*</span> CPE
              </label>
              {renderCopyButtons(instanceId, 'cpe', 'CPE')}
              <input 
                type="text" 
                id={`cpe-${instanceId}`} 
                className={`form-input ${fieldErrors[`cpe-${instanceId}`] ? 'input-error' : ''}`}
                placeholder="PT + 16 dígitos + 2 letras (ex: PT1234567890123456AB)" 
                maxLength={20}
                pattern="[Pp][Tt]\d{16}[A-Za-z]{2}"
                value={proposalData.serviceDetails[instanceId]?.cpe || ''}
                onChange={(e) => handleCpeInput(instanceId, e)}
                onPaste={(e) => handleCpePaste(instanceId, e)}
                required 
              />
              {fieldErrors[`cpe-${instanceId}`] && (
                <span className="field-error">{fieldErrors[`cpe-${instanceId}`]}</span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">
                <span className="required">*</span> Potência
              </label>
              <div className="button-group">
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.potencia === 'btn' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`potencia-${instanceId}`, 'btn')}>BTN</button>
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.potencia === 'bte' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`potencia-${instanceId}`, 'bte')}>BTE</button>
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.potencia === 'mt' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`potencia-${instanceId}`, 'mt')}>MT</button>
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.potencia === 'outra' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`potencia-${instanceId}`, 'outra')}>Outra</button>
              </div>
              {proposalData.serviceDetails[instanceId]?.potencia === 'outra' && (
                <>
                  <div style={{ marginTop: '10px' }}>
                    {renderCopyButtons(instanceId, 'potenciaOutra', 'Potência')}
                  </div>
                  <input 
                    type="text" 
                    id={`potenciaOutra-${instanceId}`} 
                    className="form-input" 
                    placeholder="Especifique outra potência" 
                    value={proposalData.serviceDetails[instanceId]?.potenciaOutra || ''}
                    onChange={(e) => handleServiceInstanceFieldChange(instanceId, 'potenciaOutra', e.target.value)}
                    style={{ marginTop: '10px' }} 
                  />
                </>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">
                <span className="required">*</span> Sistema
              </label>
              <div className="button-group">
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.sistema === 'trifasico' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`sistema-${instanceId}`, 'trifasico')}>Trifásico</button>
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.sistema === 'monofasico' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`sistema-${instanceId}`, 'monofasico')}>Monofásico</button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor={`fatura-${instanceId}`}>
                <span className="required">*</span> Fatura
              </label>
              <input 
                type="file" 
                id={`fatura-${instanceId}`} 
                className="form-input" 
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" 
                onChange={(e) => handleServiceInstanceFileChange(instanceId, 'fatura', e.target.files?.[0] || null)}
                required 
              />
              {proposalData.serviceDetails[instanceId]?.fatura_name && (
                <span className="file-name" style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#666' }}>
                  {proposalData.serviceDetails[instanceId]?.fatura_name}
                </span>
              )}
            </div>
            {proposalData.equipaInterna?.tipoPedido === 'roadtrip' && (
              <div className="form-group">
                <label className="form-label" htmlFor={`dataFecho-${instanceId}`}>
                  <span className="required">*</span> Data prevista para o Fecho
                </label>
                <input 
                  type="date" 
                  id={`dataFecho-${instanceId}`} 
                  className="form-input" 
                  value={proposalData.serviceDetails[instanceId]?.dataFecho || ''}
                  onChange={(e) => handleServiceInstanceFieldChange(instanceId, 'dataFecho', e.target.value)}
                  required 
                />
              </div>
            )}
            <div className="form-group">
              <label className="form-label" htmlFor={`observacoes-${instanceId}`}>
                Observações
              </label>
              {renderCopyButtons(instanceId, 'observacoes', 'Observações')}
              <textarea 
                id={`observacoes-${instanceId}`} 
                className="form-input" 
                placeholder="Observações adicionais" 
                rows={3}
                value={proposalData.serviceDetails[instanceId]?.observacoes || ''}
                onChange={(e) => handleServiceInstanceFieldChange(instanceId, 'observacoes', e.target.value)}
              ></textarea>
            </div>
          </>
        );
        break;
      case 'gas':
        segmentContent = (
          <>
            {moradaFornecimentoField}
            <div className="form-group">
              <label className="form-label" htmlFor={`cui-${instanceId}`}>
                <span className="required">*</span> CUI
              </label>
              {renderCopyButtons(instanceId, 'cui', 'CUI')}
              <input 
                type="text" 
                id={`cui-${instanceId}`} 
                className={`form-input ${fieldErrors[`cui-${instanceId}`] ? 'input-error' : ''}`}
                placeholder="Introduza o CUI" 
                value={proposalData.serviceDetails[instanceId]?.cui || ''}
                onChange={(e) => handleCuiInput(instanceId, e)}
                onPaste={(e) => handleCuiPaste(instanceId, e)}
                required 
              />
              {fieldErrors[`cui-${instanceId}`] && (
                <span className="field-error">{fieldErrors[`cui-${instanceId}`]}</span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">
                <span className="required">*</span> Escalão
              </label>
              <div className="button-group">
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.escalao === '1' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`escalao-${instanceId}`, '1')}>Escalão 1</button>
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.escalao === '2' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`escalao-${instanceId}`, '2')}>Escalão 2</button>
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.escalao === '3' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`escalao-${instanceId}`, '3')}>Escalão 3</button>
                <button type="button" className={`btn-option ${proposalData.serviceDetails[instanceId]?.escalao === '4' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick(`escalao-${instanceId}`, '4')}>Escalão 4</button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor={`fatura-${instanceId}`}>
                <span className="required">*</span> Fatura
              </label>
              <input 
                type="file" 
                id={`fatura-${instanceId}`} 
                className="form-input" 
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" 
                onChange={(e) => handleServiceInstanceFileChange(instanceId, 'fatura', e.target.files?.[0] || null)}
                required 
              />
              {proposalData.serviceDetails[instanceId]?.fatura_name && (
                <span className="file-name" style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#666' }}>
                  {proposalData.serviceDetails[instanceId]?.fatura_name}
                </span>
              )}
            </div>
            {proposalData.equipaInterna?.tipoPedido === 'roadtrip' && (
              <div className="form-group">
                <label className="form-label" htmlFor={`dataFecho-${instanceId}`}>
                  <span className="required">*</span> Data prevista para o Fecho
                </label>
                <input 
                  type="date" 
                  id={`dataFecho-${instanceId}`} 
                  className="form-input" 
                  value={proposalData.serviceDetails[instanceId]?.dataFecho || ''}
                  onChange={(e) => handleServiceInstanceFieldChange(instanceId, 'dataFecho', e.target.value)}
                  required 
                />
              </div>
            )}
            <div className="form-group">
              <label className="form-label" htmlFor={`observacoes-${instanceId}`}>
                Observações
              </label>
              {renderCopyButtons(instanceId, 'observacoes', 'Observações')}
              <textarea 
                id={`observacoes-${instanceId}`} 
                className="form-input" 
                placeholder="Observações adicionais" 
                rows={3}
                value={proposalData.serviceDetails[instanceId]?.observacoes || ''}
                onChange={(e) => handleServiceInstanceFieldChange(instanceId, 'observacoes', e.target.value)}
              ></textarea>
            </div>
          </>
        );
        break;
      case 'mobilidade':
        segmentContent = (
          <>
            {moradaFornecimentoField}
            <div className="form-group">
              <label className="form-label">
                <span className="required">*</span> {MOBILIDADE_FIELD_LABELS.plataformaGestao}
              </label>
              <div className="button-group">
                <button
                  type="button"
                  className={`btn-option ${proposalData.serviceDetails[instanceId]?.plataformaGestao === 'sem-gestao' ? 'selected' : ''} ${fieldErrors[`plataformaGestao-${instanceId}`] ? 'input-error' : ''}`}
                  onClick={() => handleBtnOptionClick(`plataformaGestao-${instanceId}`, 'sem-gestao')}
                >
                  Sem gestão
                </button>
                <button
                  type="button"
                  className={`btn-option ${proposalData.serviceDetails[instanceId]?.plataformaGestao === 'edp-charge' ? 'selected' : ''} ${fieldErrors[`plataformaGestao-${instanceId}`] ? 'input-error' : ''}`}
                  onClick={() => handleBtnOptionClick(`plataformaGestao-${instanceId}`, 'edp-charge')}
                >
                  EDP Charge
                </button>
              </div>
              {fieldErrors[`plataformaGestao-${instanceId}`] && (
                <span className="field-error">{fieldErrors[`plataformaGestao-${instanceId}`]}</span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">
                <span className="required">*</span> {MOBILIDADE_FIELD_LABELS.localInstalacao}
              </label>
              <div className="button-group">
                <button
                  type="button"
                  className={`btn-option ${proposalData.serviceDetails[instanceId]?.localInstalacao === 'interior' ? 'selected' : ''} ${fieldErrors[`localInstalacao-${instanceId}`] ? 'input-error' : ''}`}
                  onClick={() => handleBtnOptionClick(`localInstalacao-${instanceId}`, 'interior')}
                >
                  Interior
                </button>
                <button
                  type="button"
                  className={`btn-option ${proposalData.serviceDetails[instanceId]?.localInstalacao === 'exterior' ? 'selected' : ''} ${fieldErrors[`localInstalacao-${instanceId}`] ? 'input-error' : ''}`}
                  onClick={() => handleBtnOptionClick(`localInstalacao-${instanceId}`, 'exterior')}
                >
                  Exterior
                </button>
              </div>
              {fieldErrors[`localInstalacao-${instanceId}`] && (
                <span className="field-error">{fieldErrors[`localInstalacao-${instanceId}`]}</span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">
                <span className="required">*</span> {MOBILIDADE_FIELD_LABELS.quadroMobilidade}
              </label>
              <div className="button-group">
                <button
                  type="button"
                  className={`btn-option ${proposalData.serviceDetails[instanceId]?.quadroMobilidade === 'sim' ? 'selected' : ''} ${fieldErrors[`quadroMobilidade-${instanceId}`] ? 'input-error' : ''}`}
                  onClick={() => handleBtnOptionClick(`quadroMobilidade-${instanceId}`, 'sim')}
                >
                  Sim
                </button>
                <button
                  type="button"
                  className={`btn-option ${proposalData.serviceDetails[instanceId]?.quadroMobilidade === 'nao' ? 'selected' : ''} ${fieldErrors[`quadroMobilidade-${instanceId}`] ? 'input-error' : ''}`}
                  onClick={() => handleBtnOptionClick(`quadroMobilidade-${instanceId}`, 'nao')}
                >
                  Não
                </button>
              </div>
              {fieldErrors[`quadroMobilidade-${instanceId}`] && (
                <span className="field-error">{fieldErrors[`quadroMobilidade-${instanceId}`]}</span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">
                <span className="required">*</span> {MOBILIDADE_FIELD_LABELS.tipoFixacao}
              </label>
              <div className="button-group">
                <button
                  type="button"
                  className={`btn-option ${proposalData.serviceDetails[instanceId]?.tipoFixacao === 'solo' ? 'selected' : ''} ${fieldErrors[`tipoFixacao-${instanceId}`] ? 'input-error' : ''}`}
                  onClick={() => handleBtnOptionClick(`tipoFixacao-${instanceId}`, 'solo')}
                >
                  Solo
                </button>
                <button
                  type="button"
                  className={`btn-option ${proposalData.serviceDetails[instanceId]?.tipoFixacao === 'parede' ? 'selected' : ''} ${fieldErrors[`tipoFixacao-${instanceId}`] ? 'input-error' : ''}`}
                  onClick={() => handleBtnOptionClick(`tipoFixacao-${instanceId}`, 'parede')}
                >
                  Parede
                </button>
              </div>
              {fieldErrors[`tipoFixacao-${instanceId}`] && (
                <span className="field-error">{fieldErrors[`tipoFixacao-${instanceId}`]}</span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">
                <span className="required">*</span> {MOBILIDADE_FIELD_LABELS.tipoCarregamento}
              </label>
              <div className="button-group" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <button
                  type="button"
                  className={`btn-option ${proposalData.serviceDetails[instanceId]?.tipoCarregamento === 'normal' ? 'selected' : ''} ${fieldErrors[`tipoCarregamento-${instanceId}`] ? 'input-error' : ''}`}
                  onClick={() => handleBtnOptionClick(`tipoCarregamento-${instanceId}`, 'normal')}
                  style={{ textAlign: 'left', whiteSpace: 'normal', height: 'auto', padding: '10px 14px', lineHeight: 1.35 }}
                >
                  Normal: carrega 100 Km entre 4 horas e 40 min [3,7 - 22kVA]
                </button>
                <button
                  type="button"
                  className={`btn-option ${proposalData.serviceDetails[instanceId]?.tipoCarregamento === 'rapido' ? 'selected' : ''} ${fieldErrors[`tipoCarregamento-${instanceId}`] ? 'input-error' : ''}`}
                  onClick={() => handleBtnOptionClick(`tipoCarregamento-${instanceId}`, 'rapido')}
                  style={{ textAlign: 'left', whiteSpace: 'normal', height: 'auto', padding: '10px 14px', lineHeight: 1.35 }}
                >
                  Rápido: carrega 100Km em 20 min (45 kW)
                </button>
              </div>
              {fieldErrors[`tipoCarregamento-${instanceId}`] && (
                <span className="field-error">{fieldErrors[`tipoCarregamento-${instanceId}`]}</span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor={`distanciaQveProximo-${instanceId}`}>
                <span className="required">*</span> {MOBILIDADE_FIELD_LABELS.distanciaQveProximo}
              </label>
              <input
                type="text"
                id={`distanciaQveProximo-${instanceId}`}
                className={`form-input ${fieldErrors[`distanciaQveProximo-${instanceId}`] ? 'input-error' : ''}`}
                placeholder="Ex: 10 m"
                value={proposalData.serviceDetails[instanceId]?.distanciaQveProximo || ''}
                onChange={(e) => handleServiceInstanceFieldChange(instanceId, 'distanciaQveProximo', e.target.value)}
              />
              {fieldErrors[`distanciaQveProximo-${instanceId}`] && (
                <span className="field-error">{fieldErrors[`distanciaQveProximo-${instanceId}`]}</span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor={`distanciaQveDistante-${instanceId}`}>
                <span className="required">*</span> {MOBILIDADE_FIELD_LABELS.distanciaQveDistante}
              </label>
              <input
                type="text"
                id={`distanciaQveDistante-${instanceId}`}
                className={`form-input ${fieldErrors[`distanciaQveDistante-${instanceId}`] ? 'input-error' : ''}`}
                placeholder="Ex: 25 m"
                value={proposalData.serviceDetails[instanceId]?.distanciaQveDistante || ''}
                onChange={(e) => handleServiceInstanceFieldChange(instanceId, 'distanciaQveDistante', e.target.value)}
              />
              {fieldErrors[`distanciaQveDistante-${instanceId}`] && (
                <span className="field-error">{fieldErrors[`distanciaQveDistante-${instanceId}`]}</span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor={`observacoes-${instanceId}`}>
                Observações
              </label>
              {renderCopyButtons(instanceId, 'observacoes', 'Observações')}
              <textarea
                id={`observacoes-${instanceId}`}
                className="form-input"
                placeholder="Observações adicionais (opcional)"
                rows={3}
                value={proposalData.serviceDetails[instanceId]?.observacoes || ''}
                onChange={(e) => handleServiceInstanceFieldChange(instanceId, 'observacoes', e.target.value)}
              ></textarea>
            </div>
          </>
        );
        break;
      case 'outro':
      default:
        segmentContent = (
          <>
            {moradaFornecimentoField}
            <div className="form-group">
              <label className="form-label" htmlFor={`observacoes-${instanceId}`}>
                {serviceType === 'outro' ? (
                  <>
                    <span className="required">*</span> Observações
                  </>
                ) : (
                  'Observações'
                )}
              </label>
            {renderCopyButtons(instanceId, 'observacoes', 'Observações')}
            <textarea 
              id={`observacoes-${instanceId}`} 
              className={`form-input ${fieldErrors[`observacoes-${instanceId}`] ? 'input-error' : ''}`}
              placeholder={serviceType === 'outro' ? 'Descreva o serviço solicitado' : 'Observações sobre este serviço'}
              rows={3}
              value={proposalData.serviceDetails[instanceId]?.observacoes || ''}
              onChange={(e) => handleServiceInstanceFieldChange(instanceId, 'observacoes', e.target.value)}
            ></textarea>
            {fieldErrors[`observacoes-${instanceId}`] && (
              <span className="field-error">{fieldErrors[`observacoes-${instanceId}`]}</span>
            )}
          </div>
          </>
        );
    }

    return (
      <div className={`service-segment ${isExpanded ? 'expanded' : ''}`} key={instanceId} id={`segment-${instanceId}`}>
        <div className="service-segment-header" onClick={() => toggleServiceSegment(instanceId)}>
          <span>{segmentTitle}</span>
          <span className="service-segment-toggle" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
        </div>
        <div className="service-segment-content" style={{ display: isExpanded ? 'block' : 'none' }}>
          {segmentContent}
        </div>
      </div>
    );
  };

  // Validate feedback fields before advancing
  const validateFeedbackFields = (): boolean => {
    // Feedback is optional for Concluído Sem Proposta and Concluído Com Proposta
    const subsectionsToCheck = [
      'feedback-pendencia-pendente',
      'feedback-pendencia-not-ok-cliente',
      'feedback-pendencia-not-ok-callcenter'
    ];

    const marcacoesWithoutFeedbackList: string[] = [];
    const subsectionsWithEmptyFeedback: Record<string, boolean> = {};
    const marcacoesToHighlight: Set<string> = new Set();

    subsectionsToCheck.forEach(subsection => {
      let hasEmptyFeedback = false;
      const marcacoes = Object.values(marcacoesData).filter(m => {
        const substage = m.gestao_substage || 'agendamentos-agendamentos';
        return substage === subsection;
      });

      marcacoes.forEach(marcacao => {
        // For Not Ok CallCenter, check using hasNotOkCallCenterFeedback
        if (subsection === 'feedback-pendencia-not-ok-callcenter') {
          if (!hasNotOkCallCenterFeedback(marcacao)) {
            hasEmptyFeedback = true;
            const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
            marcacoesWithoutFeedbackList.push(`#${marcacaoIdToUse.toUpperCase()}`);
            marcacoesToHighlight.add(marcacaoIdToUse);
          }
        } else {
          // For other subsections, check text feedback
          const feedback = marcacao.feedback;
          const feedbackText = typeof feedback === 'string' ? feedback : '';
          if (!feedbackText.trim()) {
            hasEmptyFeedback = true;
            const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
            marcacoesWithoutFeedbackList.push(`#${marcacaoIdToUse.toUpperCase()}`);
            marcacoesToHighlight.add(marcacaoIdToUse);
          }
        }
      });

      if (hasEmptyFeedback) {
        subsectionsWithEmptyFeedback[subsection] = true;
      }
    });

    if (marcacoesWithoutFeedbackList.length > 0) {
      setMarcacoesWithoutFeedback(marcacoesWithoutFeedbackList);
      setHighlightedSubsections(subsectionsWithEmptyFeedback);
      setHighlightedMarcacoesForFeedback(marcacoesToHighlight);
      focusSubsectionPagesForMissingFeedback(subsectionsWithEmptyFeedback, marcacoesToHighlight);
      setShowFeedbackValidationModal(true);
      
      // Switch to feedback-pendencia section and expand relevant subsections
      setCurrentSection('feedback-pendencia');
      setExpandedSubsections(prev => ({
        ...prev,
        ...subsectionsWithEmptyFeedback
      }));

      // Scroll to feedback section after a short delay
      setTimeout(() => {
        const feedbackSection = document.querySelector('.section-content.active');
        if (feedbackSection) {
          feedbackSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);

      return false;
    }

    return true;
  };

  const advanceMarcacao = async (marcacaoId: string) => {
    // Validate feedback fields before advancing
    if (!validateFeedbackFields()) {
      return;
    }

    const marcacao = marcacoesData[marcacaoId];
    if (!marcacao) return;
    
    const currentSubstage = marcacao.gestao_substage || 'agendamentos-agendamentos';
    let newSubstage = '';
    let targetSection: Section = 'agendamentos';
    let targetSubsection = '';
    
    // Determine next sub-stage and target section based on current sub-stage
    if (currentSubstage === 'agendamentos-agendamentos' || currentSubstage === 'agendamentos-remarcacao') {
      newSubstage = 'reuniao-realizada-reuniao';
      targetSection = 'reuniao-realizada';
      targetSubsection = 'reuniao-realizada-reuniao';
    } else if (currentSubstage === 'reuniao-realizada-reuniao') {
      // Skip "Solicitar Proposta" - go directly to first feedback option
      newSubstage = 'feedback-pendencia-concluido-sem-proposta';
      targetSection = 'feedback-pendencia';
      targetSubsection = 'feedback-pendencia-concluido-sem-proposta';
    } else if (currentSubstage.startsWith('feedback-pendencia-')) {
      newSubstage = AGUARDAR_SUBSTAGE;
      targetSection = 'resultado';
      targetSubsection = AGUARDAR_SUBSTAGE;
    }
    
    if (newSubstage) {
      if (newSubstage === AGUARDAR_SUBSTAGE && !canChangeAguardarSubstage()) {
        showNotification('Apenas Gestor ou superior pode mover marcações para Aguardar.', 'error');
        return;
      }
      const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
      const success = await updateMarcacaoSubstageForMarcacao(marcacaoId, newSubstage);
      if (success) {
        const msg =
          recuperacaoMode && newSubstage.startsWith('resultado-')
            ? 'Marcação avançada para Resultado e removida da Recuperação.'
            : 'Marcação avançada com sucesso!';
        showNotification(msg, 'success');
        
        await fetchMarcacoes();

        if (recuperacaoMode && newSubstage.startsWith('resultado-')) {
          return;
        }

        setCurrentSection(targetSection);
        setExpandedSubsections(prev => ({
          ...prev,
          [targetSubsection]: true
        }));
        
        // Highlight the moved marcação after a short delay to ensure it's rendered
        setTimeout(() => {
          setHighlightedMarcacaoId(marcacaoIdToUse);
          
          // Scroll to the highlighted marcação
          const marcacaoElement = document.getElementById(`marcacao-${marcacaoIdToUse}`);
          if (marcacaoElement) {
            marcacaoElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else {
            // If element not found yet, try again after a longer delay
            setTimeout(() => {
              const retryElement = document.getElementById(`marcacao-${marcacaoIdToUse}`);
              if (retryElement) {
                retryElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 500);
          }
          
          // Remove highlight after 3 seconds
          setTimeout(() => {
            setHighlightedMarcacaoId(null);
          }, 3000);
        }, 800);
      }
    }
  };

  // Handle resultado selection from dropdown
  const handleResultadoChange = async (marcacaoId: string, selectedSubstage: string) => {
    if (!selectedSubstage || selectedSubstage === '') return;
    
    const marcacao = marcacoesData[marcacaoId];
    if (!marcacao) return;

    const currentSubstage = marcacao.gestao_substage || 'agendamentos-agendamentos';
    if (
      (selectedSubstage === AGUARDAR_SUBSTAGE || currentSubstage === AGUARDAR_SUBSTAGE) &&
      !canChangeAguardarSubstage()
    ) {
      showNotification('Apenas Gestor ou superior pode mover marcações para/de Aguardar.', 'error');
      return;
    }
    
    const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
    
    // Map sub-stage to display name for navigation - includes all subsections
    const substageMap: Record<string, { section: Section; subsection: string; label: string }> = {
      // Agendamentos subsections
      'agendamentos-agendamentos': {
        section: 'agendamentos',
        subsection: 'agendamentos-agendamentos',
        label: 'Agendamentos'
      },
      'agendamentos-remarcacao': {
        section: 'agendamentos',
        subsection: 'agendamentos-remarcacao',
        label: 'Remarcação'
      },
      // Reunião Realizada subsections
      'reuniao-realizada-reuniao': {
        section: 'reuniao-realizada',
        subsection: 'reuniao-realizada-reuniao',
        label: 'Reunião Realizada'
      },
      // Feedback/Pendência subsections
      'feedback-pendencia-concluido-sem-proposta': {
        section: 'feedback-pendencia',
        subsection: 'feedback-pendencia-concluido-sem-proposta',
        label: 'Concluído Sem Proposta'
      },
      'feedback-pendencia-concluido-com-proposta': {
        section: 'feedback-pendencia',
        subsection: 'feedback-pendencia-concluido-com-proposta',
        label: 'Concluído Com Proposta'
      },
      'feedback-pendencia-not-ok-cliente': {
        section: 'feedback-pendencia',
        subsection: 'feedback-pendencia-not-ok-cliente',
        label: 'Not Ok Cliente'
      },
      'feedback-pendencia-not-ok-callcenter': {
        section: 'feedback-pendencia',
        subsection: 'feedback-pendencia-not-ok-callcenter',
        label: 'Not Ok CallCenter'
      },
      'feedback-pendencia-pendente': {
        section: 'feedback-pendencia',
        subsection: 'feedback-pendencia-pendente',
        label: 'Pendente'
      },
      // Resultado subsections
      'resultado-fecho-ok': {
        section: 'resultado',
        subsection: 'resultado-fecho-ok',
        label: 'Fecho Ok'
      },
      'resultado-fecho-not-ok': {
        section: 'resultado',
        subsection: 'resultado-fecho-not-ok',
        label: 'Fecho Not Ok'
      },
      'resultado-aguardar': {
        section: 'resultado',
        subsection: 'resultado-aguardar',
        label: 'Aguardar'
      },
      'resultado-cancelado': {
        section: 'resultado',
        subsection: 'resultado-cancelado',
        label: 'Cancelado'
      }
    };
    
    const target = substageMap[selectedSubstage];
    if (!target) return;
    
    const success = await updateMarcacaoSubstageForMarcacao(marcacaoId, selectedSubstage);
    if (success) {
      const leavesRecuperacao =
        recuperacaoMode && selectedSubstage.startsWith('resultado-');
      showNotification(
        leavesRecuperacao
          ? 'Marcação movida para Resultado e removida da Recuperação.'
          : `Marcação movida para ${target.label}`,
        'success'
      );

      await fetchMarcacoes();

      if (leavesRecuperacao) {
        return;
      }

      setCurrentSection(target.section);
      setExpandedSubsections(prev => ({
        ...prev,
        [target.subsection]: true
      }));

      setTimeout(() => {
        setHighlightedMarcacaoId(marcacaoIdToUse);
        
        const marcacaoElement = document.getElementById(`marcacao-${marcacaoIdToUse}`);
        if (marcacaoElement) {
          marcacaoElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          setTimeout(() => {
            const retryElement = document.getElementById(`marcacao-${marcacaoIdToUse}`);
            if (retryElement) {
              retryElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 500);
        }
        
        setTimeout(() => {
          setHighlightedMarcacaoId(null);
        }, 3000);
      }, 800);
    } else {
      showNotification('Erro ao mover marcação', 'error');
    }
  };

  const handleResultadoFinalChange = async (marcacaoId: string, selectedSubstage: string) => {
    // Use the same handler as handleResultadoChange - it supports all subsections
    return handleResultadoChange(marcacaoId, selectedSubstage);
  };
  
  // Legacy function - now just calls handleResultadoChange
  const _handleResultadoFinalChangeLegacy = async (marcacaoId: string, selectedSubstage: string) => {
    if (!selectedSubstage || selectedSubstage === '') return;
    
    const marcacao = marcacoesData[marcacaoId];
    if (!marcacao) return;
    
    const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
    
    // Map sub-stage to display name for navigation - includes all subsections
    const substageMap: Record<string, { section: Section; subsection: string; label: string }> = {
      // Agendamentos subsections
      'agendamentos-agendamentos': {
        section: 'agendamentos',
        subsection: 'agendamentos-agendamentos',
        label: 'Agendamentos'
      },
      'agendamentos-remarcacao': {
        section: 'agendamentos',
        subsection: 'agendamentos-remarcacao',
        label: 'Remarcação'
      },
      // Reunião Realizada subsections
      'reuniao-realizada-reuniao': {
        section: 'reuniao-realizada',
        subsection: 'reuniao-realizada-reuniao',
        label: 'Reunião Realizada'
      },
      // Feedback/Pendência subsections
      'feedback-pendencia-concluido-sem-proposta': {
        section: 'feedback-pendencia',
        subsection: 'feedback-pendencia-concluido-sem-proposta',
        label: 'Concluído Sem Proposta'
      },
      'feedback-pendencia-concluido-com-proposta': {
        section: 'feedback-pendencia',
        subsection: 'feedback-pendencia-concluido-com-proposta',
        label: 'Concluído Com Proposta'
      },
      'feedback-pendencia-not-ok-cliente': {
        section: 'feedback-pendencia',
        subsection: 'feedback-pendencia-not-ok-cliente',
        label: 'Not Ok Cliente'
      },
      'feedback-pendencia-not-ok-callcenter': {
        section: 'feedback-pendencia',
        subsection: 'feedback-pendencia-not-ok-callcenter',
        label: 'Not Ok CallCenter'
      },
      'feedback-pendencia-pendente': {
        section: 'feedback-pendencia',
        subsection: 'feedback-pendencia-pendente',
        label: 'Pendente'
      },
      // Resultado subsections
      'resultado-fecho-ok': {
        section: 'resultado',
        subsection: 'resultado-fecho-ok',
        label: 'Fecho Ok'
      },
      'resultado-fecho-not-ok': {
        section: 'resultado',
        subsection: 'resultado-fecho-not-ok',
        label: 'Fecho Not Ok'
      },
      'resultado-aguardar': {
        section: 'resultado',
        subsection: 'resultado-aguardar',
        label: 'Aguardar'
      },
      'resultado-cancelado': {
        section: 'resultado',
        subsection: 'resultado-cancelado',
        label: 'Cancelado'
      }
    };
    
    const target = substageMap[selectedSubstage];
    if (!target) return;
    
    const success = await updateMarcacaoSubstageForMarcacao(marcacaoId, selectedSubstage);
    if (success) {
      showNotification(`Marcação movida para ${target.label}`, 'success');
      
      // Navigate to target section and expand subsection
      setCurrentSection(target.section);
      setExpandedSubsections(prev => ({
        ...prev,
        [target.subsection]: true
      }));
      
      // Refresh data after update
      await fetchMarcacoes();
      
      // Highlight the moved marcação after a short delay to ensure it's rendered
      setTimeout(() => {
        setHighlightedMarcacaoId(marcacaoIdToUse);
        
        // Scroll to the highlighted marcação
        const marcacaoElement = document.getElementById(`marcacao-${marcacaoIdToUse}`);
        if (marcacaoElement) {
          marcacaoElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          // If element not found yet, try again after a longer delay
          setTimeout(() => {
            const retryElement = document.getElementById(`marcacao-${marcacaoIdToUse}`);
            if (retryElement) {
              retryElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 500);
        }
        
        // Remove highlight after 3 seconds
        setTimeout(() => {
          setHighlightedMarcacaoId(null);
        }, 3000);
      }, 800);
    } else {
      showNotification('Erro ao mover marcação', 'error');
    }
  };

  const openRevertConfirm = (marcacaoId: string, target: 'agendamentos' | 'reuniao-realizada' = 'agendamentos') => {
    setMarcacaoToRevert(marcacaoId);
    setRevertTarget(target);
    setShowRevertConfirm(true);
  };

  const closeRevertConfirm = () => {
    setShowRevertConfirm(false);
    setMarcacaoToRevert(null);
  };

  const toggleChangeComercialSelectMode = () => {
    setChangeComercialSelectMode(prev => !prev);
    if (changeComercialSelectMode) setSelectedMarcacoesForChange(new Set());
  };

  const toggleMarcacaoForChange = (marcacaoId: string) => {
    setSelectedMarcacoesForChange(prev => {
      const next = new Set(prev);
      if (next.has(marcacaoId)) next.delete(marcacaoId);
      else next.add(marcacaoId);
      return next;
    });
  };

  const openChangeComercialModal = (marcacaoId?: string | number) => {
    if (marcacaoId !== undefined) {
      setSelectedMarcacoesForChange(new Set([String(marcacaoId)]));
    }
    if (selectedMarcacoesForChange.size === 0 && marcacaoId === undefined) {
      showNotification('Selecione pelo menos uma marcação.', 'error');
      return;
    }
    const ids = marcacaoId !== undefined ? [String(marcacaoId)] : Array.from(selectedMarcacoesForChange);
    const firstId = ids[0];
    const marcacao = marcacoesData[firstId];
    const currentUserIds = marcacao?.form_data?.marcacaoUsers || [];
    const currentFirst = Array.isArray(currentUserIds) ? String(currentUserIds[0] || '') : '';
    setChangeComercialSelectedUserId(currentFirst);
    setShowChangeComercialModal(true);
  };

  const closeChangeComercialModal = () => {
    setShowChangeComercialModal(false);
    setChangeComercialSelectedUserId('');
  };

  const saveChangeComercial = async () => {
    if (selectedMarcacoesForChange.size === 0 || !changeComercialSelectedUserId) {
      showNotification('Selecione um utilizador comercial.', 'error');
      return;
    }
    setSavingChangeComercial(true);
    const ids = Array.from(selectedMarcacoesForChange);
    let successCount = 0;
    try {
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
      for (const marcacaoId of ids) {
        const marcacao = marcacoesData[marcacaoId];
        const marcacaoIdToUse = marcacao?.marcacao_id || marcacaoId;
        const url = `${backendUrl}/api/callcenter/calendar/marcacoes/${marcacaoIdToUse}`;
        const res = await fetch(url, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({
            marcacaoUsers: [changeComercialSelectedUserId],
            form_data: { marcacaoUsers: [changeComercialSelectedUserId] }
          })
        });
        if (res.ok) {
          const json = await res.json();
          if (json.success) {
            successCount++;
            setMarcacoesData(prev => {
              const m = prev[marcacaoId];
              if (!m) return prev;
              const fd = { ...(m.form_data || {}), marcacaoUsers: [changeComercialSelectedUserId] };
              return { ...prev, [marcacaoId]: { ...m, form_data: fd } };
            });
          }
        }
      }
      if (successCount > 0) {
        showNotification(`${successCount} marcação(ões) alterada(s) com sucesso!`, 'success');
        setSelectedMarcacoesForChange(new Set());
        setChangeComercialSelectMode(false);
        refreshCalendarData?.();
        closeChangeComercialModal();
      } else {
        showNotification('Erro ao alterar comercial.', 'error');
      }
    } catch (e: any) {
      showNotification(e?.message || 'Erro ao alterar comercial.', 'error');
    } finally {
      setSavingChangeComercial(false);
    }
  };

  const revertMarcacao = async () => {
    if (!marcacaoToRevert) return;
    
    const marcacao = marcacoesData[marcacaoToRevert];
    if (!marcacao) {
      closeRevertConfirm();
      return;
    }
    
    const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
    
    // Use revertTarget to determine where to revert
    let newSubstage = '';
    let targetSection: Section = 'agendamentos';
    let targetSubsection = '';
    
    if (revertTarget === 'reuniao-realizada') {
      newSubstage = 'reuniao-realizada-reuniao';
      targetSection = 'reuniao-realizada';
      targetSubsection = 'reuniao-realizada-reuniao';
    } else {
      newSubstage = 'agendamentos-agendamentos';
      targetSection = 'agendamentos';
      targetSubsection = 'agendamentos-agendamentos';
    }
    
    const success = await updateMarcacaoSubstageForMarcacao(marcacaoToRevert, newSubstage);
    if (success) {
      closeRevertConfirm();
      
      // Navigate to target section and expand subsection
      setCurrentSection(targetSection);
      setExpandedSubsections(prev => ({
        ...prev,
        [targetSubsection]: true
      }));
      
      // Refresh data after update
      await fetchMarcacoes();
      
      // Highlight the moved marcação after a short delay to ensure it's rendered
      setTimeout(() => {
        setHighlightedMarcacaoId(marcacaoIdToUse);
        
        // Scroll to the highlighted marcação
        const marcacaoElement = document.getElementById(`marcacao-${marcacaoIdToUse}`);
        if (marcacaoElement) {
          marcacaoElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          // If element not found yet, try again after a longer delay
          setTimeout(() => {
            const retryElement = document.getElementById(`marcacao-${marcacaoIdToUse}`);
            if (retryElement) {
              retryElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 500);
        }
        
        // Remove highlight after 3 seconds
        setTimeout(() => {
          setHighlightedMarcacaoId(null);
        }, 3000);
      }, 800);
      
      const successMessage = revertTarget === 'reuniao-realizada' 
        ? 'Marcação retrocedida para Reunião Realizada com sucesso!' 
        : 'Marcação retrocedida para Agendamentos com sucesso!';
      showNotification(successMessage, 'success');
    } else {
      closeRevertConfirm();
      showNotification('Erro ao retroceder marcação', 'error');
    }
  };

  const revertFromResultado = async (marcacaoId: string) => {
    try {
      const marcacao = marcacoesData[marcacaoId];
      if (!marcacao) {
        showNotification('Marcação não encontrada', 'error');
        return;
      }

      const currentSubstage = marcacao.gestao_substage || 'agendamentos-agendamentos';
      if (currentSubstage === AGUARDAR_SUBSTAGE && !canChangeAguardarSubstage()) {
        showNotification('Apenas Gestor ou superior pode mover marcações para/de Aguardar.', 'error');
        return;
      }
      
      const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
      
      // Always revert to "Reunião Realizada" regardless of previous sub-stage
      const targetSubstage = 'reuniao-realizada-reuniao';
      const target = {
        section: 'reuniao-realizada' as Section,
        subsection: 'reuniao-realizada-reuniao',
        label: 'Reunião Realizada'
      };
      
      // Revert to Reunião Realizada
      const success = await updateMarcacaoSubstageForMarcacao(marcacaoId, targetSubstage);
      if (success) {
        showNotification(`Marcação retrocedida para ${target.label} com sucesso!`, 'success');
        
        // Navigate to target section and expand subsection
        setCurrentSection(target.section);
        setExpandedSubsections(prev => ({
          ...prev,
          [target.subsection]: true
        }));
        
        // Refresh data after update
        await fetchMarcacoes();
        
        // Highlight the moved marcação after a short delay to ensure it's rendered
        setTimeout(() => {
          setHighlightedMarcacaoId(marcacaoIdToUse);
          
          // Scroll to the highlighted marcação
          const marcacaoElement = document.getElementById(`marcacao-${marcacaoIdToUse}`);
          if (marcacaoElement) {
            marcacaoElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else {
            // If element not found yet, try again after a longer delay
            setTimeout(() => {
              const retryElement = document.getElementById(`marcacao-${marcacaoIdToUse}`);
              if (retryElement) {
                retryElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 500);
          }
          
          // Remove highlight after 3 seconds
          setTimeout(() => {
            setHighlightedMarcacaoId(null);
          }, 3000);
        }, 800);
      } else {
        showNotification('Erro ao retroceder marcação', 'error');
      }
    } catch (error: any) {
      console.error('Error reverting from Resultado:', error);
      showNotification(error.response?.data?.message || error.message || 'Erro ao retroceder marcação', 'error');
    }
  };


  const cancelMarcacao = async (marcacaoId: string) => {
    const marcacao = marcacoesData[marcacaoId];
    if (!marcacao) return;
    
    if (window.confirm('Tem a certeza que pretende cancelar esta marcação?')) {
      const success = await updateMarcacaoSubstageForMarcacao(marcacaoId, 'resultado-cancelado');
      if (success) {
        fetchMarcacoes();
      }
    }
  };


  // Handle reschedule
  const handleReschedule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentMarcacaoId) return;

    const marcacao = marcacoesData[currentMarcacaoId];
    if (!marcacao) {
      showNotification('Marcação não encontrada', 'error');
      return;
    }

    // Get form values
    const dateInput = document.getElementById('rescheduleDate') as HTMLInputElement;
    const horaInicioInput = document.getElementById('rescheduleHoraInicio') as HTMLInputElement;
    const horaFimInput = document.getElementById('rescheduleHoraFim') as HTMLInputElement;
    const reasonInput = document.getElementById('rescheduleReason') as HTMLTextAreaElement;

    if (!dateInput || !horaInicioInput || !horaFimInput) {
      showNotification('Por favor, preencha todos os campos obrigatórios', 'error');
      return;
    }

    const newDate = dateInput.value;
    const newHoraInicio = horaInicioInput.value;
    const newHoraFim = horaFimInput.value;
    const reason = reasonInput?.value || '';

    if (!newDate || !newHoraInicio || !newHoraFim) {
      showNotification('Por favor, preencha data, hora inicial e hora final', 'error');
      return;
    }

    // Validate that end time is after start time
    if (newHoraFim <= newHoraInicio) {
      showNotification('A hora final deve ser posterior à hora inicial', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
      const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      const url = `${backendUrl}/api/callcenter/calendar/marcacoes/${marcacaoIdToUse}`;

      // Format times for backend (HH:MM format)
      const [startHours, startMinutes] = newHoraInicio.split(':');
      const horaInicioFormatted = `${startHours}:${startMinutes}`;
      
      const [endHours, endMinutes] = newHoraFim.split(':');
      const horaFimFormatted = `${endHours}:${endMinutes}`;

      // Get old date and time for reschedule reason
      const oldDate = marcacao.date || marcacao.data || '';
      const oldHoraInicio = marcacao.hora_inicio || (marcacao.hora ? marcacao.hora.split(' - ')[0] : '');
      const oldHoraFim = marcacao.hora_fim || (marcacao.hora ? marcacao.hora.split(' - ')[1] : '');

      // Build payload - backend expects marcacaoData, not date
      const payload: any = {
        marcacaoData: newDate,
        horaInicio: horaInicioFormatted,
        horaFim: horaFimFormatted,
      };

      // Always save old date/time for reschedule tracking
      payload.oldDate = oldDate;
      payload.oldHoraInicio = oldHoraInicio;
      payload.oldHoraFim = oldHoraFim;
      
      // Add reschedule reason if provided
      if (reason && reason.trim()) {
        payload.rescheduleReason = reason.trim();
      }

      // Update marcação via backend
      const res = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const error = await res.json();
        showNotification(`Erro ao remarcar: ${error.message || 'Erro desconhecido'}`, 'error');
        return;
      }

      // Update sub-stage to remarcacao
      const substageSuccess = await updateMarcacaoSubstageForMarcacao(currentMarcacaoId, 'agendamentos-remarcacao');
      
      if (substageSuccess) {
        showNotification('Marcação remarcada com sucesso!', 'success');
        closeRescheduleModal();
        
        // Navigate to Agendamentos section
        setCurrentSection('agendamentos');
        
        // Keep Agendamentos subsection open and open Remarcações subsection
        setExpandedSubsections(prev => ({
          ...prev,
          'agendamentos-agendamentos': true,
          'agendamentos-remarcacao': true
        }));
        
        // Refresh data after update
        await fetchMarcacoes();
        
        // Highlight the moved marcação after a short delay to ensure it's rendered
        setTimeout(() => {
          setHighlightedMarcacaoId(marcacaoIdToUse);
          
          // Scroll to the highlighted marcação
          const marcacaoElement = document.getElementById(`marcacao-${marcacaoIdToUse}`);
          if (marcacaoElement) {
            marcacaoElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else {
            // If element not found yet, try again after a longer delay
            setTimeout(() => {
              const retryElement = document.getElementById(`marcacao-${marcacaoIdToUse}`);
              if (retryElement) {
                retryElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 500);
          }
          
          // Remove highlight after 3 seconds
          setTimeout(() => {
            setHighlightedMarcacaoId(null);
          }, 3000);
        }, 800);
      }
    } catch (error) {
      console.error('Error rescheduling:', error);
      showNotification('Erro ao remarcar marcação', 'error');
    }
  };

  const handleFeedbackChange = (marcacaoId: string, feedback: string) => {
    // Update local state immediately for visual feedback
    setMarcacoesData(prev => ({
      ...prev,
      [marcacaoId]: { ...prev[marcacaoId], feedback }
    }));
  };

  // Check if Not Ok CallCenter feedback exists for a marcação
  const hasNotOkCallCenterFeedback = (marcacao: any): boolean => {
    const feedback = marcacao.feedback;
    if (!feedback) return false;
    
    // Check if feedback is an object with callCenterFeedback property
    if (typeof feedback === 'object' && feedback.callCenterFeedback) {
      const callCenterFeedback = feedback.callCenterFeedback;
      // Check if at least one option is selected and has text
      const options = ['data', 'horario', 'contacto', 'linkGoogleMaps', 'informacoesCallCenter', 'outro'];
      return options.some(option => {
        const optionValue = callCenterFeedback[option];
        // Check if it's a string with text, or an object with text property
        if (typeof optionValue === 'string' && optionValue.trim() !== '') {
          return true;
        }
        if (typeof optionValue === 'object' && optionValue.text && optionValue.text.trim() !== '') {
          return true;
        }
        return false;
      });
    }
    
    // Legacy: check if feedback is a string and not empty
    if (typeof feedback === 'string' && feedback.trim() !== '') {
      return true;
    }
    
    return false;
  };

  // Open Not Ok CallCenter feedback modal
  const openNotOkCallCenterFeedbackModal = (marcacaoId: string) => {
    const marcacao = marcacoesData[marcacaoId];
    if (!marcacao) return;
    
    setNotOkCallCenterFeedbackMarcacaoId(marcacaoId);
    
    // Load existing feedback if it exists
    const feedback = marcacao.feedback;
    if (feedback && typeof feedback === 'object' && 'callCenterFeedback' in feedback && feedback.callCenterFeedback) {
      const callCenterFeedback = (feedback as any).callCenterFeedback;
      
      // Helper function to extract text from old or new format
      const getOptionText = (optionValue: any): string => {
        if (typeof optionValue === 'string') {
          return optionValue || '';
        }
        if (typeof optionValue === 'object' && optionValue.text) {
          return optionValue.text || '';
        }
        return '';
      };
      
      setNotOkCallCenterFeedbackOptions({
        data: {
          selected: !!(callCenterFeedback.data && (typeof callCenterFeedback.data === 'string' ? callCenterFeedback.data.trim() : callCenterFeedback.data.text?.trim())),
          text: getOptionText(callCenterFeedback.data)
        },
        horario: {
          selected: !!(callCenterFeedback.horario && (typeof callCenterFeedback.horario === 'string' ? callCenterFeedback.horario.trim() : callCenterFeedback.horario.text?.trim())),
          text: getOptionText(callCenterFeedback.horario)
        },
        contacto: {
          selected: !!(callCenterFeedback.contacto && (typeof callCenterFeedback.contacto === 'string' ? callCenterFeedback.contacto.trim() : callCenterFeedback.contacto.text?.trim())),
          text: getOptionText(callCenterFeedback.contacto)
        },
        linkGoogleMaps: {
          selected: !!(callCenterFeedback.linkGoogleMaps && (typeof callCenterFeedback.linkGoogleMaps === 'string' ? callCenterFeedback.linkGoogleMaps.trim() : callCenterFeedback.linkGoogleMaps.text?.trim())),
          text: getOptionText(callCenterFeedback.linkGoogleMaps)
        },
        informacoesCallCenter: {
          selected: !!(callCenterFeedback.informacoesCallCenter && (typeof callCenterFeedback.informacoesCallCenter === 'string' ? callCenterFeedback.informacoesCallCenter.trim() : callCenterFeedback.informacoesCallCenter.text?.trim())),
          text: getOptionText(callCenterFeedback.informacoesCallCenter)
        },
        outro: {
          selected: !!(callCenterFeedback.outro && (typeof callCenterFeedback.outro === 'string' ? callCenterFeedback.outro.trim() : callCenterFeedback.outro.text?.trim())),
          text: getOptionText(callCenterFeedback.outro)
        }
      });
    } else {
      // Reset to defaults
      setNotOkCallCenterFeedbackOptions({
        data: { selected: false, text: '' },
        horario: { selected: false, text: '' },
        contacto: { selected: false, text: '' },
        linkGoogleMaps: { selected: false, text: '' },
        informacoesCallCenter: { selected: false, text: '' },
        outro: { selected: false, text: '' }
      });
    }
    
    setShowNotOkCallCenterFeedbackModal(true);
  };

  // Close Not Ok CallCenter feedback modal
  const closeNotOkCallCenterFeedbackModal = () => {
    setShowNotOkCallCenterFeedbackModal(false);
    setNotOkCallCenterFeedbackMarcacaoId(null);
    setNotOkCallCenterFeedbackOptions({
      data: { selected: false, text: '' },
      horario: { selected: false, text: '' },
      contacto: { selected: false, text: '' },
      linkGoogleMaps: { selected: false, text: '' },
      informacoesCallCenter: { selected: false, text: '' },
      outro: { selected: false, text: '' }
    });
  };

  // Save Not Ok CallCenter feedback
  const saveNotOkCallCenterFeedback = async () => {
    if (!notOkCallCenterFeedbackMarcacaoId) return;
    
    const marcacao = marcacoesData[notOkCallCenterFeedbackMarcacaoId];
    if (!marcacao) return;
    
    // Validate that all selected options have text
    const selectedOptions = Object.entries(notOkCallCenterFeedbackOptions)
      .filter(([_, value]) => value.selected)
      .map(([key, _]) => key);
    
    if (selectedOptions.length === 0) {
      showNotification('Por favor, selecione pelo menos uma opção', 'error');
      return;
    }
    
    // Check if all selected options have text
    const missingText = selectedOptions.find(option => {
      const optionData = notOkCallCenterFeedbackOptions[option];
      return !optionData.text || !optionData.text.trim();
    });
    
    if (missingText) {
      const optionLabels: Record<string, string> = {
        data: 'Data',
        horario: 'Horário',
        contacto: 'Contacto',
        linkGoogleMaps: 'Link Google Maps',
        informacoesCallCenter: 'Informações do CallCenter',
        outro: 'Outro'
      };
      showNotification(`Por favor, preencha o campo de texto para "${optionLabels[missingText]}"`, 'error');
      return;
    }
    
    const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
    
    // Prepare feedback data - save as text for each selected option
    const feedbackData = {
      callCenterFeedback: {
        data: notOkCallCenterFeedbackOptions.data.selected ? notOkCallCenterFeedbackOptions.data.text : '',
        horario: notOkCallCenterFeedbackOptions.horario.selected ? notOkCallCenterFeedbackOptions.horario.text : '',
        contacto: notOkCallCenterFeedbackOptions.contacto.selected ? notOkCallCenterFeedbackOptions.contacto.text : '',
        linkGoogleMaps: notOkCallCenterFeedbackOptions.linkGoogleMaps.selected ? notOkCallCenterFeedbackOptions.linkGoogleMaps.text : '',
        informacoesCallCenter: notOkCallCenterFeedbackOptions.informacoesCallCenter.selected ? notOkCallCenterFeedbackOptions.informacoesCallCenter.text : '',
        outro: notOkCallCenterFeedbackOptions.outro.selected ? notOkCallCenterFeedbackOptions.outro.text : ''
      }
    };
    
    try {
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      const url = `${backendUrl}/api/callcenter/calendar/marcacoes/${marcacaoIdToUse}/not-ok-callcenter-feedback`;

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          feedback: feedbackData
        })
      });

      if (!res.ok) {
        const error = await res.json();
        showNotification(`Erro ao salvar feedback: ${error.message || 'Erro desconhecido'}`, 'error');
        return;
      }

      const json = await res.json();
      if (json.success) {
        showNotification('Feedback salvo com sucesso!', 'success');
        
        // Update local state
        setMarcacoesData(prev => ({
          ...prev,
          [notOkCallCenterFeedbackMarcacaoId]: { 
            ...prev[notOkCallCenterFeedbackMarcacaoId], 
            feedback: feedbackData as any
          }
        }));
        
        // Remove from highlighted set if feedback is now filled
        setHighlightedMarcacoesForFeedback(prev => {
          const newSet = new Set(prev);
          newSet.delete(marcacaoIdToUse);
          return newSet;
        });
        
        // Check if subsection still has empty feedback
        const substage = marcacao.gestao_substage || 'agendamentos-agendamentos';
        if (substage === 'feedback-pendencia-not-ok-callcenter') {
          const marcacoesInSubsection = Object.values(marcacoesData).filter(m => {
            const mSubstage = m.gestao_substage || 'agendamentos-agendamentos';
            return mSubstage === substage;
          });
          
          const hasEmptyFeedback = marcacoesInSubsection.some(m => {
            if (m.id === notOkCallCenterFeedbackMarcacaoId) {
              return false; // This one just got feedback
            }
            return !hasNotOkCallCenterFeedback(m);
          });
          
          if (!hasEmptyFeedback) {
            setHighlightedSubsections(prev => {
              const updated = { ...prev };
              delete updated[substage];
              return updated;
            });
          }
        }
        
        closeNotOkCallCenterFeedbackModal();
      } else {
        showNotification(json.message || 'Erro ao salvar feedback.', 'error');
      }
    } catch (error: any) {
      console.error('Error saving Not Ok CallCenter feedback:', error);
      showNotification(error.response?.data?.message || error.message || 'Erro ao salvar feedback.', 'error');
    }
  };

  const saveFeedback = async (marcacaoId: string, feedback: string) => {
    try {
      // Save to backend
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
      const marcacao = marcacoesData[marcacaoId];
      const marcacaoIdToUse = marcacao?.marcacao_id || marcacaoId;
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      const url = `${backendUrl}/api/callcenter/calendar/marcacoes/${marcacaoIdToUse}`;

      const res = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          feedback: feedback
        })
      });

      if (!res.ok) {
        const error = await res.json();
        showNotification(`Erro ao atualizar feedback: ${error.message || 'Erro desconhecido'}`, 'error');
        // Revert local state on error
        setMarcacoesData(prev => ({
          ...prev,
          [marcacaoId]: { ...prev[marcacaoId], feedback: marcacao?.feedback || '' }
        }));
        return;
      }

      const json = await res.json();
      if (json.success) {
        showNotification('Feedback salvo com sucesso!', 'success');
        
        // Remove from highlighted set if feedback is now filled
        if (feedback.trim()) {
          setHighlightedMarcacoesForFeedback(prev => {
            const newSet = new Set(prev);
            newSet.delete(marcacaoIdToUse);
            return newSet;
          });
          
          // Check if subsection still has empty feedback
          const currentMarcacao = marcacoesData[marcacaoId];
          const substage = currentMarcacao?.gestao_substage || 'agendamentos-agendamentos';
          if (['feedback-pendencia-concluido-sem-proposta', 'feedback-pendencia-concluido-com-proposta', 'feedback-pendencia-not-ok-cliente', 'feedback-pendencia-not-ok-callcenter', 'feedback-pendencia-pendente'].includes(substage)) {
            const marcacoesInSubsection = Object.values(marcacoesData).filter(m => {
              const mSubstage = m.gestao_substage || 'agendamentos-agendamentos';
              return mSubstage === substage;
            });
            
            const hasEmptyFeedback = marcacoesInSubsection.some(m => {
              const mFeedback = m.feedback;
              const mFeedbackText = typeof mFeedback === 'string' ? mFeedback : '';
              const mIdToUse = m.marcacao_id || m.id;
              // Check if this is the current marcação (updated) or another one
              if (mIdToUse === marcacaoIdToUse) {
                return false; // This one just got feedback
              }
              return !mFeedbackText.trim();
            });
            
            if (!hasEmptyFeedback) {
              setHighlightedSubsections(prev => {
                const updated = { ...prev };
                delete updated[substage];
                return updated;
              });
            }
          }
        }
      } else {
        showNotification(json.message || 'Erro ao atualizar feedback.', 'error');
      }
    } catch (error: any) {
      console.error('Error updating feedback:', error);
      showNotification(error.response?.data?.message || error.message || 'Erro ao atualizar feedback.', 'error');
      // Revert local state on error
      const marcacao = marcacoesData[marcacaoId];
      setMarcacoesData(prev => ({
        ...prev,
        [marcacaoId]: { ...prev[marcacaoId], feedback: marcacao?.feedback || '' }
      }));
    }
  };

  const showNotification = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setNotification({ message, type });
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      setNotification(null);
    }, 5000);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'scheduled': return 'status-badge status-scheduled';
      case 'in-progress': return 'status-badge status-in-progress';
      case 'completed': return 'status-badge status-completed';
      case 'cancelled': return 'status-badge status-cancelled';
      case 'rescheduled': return 'status-badge status-rescheduled';
      case 'delivered': return 'status-badge status-delivered';
      case 'pending': return 'status-badge status-pending';
      default: return 'status-badge';
    }
  };

  // Estado da Proposta badge – same statuses as Minhas Propostas (filter Estado + section labels)
  const getEstadoPropostaBadge = (estado: string | undefined) => {
    if (!estado || estado === '' || estado === '-') {
      return <span className="estado-proposta-badge estado-proposta-empty">-</span>;
    }
    const e = estado.toString().toLowerCase().trim();
    // Backend values: pendente → Pedido Submetido; available (BO "Disponível" section) → Por Iniciar
    if (e === 'pendente') {
      return <span className="estado-proposta-badge estado-proposta-pendente">Pedido Submetido</span>;
    }
    if (e === 'available') {
      return <span className="estado-proposta-badge estado-proposta-pendente">Por Iniciar</span>;
    }
    if (e === 'em_progresso' || e === 'in_progress') {
      return <span className="estado-proposta-badge estado-proposta-em-processo">Em Processo</span>;
    }
    if (e === 'concluido' || e === 'entregue' || e === 'completed') {
      return <span className="estado-proposta-badge estado-proposta-disponivel">Proposta disponível</span>;
    }
    if (e === 'cancelado' || e === 'cancelled') {
      return <span className="estado-proposta-badge estado-proposta-cancelado">Cancelado</span>;
    }
    // Section-style values (same as Minhas Propostas section labels)
    const sectionLabel = MINHAS_PROPOSTAS_SECTION_LABELS[estado] || MINHAS_PROPOSTAS_SECTION_LABELS[e];
    if (sectionLabel) {
      const sectionClass = e === 'roadtrip' ? 'estado-proposta-roadtrip' : e === 'op_criada' ? 'estado-proposta-op-criada' : e === 'proposta_aprovacao' ? 'estado-proposta-aprovacao' : e === 'exclusividade_pendente' ? 'estado-proposta-exclusividade' : e === 'pendente_gestor_edp' ? 'estado-proposta-pendente-edp' : e === 'pendente_comercial' ? 'estado-proposta-pendente-comercial' : e === 'completed' ? 'estado-proposta-disponivel' : 'estado-proposta-em-processo';
      return <span className={`estado-proposta-badge ${sectionClass}`}>{sectionLabel}</span>;
    }
    return <span className="estado-proposta-badge estado-proposta-empty">-</span>;
  };

  const getProductBadgeClass = (produto: string) => {
    switch (produto.toLowerCase()) {
      case 'bairro solar': return 'status-badge status-bairro-solar';
      case 'pap': case 'pay as produced (pap)': return 'status-badge status-pap';
      case 'gás': case 'gas': return 'status-badge status-gas';
      case 'eletricidade': case 'luz': return 'status-badge status-eletricidade';
      default: return 'status-badge';
    }
  };

  // Helper function to render products (handles single or multiple products)
  const renderProducts = (marcacao: Marcacao) => {
    // Use produtos array if available (multi-product), otherwise fall back to single produto
    const produtos = marcacao.produtos && marcacao.produtos.length > 0 
      ? marcacao.produtos 
      : marcacao.produto 
        ? [marcacao.produto] 
        : [];
    
    if (produtos.length === 0) {
      return <span className="status-badge">-</span>;
    }
    
    if (produtos.length === 1) {
      return <span className={getProductBadgeClass(produtos[0])}>{produtos[0]}</span>;
    }
    
    // Multiple products: render as badges with spacing
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
        {produtos.map((produto, index) => (
          <span key={index} className={getProductBadgeClass(produto)}>
            {produto}
          </span>
        ))}
      </div>
    );
  };

  // Resolve current marcacao: direct key lookup, or find by id/marcacao_id (case-insensitive) for all sections
  const currentMarcacao = currentMarcacaoId
    ? (marcacoesData[currentMarcacaoId] ??
        Object.values(marcacoesData).find(
          (m) =>
            String(m.id || '').toLowerCase() === String(currentMarcacaoId).toLowerCase() ||
            String(m.marcacao_id || '').toLowerCase() === String(currentMarcacaoId).toLowerCase()
        ) ??
        null)
    : null;

  return (
    <div
      className={`management-root-container${
        gestaoModalsOnly || embeddedProposalOnly || embeddedSignatureOnly ? ' gestao-modals-only-host' : ''
      }${embeddedProposalOnly ? ' embedded-proposal-host' : ''}${
        embeddedContractOnly ? ' embedded-contract-host' : ''
      }${embeddedSignatureOnly ? ' embedded-signature-host' : ''}`}
    >
      {/* Notification */}
      {notification && (
        <div 
          className={`notification notification-${notification.type}`}
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 10000,
            padding: '16px 24px',
            borderRadius: '8px',
            backgroundColor: notification.type === 'success' ? '#10b981' : notification.type === 'error' ? '#ef4444' : '#3b82f6',
            color: 'white',
            fontWeight: '600',
            fontSize: '14px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            maxWidth: '400px',
            minWidth: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            animation: 'slideInRight 0.3s ease-out',
            cursor: 'pointer'
          }}
          onClick={() => setNotification(null)}
        >
          <span style={{ flex: 1 }}>{notification.message}</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setNotification(null);
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '0',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.8,
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '0.8';
            }}
          >
            ×
          </button>
        </div>
      )}
      {embeddedProposalOnly && !showProposalModal && (
        <div className="ccc-embedded-proposal-loading">
          <WonderTradeLoader message="A abrir Solicitar Proposta…" />
        </div>
      )}
      {embeddedContractOnly &&
        !showSubmitContractModal &&
        !showSolarContractForm &&
        !showEnergyGasContractForm && (
          <div className="ccc-embedded-proposal-loading">
            <WonderTradeLoader message="A abrir Submeter Contrato…" />
          </div>
        )}
      {gestaoModalsOnly && autoOpenModal && !showMinhasPropostasModal && !showMeusContratosModal && (
        <div className="ccc-embedded-proposal-loading">
          <WonderTradeLoader message="A abrir…" />
        </div>
      )}
      {embeddedSignatureOnly && !showSignatureModal && (
        <div className="ccc-embedded-proposal-loading">
          <WonderTradeLoader message="A abrir Pedir Assinatura Digital…" />
        </div>
      )}
      {/* Assinatura Digital Modal (Comercial) */}
      {showSignatureModal && (
        <div
          className="comercial-signature-modal-overlay"
          onMouseDown={swallowModalBackdropPointer}
        >
          <div className="comercial-signature-modal-content" onMouseDown={(e) => e.stopPropagation()}>
            <div className="comercial-signature-modal-header">
              <div className="comercial-signature-modal-header-row">
                <h2 className="comercial-signature-modal-title">Pedir Assinatura Digital</h2>
                <button type="button" className="comercial-signature-modal-close" onClick={closeSignatureModal} aria-label="Fechar">×</button>
              </div>
              <p className="comercial-signature-modal-subtitle">
                Criar pedido e consultar o estado dos seus pedidos.
              </p>
            </div>

            <div className="comercial-signature-modal-body">
              {signatureNotification && (
                <div
                  className={`comercial-signature-notification ${signatureNotification.type}`}
                  style={{
                    padding: '10px 14px',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    fontWeight: 600,
                    fontSize: '14px',
                    backgroundColor: signatureNotification.type === 'success' ? '#d1fae5' : '#fee2e2',
                    color: signatureNotification.type === 'success' ? '#065f46' : '#991b1b',
                  }}
                >
                  {signatureNotification.message}
                </div>
              )}

              {/* Form: Novo pedido */}
              <section className="comercial-signature-form-section">
                <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '12px', color: '#111' }}>Novo pedido</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', marginBottom: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Nome Empresa (opcional)</label>
                    <input
                      type="text"
                      className="comercial-signature-input"
                      value={signatureForm.company_name}
                      onChange={(e) => setSignatureForm(prev => ({ ...prev, company_name: e.target.value }))}
                      placeholder="Ex.: Empresa Lda."
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>NIF (opcional)</label>
                    <input
                      type="text"
                      className="comercial-signature-input"
                      value={signatureForm.nif}
                      onChange={(e) => setSignatureForm(prev => ({ ...prev, nif: e.target.value }))}
                      placeholder="PT123456789"
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', marginBottom: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>IBAN *</label>
                    <input
                      type="text"
                      className="comercial-signature-input"
                      value={signatureForm.iban}
                      onChange={handleSignatureIbanInput}
                      onPaste={handleSignatureIbanPaste}
                      placeholder="PT50..."
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>BIC / SWIFT *</label>
                    <input
                      type="text"
                      className="comercial-signature-input"
                      value={signatureForm.bic}
                      onChange={(e) => setSignatureForm(prev => ({ ...prev, bic: e.target.value }))}
                      placeholder="Ex.: BIC / SWIFT"
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px 16px', marginBottom: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Nome do Representante (opcional)</label>
                    <input
                      type="text"
                      className="comercial-signature-input"
                      value={signatureForm.representativeName}
                      onChange={(e) => setSignatureForm(prev => ({ ...prev, representativeName: e.target.value }))}
                      placeholder="Ex.: João Silva"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Email do Representante (opcional)</label>
                    <input
                      type="email"
                      className="comercial-signature-input"
                      value={signatureForm.representativeEmail}
                      onChange={(e) => setSignatureForm(prev => ({ ...prev, representativeEmail: e.target.value }))}
                      placeholder="Ex.: joao@empresa.pt"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Contacto do Representante (opcional)</label>
                    <input
                      type="text"
                      className="comercial-signature-input"
                      value={signatureForm.representativeContact}
                      onChange={(e) => setSignatureForm(prev => ({ ...prev, representativeContact: e.target.value }))}
                      placeholder="Ex.: 912 345 678"
                    />
                  </div>
                </div>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>A assinatura deve ser enviada (opcional):</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Data</label>
                    <input
                      type="date"
                      className="comercial-signature-input"
                      value={signatureForm.expectedDate}
                      onChange={(e) => setSignatureForm(prev => ({ ...prev, expectedDate: e.target.value }))}
                      style={{ width: '160px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Hora</label>
                    <input
                      type="time"
                      className="comercial-signature-input"
                      value={signatureForm.expectedTime}
                      onChange={(e) => setSignatureForm(prev => ({ ...prev, expectedTime: e.target.value }))}
                      style={{ width: '120px' }}
                    />
                  </div>
                  <div style={{ alignSelf: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={handleSignatureHoje}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '14px',
                        cursor: 'pointer',
                        color: '#374151',
                      }}
                    >
                      Hoje
                    </button>
                  </div>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Associar pedido (opcional)</label>
                  <p style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px' }}>Pode pesquisar por NIF ou nome da empresa, ou selecionar da lista abaixo</p>
                  <input
                    type="text"
                    className="comercial-signature-input"
                    placeholder="Pesquisar por NIF ou nome da empresa..."
                    value={associateSearchQuery}
                    onChange={(e) => setAssociateSearchQuery(e.target.value)}
                    style={{ maxWidth: '400px', marginBottom: '8px' }}
                  />
                  <div className="comercial-signature-associate-list">
                    <button
                      type="button"
                      className={`comercial-signature-associate-item ${!signatureForm.associatedMarcacaoId ? 'selected' : ''}`}
                      onClick={() => handleAssociateMarcacao('')}
                    >
                      — Nenhum —
                    </button>
                    {signatureAssociateOptions
                      .filter((option) => signatureAssociateOptionMatchesSearch(option, associateSearchQuery))
                      .map((option) => (
                        <SignatureAssociateItem
                          key={option.associateId}
                          option={option}
                          isSelected={signatureForm.associatedMarcacaoId === option.associateId}
                          onSelect={() => handleAssociateMarcacao(option.associateId)}
                        />
                      ))}
                    {signatureAssociateOptions.length === 0 && (
                      <div style={{ padding: '16px', color: '#6b7280', fontSize: '13px' }}>
                        Nenhum pedido disponível para associar.
                      </div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={submittingSignature}
                  onClick={handleSubmitSignatureRequest}
                  style={{
                    padding: '10px 24px',
                    backgroundColor: submittingSignature ? '#9ca3af' : '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: 700,
                    fontSize: '14px',
                    cursor: submittingSignature ? 'not-allowed' : 'pointer',
                  }}
                >
                  {submittingSignature ? 'A enviar...' : 'Submeter pedido'}
                </button>
              </section>

              {/* List: Os meus pedidos */}
              <section className="comercial-signature-list-section" style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: '#111' }}>Os meus pedidos</h3>
                  <div className="comercial-signature-modal-filters" style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      className={`comercial-signature-filter-btn ${signatureFilter === 'pending' ? 'active' : ''}`}
                      onClick={() => handleSignatureFilterChange('pending')}
                    >
                      Por resolver
                    </button>
                    <button
                      type="button"
                      className={`comercial-signature-filter-btn ${signatureFilter === 'all' ? 'active' : ''}`}
                      onClick={() => handleSignatureFilterChange('all')}
                    >
                      Todos
                    </button>
                  </div>
                </div>
                {loadingSignatureRequests ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 0' }}>
                    <WonderTradeLoader fullScreen={false} whiteBackground={false} />
                  </div>
                ) : signatureRequests.length === 0 ? (
                  <div className="comercial-signature-empty">
                    <div className="comercial-signature-empty-icon">✒</div>
                    <p className="comercial-signature-empty-title">Nenhum pedido nesta vista</p>
                    <p className="comercial-signature-empty-text">
                      {signatureFilter === 'pending'
                        ? 'Não tem pedidos de assinatura digital por resolver. Use o formulário acima para criar um.'
                        : 'Ainda não tem pedidos de assinatura digital.'}
                    </p>
                  </div>
                ) : (
                  <div className="comercial-signature-cards-grid">
                    {signatureRequests.map((req: any) => {
                      const status = (req.status || '').toLowerCase();
                      const isPending = status === 'pending';
                      const statusLabel = status === 'completed' ? 'Feito' : status === 'cancelled' ? 'Cancelado' : 'Por resolver';
                      const statusClass = status === 'completed' ? 'completed' : status === 'cancelled' ? 'cancelled' : 'pending';
                      const expectedAt = req.expected_signature_at ? new Date(req.expected_signature_at).toLocaleString('pt-PT') : '–';
                      return (
                        <div key={req.id} className="comercial-signature-card">
                          <div className="comercial-signature-card-header">
                            <div className="comercial-signature-card-company">{req.company_name || '–'}</div>
                            <span className={`comercial-signature-card-status ${statusClass}`}>{statusLabel}</span>
                          </div>
                          <div className="comercial-signature-card-fields">
                            <div className="comercial-signature-card-field">
                              <label>NIF</label>
                              <span>{req.nif || '–'}</span>
                            </div>
                            <div className="comercial-signature-card-field">
                              <label>IBAN</label>
                              <span>{req.iban}</span>
                            </div>
                            <div className="comercial-signature-card-field">
                              <label>Envio previsto</label>
                              <span>{expectedAt}</span>
                            </div>
                          </div>
                          <div className="comercial-signature-card-meta">
                            {req.created_at && <span>{new Date(req.created_at).toLocaleString('pt-PT')}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>
      )}


      {((!embeddedProposalOnly && !gestaoModalsOnly && !embeddedSignatureOnly) || embeddedContractOnly) && (
        <>
      {/* Main Container with Sidebar and Content */}
      <div className="management-container gestao-marcacoes" style={{ display: 'flex', minHeight: '100vh', position: 'relative' }}>
        {/* Drawer Sidebar for Admin and Gerente */}
        {!recuperacaoMode && isAdminOrGerenteComercial() && (
          <Drawer
            title={<Text strong style={{ fontSize: 20 }}>Equipa</Text>}
            placement="left"
            closable={true}
            onClose={() => setDrawerOpen(false)}
            open={drawerOpen}
            width={isMobileOrTablet ? '80vw' : 300}
            style={{
              borderTopRightRadius: 18,
              borderBottomRightRadius: 18,
            }}
            headerStyle={{
              borderBottom: '1px solid #f0f0f0',
              background: '#fff',
              borderTopRightRadius: 18,
            }}
            bodyStyle={{
              padding: '16px',
              background: '#fff',
              maxHeight: '100vh',
              overflowY: 'auto',
            }}
          >
            <div className="search-section" style={{ marginBottom: '16px' }}>
              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder="Pesquisar utilizador..."
                  value={sidebarSearchTerm}
                  onChange={(e) => setSidebarSearchTerm(e.target.value)}
                  className="user-search-input"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d9d9d9' }}
                />
              </div>
            </div>
            
            <div className="user-list">
              {getFilteredSidebarUsers().map((user: any) => (
                <div 
                  key={user.id} 
                  className={`user-item ${selectedSidebarUser === user.id ? 'selected' : ''}`}
                  onClick={() => {
                    // Only update selection — the selectedSidebarUser effect fetches once
                    // with the correct userId (avoids stale double-fetch races).
                    setSelectedSidebarUser(user.id);
                    setDrawerOpen(false);
                  }}
                  style={{ 
                    cursor: 'pointer',
                    padding: '12px',
                    marginBottom: '8px',
                    borderRadius: '8px',
                    border: selectedSidebarUser === user.id ? '2px solid #1890ff' : '1px solid #f0f0f0',
                    backgroundColor: selectedSidebarUser === user.id ? '#e6f7ff' : '#fff',
                    transition: 'all 0.2s'
                  }}
                >
                  <div className="user-info" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div 
                      className="user-color"
                      style={{
                        backgroundColor: user.id % 2 === 0 ? '#ff001e' : '#cccccc',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        flexShrink: 0
                      }}
                    ></div>
                    <span
                      className="user-name"
                      style={{
                        flex: 1,
                        fontWeight: 500,
                      }}
                    >
                      {user.name || user.username || 'Unknown'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Drawer>
        )}

        {/* Main Content Area */}
        <div className="management-content-wrapper" style={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column'
        }}>
          {/* Management Header */}
          <div className="management-header">
            <div className="gestao-marcacoes-header-title-row" style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
              {!recuperacaoMode && isAdminOrGerenteComercial() && (
                <Button
                  type="text"
                  className="gestao-marcacoes-sidebar-trigger"
                  icon={<TeamOutlined style={{ fontSize: 10 }} />}
                  onClick={() => setDrawerOpen(true)}
                  aria-label="Abrir seleção de utilizador da equipa"
                  style={{ 
                    backgroundColor: '#ff4d4f',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '3px',
                    padding: 0,
                    width: 20,
                    height: 20,
                    minWidth: 20,
                    minHeight: 20,
                    marginTop: -3,
                    flexShrink: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                    boxShadow: '0 1px 2px rgba(255, 77, 79, 0.22)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ff7875';
                    e.currentTarget.style.boxShadow = '0 2px 5px rgba(255, 77, 79, 0.32)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#ff4d4f';
                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(255, 77, 79, 0.22)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                />
              )}
              <h1 className="management-title">
                {recuperacaoMode ? 'Recuperação' : 'Gestão das Marcações'}
              </h1>
              {recuperacaoMode && recuperacaoStats && (
                <p style={{ color: '#6b7280', margin: '4px 0 0', fontSize: 13 }}>
                  Marcações Comercial com 15+ dias sem resultado final (inclui comerciais eliminados ou
                  suspensos).{' '}
                  <strong>{recuperacaoStats.total}</strong> em recuperação
                  {recuperacaoStats.with_deleted_or_suspended > 0 && (
                    <>
                      {' '}
                      · <strong>{recuperacaoStats.with_deleted_or_suspended}</strong> com comercial
                      eliminado/suspenso
                    </>
                  )}
                  {(recuperacaoStats.with_hidden_assignee ?? 0) > 0 && (
                    <>
                      {' '}
                      · <strong>{recuperacaoStats.with_hidden_assignee}</strong> com comercial oculto
                    </>
                  )}
                </p>
              )}
              {!recuperacaoMode && isAdminOrGerenteComercial() && (selectedSidebarUser != null || currentUserId) && (() => {
                const viewingUserId = selectedSidebarUser ?? currentUserId;
                const viewingName = viewingUserId != null
                  ? (userMap[viewingUserId] || comercialUsers.find((u: any) => u.id === viewingUserId)?.name || comercialUsers.find((u: any) => u.id === viewingUserId)?.username || currentUserData?.name || '')
                  : '';
                if (!viewingName) return null;
                return (
                  <div className="management-viewing-user">
                    <div
                      className="user-color management-user-dot"
                      style={{
                        backgroundColor: (viewingUserId != null && comercialUsers.find((u: any) => u.id === viewingUserId))
                          ? (Number(viewingUserId) % 2 === 0 ? '#ff001e' : '#cccccc')
                          : '#333',
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        flexShrink: 0
                      }}
                    />
                    <span className="management-user-indicator">{viewingName}</span>
                  </div>
                );
              })()}
            </div>
            
            <div className="management-controls">
          <div className="filter-controls">
            {!recuperacaoMode && (
            <button
              className={`btn ${showRecentMarcacoes ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setShowRecentMarcacoes(!showRecentMarcacoes)}
              style={{
                padding: '10px 20px',
                backgroundColor: showRecentMarcacoes ? '#dc2626' : '#9ca3af',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '700',
                fontSize: '14px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginRight: '8px',
                transition: 'all 0.2s ease',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: showRecentMarcacoes ? '0 2px 4px rgba(220, 38, 38, 0.2)' : '0 1px 2px rgba(0, 0, 0, 0.1)',
                whiteSpace: 'nowrap',
                minWidth: 'auto',
                width: 'auto',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                if (e.currentTarget) {
                  e.currentTarget.style.backgroundColor = showRecentMarcacoes ? '#b91c1c' : '#6b7280';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = showRecentMarcacoes ? '0 4px 8px rgba(220, 38, 38, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (e.currentTarget) {
                  e.currentTarget.style.backgroundColor = showRecentMarcacoes ? '#dc2626' : '#9ca3af';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = showRecentMarcacoes ? '0 2px 4px rgba(220, 38, 38, 0.2)' : '0 1px 2px rgba(0, 0, 0, 0.1)';
                }
              }}
              title={
                showRecentMarcacoes
                  ? 'Mostra apenas marcações dos últimos 7 dias até ao fim da semana corrente (domingo). Clique para ver todas.'
                  : 'Clique para filtrar: últimos 7 dias e até ao fim da semana corrente.'
              }
            >
              {showRecentMarcacoes && (
                <span style={{ 
                  fontSize: '14px', 
                  fontWeight: 'bold',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  flexShrink: 0
                }}>
                  ✓
                </span>
              )}
              <span style={{ whiteSpace: 'nowrap' }}>Marcações Recentes</span>
            </button>
            )}
            
            {!recuperacaoMode && (
            <button
              type="button"
              onClick={openSignatureModal}
              style={{
                padding: '10px 20px',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '700',
                fontSize: '14px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginRight: '8px',
                transition: 'all 0.2s ease',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                if (e.currentTarget) {
                  e.currentTarget.style.backgroundColor = '#b91c1c';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(220, 38, 38, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (e.currentTarget) {
                  e.currentTarget.style.backgroundColor = '#dc2626';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.2)';
                }
              }}
            >
              ✒ Pedir Assinatura Digital
            </button>
            )}

            {recuperacaoMode && (
              <select
                className="filter-select"
                value={recuperacaoAssigneeFilter}
                onChange={(e) => setRecuperacaoAssigneeFilter(e.target.value)}
                title="Filtrar por estado do comercial atribuído"
              >
                <option value="">Todos os comerciais</option>
                <option value="deleted">Comercial eliminado</option>
                <option value="suspended">Comercial suspenso</option>
                <option value="hidden">Comercial oculto (teste)</option>
              </select>
            )}
            
            <input
              type="date"
              className="filter-select"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              title="Filtrar por data (mostra marcações a partir da data selecionada)"
            />
            <div className="filter-controls-search-row">
              <input
                type="text"
                className="search-box"
                placeholder="Pesquisar por Empresa, NIF ou Responsável..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  // Clear active search when user types new search term
                  setActiveSearchTerm(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchTerm && searchTerm.trim()) {
                    runMarcacaoSearchWithNotification(searchTerm);
                  }
                }}
              />
              <button
                className="search-btn"
                onClick={() => {
                  if (recuperacaoMode) {
                    setActiveSearchTerm(searchTerm.trim() || null);
                    return;
                  }
                  if (searchTerm && searchTerm.trim()) {
                    runMarcacaoSearchWithNotification(searchTerm);
                  } else {
                    setActiveSearchTerm(null);
                    setHighlightedMarcacaoId(null);
                  }
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  marginLeft: '8px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (e.currentTarget) e.currentTarget.style.backgroundColor = '#2563eb';
                }}
                onMouseLeave={(e) => {
                  if (e.currentTarget) e.currentTarget.style.backgroundColor = '#3b82f6';
                }}
              >
                Procurar
              </button>
            </div>
          </div>
          
          <div className="action-buttons">
            <button
              className="btn btn-secondary"
              onClick={() => { 
                setDateFilter(''); 
                setSearchTerm(''); 
                setActiveSearchTerm(null);
                if (recuperacaoMode) setRecuperacaoAssigneeFilter('');
              }}
            >
              Limpar Filtros
            </button>
            {!recuperacaoMode && (
            <button
              className="btn btn-primary"
              onClick={() => {
                setShowInstantMarcacaoModal(true);
                setCurrentInstantMarcacaoStep(1);
                setInstantMarcacaoFormData({});
                setInstantMarcacaoValidationErrors({});
                setInstantMarcacaoNotification(null);
                // Auto-fill user based on role
                const initialData: any = {};
                if (isAdminOrGerenteComercial() && selectedSidebarUser) {
                  initialData.marcacaoUsers = [selectedSidebarUser];
                } else if (currentUserId) {
                  initialData.marcacaoUsers = [currentUserId];
                }
                setInstantMarcacaoFormData(initialData);
              }}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                marginRight: '8px'
              }}
            >
              Marcação instantânea
            </button>
            )}
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <button 
                className="btn btn-primary" 
                onClick={async () => {
                  await fetchMarcacoes();
                  setLastUpdateTimestamp(new Date());
                  showNotification('Marcações atualizadas com sucesso!', 'success');
                }}
                style={{
                  background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                  color: 'white',
                  border: 'none'
                }}
              >
                Atualizar
              </button>
              <button 
                className="btn btn-primary" 
                onClick={() => {
                  setShowMinhasPropostasModal(true);
                  setCurrentProposalPage(1);
                  setProposalSearchTerm('');
                  setFilterDay('');
                  setFilterMonth('');
                  setFilterYear('');
                  setFilterServiceType('');
                  setFilterStatus('');
                  fetchProposalRequests();
                }}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: 'white',
                  border: 'none',
                  marginLeft: '8px'
                }}
              >
                Minhas Propostas
              </button>
              <button 
                className="btn btn-primary" 
                onClick={() => {
                  setContractTypeTab('energia_gas'); // Reset to default tab
                  setShowMeusContratosModal(true);
                  setCurrentContractPage(1);
                  setContractSearchTerm('');
                  setContractFilterDay('');
                  setContractFilterMonth('');
                  setContractFilterYear('');
                  setContractFilterServiceType('');
                  fetchContractSubmissions();
                }}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  marginLeft: '8px'
                }}
              >
                Meus Contratos
              </button>
              {lastUpdateTimestamp && (
                <div style={{ 
                  position: 'absolute',
                  top: '100%',
                  right: '0',
                  marginTop: '4px',
                  whiteSpace: 'nowrap',
                  fontSize: '11px', 
                  color: '#6b7280', 
                  fontStyle: 'italic',
                  zIndex: 10
                }}>
                  Última atualização: {lastUpdateTimestamp.toLocaleString('pt-PT', { 
                    day: '2-digit', 
                    month: '2-digit', 
                    year: 'numeric', 
                    hour: '2-digit', 
                    minute: '2-digit',
                    second: '2-digit'
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Management Content */}
      <div className="management-content">
        {isLoadingMarcacoes ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '400px',
            padding: '40px'
          }}>
            <WonderTradeLoader 
              message="A carregar marcações..." 
            />
          </div>
        ) : (
          <>
            {/* Section Tabs */}
            <div className="section-tabs">
          <button 
            className={`section-tab ${currentSection === 'agendamentos' ? 'active' : ''}`}
            onClick={() => switchSection('agendamentos')}
          >
            <span className="section-indicator" style={{ background: 'var(--wondertrade-blue)' }}></span>
            Agendamentos
            <span className="section-count">{getAgendamentosSubsection().length + getRemarcacoesSubsection().length}</span>
          </button>
          <button 
            className={`section-tab ${currentSection === 'reuniao-realizada' ? 'active' : ''}`}
            onClick={() => switchSection('reuniao-realizada')}
          >
            <span className="section-indicator" style={{ background: 'var(--wondertrade-yellow)' }}></span>
            Reunião Realizada
            <span className="section-count">{getMarcacoesBySection('reuniao-realizada').length}</span>
          </button>
          <button 
            className={`section-tab ${currentSection === 'feedback-pendencia' ? 'active' : ''}`}
            onClick={() => switchSection('feedback-pendencia')}
          >
            <span className="section-indicator" style={{ background: 'var(--wondertrade-purple)' }}></span>
            Feedback / Pendência
            <span className="section-count">{getMarcacoesBySection('feedback-pendencia').length}</span>
          </button>
          {!recuperacaoMode && (
          <button 
            className={`section-tab ${currentSection === 'resultado' ? 'active' : ''}`}
            onClick={() => switchSection('resultado')}
          >
            <span className="section-indicator" style={{ background: 'var(--wondertrade-green)' }}></span>
            Resultado
            <span className="section-count">{getMarcacoesBySection('resultado').length}</span>
          </button>
          )}
        </div>

        {/* Content Area */}
        <div className="content-area">
          {/* Agendamentos Section */}
          {currentSection === 'agendamentos' && (
            <div className="section-content active">
              <div className="collapse-controls">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => collapseAllSubsections('agendamentos')}
                >
                  Colapsar Todas
                </button>
                {isAdminOrGerenteComercial() && (
                  <>
                    <button
                      className={`btn ${changeComercialSelectMode ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={toggleChangeComercialSelectMode}
                      style={changeComercialSelectMode ? { backgroundColor: '#f59e0b', borderColor: '#f59e0b' } : {}}
                    >
                      {changeComercialSelectMode ? 'Cancelar Seleção' : 'Alterar Comercial'}
                    </button>
                    {changeComercialSelectMode && selectedMarcacoesForChange.size > 0 && (
                      <button
                        className="btn btn-primary"
                        onClick={() => openChangeComercialModal()}
                        style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }}
                      >
                        Alterar {selectedMarcacoesForChange.size} selecionada(s)
                      </button>
                    )}
                  </>
                )}
              </div>
              
              <div className="subsection-container">
                {/* Agendamentos Subsection */}
                <div className="subsection" data-subsection="agendamentos-agendamentos">
                  <div 
                    className="subsection-header" 
                    onClick={() => toggleSubsection('agendamentos-agendamentos')}
                  >
                    <span className="subsection-icon" style={{ transform: expandedSubsections['agendamentos-agendamentos'] ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                      ▼
                    </span>
                    <span>Agendamentos ({getAgendamentosSubsection().length})</span>
                  </div>
                  {expandedSubsections['agendamentos-agendamentos'] && (
                    <div className="subsection-content active">
                      <div className="data-table">
                        <table>
                          <thead>
                            <tr>
                              {changeComercialSelectMode && <th style={{ width: 40 }}></th>}
                              <th>Data da Marcação</th>
                              <th>Produto</th>
                              <th>Nome da Empresa</th>
                              <th>NIF Empresa</th>
                              <th>Nome do Responsável</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getPaginatedSubsection('agendamentos-agendamentos', getAgendamentosSubsection()).items.map(marcacao => {
                              const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
                              const isHighlighted = highlightedMarcacaoId === marcacaoIdToUse;
                              return (
                              <tr 
                                key={marcacao.id} 
                                id={`marcacao-${marcacaoIdToUse}`}
                                data-id={marcacao.id}
                                className={isHighlighted ? 'highlighted-row' : ''}
                              >
                                {changeComercialSelectMode && (
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={selectedMarcacoesForChange.has(marcacao.id)}
                                      onChange={() => toggleMarcacaoForChange(marcacao.id)}
                                    />
                                  </td>
                                )}
                                <td>{marcacao.data}</td>
                                <td>{renderProducts(marcacao)}</td>
                                <td>{marcacao.empresa || '-'}</td>
                                <td>{getNifFromProposals(String(marcacaoIdToUse)) || '-'}</td>
                                <td>{marcacao.responsavel}</td>
                                <td>
                                  <div className="action-buttons-container">
                                    <button className="action-btn btn-advance" onClick={() => advanceMarcacao(marcacao.id)} title="Avançar">
                                      Avançar
                                    </button>
                                    <button className="action-btn btn-reschedule" onClick={() => openRescheduleModal(marcacao.id)} title="Remarcar">
                                      Remarcar
                                    </button>
                                    <button className="action-btn btn-details" onClick={() => openDetailsModal(marcacao.id)} title="Ver Detalhes">
                                      Detalhes
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {(() => {
                        const { total, currentPage, totalPages } = getPaginatedSubsection('agendamentos-agendamentos', getAgendamentosSubsection());
                        if (total <= MARCADOES_PER_PAGE) return null;
                        return (
                          <div className="subsection-pagination">
                            <button
                              type="button"
                              className="pagination-arrow"
                              onClick={() => setSubsectionPage('agendamentos-agendamentos', currentPage - 1)}
                              disabled={currentPage <= 1}
                            >
                              ← Anterior
                            </button>
                            <span className="pagination-info">Página {currentPage} de {totalPages}</span>
                            <button
                              type="button"
                              className="pagination-arrow"
                              onClick={() => setSubsectionPage('agendamentos-agendamentos', currentPage + 1)}
                              disabled={currentPage >= totalPages}
                            >
                              Seguinte →
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
                
                {/* Remarcação Subsection */}
                <div className="subsection" data-subsection="agendamentos-remarcacao">
                    <div 
                      className="subsection-header" 
                      onClick={() => toggleSubsection('agendamentos-remarcacao')}
                    >
                      <span className="subsection-icon" style={{ transform: expandedSubsections['agendamentos-remarcacao'] ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                        ▼
                      </span>
                      <span>Remarcações ({getRemarcacoesSubsection().length})</span>
                    </div>
                    {expandedSubsections['agendamentos-remarcacao'] && (
                      <div className="subsection-content active">
                        <div className="data-table">
                        <table>
                          <thead>
                            <tr>
                              {changeComercialSelectMode && <th style={{ width: 40 }}></th>}
                              <th>Data da Marcação</th>
                              <th>Produto</th>
                              <th>Nome da Empresa</th>
                              <th>NIF Empresa</th>
                              <th>Nome do Responsável</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getPaginatedSubsection('agendamentos-remarcacao', getRemarcacoesSubsection()).items.map(marcacao => {
                              const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
                              const isHighlighted = highlightedMarcacaoId === marcacaoIdToUse;
                              return (
                              <tr 
                                key={marcacao.id} 
                                id={`marcacao-${marcacaoIdToUse}`}
                                data-id={marcacao.id}
                                className={isHighlighted ? 'highlighted-row' : ''}
                              >
                                {changeComercialSelectMode && (
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={selectedMarcacoesForChange.has(marcacao.id)}
                                      onChange={() => toggleMarcacaoForChange(marcacao.id)}
                                    />
                                  </td>
                                )}
                                  <td>{marcacao.data}</td>
                                  <td>{renderProducts(marcacao)}</td>
                                  <td>{marcacao.empresa || '-'}</td>
                                  <td>{getNifFromProposals(String(marcacaoIdToUse)) || '-'}</td>
                                  <td>{marcacao.responsavel}</td>
                                  <td>
                                    <div className="action-buttons-container">
                                      <button className="action-btn btn-advance" onClick={() => advanceMarcacao(marcacao.id)} title="Avançar">
                                        Avançar
                                      </button>
                                      <button className="action-btn btn-details" onClick={() => openDetailsModal(marcacao.id)} title="Ver Detalhes">
                                        Detalhes
                                      </button>
                                    </div>
                                  </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {(() => {
                        const { total, currentPage, totalPages } = getPaginatedSubsection('agendamentos-remarcacao', getRemarcacoesSubsection());
                        if (total <= MARCADOES_PER_PAGE) return null;
                        return (
                          <div className="subsection-pagination">
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('agendamentos-remarcacao', currentPage - 1)} disabled={currentPage <= 1}>← Anterior</button>
                            <span className="pagination-info">Página {currentPage} de {totalPages}</span>
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('agendamentos-remarcacao', currentPage + 1)} disabled={currentPage >= totalPages}>Seguinte →</button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  </div>
              </div>
            </div>
          )}

          {/* Reunião Realizada Section */}
          {currentSection === 'reuniao-realizada' && (
            <div className="section-content active">
              <div className="collapse-controls">
                <button
                  className="btn btn-secondary"
                  onClick={() => collapseAllSubsections('reuniao-realizada')}
                >
                  Colapsar Todas
                </button>
                {isAdminOrGerenteComercial() && (
                  <>
                    <button
                      className={`btn ${changeComercialSelectMode ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={toggleChangeComercialSelectMode}
                      style={changeComercialSelectMode ? { backgroundColor: '#f59e0b', borderColor: '#f59e0b' } : {}}
                    >
                      {changeComercialSelectMode ? 'Cancelar Seleção' : 'Alterar Comercial'}
                    </button>
                    {changeComercialSelectMode && selectedMarcacoesForChange.size > 0 && (
                      <button
                        className="btn btn-primary"
                        onClick={() => openChangeComercialModal()}
                        style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }}
                      >
                        Alterar {selectedMarcacoesForChange.size} selecionada(s)
                      </button>
                    )}
                  </>
                )}
              </div>

              <div className="subsection-container">
                <div className="subsection" data-subsection="reuniao-realizada-reuniao">
                  <div 
                    className="subsection-header" 
                    onClick={() => toggleSubsection('reuniao-realizada-reuniao')}
                  >
                    <span className="subsection-icon" style={{ transform: expandedSubsections['reuniao-realizada-reuniao'] ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                      ▼
                    </span>
                    <span>Reunião Realizada ({getMarcacoesBySection('reuniao-realizada').length})</span>
                  </div>
                  {expandedSubsections['reuniao-realizada-reuniao'] && (
                    <div className="subsection-content active">
                      <div className="data-table">
                        <table>
                          <thead>
                            <tr>
                              {changeComercialSelectMode && <th style={{ width: 40 }}></th>}
                              <th>Data da Marcação</th>
                              <th>Produto</th>
                              <th>Nome da Empresa</th>
                              <th>NIF Empresa</th>
                              <th>Nome do Responsável</th>
                              <th>Feedback</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getPaginatedSubsection('reuniao-realizada-reuniao', getMarcacoesBySection('reuniao-realizada')).items.map(marcacao => {
                              const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
                              const isHighlighted = highlightedMarcacaoId === marcacaoIdToUse;
                              return (
                              <tr 
                                key={marcacao.id} 
                                id={`marcacao-${marcacaoIdToUse}`}
                                data-id={marcacao.id}
                                className={isHighlighted ? 'highlighted-row' : ''}
                              >
                                {changeComercialSelectMode && (
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={selectedMarcacoesForChange.has(marcacao.id)}
                                      onChange={() => toggleMarcacaoForChange(marcacao.id)}
                                    />
                                  </td>
                                )}
                                <td>{marcacao.data}</td>
                                <td>{renderProducts(marcacao)}</td>
                                <td>{marcacao.empresa || '-'}</td>
                                <td>{getNifFromProposals(String(marcacaoIdToUse)) || '-'}</td>
                                <td>{marcacao.responsavel}</td>
                                <td>
                                  <select
                                    className="resultado-dropdown"
                                    defaultValue=""
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        handleResultadoChange(marcacao.id, e.target.value);
                                        // Reset dropdown after selection
                                        e.target.value = '';
                                      }
                                    }}
                                    style={{
                                      padding: '6px 12px',
                                      borderRadius: '4px',
                                      border: '1px solid #d1d5db',
                                      fontSize: '11px',
                                      minWidth: '180px',
                                      backgroundColor: 'white',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <option value="" disabled></option>
                                    <optgroup label="Feedback / Pendência">
                                      <option value="feedback-pendencia-concluido-sem-proposta">Concluído Sem Proposta</option>
                                      <option value="feedback-pendencia-concluido-com-proposta">Concluído Com Proposta</option>
                                      <option value="feedback-pendencia-not-ok-cliente">Not Ok Cliente</option>
                                      <option value="feedback-pendencia-not-ok-callcenter">Not Ok CallCenter</option>
                                      <option value="feedback-pendencia-pendente">Pendente</option>
                                    </optgroup>
                                  </select>
                                </td>
                                <td>
                                  <div className="action-buttons-container">
                                    <button className="action-btn btn-revert" onClick={() => openRevertConfirm(marcacao.id)} title="Retroceder para Agendamentos">
                                      Retroceder
                                    </button>
                                    <button className="action-btn btn-details" onClick={() => openDetailsModal(marcacao.id)}>
                                      Detalhes
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {(() => {
                        const reuniaoList = getMarcacoesBySection('reuniao-realizada');
                        const { total, currentPage, totalPages } = getPaginatedSubsection('reuniao-realizada-reuniao', reuniaoList);
                        if (total <= MARCADOES_PER_PAGE) return null;
                        return (
                          <div className="subsection-pagination">
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('reuniao-realizada-reuniao', currentPage - 1)} disabled={currentPage <= 1}>← Anterior</button>
                            <span className="pagination-info">Página {currentPage} de {totalPages}</span>
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('reuniao-realizada-reuniao', currentPage + 1)} disabled={currentPage >= totalPages}>Seguinte →</button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Feedback / Pendência Section */}
          {currentSection === 'feedback-pendencia' && (
            <div className="section-content active">
              <div className="collapse-controls">
                <button
                  className="btn btn-secondary"
                  onClick={() => collapseAllSubsections('feedback-pendencia')}
                >
                  Colapsar Todas
                </button>
                {isAdminOrGerenteComercial() && (
                  <>
                    <button
                      className={`btn ${changeComercialSelectMode ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={toggleChangeComercialSelectMode}
                      style={changeComercialSelectMode ? { backgroundColor: '#f59e0b', borderColor: '#f59e0b' } : {}}
                    >
                      {changeComercialSelectMode ? 'Cancelar Seleção' : 'Alterar Comercial'}
                    </button>
                    {changeComercialSelectMode && selectedMarcacoesForChange.size > 0 && (
                      <button
                        className="btn btn-primary"
                        onClick={() => openChangeComercialModal()}
                        style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }}
                      >
                        Alterar {selectedMarcacoesForChange.size} selecionada(s)
                      </button>
                    )}
                  </>
                )}
              </div>

              <div className="subsection-container">
                {/* Concluído Sem Proposta Subsection */}
                <div className="subsection" data-subsection="feedback-pendencia-concluido-sem-proposta">
                  <div
                    className="subsection-header"
                    onClick={() => toggleSubsection('feedback-pendencia-concluido-sem-proposta')}
                  >
                    <span className="subsection-icon" style={{ transform: expandedSubsections['feedback-pendencia-concluido-sem-proposta'] ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                      ▼
                    </span>
                    <span>Concluído Sem Proposta ({getConcluidoSemProposta().length})</span>
                  </div>
                  {expandedSubsections['feedback-pendencia-concluido-sem-proposta'] && (
                    <div className="subsection-content active">
                      <div className="data-table">
                        <table>
                          <thead>
                            <tr>
                              {changeComercialSelectMode && <th style={{ width: 40 }}></th>}
                              <th>Data da Marcação</th>
                              <th>Produto</th>
                              <th>Nome da Empresa</th>
                              <th>NIF Empresa</th>
                              <th>Nome do Responsável</th>
                              <th>Feedback</th>
                              <th>Estado da Proposta</th>
                              <th>Id do Pedido de Proposta</th>
                              <th>Id da Proposta</th>
                              <th>Resultado</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getPaginatedSubsection('feedback-pendencia-concluido-sem-proposta', getConcluidoSemProposta()).items.map(marcacao => {
                              const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
                              const isHighlighted = highlightedMarcacaoId === marcacaoIdToUse;
                              const needsFeedback = false; // Feedback optional in this subsection
                              const hasEmptyFeedback = false; // No highlight for empty feedback
                              
                              // Get Id do Pedido de Proposta - fetch from marcação's form_data
                              const getIdPedidoProposta = () => {
                                const allIds: string[] = [];
                                const formData = marcacao.form_data || {};
                                
                                const propostaIdDirect = formData.propostaId || formData.proposta_id || formData.propostaID;
                                if (propostaIdDirect) {
                                  if (Array.isArray(propostaIdDirect)) {
                                    propostaIdDirect.forEach((id: any) => {
                                      if (id && id.toString().trim() !== '') {
                                        const idStr = id.toString().trim();
                                        if (!allIds.includes(idStr)) {
                                          allIds.push(idStr);
                                        }
                                      }
                                    });
                                  } else if (typeof propostaIdDirect === 'string' && propostaIdDirect.trim() !== '') {
                                    allIds.push(propostaIdDirect.trim());
                                  } else if (typeof propostaIdDirect === 'number') {
                                    allIds.push(propostaIdDirect.toString());
                                  }
                                }
                                
                                const proposalData = formData.proposalData;
                                if (proposalData) {
                                  if (Array.isArray(proposalData)) {
                                    proposalData.forEach((data: any) => {
                                      const propostaId = data?.propostaId || data?.proposta_id || data?.id || data?.proposalId;
                                      if (propostaId && propostaId.toString().trim() !== '') {
                                        const id = propostaId.toString().trim();
                                        if (!allIds.includes(id)) {
                                          allIds.push(id);
                                        }
                                      }
                                    });
                                  } else if (typeof proposalData === 'object') {
                                    const propostaId = proposalData.propostaId || proposalData.proposta_id || proposalData.id || proposalData.proposalId;
                                    if (propostaId) {
                                      const id = propostaId.toString().trim();
                                      if (id && !allIds.includes(id)) {
                                        allIds.push(id);
                                      }
                                    }
                                  }
                                }
                                
                                const proposalsArray = formData.proposals || formData.proposalRequests || formData.propostaIds;
                                if (proposalsArray) {
                                  if (Array.isArray(proposalsArray)) {
                                    proposalsArray.forEach((item: any) => {
                                      const id = typeof item === 'object' ? (item?.propostaId || item?.proposta_id || item?.id || item?.proposalId) : item;
                                      if (id && id.toString().trim() !== '') {
                                        const idStr = id.toString().trim();
                                        if (!allIds.includes(idStr)) {
                                          allIds.push(idStr);
                                        }
                                      }
                                    });
                                  } else {
                                    const id = proposalsArray.toString().trim();
                                    if (id && !allIds.includes(id)) {
                                      allIds.push(id);
                                    }
                                  }
                                }
                                
                                if (allIds.length === 0) {
                                  const proposals = proposalsData[marcacaoIdToUse] || [];
                                  proposals.forEach((proposal: any) => {
                                    const formAnswers = proposal.form_answers || {};
                                    const propostaId = formAnswers.propostaId || formAnswers.proposta_id || formAnswers.propostaID ||
                                                       proposal.id?.toString() || proposal.propostaId?.toString() || proposal.proposta_id?.toString();
                                    if (propostaId && propostaId.toString().trim() !== '') {
                                      const id = propostaId.toString().trim();
                                      if (!allIds.includes(id)) {
                                        allIds.push(id);
                                      }
                                    }
                                  });
                                }
                                
                                if (allIds.length === 0) return '-';
                                return allIds.map(id => `#${id.toUpperCase()}`).join(', ');
                              };
                              
                              // Get Id da Proposta - fetch from database
                              const getIdProposta = () => {
                                const allIds: string[] = [];
                                const proposals = proposalsData[marcacaoIdToUse] || [];
                                proposals.forEach((proposal: any) => {
                                  const formAnswers = proposal.form_answers || {};
                                  const idProposta = formAnswers.idProposta || formAnswers.id_proposta || formAnswers.idPropostaID ||
                                                     formAnswers.proposta_id || formAnswers.proposalId || formAnswers.proposal_id ||
                                                     formAnswers.idPropostaFinal || formAnswers.id_proposta_final ||
                                                     formAnswers.finalPropostaId || formAnswers.final_proposta_id ||
                                                     proposal.id?.toString() || proposal.propostaId?.toString() || proposal.proposta_id?.toString();
                                  if (idProposta && idProposta.toString().trim() !== '') {
                                    const id = idProposta.toString().trim();
                                    if (!allIds.includes(id)) {
                                      allIds.push(id);
                                    }
                                  }
                                });
                                
                                const formData = marcacao.form_data || {};
                                const idPropostaDirect = formData.idProposta || formData.id_proposta || formData.idPropostaID || 
                                                         formData.proposta_id || formData.proposalId || formData.proposal_id ||
                                                         formData.idPropostaFinal || formData.id_proposta_final;
                                if (idPropostaDirect) {
                                  if (Array.isArray(idPropostaDirect)) {
                                    idPropostaDirect.forEach((id: any) => {
                                      if (id && id.toString().trim() !== '') {
                                        const idStr = id.toString().trim();
                                        if (!allIds.includes(idStr)) {
                                          allIds.push(idStr);
                                        }
                                      }
                                    });
                                  } else if (typeof idPropostaDirect === 'string' && idPropostaDirect.trim() !== '') {
                                    allIds.push(idPropostaDirect.trim());
                                  } else if (typeof idPropostaDirect === 'number') {
                                    allIds.push(idPropostaDirect.toString());
                                  }
                                }
                                
                                const proposalData = formData.proposalData || formData.proposal_data || formData.proposals;
                                if (proposalData) {
                                  if (Array.isArray(proposalData)) {
                                    proposalData.forEach((data: any) => {
                                      const idProposta = data?.idProposta || data?.id_proposta || data?.idPropostaID ||
                                                         data?.proposta_id || data?.proposalId || data?.proposal_id ||
                                                         data?.idPropostaFinal || data?.id_proposta_final ||
                                                         data?.finalPropostaId || data?.final_proposta_id;
                                      if (idProposta && idProposta.toString().trim() !== '') {
                                        const id = idProposta.toString().trim();
                                        if (!allIds.includes(id)) {
                                          allIds.push(id);
                                        }
                                      }
                                    });
                                  } else if (typeof proposalData === 'object' && proposalData !== null) {
                                    const idProposta = proposalData.idProposta || proposalData.id_proposta || proposalData.idPropostaID ||
                                                       proposalData.proposta_id || proposalData.proposalId || proposalData.proposal_id ||
                                                       proposalData.idPropostaFinal || proposalData.id_proposta_final ||
                                                       proposalData.finalPropostaId || proposalData.final_proposta_id;
                                    if (idProposta) {
                                      const id = idProposta.toString().trim();
                                      if (id && !allIds.includes(id)) {
                                        allIds.push(id);
                                      }
                                    }
                                  }
                                }
                                
                                if (allIds.length === 0) {
                                  return '-';
                                }
                                return allIds.map(id => `#${id.toUpperCase()}`).join(', ');
                              };
                              
                              return (
                              <tr 
                                key={marcacao.id} 
                                id={`marcacao-${marcacaoIdToUse}`}
                                data-id={marcacao.id}
                                className={`${isHighlighted ? 'highlighted-row' : ''} ${needsFeedback || hasEmptyFeedback ? 'feedback-empty-row' : ''}`}
                                style={needsFeedback || hasEmptyFeedback ? { backgroundColor: '#fef2f2', borderLeft: '4px solid #dc2626' } : {}}
                              >
                                {changeComercialSelectMode && (
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={selectedMarcacoesForChange.has(marcacao.id)}
                                      onChange={() => toggleMarcacaoForChange(marcacao.id)}
                                    />
                                  </td>
                                )}
                                <td>{marcacao.data}</td>
                                <td>{renderProducts(marcacao)}</td>
                                <td>{marcacao.empresa || '-'}</td>
                                <td>{getNifFromProposals(String(marcacaoIdToUse)) || '-'}</td>
                                <td>{marcacao.responsavel}</td>
                                <td>
                                  <textarea
                                    className="form-input"
                                    style={{ 
                                      width: '100%', 
                                      minWidth: '200px', 
                                      maxWidth: '300px',
                                      minHeight: '60px',
                                      resize: 'vertical',
                                      padding: '8px',
                                      fontSize: '13px',
                                      fontFamily: 'inherit'
                                    }}
                                    value={typeof marcacao.feedback === 'string' ? marcacao.feedback : ''}
                                    onChange={(e) => handleFeedbackChange(marcacao.id, e.target.value)}
                                    onBlur={(e) => saveFeedback(marcacao.id, e.target.value)}
                                    placeholder="Digite o feedback..."
                                  />
                                </td>
                                <td>
                                  {getEstadoPropostaBadge(marcacao.estado_proposta)}
                                </td>
                                <td>{getIdPedidoProposta()}</td>
                                <td>{getIdProposta()}</td>
                                <td>
                                  <select
                                    className="resultado-dropdown"
                                    defaultValue=""
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        handleResultadoChange(marcacao.id, e.target.value);
                                        e.target.value = '';
                                      }
                                    }}
                                    style={{
                                      padding: '6px 12px',
                                      borderRadius: '4px',
                                      border: '1px solid #d1d5db',
                                      fontSize: '11px',
                                      minWidth: '180px',
                                      backgroundColor: 'white',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <option value="" disabled></option>
                                    <option value="resultado-fecho-ok">Fecho Ok</option>
                                    <option value="resultado-fecho-not-ok">Fecho Not Ok</option>
                                    {canChangeAguardarSubstage() ? (
                                      <option value="resultado-aguardar">Aguardar</option>
                                    ) : null}
                                    <option value="resultado-cancelado">Cancelado</option>
                                  </select>
                                </td>
                                <td>
                                  <div className="action-buttons-container">
                                    <button className="action-btn btn-revert" onClick={() => openRevertConfirm(marcacao.id, 'reuniao-realizada')} title="Retroceder para Reunião Realizada">
                                      Retroceder
                                    </button>
                                    <button className="action-btn btn-details" onClick={() => openDetailsModal(marcacao.id)}>
                                      Detalhes
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {(() => {
                        const { total, currentPage, totalPages } = getPaginatedSubsection('feedback-pendencia-concluido-sem-proposta', getConcluidoSemProposta());
                        if (total <= MARCADOES_PER_PAGE) return null;
                        return (
                          <div className="subsection-pagination">
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('feedback-pendencia-concluido-sem-proposta', currentPage - 1)} disabled={currentPage <= 1}>← Anterior</button>
                            <span className="pagination-info">Página {currentPage} de {totalPages}</span>
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('feedback-pendencia-concluido-sem-proposta', currentPage + 1)} disabled={currentPage >= totalPages}>Seguinte →</button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Concluído Com Proposta Subsection */}
                <div className="subsection" data-subsection="feedback-pendencia-concluido-com-proposta">
                  <div
                    className="subsection-header"
                    onClick={() => toggleSubsection('feedback-pendencia-concluido-com-proposta')}
                  >
                    <span className="subsection-icon" style={{ transform: expandedSubsections['feedback-pendencia-concluido-com-proposta'] ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                      ▼
                    </span>
                    <span>Concluído Com Proposta ({getConcluidoComProposta().length})</span>
                  </div>
                  {expandedSubsections['feedback-pendencia-concluido-com-proposta'] && (
                    <div className="subsection-content active">
                      <div className="data-table">
                        <table>
                          <thead>
                            <tr>
                              {changeComercialSelectMode && <th style={{ width: 40 }}></th>}
                              <th>Data da Marcação</th>
                              <th>Produto</th>
                              <th>Nome da Empresa</th>
                              <th>NIF Empresa</th>
                              <th>Nome do Responsável</th>
                              <th>Feedback</th>
                              <th>Estado da Proposta</th>
                              <th>Id do Pedido de Proposta</th>
                              <th>Id da Proposta</th>
                              <th>Resultado</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getPaginatedSubsection('feedback-pendencia-concluido-com-proposta', getConcluidoComProposta()).items.map(marcacao => {
                              const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
                              const isHighlighted = highlightedMarcacaoId === marcacaoIdToUse;
                              const needsFeedback = false; // Feedback optional in this subsection
                              const hasEmptyFeedback = false; // No highlight for empty feedback
                              
                              // Get Id do Pedido de Proposta - fetch from marcação's form_data
                              // IMPORTANT: Use the specific marcação from the map, not from shared state
                              const getIdPedidoProposta = () => {
                                const allIds: string[] = [];
                                
                                // Get form_data from THIS specific marcação (from the map closure)
                                // This ensures we're reading the correct marcação's data
                                const formData = marcacao.form_data || {};
                                
                                
                                // First, check if propostaId is directly in form_data as array or single value
                                // This is the main array maintained by the backend
                                // Check both camelCase and snake_case variations
                                const propostaIdDirect = formData.propostaId || formData.proposta_id || formData.propostaID;
                                if (propostaIdDirect) {
                                  if (Array.isArray(propostaIdDirect)) {
                                    propostaIdDirect.forEach((id: any) => {
                                      if (id && id.toString().trim() !== '') {
                                        const idStr = id.toString().trim();
                                        if (!allIds.includes(idStr)) {
                                          allIds.push(idStr);
                                        }
                                      }
                                    });
                                  } else if (typeof propostaIdDirect === 'string' && propostaIdDirect.trim() !== '') {
                                    allIds.push(propostaIdDirect.trim());
                                  } else if (typeof propostaIdDirect === 'number') {
                                    allIds.push(propostaIdDirect.toString());
                                  }
                                }
                                
                                // Check if proposalData exists and has propostaId
                                // proposalData is now an array of proposals
                                const proposalData = formData.proposalData;
                                if (proposalData) {
                                  // If proposalData is an array, extract propostaId from each
                                  if (Array.isArray(proposalData)) {
                                    proposalData.forEach((data: any) => {
                                      // Check multiple possible locations for propostaId
                                      const propostaId = data?.propostaId || data?.proposta_id || data?.id || data?.proposalId;
                                      if (propostaId && propostaId.toString().trim() !== '') {
                                        const id = propostaId.toString().trim();
                                        if (!allIds.includes(id)) {
                                          allIds.push(id);
                                        }
                                      }
                                    });
                                  } else if (typeof proposalData === 'object') {
                                    // Single proposalData object (backward compatibility)
                                    const propostaId = proposalData.propostaId || proposalData.proposta_id || proposalData.id || proposalData.proposalId;
                                    if (propostaId) {
                                      const id = propostaId.toString().trim();
                                      if (id && !allIds.includes(id)) {
                                        allIds.push(id);
                                      }
                                    }
                                  }
                                }
                                
                                // Check if there's a proposals array or proposalRequests array
                                // Also check propostaIds array (maintained by backend for multiple proposals)
                                const proposalsArray = formData.proposals || formData.proposalRequests || formData.propostaIds;
                                if (proposalsArray) {
                                  if (Array.isArray(proposalsArray)) {
                                    proposalsArray.forEach((item: any) => {
                                      const id = typeof item === 'object' ? (item?.propostaId || item?.proposta_id || item?.id || item?.proposalId) : item;
                                      if (id && id.toString().trim() !== '') {
                                        const idStr = id.toString().trim();
                                        if (!allIds.includes(idStr)) {
                                          allIds.push(idStr);
                                        }
                                      }
                                    });
                                  } else {
                                    // Single value - add it
                                    const id = proposalsArray.toString().trim();
                                    if (id && !allIds.includes(id)) {
                                      allIds.push(id);
                                    }
                                  }
                                }
                                
                                // Fallback: check proposalsData (from ProposalRequest table, if any)
                                if (allIds.length === 0) {
                                  const proposals = proposalsData[marcacaoIdToUse] || [];
                                  proposals.forEach((proposal: any) => {
                                    // Get propostaId from proposal's form_answers or proposal ID
                                    const formAnswers = proposal.form_answers || {};
                                    const propostaId = formAnswers.propostaId || formAnswers.proposta_id || formAnswers.propostaID ||
                                                       proposal.id?.toString() || proposal.propostaId?.toString() || proposal.proposta_id?.toString();
                                    if (propostaId && propostaId.toString().trim() !== '') {
                                      const id = propostaId.toString().trim();
                                      if (!allIds.includes(id)) {
                                        allIds.push(id);
                                      }
                                    }
                                  });
                                }
                                
                                if (allIds.length === 0) return '-';
                                
                                // Return all unique IDs formatted
                                return allIds.map(id => `#${id.toUpperCase()}`).join(', ');
                              };
                              
                              // Get Id da Proposta - fetch from database
                              // Note: "Id da Proposta" might be the same as "Id do Pedido de Proposta" (propostaId)
                              // or it might be a different ID stored elsewhere. For now, we'll check for both.
                              const getIdProposta = () => {
                                const allIds: string[] = [];
                                
                                // First, get proposals from database (proposalsData state)
                                const proposals = proposalsData[marcacaoIdToUse] || [];
                                proposals.forEach((proposal: any) => {
                                  // Get idProposta from proposal's form_answers or proposal ID
                                  const formAnswers = proposal.form_answers || {};
                                  const idProposta = formAnswers.idProposta || formAnswers.id_proposta || formAnswers.idPropostaID ||
                                                     formAnswers.proposta_id || formAnswers.proposalId || formAnswers.proposal_id ||
                                                     formAnswers.idPropostaFinal || formAnswers.id_proposta_final ||
                                                     formAnswers.finalPropostaId || formAnswers.final_proposta_id ||
                                                     proposal.id?.toString() || proposal.propostaId?.toString() || proposal.proposta_id?.toString();
                                  if (idProposta && idProposta.toString().trim() !== '') {
                                    const id = idProposta.toString().trim();
                                    if (!allIds.includes(id)) {
                                      allIds.push(id);
                                    }
                                  }
                                });
                                
                                // Also check form_data as fallback (for backward compatibility)
                                const formData = marcacao.form_data || {};
                                
                                // Check multiple possible locations for idProposta (different from propostaId)
                                // Check both camelCase and snake_case variations
                                const idPropostaDirect = formData.idProposta || formData.id_proposta || formData.idPropostaID || 
                                                         formData.proposta_id || formData.proposalId || formData.proposal_id ||
                                                         formData.idPropostaFinal || formData.id_proposta_final;
                                if (idPropostaDirect) {
                                  if (Array.isArray(idPropostaDirect)) {
                                    idPropostaDirect.forEach((id: any) => {
                                      if (id && id.toString().trim() !== '') {
                                        const idStr = id.toString().trim();
                                        if (!allIds.includes(idStr)) {
                                          allIds.push(idStr);
                                        }
                                      }
                                    });
                                  } else if (typeof idPropostaDirect === 'string' && idPropostaDirect.trim() !== '') {
                                    allIds.push(idPropostaDirect.trim());
                                  } else if (typeof idPropostaDirect === 'number') {
                                    allIds.push(idPropostaDirect.toString());
                                  }
                                }
                                
                                // Also check in proposalData items
                                // Check both camelCase and snake_case variations
                                const proposalData = formData.proposalData || formData.proposal_data || formData.proposals;
                                if (proposalData) {
                                  if (Array.isArray(proposalData)) {
                                    proposalData.forEach((data: any) => {
                                      // Check for idProposta in proposal data
                                      const idProposta = data?.idProposta || data?.id_proposta || data?.idPropostaID ||
                                                         data?.proposta_id || data?.proposalId || data?.proposal_id ||
                                                         data?.idPropostaFinal || data?.id_proposta_final ||
                                                         data?.finalPropostaId || data?.final_proposta_id;
                                      if (idProposta && idProposta.toString().trim() !== '') {
                                        const id = idProposta.toString().trim();
                                        if (!allIds.includes(id)) {
                                          allIds.push(id);
                                        }
                                      }
                                    });
                                  } else if (typeof proposalData === 'object' && proposalData !== null) {
                                    const idProposta = proposalData.idProposta || proposalData.id_proposta || proposalData.idPropostaID ||
                                                       proposalData.proposta_id || proposalData.proposalId || proposalData.proposal_id ||
                                                       proposalData.idPropostaFinal || proposalData.id_proposta_final ||
                                                       proposalData.finalPropostaId || proposalData.final_proposta_id;
                                    if (idProposta) {
                                      const id = idProposta.toString().trim();
                                      if (id && !allIds.includes(id)) {
                                        allIds.push(id);
                                      }
                                    }
                                  }
                                }
                                
                                // ID da Proposta is only filled by Backoffice, so we don't fall back to propostaId
                                // Return all unique IDs formatted, or "-" if none found
                                if (allIds.length === 0) {
                                  return '-';
                                }
                                return allIds.map(id => `#${id.toUpperCase()}`).join(', ');
                              };
                              
                              return (
                              <tr 
                                key={marcacao.id} 
                                id={`marcacao-${marcacaoIdToUse}`}
                                data-id={marcacao.id}
                                className={`${isHighlighted ? 'highlighted-row' : ''} ${needsFeedback || hasEmptyFeedback ? 'feedback-empty-row' : ''}`}
                                style={needsFeedback || hasEmptyFeedback ? { backgroundColor: '#fef2f2', borderLeft: '4px solid #dc2626' } : {}}
                              >
                                {changeComercialSelectMode && (
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={selectedMarcacoesForChange.has(marcacao.id)}
                                      onChange={() => toggleMarcacaoForChange(marcacao.id)}
                                    />
                                  </td>
                                )}
                                <td>{marcacao.data}</td>
                                <td>{renderProducts(marcacao)}</td>
                                <td>{marcacao.empresa || '-'}</td>
                                <td>{getNifFromProposals(String(marcacaoIdToUse)) || '-'}</td>
                                <td>{marcacao.responsavel}</td>
                                <td>
                                  <textarea
                                    className="form-input"
                                    style={{ 
                                      width: '100%', 
                                      minWidth: '200px', 
                                      maxWidth: '300px',
                                      minHeight: '60px',
                                      resize: 'vertical',
                                      padding: '8px',
                                      fontSize: '13px',
                                      fontFamily: 'inherit'
                                    }}
                                    value={typeof marcacao.feedback === 'string' ? marcacao.feedback : ''}
                                    onChange={(e) => handleFeedbackChange(marcacao.id, e.target.value)}
                                    onBlur={(e) => saveFeedback(marcacao.id, e.target.value)}
                                    placeholder="Digite o feedback..."
                                  />
                                </td>
                                <td>
                                  {getEstadoPropostaBadge(marcacao.estado_proposta)}
                                </td>
                                <td>{getIdPedidoProposta()}</td>
                                <td>{getIdProposta()}</td>
                                <td>
                                  <select
                                    className="resultado-dropdown"
                                    defaultValue=""
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        handleResultadoChange(marcacao.id, e.target.value);
                                        e.target.value = '';
                                      }
                                    }}
                                    style={{
                                      padding: '6px 12px',
                                      borderRadius: '4px',
                                      border: '1px solid #d1d5db',
                                      fontSize: '11px',
                                      minWidth: '180px',
                                      backgroundColor: 'white',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <option value="" disabled></option>
                                    <optgroup label="Resultado">
                                      <option value="resultado-fecho-ok">Fecho Ok</option>
                                      <option value="resultado-fecho-not-ok">Fecho Not Ok</option>
                                      {canChangeAguardarSubstage() ? (
                                      <option value="resultado-aguardar">Aguardar</option>
                                    ) : null}
                                      <option value="resultado-cancelado">Cancelado</option>
                                    </optgroup>
                                  </select>
                                </td>
                                <td>
                                  <div className="action-buttons-container">
                                    <button className="action-btn btn-proposal" onClick={() => openProposalModal(marcacao.id)}>
                                      Solicitar Proposta
                                    </button>
                                    <button className="action-btn btn-proposal" onClick={() => openRefazerProposalModal(marcacao.id)}>
                                      Refazer
                                    </button>
                                    <button className="action-btn btn-revert" onClick={() => openRevertConfirm(marcacao.id, 'reuniao-realizada')} title="Retroceder para Reunião Realizada">
                                      Retroceder
                                    </button>
                                    <button className="action-btn btn-details" onClick={() => openDetailsModal(marcacao.id)}>
                                      Detalhes
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {(() => {
                        const { total, currentPage, totalPages } = getPaginatedSubsection('feedback-pendencia-concluido-com-proposta', getConcluidoComProposta());
                        if (total <= MARCADOES_PER_PAGE) return null;
                        return (
                          <div className="subsection-pagination">
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('feedback-pendencia-concluido-com-proposta', currentPage - 1)} disabled={currentPage <= 1}>← Anterior</button>
                            <span className="pagination-info">Página {currentPage} de {totalPages}</span>
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('feedback-pendencia-concluido-com-proposta', currentPage + 1)} disabled={currentPage >= totalPages}>Seguinte →</button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Not Ok Cliente Subsection */}
                <div className={`subsection ${highlightedSubsections['feedback-pendencia-not-ok-cliente'] ? 'subsection-highlight' : ''}`} data-subsection="feedback-pendencia-not-ok-cliente">
                  <div 
                    className="subsection-header" 
                    onClick={() => toggleSubsection('feedback-pendencia-not-ok-cliente')}
                    style={highlightedSubsections['feedback-pendencia-not-ok-cliente'] ? { backgroundColor: '#fef2f2', borderLeft: '4px solid #dc2626' } : {}}
                  >
                    <span className="subsection-icon" style={{ transform: expandedSubsections['feedback-pendencia-not-ok-cliente'] ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                      ▼
                    </span>
                    <span>Not Ok Cliente ({getNotOkCliente().length})</span>
                  </div>
                  {expandedSubsections['feedback-pendencia-not-ok-cliente'] && (
                    <div className="subsection-content active">
                      <div className="data-table">
                        <table>
                          <thead>
                            <tr>
                              {changeComercialSelectMode && <th style={{ width: 40 }}></th>}
                              <th>Data da Marcação</th>
                              <th>Produto</th>
                              <th>Nome da Empresa</th>
                              <th>NIF Empresa</th>
                              <th>Nome do Responsável</th>
                              <th>Feedback</th>
                              <th>Resultado</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getPaginatedSubsection('feedback-pendencia-not-ok-cliente', getNotOkCliente()).items.map(marcacao => {
                              const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
                              const isHighlighted = highlightedMarcacaoId === marcacaoIdToUse;
                              const needsFeedback = highlightedMarcacoesForFeedback.has(marcacaoIdToUse);
                              const feedbackText = typeof marcacao.feedback === 'string' ? marcacao.feedback : '';
              const hasEmptyFeedback = !marcacao.feedback || !feedbackText.trim();
                              return (
                              <tr 
                                key={marcacao.id} 
                                id={`marcacao-${marcacaoIdToUse}`}
                                data-id={marcacao.id}
                                className={`${isHighlighted ? 'highlighted-row' : ''} ${needsFeedback || hasEmptyFeedback ? 'feedback-empty-row' : ''}`}
                                style={needsFeedback || hasEmptyFeedback ? { backgroundColor: '#fef2f2', borderLeft: '4px solid #dc2626' } : {}}
                              >
                                {changeComercialSelectMode && (
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={selectedMarcacoesForChange.has(marcacao.id)}
                                      onChange={() => toggleMarcacaoForChange(marcacao.id)}
                                    />
                                  </td>
                                )}
                                <td>{marcacao.data}</td>
                                <td>{renderProducts(marcacao)}</td>
                                <td>{marcacao.empresa || '-'}</td>
                                <td>{getNifFromProposals(String(marcacaoIdToUse)) || '-'}</td>
                                <td>{marcacao.responsavel}</td>
                                <td>
                                  <textarea
                                    className="form-input"
                                    style={{ 
                                      width: '100%', 
                                      minWidth: '200px', 
                                      maxWidth: '300px',
                                      minHeight: '60px',
                                      resize: 'vertical',
                                      padding: '8px',
                                      fontSize: '13px',
                                      fontFamily: 'inherit'
                                    }}
                                    value={typeof marcacao.feedback === 'string' ? marcacao.feedback : ''}
                                    onChange={(e) => handleFeedbackChange(marcacao.id, e.target.value)}
                                    onBlur={(e) => saveFeedback(marcacao.id, e.target.value)}
                                    placeholder="Digite o feedback..."
                                  />
                                </td>
                                <td>
                                  <select
                                    className="resultado-dropdown"
                                    defaultValue=""
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        handleResultadoChange(marcacao.id, e.target.value);
                                        e.target.value = '';
                                      }
                                    }}
                                    style={{
                                      padding: '6px 12px',
                                      borderRadius: '4px',
                                      border: '1px solid #d1d5db',
                                      fontSize: '11px',
                                      minWidth: '180px',
                                      backgroundColor: 'white',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <option value="" disabled></option>
                                    <optgroup label="Resultado">
                                      <option value="resultado-fecho-ok">Fecho Ok</option>
                                      <option value="resultado-fecho-not-ok">Fecho Not Ok</option>
                                      {canChangeAguardarSubstage() ? (
                                      <option value="resultado-aguardar">Aguardar</option>
                                    ) : null}
                                      <option value="resultado-cancelado">Cancelado</option>
                                    </optgroup>
                                  </select>
                                </td>
                                <td>
                                  <div className="action-buttons-container">
                                    <button className="action-btn btn-revert" onClick={() => openRevertConfirm(marcacao.id, 'reuniao-realizada')} title="Retroceder para Reunião Realizada">
                                      Retroceder
                                    </button>
                                    <button className="action-btn btn-details" onClick={() => openDetailsModal(marcacao.id)}>
                                      Detalhes
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {(() => {
                        const { total, currentPage, totalPages } = getPaginatedSubsection('feedback-pendencia-not-ok-callcenter', getNotOkCallCenter());
                        if (total <= MARCADOES_PER_PAGE) return null;
                        return (
                          <div className="subsection-pagination">
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('feedback-pendencia-not-ok-callcenter', currentPage - 1)} disabled={currentPage <= 1}>← Anterior</button>
                            <span className="pagination-info">Página {currentPage} de {totalPages}</span>
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('feedback-pendencia-not-ok-callcenter', currentPage + 1)} disabled={currentPage >= totalPages}>Seguinte →</button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Pendente Subsection */}
                <div className={`subsection ${highlightedSubsections['feedback-pendencia-pendente'] ? 'subsection-highlight' : ''}`} data-subsection="feedback-pendencia-pendente">
                  <div 
                    className="subsection-header" 
                    onClick={() => toggleSubsection('feedback-pendencia-pendente')}
                    style={highlightedSubsections['feedback-pendencia-pendente'] ? { backgroundColor: '#fef2f2', borderLeft: '4px solid #dc2626' } : {}}
                  >
                    <span className="subsection-icon" style={{ transform: expandedSubsections['feedback-pendencia-pendente'] ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                      ▼
                    </span>
                    <span>Pendente ({getPendente().length})</span>
                  </div>
                  {expandedSubsections['feedback-pendencia-pendente'] && (
                    <div className="subsection-content active">
                      <div className="data-table">
                        <table>
                          <thead>
                            <tr>
                              {changeComercialSelectMode && <th style={{ width: 40 }}></th>}
                              <th>Data da Marcação</th>
                              <th>Produto</th>
                              <th>Nome da Empresa</th>
                              <th>NIF Empresa</th>
                              <th>Nome do Responsável</th>
                              <th>Feedback</th>
                              <th>Estado da Proposta</th>
                              <th>Id do Pedido de Proposta</th>
                              <th>Id da Proposta</th>
                              <th>Resultado</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getPaginatedSubsection('feedback-pendencia-pendente', getPendente()).items.map(marcacao => {
                              const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
                              const isHighlighted = highlightedMarcacaoId === marcacaoIdToUse;
                              const needsFeedback = highlightedMarcacoesForFeedback.has(marcacaoIdToUse);
                              const feedbackText = typeof marcacao.feedback === 'string' ? marcacao.feedback : '';
                              const hasEmptyFeedback = typeof marcacao.feedback !== 'string' || !feedbackText.trim();
                              
                              // Get Id do Pedido de Proposta - fetch from marcação's form_data
                              // IMPORTANT: Use the specific marcação from the map, not from shared state
                              const getIdPedidoProposta = (): string => {
                                const allIds: string[] = [];
                                
                                // Get form_data from THIS specific marcação (from the map closure)
                                // This ensures we're reading the correct marcação's data
                                const formData = marcacao.form_data || {};
                                
                                
                                // First, check if propostaId is directly in form_data as array or single value
                                // This is the main array maintained by the backend
                                // Check both camelCase and snake_case variations
                                const propostaIdDirect = formData.propostaId || formData.proposta_id || formData.propostaID;
                                if (propostaIdDirect) {
                                  if (Array.isArray(propostaIdDirect)) {
                                    propostaIdDirect.forEach((id: any) => {
                                      if (id && id.toString().trim() !== '') {
                                        const idStr = id.toString().trim();
                                        if (!allIds.includes(idStr)) {
                                          allIds.push(idStr);
                                        }
                                      }
                                    });
                                  } else if (typeof propostaIdDirect === 'string' && propostaIdDirect.trim() !== '') {
                                    allIds.push(propostaIdDirect.trim());
                                  } else if (typeof propostaIdDirect === 'number') {
                                    allIds.push(propostaIdDirect.toString());
                                  }
                                }
                                
                                // Check if proposalData exists and has propostaId
                                // proposalData is now an array of proposals
                                const proposalData = formData.proposalData;
                                if (proposalData) {
                                  // If proposalData is an array, extract propostaId from each
                                  if (Array.isArray(proposalData)) {
                                    proposalData.forEach((data: any) => {
                                      // Check multiple possible locations for propostaId
                                      const propostaId = data?.propostaId || data?.proposta_id || data?.id || data?.proposalId;
                                      if (propostaId && propostaId.toString().trim() !== '') {
                                        const id = propostaId.toString().trim();
                                        if (!allIds.includes(id)) {
                                          allIds.push(id);
                                        }
                                      }
                                    });
                                  } else if (typeof proposalData === 'object') {
                                    // Single proposalData object (backward compatibility)
                                    const propostaId = proposalData.propostaId || proposalData.proposta_id || proposalData.id || proposalData.proposalId;
                                    if (propostaId) {
                                      const id = propostaId.toString().trim();
                                      if (id && !allIds.includes(id)) {
                                        allIds.push(id);
                                      }
                                    }
                                  }
                                }
                                
                                // Check if there's a proposals array or proposalRequests array
                                // Also check propostaIds array (maintained by backend for multiple proposals)
                                const proposalsArray = formData.proposals || formData.proposalRequests || formData.propostaIds;
                                if (proposalsArray) {
                                  if (Array.isArray(proposalsArray)) {
                                    proposalsArray.forEach((item: any) => {
                                      const id = typeof item === 'object' ? (item?.propostaId || item?.proposta_id || item?.id || item?.proposalId) : item;
                                      if (id && id.toString().trim() !== '') {
                                        const idStr = id.toString().trim();
                                        if (!allIds.includes(idStr)) {
                                          allIds.push(idStr);
                                        }
                                      }
                                    });
                                  } else {
                                    // Single value - add it
                                    const id = proposalsArray.toString().trim();
                                    if (id && !allIds.includes(id)) {
                                      allIds.push(id);
                                    }
                                  }
                                }
                                
                                // Fallback: check proposalsData (from ProposalRequest table, if any)
                                if (allIds.length === 0) {
                                  const proposals = proposalsData[marcacaoIdToUse] || [];
                                  proposals.forEach((proposal: any) => {
                                    // Get propostaId from proposal's form_answers or proposal ID
                                    const formAnswers = proposal.form_answers || {};
                                    const propostaId = formAnswers.propostaId || formAnswers.proposta_id || formAnswers.propostaID ||
                                                       proposal.id?.toString() || proposal.propostaId?.toString() || proposal.proposta_id?.toString();
                                    if (propostaId && propostaId.toString().trim() !== '') {
                                      const id = propostaId.toString().trim();
                                      if (!allIds.includes(id)) {
                                        allIds.push(id);
                                      }
                                    }
                                  });
                                }
                                
                                if (allIds.length === 0) return '-';
                                
                                // Return all unique IDs formatted
                                return allIds.map(id => `#${id.toUpperCase()}`).join(', ');
                              };
                              
                              // Get Id da Proposta - fetch from database
                              // Note: "Id da Proposta" might be the same as "Id do Pedido de Proposta" (propostaId)
                              // or it might be a different ID stored elsewhere. For now, we'll check for both.
                              const getIdProposta = () => {
                                const allIds: string[] = [];
                                
                                // First, get proposals from database (proposalsData state)
                                const proposals = proposalsData[marcacaoIdToUse] || [];
                                proposals.forEach((proposal: any) => {
                                  // Get idProposta from proposal's form_answers or proposal ID
                                  const formAnswers = proposal.form_answers || {};
                                  const idProposta = formAnswers.idProposta || formAnswers.id_proposta || formAnswers.idPropostaID ||
                                                     formAnswers.proposta_id || formAnswers.proposalId || formAnswers.proposal_id ||
                                                     formAnswers.idPropostaFinal || formAnswers.id_proposta_final ||
                                                     formAnswers.finalPropostaId || formAnswers.final_proposta_id ||
                                                     proposal.id?.toString() || proposal.propostaId?.toString() || proposal.proposta_id?.toString();
                                  if (idProposta && idProposta.toString().trim() !== '') {
                                    const id = idProposta.toString().trim();
                                    if (!allIds.includes(id)) {
                                      allIds.push(id);
                                    }
                                  }
                                });
                                
                                // Also check form_data as fallback (for backward compatibility)
                                const formData = marcacao.form_data || {};
                                
                                // Check multiple possible locations for idProposta (different from propostaId)
                                // Check both camelCase and snake_case variations
                                const idPropostaDirect = formData.idProposta || formData.id_proposta || formData.idPropostaID || 
                                                         formData.proposta_id || formData.proposalId || formData.proposal_id ||
                                                         formData.idPropostaFinal || formData.id_proposta_final;
                                if (idPropostaDirect) {
                                  if (Array.isArray(idPropostaDirect)) {
                                    idPropostaDirect.forEach((id: any) => {
                                      if (id && id.toString().trim() !== '') {
                                        const idStr = id.toString().trim();
                                        if (!allIds.includes(idStr)) {
                                          allIds.push(idStr);
                                        }
                                      }
                                    });
                                  } else if (typeof idPropostaDirect === 'string' && idPropostaDirect.trim() !== '') {
                                    allIds.push(idPropostaDirect.trim());
                                  } else if (typeof idPropostaDirect === 'number') {
                                    allIds.push(idPropostaDirect.toString());
                                  }
                                }
                                
                                // Also check in proposalData items
                                // Check both camelCase and snake_case variations
                                const proposalData = formData.proposalData || formData.proposal_data || formData.proposals;
                                if (proposalData) {
                                  if (Array.isArray(proposalData)) {
                                    proposalData.forEach((data: any) => {
                                      // Check for idProposta in proposal data
                                      const idProposta = data?.idProposta || data?.id_proposta || data?.idPropostaID ||
                                                         data?.proposta_id || data?.proposalId || data?.proposal_id ||
                                                         data?.idPropostaFinal || data?.id_proposta_final ||
                                                         data?.finalPropostaId || data?.final_proposta_id;
                                      if (idProposta && idProposta.toString().trim() !== '') {
                                        const id = idProposta.toString().trim();
                                        if (!allIds.includes(id)) {
                                          allIds.push(id);
                                        }
                                      }
                                    });
                                  } else if (typeof proposalData === 'object' && proposalData !== null) {
                                    const idProposta = proposalData.idProposta || proposalData.id_proposta || proposalData.idPropostaID ||
                                                       proposalData.proposta_id || proposalData.proposalId || proposalData.proposal_id ||
                                                       proposalData.idPropostaFinal || proposalData.id_proposta_final ||
                                                       proposalData.finalPropostaId || proposalData.final_proposta_id;
                                    if (idProposta) {
                                      const id = idProposta.toString().trim();
                                      if (id && !allIds.includes(id)) {
                                        allIds.push(id);
                                      }
                                    }
                                  }
                                }
                                
                                // ID da Proposta is only filled by Backoffice, so we don't fall back to propostaId
                                // Return all unique IDs formatted, or "-" if none found
                                if (allIds.length === 0) {
                                  return '-';
                                }
                                return allIds.map(id => `#${id.toUpperCase()}`).join(', ');
                              };
                              
                              return (
                                <tr 
                                  key={marcacao.id} 
                                  id={`marcacao-${marcacaoIdToUse}`}
                                  data-id={marcacao.id}
                                  className={`${isHighlighted ? 'highlighted-row' : ''} ${needsFeedback || hasEmptyFeedback ? 'feedback-empty-row' : ''}`}
                                  style={needsFeedback || hasEmptyFeedback ? { backgroundColor: '#fef2f2', borderLeft: '4px solid #dc2626' } : {}}
                                >
                                  {changeComercialSelectMode && (
                                    <td>
                                      <input
                                        type="checkbox"
                                        checked={selectedMarcacoesForChange.has(marcacao.id)}
                                        onChange={() => toggleMarcacaoForChange(marcacao.id)}
                                      />
                                    </td>
                                  )}
                                  <td>{marcacao.data}</td>
                                  <td>{renderProducts(marcacao)}</td>
                                  <td>{marcacao.empresa || '-'}</td>
                                  <td>{getNifFromProposals(String(marcacaoIdToUse)) || '-'}</td>
                                  <td>{marcacao.responsavel}</td>
                                  <td>
                                    <textarea
                                      className="form-input"
                                      style={{ 
                                        width: '100%', 
                                        minWidth: '200px', 
                                        maxWidth: '300px',
                                        minHeight: '60px',
                                        resize: 'vertical',
                                        padding: '8px',
                                        fontSize: '13px',
                                        fontFamily: 'inherit'
                                      }}
                                      value={typeof marcacao.feedback === 'string' ? marcacao.feedback : ''}
                                      onChange={(e) => handleFeedbackChange(marcacao.id, e.target.value)}
                                      onBlur={(e) => saveFeedback(marcacao.id, e.target.value)}
                                      placeholder="Digite o feedback..."
                                    />
                                  </td>
                                  <td>
                                    {getEstadoPropostaBadge(marcacao.estado_proposta)}
                                  </td>
                                  <td>{getIdPedidoProposta()}</td>
                                  <td>{getIdProposta()}</td>
                                  <td>
                                    <select
                                      className="resultado-dropdown"
                                      defaultValue=""
                                      onChange={(e) => {
                                        if (e.target.value) {
                                          handleResultadoFinalChange(marcacao.id, e.target.value);
                                        }
                                      }}
                                      style={{
                                        padding: '6px 12px',
                                        borderRadius: '4px',
                                        border: '1px solid #d1d5db',
                                        fontSize: '11px',
                                        minWidth: '180px',
                                        backgroundColor: 'white',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      <option value="" disabled></option>
                                      <option value="resultado-fecho-ok">Fecho Ok</option>
                                      <option value="resultado-fecho-not-ok">Fecho Not Ok</option>
                                      {canChangeAguardarSubstage() ? (
                                      <option value="resultado-aguardar">Aguardar</option>
                                    ) : null}
                                      <option value="resultado-cancelado">Cancelado</option>
                                    </select>
                                  </td>
                                  <td>
                                    <div className="action-buttons-container">
                                      <button className="action-btn btn-proposal" onClick={() => openProposalModal(marcacao.id)}>
                                        Solicitar Proposta
                                      </button>
                                      <button className="action-btn btn-proposal" onClick={() => openRefazerProposalModal(marcacao.id)}>
                                        Refazer
                                      </button>
                                      <button className="action-btn btn-revert" onClick={() => openRevertConfirm(marcacao.id, 'reuniao-realizada')} title="Retroceder para Reunião Realizada">
                                        Retroceder
                                      </button>
                                      {isAdminOrGerenteComercial() && (
                                        <button className="action-btn btn-assign-comercial" onClick={() => openChangeComercialModal(marcacao.id)} title="Alterar Comercial">
                                          Alterar Comercial
                                        </button>
                                      )}
                                    <button className="action-btn btn-details" onClick={() => openDetailsModal(marcacao.id)}>
                                      Detalhes
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {(() => {
                        const { total, currentPage, totalPages } = getPaginatedSubsection('feedback-pendencia-not-ok-cliente', getNotOkCliente());
                        if (total <= MARCADOES_PER_PAGE) return null;
                        return (
                          <div className="subsection-pagination">
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('feedback-pendencia-not-ok-cliente', currentPage - 1)} disabled={currentPage <= 1}>← Anterior</button>
                            <span className="pagination-info">Página {currentPage} de {totalPages}</span>
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('feedback-pendencia-not-ok-cliente', currentPage + 1)} disabled={currentPage >= totalPages}>Seguinte →</button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Not Ok CallCenter Subsection */}
                <div className={`subsection ${highlightedSubsections['feedback-pendencia-not-ok-callcenter'] ? 'subsection-highlight' : ''}`} data-subsection="feedback-pendencia-not-ok-callcenter">
                  <div 
                    className="subsection-header" 
                    onClick={() => toggleSubsection('feedback-pendencia-not-ok-callcenter')}
                    style={highlightedSubsections['feedback-pendencia-not-ok-callcenter'] ? { backgroundColor: '#fef2f2', borderLeft: '4px solid #dc2626' } : {}}
                  >
                    <span className="subsection-icon" style={{ transform: expandedSubsections['feedback-pendencia-not-ok-callcenter'] ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                      ▼
                    </span>
                    <span>Not Ok CallCenter ({getNotOkCallCenter().length})</span>
                  </div>
                  {expandedSubsections['feedback-pendencia-not-ok-callcenter'] && (
                    <div className="subsection-content active">
                      <div className="data-table">
                        <table>
                          <thead>
                            <tr>
                              {changeComercialSelectMode && <th style={{ width: 40 }}></th>}
                              <th>Data da Marcação</th>
                              <th>Produto</th>
                              <th>Nome da Empresa</th>
                              <th>NIF Empresa</th>
                              <th>Nome do Responsável</th>
                              <th>Feedback</th>
                              <th>Resultado</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getPaginatedSubsection('feedback-pendencia-not-ok-callcenter', getNotOkCallCenter()).items.map(marcacao => {
                              const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
                              const isHighlighted = highlightedMarcacaoId === marcacaoIdToUse;
                              const needsFeedback = highlightedMarcacoesForFeedback.has(marcacaoIdToUse);
                              const hasFeedback = hasNotOkCallCenterFeedback(marcacao);
                              const hasEmptyFeedback = !hasFeedback;
                              return (
                              <tr 
                                key={marcacao.id} 
                                id={`marcacao-${marcacaoIdToUse}`}
                                data-id={marcacao.id}
                                className={`${isHighlighted ? 'highlighted-row' : ''} ${needsFeedback || hasEmptyFeedback ? 'feedback-empty-row' : ''}`}
                                style={needsFeedback || hasEmptyFeedback ? { backgroundColor: '#fef2f2', borderLeft: '4px solid #dc2626' } : {}}
                              >
                                {changeComercialSelectMode && (
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={selectedMarcacoesForChange.has(marcacao.id)}
                                      onChange={() => toggleMarcacaoForChange(marcacao.id)}
                                    />
                                  </td>
                                )}
                                <td>{marcacao.data}</td>
                                <td>{renderProducts(marcacao)}</td>
                                <td>{marcacao.empresa || '-'}</td>
                                <td>{getNifFromProposals(String(marcacaoIdToUse)) || '-'}</td>
                                <td>{marcacao.responsavel}</td>
                                <td>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {/* Toggle Indicator */}
                                    <div 
                                      style={{
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: hasFeedback ? '#10b981' : '#ef4444',
                                        color: 'white',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        flexShrink: 0
                                      }}
                                      title={hasFeedback ? 'Feedback submetido' : 'Feedback pendente'}
                                    >
                                      {hasFeedback ? '✓' : '✗'}
                                    </div>
                                    {/* Feedback Button */}
                                    <button 
                                      className="action-btn btn-primary"
                                      onClick={() => openNotOkCallCenterFeedbackModal(marcacao.id)}
                                      style={{
                                        padding: '6px 12px',
                                        fontSize: '12px',
                                        minWidth: '80px'
                                      }}
                                    >
                                      Feedback
                                    </button>
                                  </div>
                                </td>
                                <td>
                                  <select
                                    className="resultado-dropdown"
                                    defaultValue=""
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        handleResultadoChange(marcacao.id, e.target.value);
                                        e.target.value = '';
                                      }
                                    }}
                                    style={{
                                      padding: '6px 12px',
                                      borderRadius: '4px',
                                      border: '1px solid #d1d5db',
                                      fontSize: '11px',
                                      minWidth: '180px',
                                      backgroundColor: 'white',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <option value="" disabled></option>
                                    <optgroup label="Resultado">
                                      <option value="resultado-fecho-ok">Fecho Ok</option>
                                      <option value="resultado-fecho-not-ok">Fecho Not Ok</option>
                                      {canChangeAguardarSubstage() ? (
                                      <option value="resultado-aguardar">Aguardar</option>
                                    ) : null}
                                      <option value="resultado-cancelado">Cancelado</option>
                                    </optgroup>
                                  </select>
                                </td>
                                <td>
                                  <div className="action-buttons-container">
                                    <button className="action-btn btn-revert" onClick={() => openRevertConfirm(marcacao.id, 'reuniao-realizada')} title="Retroceder para Reunião Realizada">
                                      Retroceder
                                    </button>
                                    <button className="action-btn btn-details" onClick={() => openDetailsModal(marcacao.id)}>
                                      Detalhes
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {(() => {
                        const { total, currentPage, totalPages } = getPaginatedSubsection('feedback-pendencia-pendente', getPendente());
                        if (total <= MARCADOES_PER_PAGE) return null;
                        return (
                          <div className="subsection-pagination">
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('feedback-pendencia-pendente', currentPage - 1)} disabled={currentPage <= 1}>← Anterior</button>
                            <span className="pagination-info">Página {currentPage} de {totalPages}</span>
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('feedback-pendencia-pendente', currentPage + 1)} disabled={currentPage >= totalPages}>Seguinte →</button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Resultado Section */}
          {currentSection === 'resultado' && (
            <div className="section-content active">
              <div className="collapse-controls">
                <button
                  className="btn btn-secondary"
                  onClick={() => collapseAllSubsections('resultado')}
                >
                  Colapsar Todas
                </button>
                {isAdminOrGerenteComercial() && (
                  <>
                    <button
                      className={`btn ${changeComercialSelectMode ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={toggleChangeComercialSelectMode}
                      style={changeComercialSelectMode ? { backgroundColor: '#f59e0b', borderColor: '#f59e0b' } : {}}
                    >
                      {changeComercialSelectMode ? 'Cancelar Seleção' : 'Alterar Comercial'}
                    </button>
                    {changeComercialSelectMode && selectedMarcacoesForChange.size > 0 && (
                      <button
                        className="btn btn-primary"
                        onClick={() => openChangeComercialModal()}
                        style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }}
                      >
                        Alterar {selectedMarcacoesForChange.size} selecionada(s)
                      </button>
                    )}
                  </>
                )}
              </div>

              <div className="subsection-container">
                {/* Fecho Ok Subsection */}
                <div className="subsection" data-subsection="resultado-fecho-ok">
                  <div 
                    className="subsection-header" 
                    onClick={() => toggleSubsection('resultado-fecho-ok')}
                  >
                    <span className="subsection-icon" style={{ transform: expandedSubsections['resultado-fecho-ok'] ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                      ▼
                    </span>
                    <span>Fecho Ok ({getFechoOk().length})</span>
                    {(() => {
                      const semContrato = getFechoOk().filter((m) => !marcacaoHasSubmittedContract(m)).length;
                      if (semContrato <= 0) return null;
                      return (
                        <span className="fecho-ok-sem-contrato-count" title="Marcações em Fecho Ok sem contrato submetido">
                          · {semContrato} sem contrato
                        </span>
                      );
                    })()}
                  </div>
                  {expandedSubsections['resultado-fecho-ok'] && (
                    <div className="subsection-content active">
                      <div className="data-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Data da Marcação</th>
                              <th>Produto</th>
                              <th>Nome da Empresa</th>
                              <th>NIF Empresa</th>
                              <th>Nome do Responsável</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getPaginatedSubsection('resultado-fecho-ok', getFechoOk()).items.map(marcacao => {
                              const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
                              const isHighlighted = highlightedMarcacaoId === marcacaoIdToUse;
                              const hasContract = marcacaoHasSubmittedContract(marcacao);
                              return (
                              <tr 
                                key={marcacao.id} 
                                id={`marcacao-${marcacaoIdToUse}`}
                                data-id={marcacao.id}
                                className={`${isHighlighted ? 'highlighted-row' : ''} ${!hasContract ? 'fecho-ok-sem-contrato-row' : ''}`}
                              >
                                {changeComercialSelectMode && (
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={selectedMarcacoesForChange.has(marcacao.id)}
                                      onChange={() => toggleMarcacaoForChange(marcacao.id)}
                                    />
                                  </td>
                                )}
                                <td>{marcacao.data}</td>
                                <td>{renderProducts(marcacao)}</td>
                                <td>
                                  <div className="fecho-ok-empresa-cell">
                                    <span>{marcacao.empresa || '-'}</span>
                                    {!hasContract ? (
                                      <span
                                        className="fecho-ok-contract-badge"
                                        title="Contrato ainda não submetido"
                                      >
                                        Sem Contrato
                                      </span>
                                    ) : null}
                                  </div>
                                </td>
                                <td>{getNifFromProposals(String(marcacaoIdToUse)) || '-'}</td>
                                <td>{marcacao.responsavel}</td>
                                <td>
                                  <div className="action-buttons-container">
                                    <button 
                                      className="action-btn" 
                                      onClick={() => {
                                        setMarcacaoToSubmitContract(marcacao.id);
                                        setShowSubmitContractModal(true);
                                      }}
                                      style={{
                                        backgroundColor: '#dc2626'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#b91c1c';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#dc2626';
                                      }}
                                    >
                                      Submeter Contrato
                                    </button>
                                    <button 
                                      className="action-btn btn-revert" 
                                      onClick={() => revertFromResultado(marcacao.id)} 
                                      title="Retroceder para sub-estágio anterior"
                                    >
                                      Retroceder
                                    </button>
                                    <button 
                                      className="action-btn btn-details" 
                                      onClick={() => openDetailsModal(marcacao.id)}
                                    >
                                      Detalhes
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {(() => {
                        const { total, currentPage, totalPages } = getPaginatedSubsection('resultado-fecho-ok', getFechoOk());
                        if (total <= MARCADOES_PER_PAGE) return null;
                        return (
                          <div className="subsection-pagination">
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('resultado-fecho-ok', currentPage - 1)} disabled={currentPage <= 1}>← Anterior</button>
                            <span className="pagination-info">Página {currentPage} de {totalPages}</span>
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('resultado-fecho-ok', currentPage + 1)} disabled={currentPage >= totalPages}>Seguinte →</button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Fecho Not Ok Subsection */}
                <div className="subsection" data-subsection="resultado-fecho-not-ok">
                  <div
                    className="subsection-header" 
                    onClick={() => toggleSubsection('resultado-fecho-not-ok')}
                  >
                    <span className="subsection-icon" style={{ transform: expandedSubsections['resultado-fecho-not-ok'] ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                      ▼
                    </span>
                    <span>Fecho Not Ok ({getFechoNotOk().length})</span>
                  </div>
                  {expandedSubsections['resultado-fecho-not-ok'] && (
                    <div className="subsection-content active">
                      <div className="data-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Data da Marcação</th>
                              <th>Produto</th>
                              <th>Nome da Empresa</th>
                              <th>NIF Empresa</th>
                              <th>Nome do Responsável</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getPaginatedSubsection('resultado-fecho-not-ok', getFechoNotOk()).items.map(marcacao => {
                              const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
                              const isHighlighted = highlightedMarcacaoId === marcacaoIdToUse;
                              return (
                              <tr 
                                key={marcacao.id} 
                                id={`marcacao-${marcacaoIdToUse}`}
                                data-id={marcacao.id}
                                className={isHighlighted ? 'highlighted-row' : ''}
                              >
                                {changeComercialSelectMode && (
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={selectedMarcacoesForChange.has(marcacao.id)}
                                      onChange={() => toggleMarcacaoForChange(marcacao.id)}
                                    />
                                  </td>
                                )}
                                <td>{marcacao.data}</td>
                                <td>{renderProducts(marcacao)}</td>
                                <td>{marcacao.empresa || '-'}</td>
                                <td>{getNifFromProposals(String(marcacaoIdToUse)) || '-'}</td>
                                <td>{marcacao.responsavel}</td>
                                <td>
                                  <div className="action-buttons-container">
                                    <button className="action-btn btn-revert" onClick={() => revertFromResultado(marcacao.id)} title="Retroceder para sub-estágio anterior">
                                      Retroceder
                                    </button>
                                    <button className="action-btn btn-details" onClick={() => openDetailsModal(marcacao.id)}>
                                      Detalhes
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {(() => {
                        const { total, currentPage, totalPages } = getPaginatedSubsection('resultado-fecho-not-ok', getFechoNotOk());
                        if (total <= MARCADOES_PER_PAGE) return null;
                        return (
                          <div className="subsection-pagination">
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('resultado-fecho-not-ok', currentPage - 1)} disabled={currentPage <= 1}>← Anterior</button>
                            <span className="pagination-info">Página {currentPage} de {totalPages}</span>
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('resultado-fecho-not-ok', currentPage + 1)} disabled={currentPage >= totalPages}>Seguinte →</button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Aguardar Subsection */}
                <div className="subsection" data-subsection="resultado-aguardar">
                  <div 
                    className="subsection-header" 
                    onClick={() => toggleSubsection('resultado-aguardar')}
                  >
                    <span className="subsection-icon" style={{ transform: expandedSubsections['resultado-aguardar'] ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                      ▼
                    </span>
                    <span>Aguardar ({getAguardar().length})</span>
                  </div>
                  {expandedSubsections['resultado-aguardar'] && (
                    <div className="subsection-content active">
                      <div className="data-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Data da Marcação</th>
                              <th>Produto</th>
                              <th>Nome da Empresa</th>
                              <th>NIF Empresa</th>
                              <th>Nome do Responsável</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getPaginatedSubsection('resultado-aguardar', getAguardar()).items.map(marcacao => {
                              const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
                              const isHighlighted = highlightedMarcacaoId === marcacaoIdToUse;
                              return (
                              <tr 
                                key={marcacao.id} 
                                id={`marcacao-${marcacaoIdToUse}`}
                                data-id={marcacao.id}
                                className={isHighlighted ? 'highlighted-row' : ''}
                              >
                                {changeComercialSelectMode && (
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={selectedMarcacoesForChange.has(marcacao.id)}
                                      onChange={() => toggleMarcacaoForChange(marcacao.id)}
                                    />
                                  </td>
                                )}
                                <td>{marcacao.data}</td>
                                <td>{renderProducts(marcacao)}</td>
                                <td>{marcacao.empresa || '-'}</td>
                                <td>{getNifFromProposals(String(marcacaoIdToUse)) || '-'}</td>
                                <td>{marcacao.responsavel}</td>
                                <td>
                                  <div className="action-buttons-container">
                                    <button className="action-btn btn-proposal" onClick={() => openProposalModal(marcacao.id)}>
                                      Solicitar Proposta
                                    </button>
                                    <button className="action-btn btn-proposal" onClick={() => openRefazerProposalModal(marcacao.id)}>
                                      Refazer
                                    </button>
                                    {canChangeAguardarSubstage() && (
                                      <button className="action-btn btn-revert" onClick={() => revertFromResultado(marcacao.id)} title="Retroceder para sub-estágio anterior">
                                        Retroceder
                                      </button>
                                    )}
                                    <button className="action-btn btn-details" onClick={() => openDetailsModal(marcacao.id)}>
                                      Detalhes
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {(() => {
                        const { total, currentPage, totalPages } = getPaginatedSubsection('resultado-aguardar', getAguardar());
                        if (total <= MARCADOES_PER_PAGE) return null;
                        return (
                          <div className="subsection-pagination">
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('resultado-aguardar', currentPage - 1)} disabled={currentPage <= 1}>← Anterior</button>
                            <span className="pagination-info">Página {currentPage} de {totalPages}</span>
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('resultado-aguardar', currentPage + 1)} disabled={currentPage >= totalPages}>Seguinte →</button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Cancelado Subsection */}
                <div className="subsection" data-subsection="resultado-cancelado">
                  <div 
                    className="subsection-header" 
                    onClick={() => toggleSubsection('resultado-cancelado')}
                  >
                    <span className="subsection-icon" style={{ transform: expandedSubsections['resultado-cancelado'] ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                      ▼
                    </span>
                    <span>Cancelado ({getCancelado().length})</span>
                  </div>
                  {expandedSubsections['resultado-cancelado'] && (
                    <div className="subsection-content active">
                      <div className="data-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Data da Marcação</th>
                              <th>Produto</th>
                              <th>Nome da Empresa</th>
                              <th>NIF Empresa</th>
                              <th>Nome do Responsável</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getPaginatedSubsection('resultado-cancelado', getCancelado()).items.map(marcacao => {
                              const marcacaoIdToUse = marcacao.marcacao_id || marcacao.id;
                              const isHighlighted = highlightedMarcacaoId === marcacaoIdToUse;
                              return (
                              <tr 
                                key={marcacao.id} 
                                id={`marcacao-${marcacaoIdToUse}`}
                                data-id={marcacao.id}
                                className={isHighlighted ? 'highlighted-row' : ''}
                              >
                                {changeComercialSelectMode && (
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={selectedMarcacoesForChange.has(marcacao.id)}
                                      onChange={() => toggleMarcacaoForChange(marcacao.id)}
                                    />
                                  </td>
                                )}
                                <td>{marcacao.data}</td>
                                <td>{renderProducts(marcacao)}</td>
                                <td>{marcacao.empresa || '-'}</td>
                                <td>{getNifFromProposals(String(marcacaoIdToUse)) || '-'}</td>
                                <td>{marcacao.responsavel}</td>
                                <td>
                                  <div className="action-buttons-container">
                                    <button className="action-btn btn-revert" onClick={() => revertFromResultado(marcacao.id)} title="Retroceder para sub-estágio anterior">
                                      Retroceder
                                    </button>
                                    <button className="action-btn btn-details" onClick={() => openDetailsModal(marcacao.id)}>
                                      Detalhes
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {(() => {
                        const { total, currentPage, totalPages } = getPaginatedSubsection('resultado-cancelado', getCancelado());
                        if (total <= MARCADOES_PER_PAGE) return null;
                        return (
                          <div className="subsection-pagination">
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('resultado-cancelado', currentPage - 1)} disabled={currentPage <= 1}>← Anterior</button>
                            <span className="pagination-info">Página {currentPage} de {totalPages}</span>
                            <button type="button" className="pagination-arrow" onClick={() => setSubsectionPage('resultado-cancelado', currentPage + 1)} disabled={currentPage >= totalPages}>Seguinte →</button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
          </>
        )}
      </div>

      {/* Details Modal: exact same modal as Comercial Calendar (modalOnly + initialEvent) */}
      {showDetailsModal && currentMarcacao && createPortal(
        (() => {
          const formDataRaw = currentMarcacao.form_data || {};
          const productDetails = currentMarcacao.product_details || {};
          const oc = (formDataRaw.original_callcenter || {}) as any;
          const co = formDataRaw.comercial_overrides || {};
          const getVal = (...sources: (string | boolean | undefined)[]) => {
            const v = sources.find(x => x != null && x !== '');
            return v === true || v === false ? String(v) : (v ?? '');
          };
          const fullFormData = {
            ...oc,
            ...formDataRaw,
            ...productDetails,
            ...co,
            comercializadoraBairroSolar: getVal(co.comercializadoraBairroSolar, formDataRaw.comercializadoraBairroSolar, productDetails.comercializadoraBairroSolar, oc.comercializadoraBairroSolar, formDataRaw.comercializadora),
            comercializadoraPAP: getVal(co.comercializadoraPAP, formDataRaw.comercializadoraPAP, productDetails.comercializadoraPAP, oc.comercializadoraPAP, formDataRaw.comercializadora),
            comercializadoraGas: getVal(co.comercializadoraGas, formDataRaw.comercializadoraGas, productDetails.comercializadoraGas, oc.comercializadoraGas, formDataRaw.comercializadora),
            comercializadoraEletricidade: getVal(co.comercializadoraEletricidade, formDataRaw.comercializadoraEletricidade, productDetails.comercializadoraEletricidade, oc.comercializadoraEletricidade, formDataRaw.comercializadora),
            valorFaturaGas: getVal(co.valorFaturaGas, formDataRaw.valorFaturaGas, productDetails.valorFaturaGas, oc.valorFaturaGas),
            valorFaturaEletricidade: getVal(co.valorFaturaEletricidade, formDataRaw.valorFaturaEletricidade, productDetails.valorFaturaEletricidade, oc.valorFaturaEletricidade),
            faturaPresenteGas: getVal(co.faturaPresenteGas, formDataRaw.faturaPresenteGas, productDetails.faturaPresenteGas, oc.faturaPresenteGas),
            faturaPresenteEletricidade: getVal(co.faturaPresenteEletricidade, formDataRaw.faturaPresenteEletricidade, productDetails.faturaPresenteEletricidade, oc.faturaPresenteEletricidade),
            gasNatural: getVal(co.gasNatural, formDataRaw.gasNatural, productDetails.gasNatural, oc.gasNatural),
            nomeEmpresa: co.nomeEmpresa ?? formDataRaw.nomeEmpresa ?? oc.nomeEmpresa ?? currentMarcacao.nome_empresa ?? currentMarcacao.empresa,
            nomeResponsavel: co.nomeResponsavel ?? formDataRaw.nomeResponsavel ?? oc.nomeResponsavel ?? currentMarcacao.nome_responsavel ?? currentMarcacao.cliente,
            morada: co.morada ?? formDataRaw.morada ?? oc.morada ?? currentMarcacao.morada,
            contactoCliente: co.contactoCliente ?? formDataRaw.contactoCliente ?? oc.contactoCliente ?? currentMarcacao.contacto_cliente ?? currentMarcacao.contacto,
            marcacaoDescription: co.marcacaoDescription ?? formDataRaw.marcacaoDescription ?? oc.marcacaoDescription ?? currentMarcacao.marcacao_description ?? currentMarcacao.observacoes,
            horaInicio: formDataRaw.horaInicio || currentMarcacao.hora_inicio || (currentMarcacao.hora ? currentMarcacao.hora.split(' - ')[0] : ''),
            horaFim: formDataRaw.horaFim || currentMarcacao.hora_fim || (currentMarcacao.hora ? currentMarcacao.hora.split(' - ')[1] : ''),
            marcacaoData: formDataRaw.marcacaoData || currentMarcacao.date || currentMarcacao.data,
            source: formDataRaw.source || currentMarcacao.form_data?.source || 'callcenter',
            createdBy: formDataRaw.createdBy || currentMarcacao.created_by || (currentMarcacao as any).created_by,
            marcacaoId: formDataRaw.marcacaoId || formDataRaw.marcacao_id || currentMarcacao.marcacao_id || currentMarcacao.id,
            gestao_substage: formDataRaw.gestao_substage || currentMarcacao.gestao_substage,
            comercial_overrides: co,
            original_callcenter: oc
          };
          const produto = (currentMarcacao.produto?.toLowerCase() || fullFormData.produto || 'N/A').replace(/\s+/g, '-');
          let produtos: string[] = [];
          if (currentMarcacao.produtos && Array.isArray(currentMarcacao.produtos)) {
            produtos = currentMarcacao.produtos.map((p: string) => String(p).toLowerCase().replace(/\s+/g, '-'));
          } else if (fullFormData.produtos && Array.isArray(fullFormData.produtos)) {
            produtos = fullFormData.produtos.map((p: string) => String(p).toLowerCase().replace(/\s+/g, '-'));
          }
          if (produtos.length === 0 && produto && produto.toLowerCase() !== 'n/a') {
            produtos = [produto];
          }
          const formData = { ...fullFormData, produto: produtos[0] || produto, produtos };
          const produtoTitles: Record<string, string> = {
            'bairro-solar': 'Bairro Solar',
            'pap': 'PAP',
            'gas': 'Gás',
            'eletricidade': 'Eletricidade'
          };
          const nomeEmpresa = currentMarcacao.nome_empresa || currentMarcacao.empresa || fullFormData.nomeEmpresa || 'N/A';
          const eventTitle = `${produtoTitles[produto] || produto} - ${nomeEmpresa}`;
          const horaInicio = formData.horaInicio || '';
          const horaFim = formData.horaFim || '';
          let dateObj: Date = new Date();
          if (currentMarcacao.date) {
            dateObj = typeof currentMarcacao.date === 'string' ? new Date(currentMarcacao.date) : new Date((currentMarcacao as any).date);
          } else if (currentMarcacao.data) {
            const s = String(currentMarcacao.data);
            if (s.includes('/')) {
              const [d, m, y] = s.split('/');
              dateObj = new Date(parseInt(y!, 10), parseInt(m!, 10) - 1, parseInt(d!, 10));
            } else {
              dateObj = new Date(s);
            }
          }
          const initialEvent: CalendarEvent = {
            id: currentMarcacao.marcacao_id || currentMarcacao.id,
            title: eventTitle,
            time: horaInicio,
            duration: horaFim,
            location: currentMarcacao.morada,
            status: 'scheduled',
            date: dateObj,
            product: (currentMarcacao.produto?.toLowerCase() || produto) as CalendarEvent['product'],
            type: 'marcacao',
            formData
          };
          const handleClose = () => { closeDetailsModal(); setIsCallCenterInfoCollapsed(false); };
          return (
            <ComercialCalendar
              modalOnly
              initialEvent={initialEvent}
              onCloseModal={handleClose}
            />
          );
        })(),
        document.body
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && currentMarcacaoId && (
        <div className="modal active">
          <div className="modal-content">
            <div className="modal-header reschedule-modal-header">
              <button className="modal-close reschedule-modal-close" onClick={closeRescheduleModal}>×</button>
              <h2 className="modal-title reschedule-modal-title">Remarcar Marcação</h2>
              <p className="modal-subtitle reschedule-modal-subtitle">Alterar data e hora da marcação</p>
            </div>
            <div className="modal-body">
              <form onSubmit={handleReschedule}>
                <div className="form-group">
                  <label className="form-label" htmlFor="rescheduleDate">
                    <span className="required">*</span> Nova Data
                  </label>
                  <input type="date" id="rescheduleDate" className="form-input" required />
                </div>
                
                <div className="form-group">
                  <label className="form-label" htmlFor="rescheduleHoraInicio">
                    <span className="required">*</span> Nova Hora Inicial
                  </label>
                  <input type="time" id="rescheduleHoraInicio" className="form-input" required />
                </div>
                
                <div className="form-group">
                  <label className="form-label" htmlFor="rescheduleHoraFim">
                    <span className="required">*</span> Nova Hora Final
                  </label>
                  <input type="time" id="rescheduleHoraFim" className="form-input" required />
                </div>
                
                <div className="form-group">
                  <label className="form-label" htmlFor="rescheduleReason">
                    Motivo da Remarcação
                  </label>
                  <textarea
                    id="rescheduleReason"
                    className="form-input"
                    placeholder="Explicar o motivo da remarcação..."
                    rows={3}
                    style={{ resize: 'vertical', minHeight: '80px' }}
                  />
                </div>
                
                <div className="form-buttons">
                  <button type="button" className="btn btn-secondary" onClick={closeRescheduleModal}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Remarcar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Update Modal */}
      {showUpdateModal && currentMarcacaoId && (
        <div className="modal active">
          <div className="modal-content">
            <div className="modal-header">
              <button className="modal-close" onClick={closeUpdateModal}>×</button>
              <h2 className="modal-title">Atualizar Marcação</h2>
              <p className="modal-subtitle">Atualizar informações da marcação</p>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => { e.preventDefault(); closeUpdateModal(); }}>
                <div className="form-group">
                  <label className="form-label" htmlFor="updateStatus">
                    Estado
                  </label>
                  <select id="updateStatus" className="form-input">
                    <option value="scheduled">Agendada</option>
                    <option value="in-progress">Em Progresso</option>
                    <option value="completed">Concluída</option>
                    <option value="cancelled">Cancelada</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label" htmlFor="updateComplement">
                    Complemento do Estado
                  </label>
                  <input
                    type="text"
                    id="updateComplement"
                    className="form-input"
                    placeholder="Ex: Aguardando resposta do cliente"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label" htmlFor="updateNotes">
                    Notas
                  </label>
                  <textarea
                    id="updateNotes"
                    className="form-input"
                    placeholder="Adicionar notas sobre a marcação..."
                    rows={4}
                    style={{ resize: 'vertical', minHeight: '100px' }}
                  />
                </div>
                
                <div className="form-buttons">
                  <button type="button" className="btn btn-secondary" onClick={closeUpdateModal}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Atualizar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && currentMarcacao && (
        <div className="modal active">
          <div className="modal-content">
            <div className="modal-header">
              <button className="modal-close" onClick={closeEditModal}>×</button>
              <h2 className="modal-title">Editar Marcação</h2>
              <p className="modal-subtitle">Edite as informações da marcação</p>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => { e.preventDefault(); closeEditModal(); }}>
                <p style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  Funcionalidade de edição será implementada na versão completa do sistema.
                </p>
                <div className="form-buttons">
                  <button type="button" className="btn btn-secondary" onClick={closeEditModal}>
                    Cancelar
                  </button>
                  <button type="button" className="btn btn-primary" onClick={closeEditModal}>
                    Fechar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Change Comercial Modal (Admin/Gerente only) */}
      {showChangeComercialModal && selectedMarcacoesForChange.size > 0 && (() => {
        const firstId = Array.from(selectedMarcacoesForChange)[0];
        const marcacao = marcacoesData[firstId];
        if (!marcacao) return null;
        const currentUserHidden = (currentUserData as any)?.is_hidden ?? false;
        const filteredUsers = currentUserHidden ? comercialUsers : comercialUsers.filter((u: any) => !u.is_hidden);
        return (
          <div
            className="modal active revert-confirm-modal"
            style={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '10px',
              zIndex: 12000
            }}
          >
            <div
              className="modal-content"
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 'min(94vw, 420px)',
                maxWidth: 'min(94vw, 420px)',
                maxHeight: '86vh',
                margin: 0
              }}
            >
              <div className="modal-header" style={{ background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)', color: 'white', padding: '20px', borderRadius: '8px 8px 0 0' }}>
                <button className="modal-close" onClick={closeChangeComercialModal} style={{ color: 'white', background: 'rgba(255, 255, 255, 0.2)' }}>×</button>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>Alterar Comercial Atribuído</h2>
                <p style={{ margin: '8px 0 0 0', fontSize: '14px', opacity: 0.9 }}>
                  {selectedMarcacoesForChange.size} marcação(ões) selecionada(s)
                </p>
              </div>
              <div className="modal-body" style={{ padding: '24px' }}>
                <div className="form-group">
                  <label className="form-label" htmlFor="changeComercialSelect">
                    Novo Comercial Atribuído <span className="required">*</span>
                  </label>
                  <select
                    id="changeComercialSelect"
                    className="form-input"
                    value={changeComercialSelectedUserId}
                    onChange={(e) => setChangeComercialSelectedUserId(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', fontSize: '14px' }}
                  >
                    <option value="">-- Selecione o comercial --</option>
                    {filteredUsers.map((u: any) => (
                      <option key={u.id} value={String(u.id)}>
                        {u.name || u.username || `Utilizador ${u.id}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer" style={{ padding: '20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button className="btn btn-secondary" onClick={closeChangeComercialModal} disabled={savingChangeComercial}>
                  Cancelar
                </button>
                <button className="btn btn-primary" onClick={saveChangeComercial} disabled={savingChangeComercial || !changeComercialSelectedUserId} style={{ background: '#dc2626' }}>
                  {savingChangeComercial ? 'A guardar...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Revert Confirmation Modal */}
      {showRevertConfirm && marcacaoToRevert && (() => {
        const marcacao = marcacoesData[marcacaoToRevert];
        if (!marcacao) return null;
        
        return (
          <div className="modal active">
            <div className="modal-content">
              <div className="modal-body" style={{ position: 'relative', paddingTop: '50px' }}>
                <button className="modal-close" onClick={closeRevertConfirm} style={{ position: 'absolute', top: '20px', right: '20px' }}>×</button>
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  <p style={{ marginBottom: '20px', fontSize: '16px', color: '#374151' }}>
                    A marcação <strong>#{marcacao.id.toUpperCase()}</strong> será movida de volta para a secção <strong>{revertTarget === 'reuniao-realizada' ? 'Reunião Realizada' : 'Agendamentos'}</strong>.
                  </p>
                  <div style={{ background: '#f3f4f6', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <span style={{ fontWeight: '600', color: '#6b7280' }}>Empresa:</span>
                      <span style={{ color: '#1f2937' }}>{marcacao.empresa || 'N/A'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <span style={{ fontWeight: '600', color: '#6b7280' }}>Produto:</span>
                      <span style={{ color: '#1f2937' }}>{marcacao.produto}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: '600', color: '#6b7280' }}>Data:</span>
                      <span style={{ color: '#1f2937' }}>{marcacao.data}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ padding: '20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', gap: '15px' }}>
                <button className="btn btn-secondary" onClick={closeRevertConfirm}>
                  Cancelar
                </button>
                <button className="btn btn-primary" onClick={revertMarcacao} style={{ background: '#6b7280' }}>
                  Confirmar Retrocesso
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Feedback Validation Modal */}
      {showFeedbackValidationModal && (
        <div className="modal active">
          <div className="modal-content">
            <div className="modal-body" style={{ position: 'relative', paddingTop: '50px' }}>
              <button className="modal-close" onClick={() => setShowFeedbackValidationModal(false)} style={{ position: 'absolute', top: '20px', right: '20px' }}>×</button>
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <h2 style={{ marginBottom: '20px', fontSize: '18px', color: '#dc2626', fontWeight: 'bold' }}>
                  Preencha o feedback das Marcações:
                </h2>
                <div style={{ background: '#fef2f2', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #fecaca' }}>
                  <p style={{ marginBottom: '10px', fontSize: '14px', color: '#991b1b', fontWeight: '600' }}>
                    As seguintes marcações não têm feedback preenchido:
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                    {marcacoesWithoutFeedback.map((id, index) => (
                      <span key={index} style={{ 
                        background: '#fee2e2', 
                        color: '#991b1b', 
                        padding: '6px 12px', 
                        borderRadius: '6px', 
                        fontSize: '13px',
                        fontWeight: '600',
                        border: '1px solid #fecaca'
                      }}>
                        {id}
                      </span>
                    ))}
                  </div>
                </div>
                <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
                  Por favor, preencha o feedback para todas as marcações antes de continuar.
                </p>
              </div>
            </div>
            <div className="modal-footer" style={{ padding: '20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" onClick={() => setShowFeedbackValidationModal(false)}>
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Not Ok CallCenter Feedback Modal */}
      {showNotOkCallCenterFeedbackModal && notOkCallCenterFeedbackMarcacaoId && (() => {
        const marcacao = marcacoesData[notOkCallCenterFeedbackMarcacaoId];
        if (!marcacao) return null;
        
        return (
          <div className="modal active">
            <div className="modal-content" style={{ maxWidth: '600px', width: '90%' }}>
              <div className="modal-header" style={{ background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)', color: 'white', padding: '20px', borderRadius: '8px 8px 0 0' }}>
                <button className="modal-close" onClick={closeNotOkCallCenterFeedbackModal} style={{ color: 'white', background: 'rgba(255, 255, 255, 0.2)' }}>×</button>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>Feedback - Not Ok CallCenter</h2>
                <p style={{ margin: '8px 0 0 0', fontSize: '14px', opacity: 0.9 }}>
                  Marcação: #{marcacao.id.toUpperCase()}
                </p>
              </div>
              <div className="modal-body" style={{ padding: '30px' }}>
                <form onSubmit={(e) => { e.preventDefault(); saveNotOkCallCenterFeedback(); }}>
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: '16px', fontWeight: '600', marginBottom: '15px', display: 'block' }}>
                      O que estava mal na marcação?
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {/* Data */}
                      <div style={{ padding: '10px', borderRadius: '6px', border: '1px solid #e5e7eb', backgroundColor: notOkCallCenterFeedbackOptions.data.selected ? '#f9fafb' : 'transparent' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: notOkCallCenterFeedbackOptions.data.selected ? '8px' : '0' }}>
                          <input
                            type="checkbox"
                            checked={notOkCallCenterFeedbackOptions.data.selected}
                            onChange={(e) => setNotOkCallCenterFeedbackOptions(prev => ({
                              ...prev,
                              data: { selected: e.target.checked, text: e.target.checked ? prev.data.text : '' }
                            }))}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>Data <span style={{ color: '#ef4444' }}>*</span></span>
                        </label>
                        {notOkCallCenterFeedbackOptions.data.selected && (
                          <textarea
                            value={notOkCallCenterFeedbackOptions.data.text}
                            onChange={(e) => setNotOkCallCenterFeedbackOptions(prev => ({
                              ...prev,
                              data: { selected: true, text: e.target.value }
                            }))}
                            placeholder="Descreva o problema com a data..."
                            required
                            style={{
                              width: '100%',
                              minHeight: '80px',
                              padding: '10px',
                              borderRadius: '6px',
                              border: '1px solid #d1d5db',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              resize: 'vertical',
                              marginTop: '8px'
                            }}
                          />
                        )}
                      </div>
                      
                      {/* Horário */}
                      <div style={{ padding: '10px', borderRadius: '6px', border: '1px solid #e5e7eb', backgroundColor: notOkCallCenterFeedbackOptions.horario.selected ? '#f9fafb' : 'transparent' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: notOkCallCenterFeedbackOptions.horario.selected ? '8px' : '0' }}>
                          <input
                            type="checkbox"
                            checked={notOkCallCenterFeedbackOptions.horario.selected}
                            onChange={(e) => setNotOkCallCenterFeedbackOptions(prev => ({
                              ...prev,
                              horario: { selected: e.target.checked, text: e.target.checked ? prev.horario.text : '' }
                            }))}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>Horário <span style={{ color: '#ef4444' }}>*</span></span>
                        </label>
                        {notOkCallCenterFeedbackOptions.horario.selected && (
                          <textarea
                            value={notOkCallCenterFeedbackOptions.horario.text}
                            onChange={(e) => setNotOkCallCenterFeedbackOptions(prev => ({
                              ...prev,
                              horario: { selected: true, text: e.target.value }
                            }))}
                            placeholder="Descreva o problema com o horário..."
                            required
                            style={{
                              width: '100%',
                              minHeight: '80px',
                              padding: '10px',
                              borderRadius: '6px',
                              border: '1px solid #d1d5db',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              resize: 'vertical',
                              marginTop: '8px'
                            }}
                          />
                        )}
                      </div>
                      
                      {/* Contacto */}
                      <div style={{ padding: '10px', borderRadius: '6px', border: '1px solid #e5e7eb', backgroundColor: notOkCallCenterFeedbackOptions.contacto.selected ? '#f9fafb' : 'transparent' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: notOkCallCenterFeedbackOptions.contacto.selected ? '8px' : '0' }}>
                          <input
                            type="checkbox"
                            checked={notOkCallCenterFeedbackOptions.contacto.selected}
                            onChange={(e) => setNotOkCallCenterFeedbackOptions(prev => ({
                              ...prev,
                              contacto: { selected: e.target.checked, text: e.target.checked ? prev.contacto.text : '' }
                            }))}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>Contacto <span style={{ color: '#ef4444' }}>*</span></span>
                        </label>
                        {notOkCallCenterFeedbackOptions.contacto.selected && (
                          <textarea
                            value={notOkCallCenterFeedbackOptions.contacto.text}
                            onChange={(e) => setNotOkCallCenterFeedbackOptions(prev => ({
                              ...prev,
                              contacto: { selected: true, text: e.target.value }
                            }))}
                            placeholder="Descreva o problema com o contacto..."
                            required
                            style={{
                              width: '100%',
                              minHeight: '80px',
                              padding: '10px',
                              borderRadius: '6px',
                              border: '1px solid #d1d5db',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              resize: 'vertical',
                              marginTop: '8px'
                            }}
                          />
                        )}
                      </div>
                      
                      {/* Link Google Maps */}
                      <div style={{ padding: '10px', borderRadius: '6px', border: '1px solid #e5e7eb', backgroundColor: notOkCallCenterFeedbackOptions.linkGoogleMaps.selected ? '#f9fafb' : 'transparent' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: notOkCallCenterFeedbackOptions.linkGoogleMaps.selected ? '8px' : '0' }}>
                          <input
                            type="checkbox"
                            checked={notOkCallCenterFeedbackOptions.linkGoogleMaps.selected}
                            onChange={(e) => setNotOkCallCenterFeedbackOptions(prev => ({
                              ...prev,
                              linkGoogleMaps: { selected: e.target.checked, text: e.target.checked ? prev.linkGoogleMaps.text : '' }
                            }))}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>Link Google Maps <span style={{ color: '#ef4444' }}>*</span></span>
                        </label>
                        {notOkCallCenterFeedbackOptions.linkGoogleMaps.selected && (
                          <textarea
                            value={notOkCallCenterFeedbackOptions.linkGoogleMaps.text}
                            onChange={(e) => setNotOkCallCenterFeedbackOptions(prev => ({
                              ...prev,
                              linkGoogleMaps: { selected: true, text: e.target.value }
                            }))}
                            placeholder="Descreva o problema com o Link Google Maps..."
                            required
                            style={{
                              width: '100%',
                              minHeight: '80px',
                              padding: '10px',
                              borderRadius: '6px',
                              border: '1px solid #d1d5db',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              resize: 'vertical',
                              marginTop: '8px'
                            }}
                          />
                        )}
                      </div>
                      
                      {/* Informações do CallCenter */}
                      <div style={{ padding: '10px', borderRadius: '6px', border: '1px solid #e5e7eb', backgroundColor: notOkCallCenterFeedbackOptions.informacoesCallCenter.selected ? '#f9fafb' : 'transparent' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: notOkCallCenterFeedbackOptions.informacoesCallCenter.selected ? '8px' : '0' }}>
                          <input
                            type="checkbox"
                            checked={notOkCallCenterFeedbackOptions.informacoesCallCenter.selected}
                            onChange={(e) => setNotOkCallCenterFeedbackOptions(prev => ({
                              ...prev,
                              informacoesCallCenter: { selected: e.target.checked, text: e.target.checked ? prev.informacoesCallCenter.text : '' }
                            }))}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>Informações do CallCenter <span style={{ color: '#ef4444' }}>*</span></span>
                        </label>
                        {notOkCallCenterFeedbackOptions.informacoesCallCenter.selected && (
                          <textarea
                            value={notOkCallCenterFeedbackOptions.informacoesCallCenter.text}
                            onChange={(e) => setNotOkCallCenterFeedbackOptions(prev => ({
                              ...prev,
                              informacoesCallCenter: { selected: true, text: e.target.value }
                            }))}
                            placeholder="Descreva o problema com as informações do CallCenter..."
                            required
                            style={{
                              width: '100%',
                              minHeight: '80px',
                              padding: '10px',
                              borderRadius: '6px',
                              border: '1px solid #d1d5db',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              resize: 'vertical',
                              marginTop: '8px'
                            }}
                          />
                        )}
                      </div>
                      
                      {/* Outro */}
                      <div style={{ padding: '10px', borderRadius: '6px', border: '1px solid #e5e7eb', backgroundColor: notOkCallCenterFeedbackOptions.outro.selected ? '#f9fafb' : 'transparent' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: notOkCallCenterFeedbackOptions.outro.selected ? '8px' : '0' }}>
                          <input
                            type="checkbox"
                            checked={notOkCallCenterFeedbackOptions.outro.selected}
                            onChange={(e) => setNotOkCallCenterFeedbackOptions(prev => ({
                              ...prev,
                              outro: { selected: e.target.checked, text: e.target.checked ? prev.outro.text : '' }
                            }))}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>Outro: <span style={{ color: '#ef4444' }}>*</span></span>
                        </label>
                        {notOkCallCenterFeedbackOptions.outro.selected && (
                          <textarea
                            value={notOkCallCenterFeedbackOptions.outro.text}
                            onChange={(e) => setNotOkCallCenterFeedbackOptions(prev => ({
                              ...prev,
                              outro: { selected: true, text: e.target.value }
                            }))}
                            placeholder="Descreva o problema..."
                            required
                            style={{
                              width: '100%',
                              minHeight: '80px',
                              padding: '10px',
                              borderRadius: '6px',
                              border: '1px solid #d1d5db',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              resize: 'vertical',
                              marginTop: '8px'
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="modal-footer" style={{ padding: '20px 0 0 0', borderTop: '1px solid #e5e7eb', marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    <button type="button" className="btn btn-secondary" onClick={closeNotOkCallCenterFeedbackModal}>
                      Cancelar
                    </button>
                    <button type="submit" className="btn btn-primary" style={{ background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)' }}>
                      Salvar Feedback
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Submit Contract Modal */}
      {showSubmitContractModal && marcacaoToSubmitContract && (() => {
        const marcacao =
          marcacoesData[marcacaoToSubmitContract] ||
          Object.values(marcacoesData).find(
            (m) =>
              String(m.marcacao_id || '').trim() === String(marcacaoToSubmitContract).trim() ||
              String(m.id || '').trim() === String(marcacaoToSubmitContract).trim()
          );
        if (!marcacao) return null;
        
        return (
          <div
            className="modal active submit-contract-selector-modal"
            style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            onMouseDown={swallowModalBackdropPointer}
          >
            <div className="modal-content" style={{ 
              maxWidth: '600px', 
              width: '90%', 
              borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              overflow: 'hidden',
              animation: 'slideInUp 0.3s ease-out'
            }} onMouseDown={(e) => e.stopPropagation()}>
              <div className="submit-contract-modal-header" style={{ 
                backgroundColor: '#dc2626', 
                background: '#dc2626', 
                color: 'white', 
                padding: '28px 30px', 
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderRadius: '16px 16px 0 0'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-50%',
                  right: '-50%',
                  width: '200%',
                  height: '200%',
                  background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
                  pointerEvents: 'none'
                }}></div>
                <h2 style={{ 
                  margin: 0, 
                  fontSize: '24px', 
                  fontWeight: '700',
                  color: 'white',
                  position: 'relative',
                  zIndex: 1
                }}>Submeter Contrato</h2>
                <button 
                  className="modal-close" 
                  onClick={() => {
                    setShowSubmitContractModal(false);
                    setMarcacaoToSubmitContract(null);
                    if (embeddedContractOnly) onEmbeddedContractClose?.();
                  }}
                  style={{ 
                    color: 'white', 
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    transition: 'all 0.2s',
                    position: 'relative',
                    zIndex: 1
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >×</button>
              </div>
              <div className="modal-body" style={{ 
                padding: '40px 30px',
                background: 'linear-gradient(to bottom, #ffffff 0%, #f9fafb 100%)'
              }}>
                <div style={{ 
                  textAlign: 'center', 
                  marginBottom: '32px'
                }}>
                  <p style={{ 
                    fontSize: '18px', 
                    color: '#1f2937', 
                    fontWeight: '600',
                    margin: 0
                  }}>
                    Selecione o tipo de contratação
                  </p>
                </div>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '16px'
                }}>
                  <button
                    onClick={() => {
                      setShowSubmitContractModal(false);
                      setShowSolarContractForm(true);
                    }}
                    style={{
                      padding: '20px 24px',
                      background: 'linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)',
                      color: '#dc2626',
                      border: '2px solid #dc2626',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      textAlign: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(220, 38, 38, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)';
                      e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(220, 38, 38, 0.25)';
                      e.currentTarget.style.borderColor = '#b91c1c';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)';
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.1)';
                      e.currentTarget.style.borderColor = '#dc2626';
                    }}
                  >
                    <span style={{ 
                      display: 'inline-block',
                      fontSize: '20px',
                      marginRight: '10px',
                      verticalAlign: 'middle'
                    }}>☀️</span>
                    Contratação Solar
                  </button>
                  <button
                    onClick={() => {
                      setShowSubmitContractModal(false);
                      setShowEnergyGasContractForm(true);
                    }}
                    style={{
                      padding: '20px 24px',
                      background: 'linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)',
                      color: '#dc2626',
                      border: '2px solid #dc2626',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      textAlign: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(220, 38, 38, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)';
                      e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(220, 38, 38, 0.25)';
                      e.currentTarget.style.borderColor = '#b91c1c';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)';
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.1)';
                      e.currentTarget.style.borderColor = '#dc2626';
                    }}
                  >
                    <span style={{ 
                      display: 'inline-block',
                      fontSize: '20px',
                      marginRight: '10px',
                      verticalAlign: 'middle'
                    }}>⚡</span>
                    Contratação de Energia e Gás Natural
                  </button>
                </div>
              </div>
              <div className="modal-footer" style={{ 
                padding: '20px 30px', 
                borderTop: '1px solid #e5e7eb', 
                display: 'flex', 
                justifyContent: 'flex-end',
                background: '#f9fafb'
              }}>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowSubmitContractModal(false);
                    setMarcacaoToSubmitContract(null);
                    if (embeddedContractOnly) onEmbeddedContractClose?.();
                  }}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    background: 'white',
                    color: '#6b7280',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f3f4f6';
                    e.currentTarget.style.borderColor = '#9ca3af';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Solar Contract Form Modal */}
      {showSolarContractForm && marcacaoToSubmitContract && (
        <div
          className="modal active submit-contract-solar-modal"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onMouseDown={swallowModalBackdropPointer}
        >
          <div className="modal-content" style={{ 
            maxWidth: '900px', 
            width: '90%', 
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }} onMouseDown={(e) => e.stopPropagation()}>
            <div className="submit-contract-modal-header">
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: 'white', position: 'relative', zIndex: 1 }}>Contratação Solar</h2>
              <button 
                className="modal-close" 
                onClick={() => {
                  setShowSolarContractForm(false);
                  setMarcacaoToSubmitContract(null);
                  if (embeddedContractOnly) onEmbeddedContractClose?.();
                  setSolarContractData({
                    urgente: false,
                    tipoServico: '',
                    numeroPaineis: '',
                    nomeEmpresa: '',
                    moradaInstalacao: '',
                    distrito: '',
                    nifEmpresa: '',
                    emailEmpresa: '',
                    nomeRepresentante: '',
                    nifRepresentanteLegal: '',
                    contactoResponsavel: '',
                    emailRepresentanteLegal: '',
                    cpe: '',
                    iban: '',
                    potenciaContratada: '',
                    idPropostaS2C: '',
                    valorProposta: '',
                    codigoCertidaoPermanente: '',
                    nomeComercial: '',
                    codigoComercial: '',
                    vyn: '',
                    documentos: {
                      contratoAssinado: [],
                      contratoAssinadoDigital: false,
                      teaserS2C: [],
                      cc: [],
                      crcOuInicioAtividade: [],
                      fatura: [],
                      documentosAdicionais: []
                    }
                  });
                  setSolarContractFormErrors({});
                }} 
                style={{ 
                  color: 'white', 
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  transition: 'all 0.2s',
                  position: 'relative',
                  zIndex: 1
                }}
              >×</button>
            </div>
            
            <div className="modal-body" style={{ 
              padding: '30px', 
              flex: '1 1 auto',
              overflowY: 'auto',
              minHeight: 0
            }}>
              {/* Associar Proposta(s) - similar to Assinatura Digital */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                  Associar Proposta(s) ao contrato
                </label>
                <p style={{ fontSize: '11px', color: '#6b7280', marginBottom: '8px' }}>
                  Apenas propostas concluídas pelo BackOffice são listadas. Selecione uma ou mais para preencher automaticamente os dados do contrato.
                  {embeddedContractOnly
                    ? ' Pedidos originados no CCC estão identificados com etiqueta.'
                    : ''}
                </p>
                <input
                  type="text"
                  className="comercial-signature-input"
                  placeholder="Pesquisar por NIF, empresa, ID ou origem CCC..."
                  value={contractProposalSearchQuery}
                  onChange={(e) => setContractProposalSearchQuery(e.target.value)}
                  style={{ maxWidth: '400px', marginBottom: '8px' }}
                />
                <div className="comercial-signature-associate-list" style={{ maxHeight: '160px' }}>
                  {contractProposalsForMarcacao
                    .filter((p) => contractProposalMatchesSearch(p, contractProposalSearchQuery))
                    .map((p) => {
                      const isSelected = contractSelectedProposalIds.includes(p.id);
                      return (
                        <ContractProposalAssociateItem
                          key={p.id}
                          p={p}
                          isSelected={isSelected}
                          onToggle={() => {
                            setContractSelectedProposalIds((prev) =>
                              prev.includes(p.id) ? prev.filter((id) => id !== p.id) : [...prev, p.id]
                            );
                          }}
                        />
                      );
                    })}
                  {contractProposalsForMarcacao.length === 0 && (
                    <div style={{ padding: '16px', color: '#6b7280', fontSize: '13px' }}>
                      Nenhuma proposta concluída (BackOffice) associada a esta marcação.
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                {/* Tipo de Serviço */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span> Tipo de Serviço:
                  </label>
                  <select
                    className={`form-input ${solarContractFormErrors['tipoServico'] ? 'error' : ''}`}
                    value={solarContractData.tipoServico}
                    onChange={(e) => handleSolarContractFieldChange('tipoServico', e.target.value)}
                    data-field="tipoServico"
                    required
                  >
                    <option value="">Selecione...</option>
                    <option value="BS">BS (Bairro Solar)</option>
                    <option value="VP">VP (Venda a Prestações)</option>
                    <option value="PP">PP (Pronto Pagamento)</option>
                    <option value="PAP">PAP (Pay as Produced)</option>
                  </select>
                  {solarContractFormErrors['tipoServico'] && (
                    <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {solarContractFormErrors['tipoServico']}
                    </span>
                  )}
                </div>

                {/* Número de Painéis */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span> Número de Painéis:
                  </label>
                  <input
                    type="number"
                    className={`form-input ${solarContractFormErrors['numeroPaineis'] ? 'error' : ''}`}
                    value={solarContractData.numeroPaineis}
                    onChange={(e) => handleSolarContractFieldChange('numeroPaineis', e.target.value)}
                    placeholder="Ex: 10"
                    data-field="numeroPaineis"
                    required
                  />
                  {solarContractFormErrors['numeroPaineis'] && (
                    <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {solarContractFormErrors['numeroPaineis']}
                    </span>
                  )}
                </div>

                {/* Nome Empresa */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span> Nome Empresa:
                  </label>
                  <input
                    type="text"
                    className={`form-input ${solarContractFormErrors['nomeEmpresa'] ? 'error' : ''}`}
                    value={solarContractData.nomeEmpresa}
                    onChange={(e) => handleSolarContractFieldChange('nomeEmpresa', e.target.value)}
                    placeholder="Ex: Empresa Exemplo Lda"
                    data-field="nomeEmpresa"
                    required
                  />
                  {solarContractFormErrors['nomeEmpresa'] && (
                    <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {solarContractFormErrors['nomeEmpresa']}
                    </span>
                  )}
                </div>

                {/* Morada da instalação */}
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">
                    <span className="required">*</span> Morada da instalação:
                  </label>
                  <input
                    type="text"
                    className={`form-input ${solarContractFormErrors['moradaInstalacao'] ? 'error' : ''}`}
                    value={solarContractData.moradaInstalacao}
                    onChange={(e) => handleSolarContractFieldChange('moradaInstalacao', e.target.value)}
                    placeholder="Ex: Rua Exemplo, 123"
                    data-field="moradaInstalacao"
                    required
                  />
                  {solarContractFormErrors['moradaInstalacao'] && (
                    <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {solarContractFormErrors['moradaInstalacao']}
                    </span>
                  )}
                </div>

                {/* Distrito */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span> Distrito:
                  </label>
                  <input
                    type="text"
                    className={`form-input ${solarContractFormErrors['distrito'] ? 'error' : ''}`}
                    value={solarContractData.distrito}
                    onChange={(e) => handleSolarContractFieldChange('distrito', e.target.value)}
                    placeholder="Ex: Lisboa"
                    data-field="distrito"
                    required
                  />
                  {solarContractFormErrors['distrito'] && (
                    <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {solarContractFormErrors['distrito']}
                    </span>
                  )}
                </div>

                {/* NIF empresa */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span> NIF empresa:
                  </label>
                  <input
                    type="text"
                    className={`form-input ${solarContractFormErrors['nifEmpresa'] ? 'error' : ''}`}
                    value={solarContractData.nifEmpresa}
                    onChange={(e) => handleSolarContractFieldChange('nifEmpresa', e.target.value)}
                    placeholder="Ex: 123456789"
                    data-field="nifEmpresa"
                    required
                  />
                  {solarContractFormErrors['nifEmpresa'] && (
                    <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {solarContractFormErrors['nifEmpresa']}
                    </span>
                  )}
                </div>

                {/* Email da empresa */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span> Email da empresa:
                  </label>
                  <input
                    type="email"
                    className={`form-input ${solarContractFormErrors['emailEmpresa'] ? 'error' : ''}`}
                    value={solarContractData.emailEmpresa}
                    onChange={(e) => handleSolarContractFieldChange('emailEmpresa', e.target.value)}
                    placeholder="Ex: empresa@exemplo.pt"
                    data-field="emailEmpresa"
                    required
                  />
                  {solarContractFormErrors['emailEmpresa'] && (
                    <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {solarContractFormErrors['emailEmpresa']}
                    </span>
                  )}
                </div>

                {/* Nome Representante */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span> Nome Representante:
                  </label>
                  <input
                    type="text"
                    className={`form-input ${solarContractFormErrors['nomeRepresentante'] ? 'error' : ''}`}
                    value={solarContractData.nomeRepresentante}
                    onChange={(e) => handleSolarContractFieldChange('nomeRepresentante', e.target.value)}
                    placeholder="Ex: João Silva"
                    data-field="nomeRepresentante"
                    required
                  />
                  {solarContractFormErrors['nomeRepresentante'] && (
                    <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {solarContractFormErrors['nomeRepresentante']}
                    </span>
                  )}
                </div>

                {/* NIF Representante Legal */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span> NIF Representante Legal:
                  </label>
                  <input
                    type="text"
                    className={`form-input ${solarContractFormErrors['nifRepresentanteLegal'] ? 'error' : ''}`}
                    value={solarContractData.nifRepresentanteLegal}
                    onChange={(e) => handleSolarContractFieldChange('nifRepresentanteLegal', e.target.value)}
                    placeholder="Ex: 987654321"
                    data-field="nifRepresentanteLegal"
                    required
                  />
                  {solarContractFormErrors['nifRepresentanteLegal'] && (
                    <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {solarContractFormErrors['nifRepresentanteLegal']}
                    </span>
                  )}
                </div>

                {/* Contacto Responsável */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span> Contacto Responsável:
                  </label>
                  <input
                    type="text"
                    className={`form-input ${solarContractFormErrors['contactoResponsavel'] ? 'error' : ''}`}
                    value={solarContractData.contactoResponsavel}
                    onChange={(e) => handleSolarContractFieldChange('contactoResponsavel', e.target.value)}
                    placeholder="Ex: 912345678"
                    data-field="contactoResponsavel"
                    required
                  />
                  {solarContractFormErrors['contactoResponsavel'] && (
                    <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {solarContractFormErrors['contactoResponsavel']}
                    </span>
                  )}
                </div>

                {/* Email Representante legal */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span> Email Representante legal:
                  </label>
                  <input
                    type="email"
                    className={`form-input ${solarContractFormErrors['emailRepresentanteLegal'] ? 'error' : ''}`}
                    value={solarContractData.emailRepresentanteLegal}
                    onChange={(e) => handleSolarContractFieldChange('emailRepresentanteLegal', e.target.value)}
                    placeholder="Ex: representante@exemplo.pt"
                    data-field="emailRepresentanteLegal"
                    required
                  />
                  {solarContractFormErrors['emailRepresentanteLegal'] && (
                    <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {solarContractFormErrors['emailRepresentanteLegal']}
                    </span>
                  )}
                </div>

                {/* CPE */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span> CPE:
                  </label>
                  <input
                    type="text"
                    className={`form-input ${solarContractFormErrors['cpe'] ? 'error' : ''}`}
                    value={solarContractData.cpe}
                    onChange={handleSolarCpeInput}
                    onPaste={handleSolarCpePaste}
                    placeholder="PT + 16 dígitos + 2 letras (ex: PT1234567890123456AB)"
                    maxLength={20}
                    data-field="cpe"
                    required
                  />
                  {solarContractFormErrors['cpe'] && (
                    <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {solarContractFormErrors['cpe']}
                    </span>
                  )}
                </div>

                {/* IBAN */}
                <div className="form-group">
                  <label className="form-label">
                    IBAN:
                  </label>
                  <input
                    type="text"
                    className={`form-input ${solarContractFormErrors['iban'] ? 'error' : ''}`}
                    value={solarContractData.iban}
                    onChange={(e) => handleSolarContractFieldChange('iban', e.target.value)}
                    placeholder="PT50 + 21 dígitos (ex: PT501234567890123456789)"
                    data-field="iban"
                  />
                  {solarContractFormErrors['iban'] && (
                    <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {solarContractFormErrors['iban']}
                    </span>
                  )}
                </div>

                {/* Potência Contratada */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span> Potência Contratada:
                  </label>
                  <input
                    type="text"
                    className={`form-input ${solarContractFormErrors['potenciaContratada'] ? 'error' : ''}`}
                    value={solarContractData.potenciaContratada}
                    onChange={(e) => handleSolarContractFieldChange('potenciaContratada', e.target.value)}
                    placeholder="Ex: 5.5 kW"
                    data-field="potenciaContratada"
                    required
                  />
                  {solarContractFormErrors['potenciaContratada'] && (
                    <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {solarContractFormErrors['potenciaContratada']}
                    </span>
                  )}
                </div>

                {/* ID Proposta S2C */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span> ID Proposta S2C:
                  </label>
                  <input
                    type="text"
                    className={`form-input ${solarContractFormErrors['idPropostaS2C'] ? 'error' : ''}`}
                    value={solarContractData.idPropostaS2C}
                    onChange={(e) => handleSolarContractFieldChange('idPropostaS2C', e.target.value)}
                    placeholder="Ex: S2C-12345"
                    data-field="idPropostaS2C"
                    required
                  />
                  {solarContractFormErrors['idPropostaS2C'] && (
                    <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {solarContractFormErrors['idPropostaS2C']}
                    </span>
                  )}
                </div>

                {/* Valor da proposta */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span> Valor da proposta:
                  </label>
                  <input
                    type="text"
                    className={`form-input ${solarContractFormErrors['valorProposta'] ? 'error' : ''}`}
                    value={solarContractData.valorProposta}
                    onChange={(e) => handleSolarContractFieldChange('valorProposta', e.target.value)}
                    placeholder="Ex: 15000.00"
                    data-field="valorProposta"
                    required
                  />
                  {solarContractFormErrors['valorProposta'] && (
                    <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {solarContractFormErrors['valorProposta']}
                    </span>
                  )}
                </div>

                {/* Código Certidão Permanente */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span> Código Certidão Permanente:
                  </label>
                  <input
                    type="text"
                    className={`form-input ${solarContractFormErrors['codigoCertidaoPermanente'] ? 'error' : ''}`}
                    value={solarContractData.codigoCertidaoPermanente}
                    onChange={(e) => handleSolarContractFieldChange('codigoCertidaoPermanente', e.target.value)}
                    placeholder="Ex: CERT-12345"
                    data-field="codigoCertidaoPermanente"
                    required
                  />
                  {solarContractFormErrors['codigoCertidaoPermanente'] && (
                    <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {solarContractFormErrors['codigoCertidaoPermanente']}
                    </span>
                  )}
                </div>

                {/* Nome comercial - Hidden field, auto-filled from current user */}
                <input
                  type="hidden"
                  value={solarContractData.nomeComercial}
                  data-field="nomeComercial"
                />

                {/* Código Comercial - Hidden field, auto-filled from current user */}
                <input
                  type="hidden"
                  value={solarContractData.codigoComercial}
                  data-field="codigoComercial"
                />

                {/* VYN */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span> VYN:
                  </label>
                  <select
                    className={`form-input ${solarContractFormErrors['vyn'] ? 'error' : ''}`}
                    value={solarContractData.vyn}
                    onChange={(e) => handleSolarContractFieldChange('vyn', e.target.value)}
                    data-field="vyn"
                    required
                  >
                    <option value="">Selecione...</option>
                    <option value="SIM">SIM</option>
                    <option value="NÃO">NÃO</option>
                  </select>
                  {solarContractFormErrors['vyn'] && (
                    <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {solarContractFormErrors['vyn']}
                    </span>
                  )}
                </div>
              </div>

              {/* Documentos Section */}
              <div style={{ marginTop: '30px' }}>
                <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: '#333' }}>Documentos:</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  {/* Contrato assinado */}
                  <div className="form-group">
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      {!solarContractData.documentos.contratoAssinadoDigital && <span className="required">*</span>}
                      Contrato assinado:
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'normal', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={!!solarContractData.documentos.contratoAssinadoDigital}
                          onChange={() => setSolarContractData((prev: any) => ({ ...prev, documentos: { ...prev.documentos, contratoAssinadoDigital: !prev.documentos.contratoAssinadoDigital } }))}
                        />
                        Digital
                      </label>
                    </label>
                    <input
                      type="file"
                      multiple
                      className={`form-input ${solarContractFormErrors['documentos.contratoAssinado'] ? 'error' : ''}`}
                      accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                      onChange={(e) => handleSolarContractFileChange('contratoAssinado', e.target.files)}
                      data-field="documentos.contratoAssinado"
                      required={!solarContractData.documentos.contratoAssinadoDigital}
                    />
                    {Array.isArray(solarContractData.documentos.contratoAssinado) && solarContractData.documentos.contratoAssinado.length > 0 && (
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {solarContractData.documentos.contratoAssinado.map((file, index) => (
                          <div key={index} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '6px 10px',
                            background: '#f3f4f6',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            <span style={{ color: '#666', flex: 1 }}>{file.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveSolarFile('contratoAssinado', index)}
                              style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '2px 8px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                marginLeft: '8px'
                              }}
                              title="Remover ficheiro"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {solarContractFormErrors['documentos.contratoAssinado'] && (
                      <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        {solarContractFormErrors['documentos.contratoAssinado']}
                      </span>
                    )}
                  </div>

                  {/* Teaser S2C */}
                  <div className="form-group">
                    <label className="form-label">
                      <span className="required">*</span> Teaser S2C:
                    </label>
                    <input
                      type="file"
                      multiple
                      className={`form-input ${solarContractFormErrors['documentos.teaserS2C'] ? 'error' : ''}`}
                      accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                      onChange={(e) => handleSolarContractFileChange('teaserS2C', e.target.files)}
                      data-field="documentos.teaserS2C"
                      required
                    />
                    {Array.isArray(solarContractData.documentos.teaserS2C) && solarContractData.documentos.teaserS2C.length > 0 && (
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {solarContractData.documentos.teaserS2C.map((file, index) => (
                          <div key={index} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '6px 10px',
                            background: '#f3f4f6',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            <span style={{ color: '#666', flex: 1 }}>{file.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveSolarFile('teaserS2C', index)}
                              style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '2px 8px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                marginLeft: '8px'
                              }}
                              title="Remover ficheiro"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {solarContractFormErrors['documentos.teaserS2C'] && (
                      <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        {solarContractFormErrors['documentos.teaserS2C']}
                      </span>
                    )}
                  </div>

                  {/* CC */}
                  <div className="form-group">
                    <label className="form-label">
                      <span className="required">*</span> CC:
                    </label>
                    <input
                      type="file"
                      multiple
                      className={`form-input ${solarContractFormErrors['documentos.cc'] ? 'error' : ''}`}
                      accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                      onChange={(e) => handleSolarContractFileChange('cc', e.target.files)}
                      data-field="documentos.cc"
                      required
                    />
                    {Array.isArray(solarContractData.documentos.cc) && solarContractData.documentos.cc.length > 0 && (
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {solarContractData.documentos.cc.map((file, index) => (
                          <div key={index} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '6px 10px',
                            background: '#f3f4f6',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            <span style={{ color: '#666', flex: 1 }}>{file.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveSolarFile('cc', index)}
                              style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '2px 8px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                marginLeft: '8px'
                              }}
                              title="Remover ficheiro"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {solarContractFormErrors['documentos.cc'] && (
                      <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        {solarContractFormErrors['documentos.cc']}
                      </span>
                    )}
                  </div>

                  {/* CRC OU Inicio de atividade */}
                  <div className="form-group">
                    <label className="form-label">
                      <span className="required">*</span> CRC OU Inicio de atividade:
                    </label>
                    <input
                      type="file"
                      multiple
                      className={`form-input ${solarContractFormErrors['documentos.crcOuInicioAtividade'] ? 'error' : ''}`}
                      accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                      onChange={(e) => handleSolarContractFileChange('crcOuInicioAtividade', e.target.files)}
                      data-field="documentos.crcOuInicioAtividade"
                      required
                    />
                    {Array.isArray(solarContractData.documentos.crcOuInicioAtividade) && solarContractData.documentos.crcOuInicioAtividade.length > 0 && (
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {solarContractData.documentos.crcOuInicioAtividade.map((file, index) => (
                          <div key={index} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '6px 10px',
                            background: '#f3f4f6',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            <span style={{ color: '#666', flex: 1 }}>{file.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveSolarFile('crcOuInicioAtividade', index)}
                              style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '2px 8px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                marginLeft: '8px'
                              }}
                              title="Remover ficheiro"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {solarContractFormErrors['documentos.crcOuInicioAtividade'] && (
                      <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        {solarContractFormErrors['documentos.crcOuInicioAtividade']}
                      </span>
                    )}
                  </div>

                  {/* Fatura */}
                  <div className="form-group">
                    <label className="form-label">
                      <span className="required">*</span> Fatura:
                    </label>
                    <input
                      type="file"
                      multiple
                      className={`form-input ${solarContractFormErrors['documentos.fatura'] ? 'error' : ''}`}
                      accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                      onChange={(e) => handleSolarContractFileChange('fatura', e.target.files)}
                      data-field="documentos.fatura"
                      required
                    />
                    {Array.isArray(solarContractData.documentos.fatura) && solarContractData.documentos.fatura.length > 0 && (
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {solarContractData.documentos.fatura.map((file, index) => (
                          <div key={index} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '6px 10px',
                            background: '#f3f4f6',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            <span style={{ color: '#666', flex: 1 }}>{file.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveSolarFile('fatura', index)}
                              style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '2px 8px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                marginLeft: '8px'
                              }}
                              title="Remover ficheiro"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {solarContractFormErrors['documentos.fatura'] && (
                      <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        {solarContractFormErrors['documentos.fatura']}
                      </span>
                    )}
                  </div>

                  {/* Documentos Adicionais (optional) */}
                  <div className="form-group">
                    <label className="form-label">
                      Documentos Adicionais:
                    </label>
                    <input
                      type="file"
                      multiple
                      className="form-input"
                      accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                      onChange={(e) => handleSolarContractFileChange('documentosAdicionais', e.target.files)}
                      data-field="documentos.documentosAdicionais"
                    />
                    {Array.isArray(solarContractData.documentos.documentosAdicionais) && solarContractData.documentos.documentosAdicionais.length > 0 && (
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {solarContractData.documentos.documentosAdicionais.map((file: File, index: number) => (
                          <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '6px 10px',
                            background: '#f3f4f6',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            <span style={{ color: '#666', flex: 1 }}>{file.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveSolarFile('documentosAdicionais', index)}
                              style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '2px 8px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                marginLeft: '8px'
                              }}
                              title="Remover ficheiro"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="submit-contract-form-footer" style={{ 
              padding: '20px 30px', 
              background: '#f9fafb', 
              borderTop: '1px solid #e5e7eb', 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexShrink: 0,
              position: 'sticky',
              bottom: 0,
              zIndex: 10
            }}>
              <label className="submit-contract-urgent-toggle" style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '10px', 
                cursor: 'pointer', 
                fontWeight: 700, 
                fontSize: '16px',
                color: '#374151',
                padding: '10px 16px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px'
              }}>
                <input
                  type="checkbox"
                  checked={!!solarContractData.urgente}
                  onChange={(e) => setSolarContractData((prev: any) => ({ ...prev, urgente: e.target.checked }))}
                  style={{ width: '20px', height: '20px', accentColor: '#dc2626', cursor: 'pointer' }}
                />
                <span>Urgente</span>
              </label>
              <div className="submit-contract-form-actions" style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowSolarContractForm(false);
                  setMarcacaoToSubmitContract(null);
                  if (embeddedContractOnly) onEmbeddedContractClose?.();
                  setSolarContractData({
                    urgente: false,
                    tipoServico: '',
                    numeroPaineis: '',
                    nomeEmpresa: '',
                    moradaInstalacao: '',
                    distrito: '',
                    nifEmpresa: '',
                    emailEmpresa: '',
                    nomeRepresentante: '',
                    nifRepresentanteLegal: '',
                    contactoResponsavel: '',
                    emailRepresentanteLegal: '',
                    cpe: '',
                    iban: '',
                    potenciaContratada: '',
                    idPropostaS2C: '',
                    valorProposta: '',
                    codigoCertidaoPermanente: '',
                    nomeComercial: '',
                    codigoComercial: '',
                    vyn: '',
                    documentos: {
                      contratoAssinado: [],
                      contratoAssinadoDigital: false,
                      teaserS2C: [],
                      cc: [],
                      crcOuInicioAtividade: [],
                      fatura: [],
                      documentosAdicionais: []
                    }
                  });
                  setSolarContractFormErrors({});
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  color: '#6b7280',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  minWidth: '120px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.borderColor = '#9ca3af';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={submitSolarContract}
                disabled={isSubmittingSolarContract}
                style={{ 
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: isSubmittingSolarContract 
                    ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                    : 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                  color: 'white',
                  fontWeight: '600',
                  cursor: isSubmittingSolarContract ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  minWidth: '150px',
                  boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)',
                  opacity: isSubmittingSolarContract ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSubmittingSolarContract) {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #b91c1c 0%, #7f1d1d 100%)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(220, 38, 38, 0.3)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmittingSolarContract) {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.2)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                {isSubmittingSolarContract ? 'A submeter...' : 'Submeter Contrato'}
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Energy/Gas Contract Form Modal */}
      {showEnergyGasContractForm && marcacaoToSubmitContract && (
        <div
          className="modal active submit-contract-energy-modal"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onMouseDown={swallowModalBackdropPointer}
        >
          <div className="modal-content" style={{ 
            maxWidth: '800px', 
            width: '90%', 
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }} onMouseDown={(e) => e.stopPropagation()}>
            <div className="submit-contract-modal-header">
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: 'white', position: 'relative', zIndex: 1 }}>Contratação de Energia e Gás Natural</h2>
              <button 
                className="modal-close" 
                onClick={() => {
                  setShowEnergyGasContractForm(false);
                  setMarcacaoToSubmitContract(null);
                  if (embeddedContractOnly) onEmbeddedContractClose?.();
                  setEnergyGasContractData(createEmptyEnergyGasContractData());
                  setEnergyGasEeExpanded(true);
                  setEnergyGasGasExpanded(true);
                  setContractFormErrors({});
                }} 
                style={{ 
                  color: 'white', 
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  transition: 'all 0.2s',
                  position: 'relative',
                  zIndex: 1
                }}
              >×</button>
            </div>
            
            <div className="modal-body" style={{ 
              padding: '30px', 
              flex: '1 1 auto',
              overflowY: 'auto',
              minHeight: 0
            }}>
              {/* Associar Proposta(s) - similar to Assinatura Digital */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                  Associar Proposta(s) ao contrato
                </label>
                <p style={{ fontSize: '11px', color: '#6b7280', marginBottom: '8px' }}>
                  Apenas propostas concluídas pelo BackOffice são listadas. Selecione uma ou mais para preencher automaticamente os dados do contrato.
                  {embeddedContractOnly
                    ? ' Pedidos originados no CCC estão identificados com etiqueta.'
                    : ''}
                </p>
                <input
                  type="text"
                  className="comercial-signature-input"
                  placeholder="Pesquisar por NIF, empresa, ID ou origem CCC..."
                  value={contractProposalSearchQuery}
                  onChange={(e) => setContractProposalSearchQuery(e.target.value)}
                  style={{ maxWidth: '400px', marginBottom: '8px' }}
                />
                <div className="comercial-signature-associate-list" style={{ maxHeight: '160px' }}>
                  {contractProposalsForMarcacao
                    .filter((p) => contractProposalMatchesSearch(p, contractProposalSearchQuery))
                    .map((p) => {
                      const isSelected = contractSelectedProposalIds.includes(p.id);
                      return (
                        <ContractProposalAssociateItem
                          key={p.id}
                          p={p}
                          isSelected={isSelected}
                          onToggle={() => {
                            setContractSelectedProposalIds((prev) =>
                              prev.includes(p.id) ? prev.filter((id) => id !== p.id) : [...prev, p.id]
                            );
                          }}
                        />
                      );
                    })}
                  {contractProposalsForMarcacao.length === 0 && (
                    <div style={{ padding: '16px', color: '#6b7280', fontSize: '13px' }}>
                      Nenhuma proposta concluída (BackOffice) associada a esta marcação.
                    </div>
                  )}
                </div>
              </div>

              {/* Angariação Section — shared + EE / Gás collapsible blocks */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: '#333' }}>Angariação:</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div className="form-group">
                    <label className="form-label">
                      <span className="required">*</span> ID da Proposta:
                    </label>
                    <input
                      type="text"
                      className={`form-input ${contractFormErrors['angariacao.idProposta'] ? 'error' : ''}`}
                      value={energyGasContractData.angariacao.idProposta}
                      onChange={(e) => handleEnergyGasContractFieldChange('angariacao', 'idProposta', e.target.value)}
                      placeholder="Ex: PROP-12345"
                      required
                    />
                    {contractFormErrors['angariacao.idProposta'] && (
                      <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        {contractFormErrors['angariacao.idProposta']}
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      <span className="required">*</span> Válida até dia:
                    </label>
                    <input
                      type="text"
                      className={`form-input ${contractFormErrors['angariacao.validaAteDia'] ? 'error' : ''}`}
                      value={energyGasContractData.angariacao.validaAteDia}
                      onChange={(e) => handleEnergyGasContractFieldChange('angariacao', 'validaAteDia', e.target.value)}
                      placeholder="Ex: 15/01/2025"
                      required
                    />
                    {contractFormErrors['angariacao.validaAteDia'] && (
                      <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        {contractFormErrors['angariacao.validaAteDia']}
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      IBAN:
                    </label>
                    <input
                      type="text"
                      className={`form-input ${contractFormErrors['angariacao.iban'] ? 'error' : ''}`}
                      value={energyGasContractData.angariacao.iban}
                      onChange={handleIbanInput}
                      onPaste={handleIbanPaste}
                      placeholder="PT50 + 21 dígitos (ex: PT501234567890123456789)"
                      maxLength={25}
                      pattern="PT50\d{21}"
                    />
                    {contractFormErrors['angariacao.iban'] && (
                      <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        {contractFormErrors['angariacao.iban']}
                      </span>
                    )}
                  </div>
                </div>

                {/* EE block */}
                <div style={{ marginBottom: '12px', border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
                  <button
                    type="button"
                    onClick={() => setEnergyGasEeExpanded((v) => !v)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      background: '#eff6ff',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 600,
                      color: '#1e3a8a',
                      fontSize: '15px'
                    }}
                  >
                    <span>Eletricidade (EE){energyGasEeHasAnyValue((energyGasContractData as any).ee) ? '' : ' — vazio'}</span>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>{energyGasEeExpanded ? 'Recolher ▲' : 'Expandir ▼'}</span>
                  </button>
                  {energyGasEeExpanded && (
                    <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', background: '#fff' }}>
                      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                        <label className="form-label">
                          <span className="required">*</span> CPE(s):
                        </label>
                        {(Array.isArray((energyGasContractData as any).ee?.cpes) ? (energyGasContractData as any).ee.cpes : ['']).map((cpeVal: string, cpeIdx: number) => (
                          <div key={`cpe-${cpeIdx}`} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                            <input
                              type="text"
                              className={`form-input ${contractFormErrors['ee.cpes'] ? 'error' : ''}`}
                              value={cpeVal || ''}
                              onChange={(e) => handleEnergyGasCpeChange(cpeIdx, e.target.value)}
                              placeholder={`CPE ${cpeIdx + 1}`}
                              style={{ flex: 1 }}
                            />
                            <button
                              type="button"
                              onClick={() => handleEnergyGasRemoveCpe(cpeIdx)}
                              disabled={(Array.isArray((energyGasContractData as any).ee?.cpes) ? (energyGasContractData as any).ee.cpes.length : 1) <= 1}
                              title="Remover CPE"
                              style={{
                                padding: '8px 10px',
                                borderRadius: '6px',
                                border: '1px solid #fecaca',
                                background: '#fef2f2',
                                color: '#b91c1c',
                                cursor: 'pointer',
                                fontSize: '12px',
                                opacity: (Array.isArray((energyGasContractData as any).ee?.cpes) ? (energyGasContractData as any).ee.cpes.length : 1) <= 1 ? 0.5 : 1
                              }}
                            >
                              Remover
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={handleEnergyGasAddCpe}
                          style={{
                            marginTop: '4px',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid #bfdbfe',
                            background: '#eff6ff',
                            color: '#1d4ed8',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 600
                          }}
                        >
                          + Adicionar CPE
                        </button>
                        {contractFormErrors['ee.cpes'] && (
                          <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                            {contractFormErrors['ee.cpes']}
                          </span>
                        )}
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          {normalizeEnergyGasIdList((energyGasContractData as any).ee, 'cpes', 'cpe').length > 0 && (
                            <span className="required">*</span>
                          )}{' '}
                          Data de início de fornecimento (EE):
                        </label>
                        <input
                          type="text"
                          className={`form-input ${contractFormErrors['ee.dataInicioFornecimento'] ? 'error' : ''}`}
                          value={(energyGasContractData as any).ee?.dataInicioFornecimento || ''}
                          onChange={(e) => handleEnergyGasContractFieldChange('ee', 'dataInicioFornecimento', e.target.value)}
                          placeholder="Ex: 01/01/2025"
                        />
                        {contractFormErrors['ee.dataInicioFornecimento'] && (
                          <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                            {contractFormErrors['ee.dataInicioFornecimento']}
                          </span>
                        )}
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          {normalizeEnergyGasIdList((energyGasContractData as any).ee, 'cpes', 'cpe').length > 0 && (
                            <span className="required">*</span>
                          )}{' '}
                          Data de fim de fornecimento (EE):
                        </label>
                        <input
                          type="text"
                          className={`form-input ${contractFormErrors['ee.dataFimFornecimento'] ? 'error' : ''}`}
                          value={(energyGasContractData as any).ee?.dataFimFornecimento || ''}
                          onChange={(e) => handleEnergyGasContractFieldChange('ee', 'dataFimFornecimento', e.target.value)}
                          placeholder="Ex: 31/12/2025"
                        />
                        {contractFormErrors['ee.dataFimFornecimento'] && (
                          <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                            {contractFormErrors['ee.dataFimFornecimento']}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Gás block */}
                <div style={{ marginBottom: '12px', border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
                  <button
                    type="button"
                    onClick={() => setEnergyGasGasExpanded((v) => !v)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      background: '#fef3c7',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 600,
                      color: '#92400e',
                      fontSize: '15px'
                    }}
                  >
                    <span>Gás Natural (GN){energyGasGasHasAnyValue((energyGasContractData as any).gas) ? '' : ' — vazio'}</span>
                    <span style={{ fontSize: '12px', color: '#a16207' }}>{energyGasGasExpanded ? 'Recolher ▲' : 'Expandir ▼'}</span>
                  </button>
                  {energyGasGasExpanded && (
                    <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', background: '#fff' }}>
                      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                        <label className="form-label">
                          <span className="required">*</span> CUI(s):
                        </label>
                        {(Array.isArray((energyGasContractData as any).gas?.cuis) ? (energyGasContractData as any).gas.cuis : ['']).map((cuiVal: string, cuiIdx: number) => (
                          <div key={`cui-${cuiIdx}`} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                            <input
                              type="text"
                              className={`form-input ${contractFormErrors['gas.cuis'] ? 'error' : ''}`}
                              value={cuiVal || ''}
                              onChange={(e) => handleEnergyGasCuiChange(cuiIdx, e.target.value)}
                              placeholder={`CUI ${cuiIdx + 1}`}
                              style={{ flex: 1 }}
                            />
                            <button
                              type="button"
                              onClick={() => handleEnergyGasRemoveCui(cuiIdx)}
                              disabled={(Array.isArray((energyGasContractData as any).gas?.cuis) ? (energyGasContractData as any).gas.cuis.length : 1) <= 1}
                              title="Remover CUI"
                              style={{
                                padding: '8px 10px',
                                borderRadius: '6px',
                                border: '1px solid #fecaca',
                                background: '#fef2f2',
                                color: '#b91c1c',
                                cursor: 'pointer',
                                fontSize: '12px',
                                opacity: (Array.isArray((energyGasContractData as any).gas?.cuis) ? (energyGasContractData as any).gas.cuis.length : 1) <= 1 ? 0.5 : 1
                              }}
                            >
                              Remover
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={handleEnergyGasAddCui}
                          style={{
                            marginTop: '4px',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid #fde68a',
                            background: '#fffbeb',
                            color: '#92400e',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 600
                          }}
                        >
                          + Adicionar CUI
                        </button>
                        {contractFormErrors['gas.cuis'] && (
                          <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                            {contractFormErrors['gas.cuis']}
                          </span>
                        )}
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          {normalizeEnergyGasIdList((energyGasContractData as any).gas, 'cuis', 'cui').length > 0 && (
                            <span className="required">*</span>
                          )}{' '}
                          Data de início de fornecimento (Gás):
                        </label>
                        <input
                          type="text"
                          className={`form-input ${contractFormErrors['gas.dataInicioFornecimento'] ? 'error' : ''}`}
                          value={(energyGasContractData as any).gas?.dataInicioFornecimento || ''}
                          onChange={(e) => handleEnergyGasContractFieldChange('gas', 'dataInicioFornecimento', e.target.value)}
                          placeholder="Ex: 01/01/2025"
                        />
                        {contractFormErrors['gas.dataInicioFornecimento'] && (
                          <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                            {contractFormErrors['gas.dataInicioFornecimento']}
                          </span>
                        )}
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          {normalizeEnergyGasIdList((energyGasContractData as any).gas, 'cuis', 'cui').length > 0 && (
                            <span className="required">*</span>
                          )}{' '}
                          Data de fim de fornecimento (Gás):
                        </label>
                        <input
                          type="text"
                          className={`form-input ${contractFormErrors['gas.dataFimFornecimento'] ? 'error' : ''}`}
                          value={(energyGasContractData as any).gas?.dataFimFornecimento || ''}
                          onChange={(e) => handleEnergyGasContractFieldChange('gas', 'dataFimFornecimento', e.target.value)}
                          placeholder="Ex: 31/12/2025"
                        />
                        {contractFormErrors['gas.dataFimFornecimento'] && (
                          <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                            {contractFormErrors['gas.dataFimFornecimento']}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Documentos Section */}
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: '#333' }}>Documentos:</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div className="form-group">
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      {!energyGasContractData.documentos.contratoAssinadoDigital && <span className="required">*</span>}
                      Contrato assinado:
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'normal', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={!!energyGasContractData.documentos.contratoAssinadoDigital}
                          onChange={(e) => handleEnergyGasContractFieldChange('documentos', 'contratoAssinadoDigital', e.target.checked)}
                        />
                        Digital
                      </label>
                    </label>
                    <input
                      type="file"
                      multiple
                      className={`form-input ${contractFormErrors['documentos.contratoAssinado'] ? 'error' : ''}`}
                      accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                      onChange={(e) => handleEnergyGasContractFileChange('contratoAssinado', e.target.files)}
                      required={!energyGasContractData.documentos.contratoAssinadoDigital}
                    />
                    {Array.isArray(energyGasContractData.documentos.contratoAssinado) && energyGasContractData.documentos.contratoAssinado.length > 0 && (
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {energyGasContractData.documentos.contratoAssinado.map((file, index) => (
                          <div key={index} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '6px 10px',
                            background: '#f3f4f6',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            <span style={{ color: '#666', flex: 1 }}>{file.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveFile('contratoAssinado', index)}
                              style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '2px 8px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                marginLeft: '8px'
                              }}
                              title="Remover ficheiro"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {contractFormErrors['documentos.contratoAssinado'] && (
                      <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        {contractFormErrors['documentos.contratoAssinado']}
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      <span className="required">*</span> Cartão Cidadão:
                    </label>
                    <input
                      type="file"
                      multiple
                      className={`form-input ${contractFormErrors['documentos.cartaoCidadao'] ? 'error' : ''}`}
                      accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                      onChange={(e) => handleEnergyGasContractFileChange('cartaoCidadao', e.target.files)}
                      required
                    />
                    {Array.isArray(energyGasContractData.documentos.cartaoCidadao) && energyGasContractData.documentos.cartaoCidadao.length > 0 && (
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {energyGasContractData.documentos.cartaoCidadao.map((file, index) => (
                          <div key={index} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '6px 10px',
                            background: '#f3f4f6',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            <span style={{ color: '#666', flex: 1 }}>{file.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveFile('cartaoCidadao', index)}
                              style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '2px 8px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                marginLeft: '8px'
                              }}
                              title="Remover ficheiro"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {contractFormErrors['documentos.cartaoCidadao'] && (
                      <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        {contractFormErrors['documentos.cartaoCidadao']}
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      <span className="required">*</span> Fatura Atual:
                    </label>
                    <input
                      type="file"
                      multiple
                      className={`form-input ${contractFormErrors['documentos.faturaAtual'] ? 'error' : ''}`}
                      accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                      onChange={(e) => handleEnergyGasContractFileChange('faturaAtual', e.target.files)}
                      required
                    />
                    {Array.isArray(energyGasContractData.documentos.faturaAtual) && energyGasContractData.documentos.faturaAtual.length > 0 && (
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {energyGasContractData.documentos.faturaAtual.map((file, index) => (
                          <div key={index} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '6px 10px',
                            background: '#f3f4f6',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            <span style={{ color: '#666', flex: 1 }}>{file.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveFile('faturaAtual', index)}
                              style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '2px 8px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                marginLeft: '8px'
                              }}
                              title="Remover ficheiro"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {contractFormErrors['documentos.faturaAtual'] && (
                      <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        {contractFormErrors['documentos.faturaAtual']}
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      {shouldRequireIbanComprovativo() && <span className="required">*</span>}
                      Comprovativo de IBAN:
                    </label>
                    <input
                      type="file"
                      multiple
                      className={`form-input ${contractFormErrors['documentos.ibanComprovativo'] ? 'error' : ''}`}
                      accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                      onChange={(e) => handleEnergyGasContractFileChange('ibanComprovativo', e.target.files)}
                    />
                    {Array.isArray(energyGasContractData.documentos.ibanComprovativo) && energyGasContractData.documentos.ibanComprovativo.length > 0 && (
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {energyGasContractData.documentos.ibanComprovativo.map((file, index) => (
                          <div key={index} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '6px 10px',
                            background: '#f3f4f6',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            <span style={{ color: '#666', flex: 1 }}>{file.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveFile('ibanComprovativo', index)}
                              style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '2px 8px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                marginLeft: '8px'
                              }}
                              title="Remover ficheiro"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {contractFormErrors['documentos.ibanComprovativo'] && (
                      <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        {contractFormErrors['documentos.ibanComprovativo']}
                      </span>
                    )}
                  </div>
                  {shouldRequireIbanComprovativo() && (
                    <div className="form-group">
                      <label className="form-label">
                        {!energyGasContractData.documentos.contratoAssinadoDigital && <span className="required">*</span>}
                        SEPA:
                      </label>
                      <input
                        type="file"
                        multiple
                        className={`form-input ${contractFormErrors['documentos.comprovativoTitularidadeConta'] ? 'error' : ''}`}
                        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                        onChange={(e) => handleEnergyGasContractFileChange('comprovativoTitularidadeConta', e.target.files)}
                        required={!energyGasContractData.documentos.contratoAssinadoDigital}
                      />
                      {Array.isArray(energyGasContractData.documentos.comprovativoTitularidadeConta) && energyGasContractData.documentos.comprovativoTitularidadeConta.length > 0 && (
                        <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {energyGasContractData.documentos.comprovativoTitularidadeConta.map((file, index) => (
                            <div key={index} style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              padding: '6px 10px',
                              background: '#f3f4f6',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}>
                              <span style={{ color: '#666', flex: 1 }}>{file.name}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveFile('comprovativoTitularidadeConta', index)}
                                style={{
                                  background: '#ef4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '2px 8px',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  marginLeft: '8px'
                                }}
                                title="Remover ficheiro"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {contractFormErrors['documentos.comprovativoTitularidadeConta'] && (
                        <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                          {contractFormErrors['documentos.comprovativoTitularidadeConta']}
                        </span>
                      )}
                    </div>
                  )}
                  {shouldShowDeclaracaoInicioAtividade() ? (
                    // If NIF doesn't start with "5", show "CRC ou Início de Atividade" (file upload)
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label className="form-label">
                        <span className="required">*</span> CRC ou Início de Atividade:
                      </label>
                      <input
                        type="file"
                        multiple
                        className={`form-input ${contractFormErrors['documentos.declaracaoInicioAtividade'] ? 'error' : ''}`}
                        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                        onChange={(e) => handleEnergyGasContractFileChange('declaracaoInicioAtividade', e.target.files)}
                        required
                      />
                      {Array.isArray(energyGasContractData.documentos.declaracaoInicioAtividade) && energyGasContractData.documentos.declaracaoInicioAtividade.length > 0 && (
                        <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {energyGasContractData.documentos.declaracaoInicioAtividade.map((file, index) => (
                            <div key={index} style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              padding: '6px 10px',
                              background: '#f3f4f6',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}>
                              <span style={{ color: '#666', flex: 1 }}>{file.name}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveFile('declaracaoInicioAtividade', index)}
                                style={{
                                  background: '#ef4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '2px 8px',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  marginLeft: '8px'
                                }}
                                title="Remover ficheiro"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {contractFormErrors['documentos.declaracaoInicioAtividade'] && (
                        <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                          {contractFormErrors['documentos.declaracaoInicioAtividade']}
                        </span>
                      )}
                    </div>
                  ) : (
                    // If NIF starts with "5", show "CRC ou Início de Atividade" (text field)
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label className="form-label">
                        <span className="required">*</span> CRC ou Início de Atividade:
                      </label>
                      <input
                        type="text"
                        className={`form-input ${contractFormErrors['documentos.inicioAtividade'] ? 'error' : ''}`}
                        value={energyGasContractData.documentos.inicioAtividade}
                        onChange={(e) => handleEnergyGasContractFieldChange('documentos', 'inicioAtividade', e.target.value)}
                        placeholder="Ex: 01/01/2020"
                        required
                      />
                      {contractFormErrors['documentos.inicioAtividade'] && (
                        <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                          {contractFormErrors['documentos.inicioAtividade']}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Documentos Adicionais:</label>
                    <input
                      type="file"
                      multiple
                      className="form-input"
                      accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                      onChange={(e) => handleEnergyGasContractFileChange('documentosAdicionais', e.target.files)}
                    />
                    {Array.isArray(energyGasContractData.documentos.documentosAdicionais) && energyGasContractData.documentos.documentosAdicionais.length > 0 && (
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {energyGasContractData.documentos.documentosAdicionais.map((file, index) => (
                          <div key={index} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '6px 10px',
                            background: '#f3f4f6',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            <span style={{ color: '#666', flex: 1 }}>{file.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveFile('documentosAdicionais', index)}
                              style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '2px 8px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                marginLeft: '8px'
                              }}
                              title="Remover ficheiro"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="submit-contract-form-footer" style={{ 
              padding: '20px 30px', 
              background: '#f9fafb', 
              borderTop: '1px solid #e5e7eb', 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexShrink: 0,
              position: 'sticky',
              bottom: 0,
              zIndex: 10
            }}>
              <label className="submit-contract-urgent-toggle" style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '10px', 
                cursor: 'pointer', 
                fontWeight: 700, 
                fontSize: '16px',
                color: '#374151',
                padding: '10px 16px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px'
              }}>
                <input
                  type="checkbox"
                  checked={!!energyGasContractData.urgente}
                  onChange={(e) => setEnergyGasContractData((prev: any) => ({ ...prev, urgente: e.target.checked }))}
                  style={{ width: '20px', height: '20px', accentColor: '#dc2626', cursor: 'pointer' }}
                />
                <span>Urgente</span>
              </label>
              <div className="submit-contract-form-actions" style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowEnergyGasContractForm(false);
                  setMarcacaoToSubmitContract(null);
                  if (embeddedContractOnly) onEmbeddedContractClose?.();
                  setEnergyGasContractData(createEmptyEnergyGasContractData());
                  setEnergyGasEeExpanded(true);
                  setEnergyGasGasExpanded(true);
                  setContractFormErrors({});
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  color: '#6b7280',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  minWidth: '120px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.borderColor = '#9ca3af';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={submitEnergyGasContract}
                disabled={isSubmittingEnergyGasContract}
                style={{ 
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: isSubmittingEnergyGasContract ? '#9ca3af' : 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                  color: 'white',
                  fontWeight: '600',
                  cursor: isSubmittingEnergyGasContract ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  minWidth: '150px',
                  boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmittingEnergyGasContract) {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #b91c1c 0%, #7f1d1d 100%)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(220, 38, 38, 0.3)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isSubmittingEnergyGasContract ? '#9ca3af' : 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.2)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {isSubmittingEnergyGasContract ? 'A submeter...' : 'Submeter Contrato'}
              </button>
              </div>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
        </>
      )}

      {/* Proposal Modal */}
      {!gestaoModalsOnly && showProposalModal && currentMarcacaoId && (
        <div className="modal active proposal-modal" onMouseDown={swallowModalBackdropPointer}>
          <div className="modal-content proposal-modal-content" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header proposal-modal-header">
              <button className="modal-close" onClick={closeProposalModal}>×</button>
              
              {/* Line 1: Title */}
              <h1>{proposalModalMode === 'refazer' ? 'Refazer' : 'Solicitar Proposta'}</h1>
              
              {/* Line 2: Subtitle */}
              <p className="modal-subtitle">
                {proposalModalMode === 'refazer'
                  ? 'Selecione um pedido concluído e reenvie a proposta com os dados pré-preenchidos'
                  : 'Preencha os dados para solicitar uma proposta detalhada'}
              </p>
              
              {/* Line 3: Progress Steps */}
              <div className="progress-steps-grid">
                {proposalModalMode === 'refazer' ? (
                  <>
                    {[
                      { step: 0, label: 'Pedido Anterior' },
                      { step: 2, label: 'Empresa' },
                      { step: 3, label: 'Serviço' },
                      { step: 4, label: 'Resumo' },
                    ].map(({ step, label }, index) => (
                      <div
                        key={step}
                        className={`step ${currentProposalStep === step ? 'active' : currentProposalStep > step ? 'completed' : ''}`}
                        data-step={String(step)}
                      >
                        <div className="step-icon">{index + 1}</div>
                        <div className="step-title">{label}</div>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <div className={`step ${currentProposalStep === 1 ? 'active' : currentProposalStep > 1 ? 'completed' : ''}`} data-step="1">
                      <div className="step-icon">1</div>
                      <div className="step-title">Equipa Interna</div>
                    </div>
                    <div className={`step ${currentProposalStep === 2 ? 'active' : currentProposalStep > 2 ? 'completed' : ''}`} data-step="2">
                      <div className="step-icon">2</div>
                      <div className="step-title">Empresa</div>
                    </div>
                    <div className={`step ${currentProposalStep === 3 ? 'active' : currentProposalStep > 3 ? 'completed' : ''}`} data-step="3">
                      <div className="step-icon">3</div>
                      <div className="step-title">Serviço</div>
                    </div>
                    <div className={`step ${currentProposalStep === 4 ? 'active' : currentProposalStep > 4 ? 'completed' : ''}`} data-step="4">
                      <div className="step-icon">4</div>
                      <div className="step-title">Resumo</div>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            <div className="modal-body">

              {proposalModalMode !== 'refazer' && proposalNotesImportBanner && (
                <div className="proposal-notes-import-banner proposal-notes-import-banner--sticky" role="status">
                  Campos preenchidos a partir das Notas. Falta anexar: Fatura / Print do Telhado nos serviços aplicáveis.
                </div>
              )}

              {/* Step 0 (Refazer): select completed request */}
              <div className={`form-section ${currentProposalStep === 0 ? 'active' : ''}`} id="proposalStep0">
                <h3 className="section-title">Pedido Anterior</h3>
                <p className="section-description">Selecione um pedido concluído desta marcação para reutilizar os dados e documentos</p>
                <div className="comercial-signature-associate-list" style={{ maxHeight: '280px' }}>
                  {refazerCompletedProposals.map((p) => {
                    const isSelected = selectedRefazerProposalId === p.id;
                    return (
                      <label
                        key={p.id}
                        className={`comercial-signature-associate-item ${isSelected ? 'selected' : ''}`}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: 0 }}
                      >
                        <input
                          type="radio"
                          name="refazerProposalSelection"
                          checked={isSelected}
                          onChange={() => {
                            setSelectedRefazerProposalId(p.id);
                            if (fieldErrors.refazerSelection) {
                              setFieldErrors((prev) => {
                                const next = { ...prev };
                                delete next.refazerSelection;
                                return next;
                              });
                            }
                          }}
                        />
                        {p.display}
                      </label>
                    );
                  })}
                  {refazerCompletedProposals.length === 0 && (
                    <div style={{ padding: '16px', color: '#6b7280', fontSize: '13px' }}>
                      Nenhum pedido concluído (BackOffice) associado a esta marcação.
                    </div>
                  )}
                </div>
                {fieldErrors.refazerSelection && (
                  <span className="field-error">{fieldErrors.refazerSelection}</span>
                )}
              </div>

              {/* Step 1: Equipa Interna */}
              <div className={`form-section ${proposalModalMode !== 'refazer' && currentProposalStep === 1 ? 'active' : ''}`} id="proposalStep1">
                <h3 className="section-title">Equipa Interna</h3>
                <p className="section-description">Confirme os requisitos internos para esta proposta</p>

                {proposalModalMode !== 'refazer' && (
                  <div className="proposal-notes-import-panel">
                    <div className="proposal-notes-import-title">Importar das Notas</div>
                    <p className="proposal-notes-import-hint">
                      Descarregue ou copie o modelo, preencha nas Notas do telemóvel, e carregue o .txt ou cole o texto aqui.
                      Os ficheiros (Fatura / Print do Telhado) anexam-se depois no formulário.
                    </p>
                    <div className="proposal-notes-import-actions">
                      <button
                        type="button"
                        className="btn btn-secondary proposal-notes-import-btn"
                        onClick={() => downloadProposalNotesTemplate()}
                      >
                        Descarregar modelo
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary proposal-notes-import-btn"
                        onClick={() => void handleCopyProposalNotesTemplate()}
                      >
                        Copiar modelo
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary proposal-notes-import-btn"
                        onClick={() => proposalNotesFileInputRef.current?.click()}
                      >
                        Carregar ficheiro .txt
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary proposal-notes-import-btn"
                        onClick={() => setProposalNotesPasteOpen((open) => !open)}
                      >
                        {proposalNotesPasteOpen ? 'Ocultar colar texto' : 'Colar texto'}
                      </button>
                      <input
                        ref={proposalNotesFileInputRef}
                        type="file"
                        accept=".txt,text/plain"
                        className="proposal-notes-file-input"
                        onChange={handleProposalNotesFileChange}
                      />
                    </div>

                    {proposalNotesPasteOpen && (
                      <div className="proposal-notes-paste-block">
                        <label className="form-label" htmlFor="proposalNotesPaste">
                          Cole aqui o conteúdo completo do .txt
                        </label>
                        <textarea
                          id="proposalNotesPaste"
                          className="form-input proposal-notes-paste-textarea"
                          rows={10}
                          value={proposalNotesPasteText}
                          onChange={(e) => setProposalNotesPasteText(e.target.value)}
                          placeholder="Cole o modelo preenchido (Campo - valor # comentário)…"
                        />
                        <button
                          type="button"
                          className="btn btn-primary proposal-notes-import-btn"
                          onClick={() => importProposalNotesText(proposalNotesPasteText)}
                          disabled={!proposalNotesPasteText.trim()}
                        >
                          Aplicar texto colado
                        </button>
                      </div>
                    )}

                    {proposalNotesImportBanner && (
                      <div className="proposal-notes-import-banner" role="status">
                        Campos preenchidos a partir das Notas. Falta anexar: Fatura / Print do Telhado nos serviços aplicáveis.
                      </div>
                    )}

                    {proposalNotesImportErrors.length > 0 && (
                      <ul className="proposal-notes-import-errors">
                        {proposalNotesImportErrors.map((err) => (
                          <li key={err}>{err}</li>
                        ))}
                      </ul>
                    )}

                    {proposalNotesImportWarnings.length > 0 && (
                      <details className="proposal-notes-import-warnings" open={proposalNotesImportWarnings.length <= 8}>
                        <summary>
                          {proposalNotesImportWarnings.length} aviso{proposalNotesImportWarnings.length === 1 ? '' : 's'} na importação
                        </summary>
                        <ul>
                          {proposalNotesImportWarnings.map((w) => (
                            <li key={w}>{w}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
                
                <div className="form-group">
                  <label className="form-label">Proposta de Marcação? <span className="required">*</span></label>
                  <div className={`button-group ${fieldErrors.propostaMarcacao ? 'button-group-error' : ''}`}>
                    <button type="button" className={`btn-option ${proposalData.equipaInterna?.propostaMarcacao === 'sim' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick('propostaMarcacao', 'sim')}>Sim</button>
                    <button type="button" className={`btn-option ${proposalData.equipaInterna?.propostaMarcacao === 'nao' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick('propostaMarcacao', 'nao')}>Não</button>
                  </div>
                  {fieldErrors.propostaMarcacao && (
                    <span className="field-error">{fieldErrors.propostaMarcacao}</span>
                  )}
                </div>
                
                <div className="form-group">
                  <label className="form-label">Assinatura do Contrato? <span className="required">*</span></label>
                  <div className={`button-group ${fieldErrors.assinaturaContrato ? 'button-group-error' : ''}`}>
                    <button type="button" className={`btn-option ${proposalData.equipaInterna?.assinaturaContrato === 'digital' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick('assinaturaContrato', 'digital')}>Digital</button>
                    <button type="button" className={`btn-option ${proposalData.equipaInterna?.assinaturaContrato === 'impressa' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick('assinaturaContrato', 'impressa')}>Impressa</button>
                  </div>
                  {fieldErrors.assinaturaContrato && (
                    <span className="field-error">{fieldErrors.assinaturaContrato}</span>
                  )}
                </div>
                
                <div className="form-group">
                  <label className="form-label">Tipo de Pedido <span className="required">*</span></label>
                  <div className={`button-group ${fieldErrors.tipoPedido ? 'button-group-error' : ''}`}>
                    <button type="button" className={`btn-option ${proposalData.equipaInterna?.tipoPedido === 'normal' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick('tipoPedido', 'normal')}>Normal</button>
                    <button type="button" className={`btn-option ${proposalData.equipaInterna?.tipoPedido === 'roadtrip' ? 'selected' : ''}`} onClick={() => handleBtnOptionClick('tipoPedido', 'roadtrip')}>RoadTrip</button>
                  </div>
                  {fieldErrors.tipoPedido && (
                    <span className="field-error">{fieldErrors.tipoPedido}</span>
                  )}
                </div>

                {allowProposalSharing && (
                  <div className="form-group">
                    <label className="form-label">
                      Partilhar com comercial{' '}
                      <span style={{ fontWeight: 400, opacity: 0.75 }}>(opcional)</span>
                    </label>
                    <ProposalShareComercialPicker
                      options={shareableComercialUserOptions}
                      value={sharedComercialUserIds}
                      onChange={setSharedComercialUserIds}
                      placeholder="Pesquisar comercial..."
                    />
                  </div>
                )}
              </div>

              {/* Step 2: Empresa */}
              <div className={`form-section ${currentProposalStep === 2 ? 'active' : ''}`} id="proposalStep2">
                <h3 className="section-title">Empresa</h3>
                <p className="section-description">Informações da empresa cliente</p>
                
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label" htmlFor="nomeEmpresa">
                      Nome da Empresa <span className="required">*</span>
                    </label>
                    <input 
                      type="text" 
                      id="nomeEmpresa" 
                      className={`form-input ${fieldErrors.nomeEmpresa ? 'input-error' : ''}`}
                      placeholder="Nome da empresa" 
                      value={proposalData.empresa.nomeEmpresa || ''}
                      onChange={(e) => handleEmpresaFieldChange('nomeEmpresa', e.target.value)}
                      required 
                    />
                    {fieldErrors.nomeEmpresa && (
                      <span className="field-error">{fieldErrors.nomeEmpresa}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label" htmlFor="nifEmpresa">
                      NIF da Empresa <span className="required">*</span>
                    </label>
                    <input 
                      type="text" 
                      id="nifEmpresa" 
                      className={`form-input ${fieldErrors.nifEmpresa ? 'input-error' : ''}`}
                      placeholder="Número de identificação fiscal" 
                      maxLength={9}
                      pattern="[0-9]{9}"
                      value={proposalData.empresa.nifEmpresa || ''}
                      onChange={handleNifInput}
                      onPaste={handleNifPaste}
                      required
                    />
                    {fieldErrors.nifEmpresa && (
                      <span className="field-error">{fieldErrors.nifEmpresa}</span>
                    )}
                  </div>
                  
                  <div className="form-group full-width">
                    <label className="form-label" htmlFor="moradaCorrespondencia">
                      Morada de Correspondência
                    </label>
                    <input 
                      type="text" 
                      id="moradaCorrespondencia" 
                      className="form-input" 
                      placeholder="Morada de correspondência (opcional)" 
                      value={proposalData.empresa.moradaCorrespondencia || ''}
                      onChange={(e) => handleEmpresaFieldChange('moradaCorrespondencia', e.target.value)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label" htmlFor="regiao">
                      Localidade <span className="required">*</span>
                    </label>
                    <input 
                      type="text" 
                      id="regiao" 
                      className={`form-input ${fieldErrors.regiao ? 'input-error' : ''}`}
                      placeholder="Localidade" 
                      value={proposalData.empresa.regiao || ''}
                      onChange={(e) => handleEmpresaFieldChange('regiao', e.target.value)}
                      required 
                    />
                    {fieldErrors.regiao && (
                      <span className="field-error">{fieldErrors.regiao}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label" htmlFor="cae">
                      CAE <span className="required">*</span>
                    </label>
                    <input 
                      type="text" 
                      id="cae" 
                      className={`form-input ${fieldErrors.cae ? 'input-error' : ''}`}
                      placeholder="Código de atividade económica" 
                      maxLength={5}
                      pattern="[0-9]{5}"
                      value={proposalData.empresa.cae || ''}
                      onChange={handleCaeInput}
                      onPaste={handleCaePaste}
                      required 
                    />
                    {fieldErrors.cae && (
                      <span className="field-error">{fieldErrors.cae}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label" htmlFor="nomeRepresentante">
                      Nome do Representante <span className="required">*</span>
                    </label>
                    <input 
                      type="text" 
                      id="nomeRepresentante" 
                      className={`form-input ${fieldErrors.nomeRepresentante ? 'input-error' : ''}`}
                      placeholder="Nome completo do representante" 
                      value={proposalData.empresa.nomeRepresentante || ''}
                      onChange={(e) => handleEmpresaFieldChange('nomeRepresentante', e.target.value)}
                      required 
                    />
                    {fieldErrors.nomeRepresentante && (
                      <span className="field-error">{fieldErrors.nomeRepresentante}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label" htmlFor="contactoRepresentante">
                      Contacto do Representante <span className="required">*</span>
                    </label>
                    <input 
                      type="tel" 
                      id="contactoRepresentante" 
                      className={`form-input ${fieldErrors.contactoRepresentante ? 'input-error' : ''}`}
                      placeholder="Número de telefone" 
                      value={proposalData.empresa.contactoRepresentante || ''}
                      onChange={(e) => handleEmpresaFieldChange('contactoRepresentante', e.target.value)}
                      required 
                    />
                    {fieldErrors.contactoRepresentante && (
                      <span className="field-error">{fieldErrors.contactoRepresentante}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label" htmlFor="emailRepresentante">
                      Email do Representante <span className="required">*</span>
                    </label>
                    <input 
                      type="email" 
                      id="emailRepresentante" 
                      className={`form-input ${fieldErrors.emailRepresentante ? 'input-error' : ''}`}
                      placeholder="Email do representante" 
                      value={proposalData.empresa.emailRepresentante || ''}
                      onChange={(e) => handleEmpresaFieldChange('emailRepresentante', e.target.value)}
                      required 
                    />
                    {fieldErrors.emailRepresentante && (
                      <span className="field-error">{fieldErrors.emailRepresentante}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label" htmlFor="emailEmpresa">
                      Email da Empresa
                    </label>
                    <input 
                      type="email" 
                      id="emailEmpresa" 
                      className={`form-input ${fieldErrors.emailEmpresa ? 'input-error' : ''}`}
                      placeholder="Email geral da empresa" 
                      value={proposalData.empresa.emailEmpresa || ''}
                      onChange={(e) => handleEmpresaFieldChange('emailEmpresa', e.target.value)}
                    />
                    {fieldErrors.emailEmpresa && (
                      <span className="field-error">{fieldErrors.emailEmpresa}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Step 3: Serviço */}
              <div className={`form-section ${currentProposalStep === 3 ? 'active' : ''}`} id="proposalStep3">
                <h3 className="section-title">Serviço</h3>
                <p className="section-description">Selecione os serviços e configure os detalhes</p>
                
                {/* Serviço Selection */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span> Serviços
                  </label>
                  <div className={`service-options ${fieldErrors.servicos ? 'service-options-error' : ''}`}>
                    {/* Bairro Solar */}
                    <div className="service-quantity-control">
                      <span className="service-label">Bairro Solar</span>
                      <div className="quantity-control">
                        <button 
                          type="button" 
                          className="quantity-btn" 
                          onClick={() => handleServiceQuantityChange('bairro-solar', Math.max(0, (proposalData.serviceQuantities['bairro-solar'] || 0) - 1))}
                          disabled={(proposalData.serviceQuantities['bairro-solar'] || 0) === 0}
                        >
                          −
                        </button>
                        <input 
                          type="number" 
                          min="0" 
                          max="99" 
                          value={proposalData.serviceQuantities['bairro-solar'] || 0} 
                          onChange={(e) => handleServiceQuantityChange('bairro-solar', parseInt(e.target.value) || 0)}
                          className="quantity-input"
                        />
                        <button 
                          type="button" 
                          className="quantity-btn" 
                          onClick={() => handleServiceQuantityChange('bairro-solar', Math.min(99, (proposalData.serviceQuantities['bairro-solar'] || 0) + 1))}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    
                    {/* PAP */}
                    <div className="service-quantity-control">
                      <span className="service-label">Pay AS Produced (PAP)</span>
                      <div className="quantity-control">
                        <button 
                          type="button" 
                          className="quantity-btn" 
                          onClick={() => handleServiceQuantityChange('pap', Math.max(0, (proposalData.serviceQuantities['pap'] || 0) - 1))}
                          disabled={(proposalData.serviceQuantities['pap'] || 0) === 0}
                        >
                          −
                        </button>
                        <input 
                          type="number" 
                          min="0" 
                          max="99" 
                          value={proposalData.serviceQuantities['pap'] || 0} 
                          onChange={(e) => handleServiceQuantityChange('pap', parseInt(e.target.value) || 0)}
                          className="quantity-input"
                        />
                        <button 
                          type="button" 
                          className="quantity-btn" 
                          onClick={() => handleServiceQuantityChange('pap', Math.min(99, (proposalData.serviceQuantities['pap'] || 0) + 1))}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    
                    {/* Transacional */}
                    <div className="service-quantity-control">
                      <span className="service-label">Transacional</span>
                      <div className="quantity-control">
                        <button 
                          type="button" 
                          className="quantity-btn" 
                          onClick={() => handleServiceQuantityChange('transacional', Math.max(0, (proposalData.serviceQuantities['transacional'] || 0) - 1))}
                          disabled={(proposalData.serviceQuantities['transacional'] || 0) === 0}
                        >
                          −
                        </button>
                        <input 
                          type="number" 
                          min="0" 
                          max="99" 
                          value={proposalData.serviceQuantities['transacional'] || 0} 
                          onChange={(e) => handleServiceQuantityChange('transacional', parseInt(e.target.value) || 0)}
                          className="quantity-input"
                        />
                        <button 
                          type="button" 
                          className="quantity-btn" 
                          onClick={() => handleServiceQuantityChange('transacional', Math.min(99, (proposalData.serviceQuantities['transacional'] || 0) + 1))}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    
                    {/* Luz */}
                    <div className="service-quantity-control">
                      <span className="service-label">Luz</span>
                      <div className="quantity-control">
                        <button 
                          type="button" 
                          className="quantity-btn" 
                          onClick={() => handleServiceQuantityChange('luz', Math.max(0, (proposalData.serviceQuantities['luz'] || 0) - 1))}
                          disabled={(proposalData.serviceQuantities['luz'] || 0) === 0}
                        >
                          −
                        </button>
                        <input 
                          type="number" 
                          min="0" 
                          max="99" 
                          value={proposalData.serviceQuantities['luz'] || 0} 
                          onChange={(e) => handleServiceQuantityChange('luz', parseInt(e.target.value) || 0)}
                          className="quantity-input"
                        />
                        <button 
                          type="button" 
                          className="quantity-btn" 
                          onClick={() => handleServiceQuantityChange('luz', Math.min(99, (proposalData.serviceQuantities['luz'] || 0) + 1))}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    
                    {/* Gás */}
                    <div className="service-quantity-control">
                      <span className="service-label">Gás</span>
                      <div className="quantity-control">
                        <button 
                          type="button" 
                          className="quantity-btn" 
                          onClick={() => handleServiceQuantityChange('gas', Math.max(0, (proposalData.serviceQuantities['gas'] || 0) - 1))}
                          disabled={(proposalData.serviceQuantities['gas'] || 0) === 0}
                        >
                          −
                        </button>
                        <input 
                          type="number" 
                          min="0" 
                          max="99" 
                          value={proposalData.serviceQuantities['gas'] || 0} 
                          onChange={(e) => handleServiceQuantityChange('gas', parseInt(e.target.value) || 0)}
                          className="quantity-input"
                        />
                        <button 
                          type="button" 
                          className="quantity-btn" 
                          onClick={() => handleServiceQuantityChange('gas', Math.min(99, (proposalData.serviceQuantities['gas'] || 0) + 1))}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    
                    {/* Mobilidade */}
                    <div className="service-quantity-control">
                      <span className="service-label">Mobilidade Elétrica</span>
                      <div className="quantity-control">
                        <button 
                          type="button" 
                          className="quantity-btn" 
                          onClick={() => handleServiceQuantityChange('mobilidade', Math.max(0, (proposalData.serviceQuantities['mobilidade'] || 0) - 1))}
                          disabled={(proposalData.serviceQuantities['mobilidade'] || 0) === 0}
                        >
                          −
                        </button>
                        <input 
                          type="number" 
                          min="0" 
                          max="99" 
                          value={proposalData.serviceQuantities['mobilidade'] || 0} 
                          onChange={(e) => handleServiceQuantityChange('mobilidade', parseInt(e.target.value) || 0)}
                          className="quantity-input"
                        />
                        <button 
                          type="button" 
                          className="quantity-btn" 
                          onClick={() => handleServiceQuantityChange('mobilidade', Math.min(99, (proposalData.serviceQuantities['mobilidade'] || 0) + 1))}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    
                    {/* Outro */}
                    <div className="service-quantity-control">
                      <span className="service-label">Outro</span>
                      <div className="quantity-control">
                        <button 
                          type="button" 
                          className="quantity-btn" 
                          onClick={() => handleServiceQuantityChange('outro', Math.max(0, (proposalData.serviceQuantities['outro'] || 0) - 1))}
                          disabled={(proposalData.serviceQuantities['outro'] || 0) === 0}
                        >
                          −
                        </button>
                        <input 
                          type="number" 
                          min="0" 
                          max="99" 
                          value={proposalData.serviceQuantities['outro'] || 0} 
                          onChange={(e) => handleServiceQuantityChange('outro', parseInt(e.target.value) || 0)}
                          className="quantity-input"
                        />
                        <button 
                          type="button" 
                          className="quantity-btn" 
                          onClick={() => handleServiceQuantityChange('outro', Math.min(99, (proposalData.serviceQuantities['outro'] || 0) + 1))}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                  {fieldErrors.servicos && (
                    <span className="field-error">{fieldErrors.servicos}</span>
                  )}
                </div>
                
                {/* Detalhes do Serviço */}
                <div className="service-details" id="serviceDetails">
                  {proposalData.servicos.map((instance: any) => createServiceSegment(instance))}
                </div>
              </div>

              {/* Step 4: Resumo */}
              <div className={`form-section ${currentProposalStep === 4 ? 'active' : ''}`} id="proposalStep4">
                <h3 className="section-title">Resumo</h3>
                <p className="section-description">Revisão de todas as informações antes de submeter</p>
                
                <div className="summary-container" id="proposalSummary">
                  <div className="summary-section">
                    <h4>Equipa Interna</h4>
                    <p><strong>Proposta de Marcação:</strong> <span style={{ color: proposalData.equipaInterna?.propostaMarcacao ? 'inherit' : 'red', fontWeight: proposalData.equipaInterna?.propostaMarcacao ? 'normal' : 'bold' }}>{proposalData.equipaInterna?.propostaMarcacao || '-'}</span></p>
                    <p><strong>Assinatura do Contrato:</strong> <span style={{ color: proposalData.equipaInterna?.assinaturaContrato ? 'inherit' : 'red', fontWeight: proposalData.equipaInterna?.assinaturaContrato ? 'normal' : 'bold' }}>{proposalData.equipaInterna?.assinaturaContrato || '-'}</span></p>
                    <p><strong>Tipo de Pedido:</strong> <span style={{ color: proposalData.equipaInterna?.tipoPedido ? 'inherit' : 'red', fontWeight: proposalData.equipaInterna?.tipoPedido ? 'normal' : 'bold' }}>{proposalData.equipaInterna?.tipoPedido === 'normal' ? 'Normal' : proposalData.equipaInterna?.tipoPedido === 'roadtrip' ? 'RoadTrip' : proposalData.equipaInterna?.tipoPedido || '-'}</span></p>
                  </div>
                  
                  <div className="summary-section">
                    <h4>Empresa</h4>
                    <p><strong>Nome da Empresa:</strong> <span style={{ color: proposalData.empresa?.nomeEmpresa ? 'inherit' : 'red', fontWeight: proposalData.empresa?.nomeEmpresa ? 'normal' : 'bold' }}>{proposalData.empresa?.nomeEmpresa || '-'}</span></p>
                    <p><strong>NIF:</strong> <span style={{ color: proposalData.empresa?.nifEmpresa ? 'inherit' : 'red', fontWeight: proposalData.empresa?.nifEmpresa ? 'normal' : 'bold' }}>{proposalData.empresa?.nifEmpresa || '-'}</span></p>
                    <p><strong>Morada de Correspondência:</strong> <span style={{ color: proposalData.empresa?.moradaCorrespondencia ? 'inherit' : 'red', fontWeight: proposalData.empresa?.moradaCorrespondencia ? 'normal' : 'bold' }}>{proposalData.empresa?.moradaCorrespondencia || '-'}</span></p>
                    <p><strong>Localidade:</strong> <span style={{ color: proposalData.empresa?.regiao ? 'inherit' : 'red', fontWeight: proposalData.empresa?.regiao ? 'normal' : 'bold' }}>{proposalData.empresa?.regiao || '-'}</span></p>
                    <p><strong>CAE:</strong> <span style={{ color: proposalData.empresa?.cae ? 'inherit' : 'red', fontWeight: proposalData.empresa?.cae ? 'normal' : 'bold' }}>{proposalData.empresa?.cae || '-'}</span></p>
                    <p><strong>Nome do Representante:</strong> <span style={{ color: proposalData.empresa?.nomeRepresentante ? 'inherit' : 'red', fontWeight: proposalData.empresa?.nomeRepresentante ? 'normal' : 'bold' }}>{proposalData.empresa?.nomeRepresentante || '-'}</span></p>
                    <p><strong>Contacto do Representante:</strong> <span style={{ color: proposalData.empresa?.contactoRepresentante ? 'inherit' : 'red', fontWeight: proposalData.empresa?.contactoRepresentante ? 'normal' : 'bold' }}>{proposalData.empresa?.contactoRepresentante || '-'}</span></p>
                    <p><strong>Email do Representante:</strong> <span style={{ color: proposalData.empresa?.emailRepresentante ? 'inherit' : 'red', fontWeight: proposalData.empresa?.emailRepresentante ? 'normal' : 'bold' }}>{proposalData.empresa?.emailRepresentante || '-'}</span></p>
                    <p><strong>Email da Empresa:</strong> <span style={{ color: proposalData.empresa?.emailEmpresa ? 'inherit' : 'red', fontWeight: proposalData.empresa?.emailEmpresa ? 'normal' : 'bold' }}>{proposalData.empresa?.emailEmpresa || '-'}</span></p>
                  </div>
                  
                  <div className="summary-section">
                    <h4>Serviço</h4>
                    {proposalData.servicos.length > 0 ? (
                      <div className="summary-service-details">
                        {proposalData.servicos.map((instance: any) => {
                          const instanceId = instance.instanceId;
                          const instanceDetails = proposalData.serviceDetails[instanceId] || {};
                          const serviceNames: Record<string, string> = {
                            'bairro-solar': 'Bairro Solar',
                            'pap': 'Pay AS Produced (PAP)',
                            'transacional': 'Transacional',
                            'luz': 'Luz',
                            'gas': 'Gás',
                            'mobilidade': 'Mobilidade Elétrica',
                            'outro': 'Outro'
                          };
                          const serviceName = serviceNames[instance.service] || instance.service;
                          const segmentTitle = `${serviceName} - ${instance.instanceNumber}`;
                          const isExpanded = expandedServiceSegments[`summary-${instanceId}`] || false;
                          
                          return (
                            <div key={`summary-${instanceId}`} className={`service-segment ${isExpanded ? 'expanded' : ''}`} style={{ marginBottom: '16px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#ffffff' }}>
                              <div className="service-segment-header" onClick={() => toggleServiceSegment(`summary-${instanceId}`)} style={{ padding: '12px 16px', cursor: 'pointer', backgroundColor: isExpanded ? '#f9fafb' : '#ffffff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#374151' }}>
                                <span style={{ fontWeight: 600, color: '#374151' }}>{segmentTitle}</span>
                                <span className="service-segment-toggle" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease', color: '#374151' }}>▼</span>
                              </div>
                              <div className="service-segment-content" style={{ display: isExpanded ? 'block' : 'none', padding: '16px', backgroundColor: '#ffffff', borderTop: '1px solid #e5e7eb', color: '#374151' }}>
                                <div style={{ display: 'grid', gap: '12px', color: '#374151' }}>
                                  {/* Morada de Fornecimento - all services */}
                                  <div style={{ color: '#374151' }}>
                                    <strong style={{ color: '#374151' }}>Morada de Fornecimento:</strong> <span style={{ color: instanceDetails.moradaFornecimento ? '#374151' : '#ef4444' }}>{instanceDetails.moradaFornecimento || '-'}</span>
                                  </div>
                                  {/* Bairro Solar, PAP, Luz, Transacional fields */}
                                  {(instance.service === 'bairro-solar' || instance.service === 'pap' || instance.service === 'luz' || instance.service === 'transacional') && (
                                    <>
                                      <div style={{ color: '#374151' }}>
                                        <strong style={{ color: '#374151' }}>CPE:</strong> <span style={{ color: instanceDetails.cpe ? '#374151' : '#ef4444' }}>{instanceDetails.cpe || '-'}</span>
                                      </div>
                                      <div style={{ color: '#374151' }}>
                                        <strong style={{ color: '#374151' }}>Potência:</strong> <span style={{ color: instanceDetails.potencia ? '#374151' : '#ef4444' }}>
                                          {instanceDetails.potencia 
                                            ? (instanceDetails.potencia === 'outra' ? (instanceDetails.potenciaOutra || instanceDetails.potencia) : instanceDetails.potencia.toUpperCase())
                                            : '-'
                                          }
                                        </span>
                                      </div>
                                      {instanceDetails.potencia === 'outra' && instanceDetails.potenciaOutra && (
                                        <div style={{ color: '#374151' }}>
                                          <strong style={{ color: '#374151' }}>Potência (Outra):</strong> <span style={{ color: '#374151' }}>{instanceDetails.potenciaOutra}</span>
                                        </div>
                                      )}
                                      <div style={{ color: '#374151' }}>
                                        <strong style={{ color: '#374151' }}>Fatura:</strong> <span style={{ color: instanceDetails.fatura_name ? '#374151' : '#ef4444' }}>{instanceDetails.fatura_name || '-'}</span>
                                      </div>
                                      {instance.service !== 'luz' && (
                                        <div style={{ color: '#374151' }}>
                                          <strong style={{ color: '#374151' }}>Print do Telhado:</strong> <span style={{ color: instanceDetails.printTelhado_name ? '#374151' : '#ef4444' }}>{instanceDetails.printTelhado_name || '-'}</span>
                                        </div>
                                      )}
                                      {instance.service === 'luz' && (
                                        <div style={{ color: '#374151' }}>
                                          <strong style={{ color: '#374151' }}>Sistema:</strong> <span style={{ color: instanceDetails.sistema ? '#374151' : '#ef4444' }}>
                                            {instanceDetails.sistema === 'trifasico' ? 'Trifásico' : instanceDetails.sistema === 'monofasico' ? 'Monofásico' : (instanceDetails.sistema || '-')}
                                          </span>
                                        </div>
                                      )}
                                      {proposalData.equipaInterna?.tipoPedido === 'roadtrip' && (
                                        <div style={{ color: '#374151' }}>
                                          <strong style={{ color: '#374151' }}>Data prevista para o Fecho:</strong> <span style={{ color: instanceDetails.dataFecho ? '#374151' : '#ef4444' }}>
                                            {instanceDetails.dataFecho 
                                              ? new Date(instanceDetails.dataFecho).toLocaleDateString('pt-PT')
                                              : '-'
                                            }
                                          </span>
                                        </div>
                                      )}
                                      {/* Bairro Solar specific - Coordenadas Geográficas */}
                                      {instance.service === 'bairro-solar' && (
                                        <div style={{ color: '#374151' }}>
                                          <strong style={{ color: '#374151' }}>Coordenadas Geográficas:</strong> <span style={{ whiteSpace: 'pre-wrap', color: instanceDetails.coordenadasGeograficas ? '#374151' : '#ef4444' }}>{instanceDetails.coordenadasGeograficas || '-'}</span>
                                        </div>
                                      )}
                                      {instanceDetails.observacoes && (
                                        <div style={{ color: '#374151' }}>
                                          <strong style={{ color: '#374151' }}>Observações:</strong> <span style={{ whiteSpace: 'pre-wrap', color: '#374151' }}>{instanceDetails.observacoes}</span>
                                        </div>
                                      )}
                                    </>
                                  )}
                                  
                                  {/* Transacional specific - Forma de Pagamento */}
                                  {instance.service === 'transacional' && (
                                    <div style={{ color: '#374151' }}>
                                      <strong style={{ color: '#374151' }}>Forma de Pagamento:</strong> <span style={{ color: instanceDetails.formaPagamento ? '#374151' : '#ef4444' }}>
                                        {instanceDetails.formaPagamento 
                                          ? (instanceDetails.formaPagamento === 'pronto' ? 'Pronto Pagamento' : instanceDetails.formaPagamento === 'prestacoes' ? 'Prestações' : instanceDetails.formaPagamento)
                                          : '-'
                                        }
                                      </span>
                                    </div>
                                  )}
                                  
                                  {/* Gas specific fields */}
                                  {instance.service === 'gas' && (
                                    <>
                                      <div style={{ color: '#374151' }}>
                                        <strong style={{ color: '#374151' }}>CUI:</strong> <span style={{ color: instanceDetails.cui ? '#374151' : '#ef4444' }}>{instanceDetails.cui || '-'}</span>
                                      </div>
                                      <div style={{ color: '#374151' }}>
                                        <strong style={{ color: '#374151' }}>Escalão:</strong> <span style={{ color: instanceDetails.escalao ? '#374151' : '#ef4444' }}>
                                          {instanceDetails.escalao ? `Escalão ${instanceDetails.escalao}` : '-'}
                                        </span>
                                      </div>
                                      <div style={{ color: '#374151' }}>
                                        <strong style={{ color: '#374151' }}>Fatura:</strong> <span style={{ color: instanceDetails.fatura_name ? '#374151' : '#ef4444' }}>{instanceDetails.fatura_name || '-'}</span>
                                      </div>
                                      {proposalData.equipaInterna?.tipoPedido === 'roadtrip' && (
                                        <div style={{ color: '#374151' }}>
                                          <strong style={{ color: '#374151' }}>Data prevista para o Fecho:</strong> <span style={{ color: instanceDetails.dataFecho ? '#374151' : '#ef4444' }}>
                                            {instanceDetails.dataFecho 
                                              ? new Date(instanceDetails.dataFecho).toLocaleDateString('pt-PT')
                                              : '-'
                                            }
                                          </span>
                                        </div>
                                      )}
                                      {instanceDetails.observacoes && (
                                        <div style={{ color: '#374151' }}>
                                          <strong style={{ color: '#374151' }}>Observações:</strong> <span style={{ whiteSpace: 'pre-wrap', color: '#374151' }}>{instanceDetails.observacoes}</span>
                                        </div>
                                      )}
                                    </>
                                  )}
                                  
                                  {/* Mobilidade Elétrica questionnaire */}
                                  {instance.service === 'mobilidade' && (
                                    <>
                                      {MOBILIDADE_FIELD_KEYS.map((fieldKey) => {
                                        const raw = instanceDetails[fieldKey];
                                        const display = formatMobilidadeFieldValue(fieldKey, raw);
                                        return (
                                          <div key={fieldKey} style={{ color: '#374151' }}>
                                            <strong style={{ color: '#374151' }}>{MOBILIDADE_FIELD_LABELS[fieldKey]}:</strong>{' '}
                                            <span style={{ color: display ? '#374151' : '#ef4444', whiteSpace: 'pre-wrap' }}>
                                              {display || '-'}
                                            </span>
                                          </div>
                                        );
                                      })}
                                      {instanceDetails.observacoes && (
                                        <div style={{ color: '#374151' }}>
                                          <strong style={{ color: '#374151' }}>Observações:</strong>{' '}
                                          <span style={{ whiteSpace: 'pre-wrap', color: '#374151' }}>{instanceDetails.observacoes}</span>
                                        </div>
                                      )}
                                    </>
                                  )}

                                  {/* Outro - only Observações (Morada de Fornecimento shown above for all services) */}
                                  {instance.service === 'outro' && (
                                    <div style={{ color: '#374151' }}>
                                      <strong style={{ color: '#374151' }}>Observações:</strong> <span style={{ whiteSpace: 'pre-wrap', color: '#374151' }}>{instanceDetails.observacoes || '-'}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p style={{ color: 'red', fontWeight: 'bold' }}>-</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Form Buttons */}
            <div className="form-buttons">
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                {getProposalPrevStep(currentProposalStep) != null && (
                  <button type="button" className="btn btn-secondary" id="proposalPrevBtn" onClick={() => changeProposalStep('prev')}>
                    ← Anterior
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                {getProposalNextStep(currentProposalStep) != null ? (
                  <button type="button" className="btn btn-primary" id="proposalNextBtn" onClick={() => changeProposalStep('next')}>
                    Seguinte →
                  </button>
                ) : (
                  <button type="button" className="btn btn-primary" id="proposalSubmitBtn" onClick={submitProposal} disabled={isSubmittingProposal}>
                    {isSubmittingProposal ? 'A submeter...' : '✓ Submeter'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Instant Marcação Modal */}
      {showInstantMarcacaoModal && (
        <div className="event-modal marcacao-modal marcacao-instant-modal">
          <div className="event-modal-content marcacao-modal-content">
            <div className="event-modal-header marcacao-header">
              <button 
                className="close-btn" 
                onClick={() => {
                  setShowInstantMarcacaoModal(false);
                  setInstantMarcacaoNotification(null);
                  setInstantMarcacaoFormData({});
                  setCurrentInstantMarcacaoStep(1);
                  setInstantMarcacaoValidationErrors({});
                }}
              >
                ×
              </button>
              
              <h1>Marcação instantânea</h1>
              
              <p className="modal-subtitle">Criar marcação e solicitar proposta em um único fluxo</p>
              
              <div className="progress-steps-grid">
                <div className={`step ${currentInstantMarcacaoStep === 1 ? 'active' : currentInstantMarcacaoStep > 1 ? 'completed' : ''}`}>
                  <div className="step-icon">1</div>
                  <div className="step-title">Agendamento</div>
                </div>
                <div className={`step ${currentInstantMarcacaoStep === 2 ? 'active' : currentInstantMarcacaoStep > 2 ? 'completed' : ''}`}>
                  <div className="step-icon">2</div>
                  <div className="step-title">Produto</div>
                </div>
                <div className={`step ${currentInstantMarcacaoStep === 3 ? 'active' : currentInstantMarcacaoStep > 3 ? 'completed' : ''}`}>
                  <div className="step-icon">3</div>
                  <div className="step-title">Cliente</div>
                </div>
              </div>
            </div>
            
            <div className="event-modal-body" style={{ position: 'relative' }}>
              {instantMarcacaoNotification && (
                <div 
                  className={`marcacao-notification marcacao-notification-${instantMarcacaoNotification.type}`}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1000,
                    padding: '12px 24px',
                    borderRadius: '8px',
                    backgroundColor: instantMarcacaoNotification.type === 'success' ? '#10b981' : '#ef4444',
                    color: 'white',
                    fontWeight: '500',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    maxWidth: '90%',
                    textAlign: 'center'
                  }}
                >
                  {instantMarcacaoNotification.message}
                </div>
              )}
              <form className="marcacao-form">
                {/* Step 1: Agendamento */}
                {currentInstantMarcacaoStep === 1 && (
                  <div className="form-section active">
                    <h3 className="section-title">Agendamento</h3>
                    <p className="section-description">Defina a data, hora e responsável para esta marcação</p>
                    
                    <div className="form-grid">
                      <div className="form-group">
                        <label className="form-label">
                          <span className="required">*</span> Data
                        </label>
                        <input 
                          type="date" 
                          className={`form-input ${instantMarcacaoValidationErrors.marcacaoData ? 'error' : ''}`}
                          value={instantMarcacaoFormData.marcacaoData || ''}
                          onChange={(e) => setInstantMarcacaoFormData({...instantMarcacaoFormData, marcacaoData: e.target.value})}
                        />
                        {instantMarcacaoValidationErrors.marcacaoData && (
                          <span className="error-message">{instantMarcacaoValidationErrors.marcacaoData}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          <span className="required">*</span> Hora de Início
                        </label>
                        <input 
                          type="time" 
                          className={`form-input ${instantMarcacaoValidationErrors.horaInicio ? 'error' : ''}`}
                          value={instantMarcacaoFormData.horaInicio || ''}
                          onChange={(e) => setInstantMarcacaoFormData({...instantMarcacaoFormData, horaInicio: e.target.value})}
                        />
                        {instantMarcacaoValidationErrors.horaInicio && (
                          <span className="error-message">{instantMarcacaoValidationErrors.horaInicio}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          <span className="required">*</span> Hora de Fim
                        </label>
                        <input 
                          type="time" 
                          className={`form-input ${instantMarcacaoValidationErrors.horaFim ? 'error' : ''}`}
                          value={instantMarcacaoFormData.horaFim || ''}
                          onChange={(e) => setInstantMarcacaoFormData({...instantMarcacaoFormData, horaFim: e.target.value})}
                        />
                        {instantMarcacaoValidationErrors.horaFim && (
                          <span className="error-message">{instantMarcacaoValidationErrors.horaFim}</span>
                        )}
                      </div>
                      
                      <div className="form-group full-width">
                        <label className="form-label">Observações</label>
                        <textarea 
                          className="form-input" 
                          rows={3} 
                          placeholder="Observações adicionais sobre esta marcação..."
                          value={instantMarcacaoFormData.marcacaoDescription || ''}
                          onChange={(e) => setInstantMarcacaoFormData({...instantMarcacaoFormData, marcacaoDescription: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Produto da Marcação */}
                {currentInstantMarcacaoStep === 2 && (
                  <div className="form-section active">
                    <h3 className="section-title">Produto da Marcação</h3>
                    <p className="section-description">Selecione o tipo de produto para esta marcação</p>
                    
                    <div className="form-group">
                      <label className="form-label">
                        <span className="required">*</span> PRODUTO DA MARCAÇÃO
                      </label>
                      <select 
                        className={`form-input ${instantMarcacaoValidationErrors.produto ? 'error' : ''}`}
                        value={instantMarcacaoFormData.produto || ''} 
                        onChange={(e) => setInstantMarcacaoFormData({...instantMarcacaoFormData, produto: e.target.value})}
                      >
                        <option value="">Selecione o produto</option>
                        <option value="bairro-solar">Bairro Solar</option>
                        <option value="pap">PAY AS PRODUCED (PAP)</option>
                        <option value="gas">Gás</option>
                        <option value="eletricidade">Eletricidade</option>
                      </select>
                      {instantMarcacaoValidationErrors.produto && (
                        <span className="error-message">{instantMarcacaoValidationErrors.produto}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 3: Informação do Cliente */}
                {currentInstantMarcacaoStep === 3 && (
                  <div className="form-section active">
                    <h3 className="section-title">Informação do Cliente</h3>
                    <p className="section-description">Dados básicos do cliente para esta marcação</p>
                    
                    <div className="form-grid">
                      <div className="form-group">
                        <label className="form-label">
                          <span className="required">*</span> Nome Empresa
                        </label>
                        <input 
                          type="text" 
                          className={`form-input ${instantMarcacaoValidationErrors.nomeEmpresa ? 'error' : ''}`}
                          placeholder="Nome da empresa"
                          value={instantMarcacaoFormData.nomeEmpresa || ''}
                          onChange={(e) => setInstantMarcacaoFormData({...instantMarcacaoFormData, nomeEmpresa: e.target.value})}
                        />
                        {instantMarcacaoValidationErrors.nomeEmpresa && (
                          <span className="error-message">{instantMarcacaoValidationErrors.nomeEmpresa}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          <span className="required">*</span> Nome Responsável/Proprietário
                        </label>
                        <input 
                          type="text" 
                          className={`form-input ${instantMarcacaoValidationErrors.nomeResponsavel ? 'error' : ''}`}
                          placeholder="Nome do responsável"
                          value={instantMarcacaoFormData.nomeResponsavel || ''}
                          onChange={(e) => setInstantMarcacaoFormData({...instantMarcacaoFormData, nomeResponsavel: e.target.value})}
                        />
                        {instantMarcacaoValidationErrors.nomeResponsavel && (
                          <span className="error-message">{instantMarcacaoValidationErrors.nomeResponsavel}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          <span className="required">*</span> Contacto Cliente
                        </label>
                        <input 
                          type="tel" 
                          className={`form-input ${instantMarcacaoValidationErrors.contactoCliente ? 'error' : ''}`}
                          placeholder="Número de telefone"
                          value={instantMarcacaoFormData.contactoCliente || ''}
                          onChange={(e) => setInstantMarcacaoFormData({...instantMarcacaoFormData, contactoCliente: e.target.value})}
                        />
                        {instantMarcacaoValidationErrors.contactoCliente && (
                          <span className="error-message">{instantMarcacaoValidationErrors.contactoCliente}</span>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label">Email da Empresa (opcional)</label>
                        <input
                          type="email"
                          className={`form-input ${instantMarcacaoValidationErrors.emailEmpresa ? 'error' : ''}`}
                          placeholder="email@empresa.pt"
                          value={instantMarcacaoFormData.emailEmpresa || ''}
                          onChange={(e) => setInstantMarcacaoFormData({...instantMarcacaoFormData, emailEmpresa: e.target.value})}
                        />
                        {instantMarcacaoValidationErrors.emailEmpresa && (
                          <span className="error-message">{instantMarcacaoValidationErrors.emailEmpresa}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Código-Postal / Localidade</label>
                        <div className="postal-code-group">
                          <div className="postal-code-field">
                            <input 
                              type="text" 
                              className="form-input" 
                              value={instantMarcacaoFormData.codigoPostal1 || ''}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                                setInstantMarcacaoFormData({...instantMarcacaoFormData, codigoPostal1: value});
                              }}
                              placeholder="0000"
                              maxLength={4}
                            />
                            <div className="field-hint">4 dígitos</div>
                          </div>
                          <div className="postal-code-field">
                            <input 
                              type="text" 
                              className="form-input" 
                              value={instantMarcacaoFormData.codigoPostal2 || ''}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '').slice(0, 3);
                                setInstantMarcacaoFormData({...instantMarcacaoFormData, codigoPostal2: value});
                              }}
                              placeholder="000"
                              maxLength={3}
                            />
                            <div className="field-hint">3 dígitos</div>
                          </div>
                          <div className="postal-code-field localidade-field">
                            <input 
                              type="text" 
                              className="form-input" 
                              value={instantMarcacaoFormData.localidade || ''}
                              onChange={(e) => {
                                setInstantMarcacaoFormData({...instantMarcacaoFormData, localidade: e.target.value});
                              }}
                              placeholder="Localidade"
                            />
                            <div className="field-hint">Localidade</div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="form-group full-width">
                        <label className="form-label">
                          <span className="required">*</span> Morada
                        </label>
                        <input 
                          type="text" 
                          className={`form-input ${instantMarcacaoValidationErrors.morada ? 'error' : ''}`}
                          placeholder="Morada completa"
                          value={instantMarcacaoFormData.morada || ''}
                          onChange={(e) => setInstantMarcacaoFormData({...instantMarcacaoFormData, morada: e.target.value})}
                        />
                        {instantMarcacaoValidationErrors.morada && (
                          <span className="error-message">{instantMarcacaoValidationErrors.morada}</span>
                        )}
                      </div>
                      
                      <div className="form-group full-width">
                        <label className="form-label">Link Google Maps</label>
                        <input 
                          type="url" 
                          className="form-input" 
                          value={instantMarcacaoFormData.linkGoogleMaps || ''}
                          onChange={(e) => setInstantMarcacaoFormData({...instantMarcacaoFormData, linkGoogleMaps: e.target.value})}
                          placeholder="https://maps.google.com/..."
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Form Navigation */}
                <div className="form-navigation">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={handleInstantMarcacaoPrevStep}
                    style={{ display: currentInstantMarcacaoStep === 1 ? 'none' : 'block' }}
                  >
                    ← Anterior
                  </button>
                  <div style={{ flex: 1 }}></div>
                  {currentInstantMarcacaoStep < 3 ? (
                    <button 
                      type="button" 
                      className="btn btn-primary" 
                      onClick={handleInstantMarcacaoNextStep}
                    >
                      Seguinte →
                    </button>
                  ) : (
                    <button 
                      type="button" 
                      className="btn btn-primary" 
                      onClick={handleInstantMarcacaoSubmit}
                      disabled={savingInstantMarcacao}
                    >
                      {savingInstantMarcacao ? 'A criar...' : '✓ Criar Marcação'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Minhas Propostas Modal */}
      {showMinhasPropostasModal && (
        <div className="modal active minhas-propostas-modal" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
          <div className="modal-content minhas-propostas-modal-content" style={{ 
            maxWidth: '1920px',
            width: 'min(95vw, 1920px)',
            maxHeight: 'calc(100dvh - 32px)',
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            marginTop: '0'
          }}>
            <div className="modal-header minhas-propostas-modal-header" style={{
              padding: '1.5rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0,
              borderRadius: '20px 20px 0 0'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', zIndex: 10, position: 'relative' }}>
                <h2 className="minhas-propostas-title" style={{ 
                  color: '#ffffff', 
                  fontSize: '24px', 
                  fontWeight: '700', 
                  margin: 0,
                  padding: 0,
                  lineHeight: '1.2',
                  textShadow: 'none',
                  filter: 'none',
                  WebkitTextStroke: 'transparent',
                  opacity: 1,
                  visibility: 'visible'
                }}>Minhas Propostas</h2>
                {!gestaoModalsOnly && (
                  <p style={{
                    color: '#ffffff',
                    opacity: 0.9,
                    fontSize: '16px',
                    margin: 0,
                    padding: 0,
                    textShadow: 'none'
                  }}>
                    {canFilterGestaoBySidebarUser() && selectedSidebarUser
                      ? (() => {
                          const name =
                            userMap[selectedSidebarUser] ||
                            comercialUsers.find((u: { id: number }) => u.id === selectedSidebarUser)?.name ||
                            comercialUsers.find((u: { id: number }) => u.id === selectedSidebarUser)?.username ||
                            '';
                          return name
                            ? `Propostas submetidas e partilhadas com ${name}`
                            : 'Propostas do utilizador selecionado';
                        })()
                      : 'As suas propostas submetidas e pedidos partilhados consigo'}
                  </p>
                )}
                {gestaoModalsOnly && gestaoFilterUserLabel && gestaoFilterUserLabel !== 'Toda a Equipa' && (
                  <p style={{
                    color: '#ffffff',
                    opacity: 0.9,
                    fontSize: '16px',
                    margin: 0,
                    padding: 0,
                    textShadow: 'none'
                  }}>
                    {`Propostas submetidas e partilhadas com ${gestaoFilterUserLabel}`}
                  </p>
                )}
              </div>
              <button
                onClick={closeMinhasPropostasModal}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'white',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                }}
              >
                ×
              </button>
            </div>
            
            {/* Subheader with Search and Filters */}
            <div className="modal-subheader" style={{
              padding: '1.5rem',
              background: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Search Bar */}
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="Pesquisar por Empresa, NIF, CPE, Contacto Representante ou ID..."
                    value={proposalSearchTerm}
                    onChange={(e) => setProposalSearchTerm(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleProposalSearch();
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                  <button
                    onClick={handleProposalSearch}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: '#4b5563',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#374151';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#4b5563';
                    }}
                  >
                    🔍 Pesquisar
                  </button>
                </div>

                {/* Status sections (same as BackOffice proposal board) */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                  {(['available', 'roadtrip', 'op_criada', 'in_progress', 'proposta_aprovacao', 'exclusividade_pendente', 'pendente_gestor_edp', 'pendente_comercial', 'completed'] as const).map((sectionId) => (
                    <button
                      key={sectionId}
                      type="button"
                      onClick={() => {
                        setFilterSection(filterSection === sectionId ? '' : sectionId);
                        setCurrentProposalPage(1);
                      }}
                      style={{
                        padding: '8px 14px',
                        borderRadius: '8px',
                        border: 'none',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: filterSection === sectionId ? '#4f46e5' : '#e5e7eb',
                        color: filterSection === sectionId ? '#fff' : '#374151',
                        transition: 'background 0.2s, color 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        if (filterSection !== sectionId) {
                          e.currentTarget.style.background = '#d1d5db';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (filterSection !== sectionId) {
                          e.currentTarget.style.background = '#e5e7eb';
                        }
                      }}
                    >
                      <span>{MINHAS_PROPOSTAS_SECTION_LABELS[sectionId] || sectionId}</span>
                      <span style={{
                        minWidth: '20px',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: filterSection === sectionId ? 'rgba(255,255,255,0.3)' : '#9ca3af',
                        color: filterSection === sectionId ? '#fff' : '#374151',
                      }}>
                        {minhasPropostasSectionCounts[sectionId] ?? 0}
                      </span>
                    </button>
                  ))}
                </div>
                
                {/* Filters Row */}
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  {/* Date Filters */}
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Data:</label>
                    <select
                      value={filterDay}
                      onChange={(e) => setFilterDay(e.target.value)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">Dia</option>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                        <option key={day} value={String(day).padStart(2, '0')}>{day}</option>
                      ))}
                    </select>
                    <select
                      value={filterMonth}
                      onChange={(e) => setFilterMonth(e.target.value)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">Mês</option>
                      {[
                        { value: '01', label: 'Janeiro' },
                        { value: '02', label: 'Fevereiro' },
                        { value: '03', label: 'Março' },
                        { value: '04', label: 'Abril' },
                        { value: '05', label: 'Maio' },
                        { value: '06', label: 'Junho' },
                        { value: '07', label: 'Julho' },
                        { value: '08', label: 'Agosto' },
                        { value: '09', label: 'Setembro' },
                        { value: '10', label: 'Outubro' },
                        { value: '11', label: 'Novembro' },
                        { value: '12', label: 'Dezembro' }
                      ].map(month => (
                        <option key={month.value} value={month.value}>{month.label}</option>
                      ))}
                    </select>
                    <select
                      value={filterYear}
                      onChange={(e) => setFilterYear(e.target.value)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">Ano</option>
                      {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                        <option key={year} value={String(year)}>{year}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Service Type Filter */}
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Serviço:</label>
                    <select
                      value={filterServiceType}
                      onChange={(e) => setFilterServiceType(e.target.value)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: 'white',
                        cursor: 'pointer',
                        minWidth: '180px'
                      }}
                    >
                      <option value="">Todos os Serviços</option>
                      <option value="bairro-solar">Bairro Solar</option>
                      <option value="pap">Pay AS Produced (PAP)</option>
                      <option value="transacional">Transacional</option>
                      <option value="luz">Luz</option>
                      <option value="gas">Gás</option>
                      <option value="mobilidade">Mobilidade Elétrica</option>
                      <option value="outro">Outro</option>
                    </select>
                  </div>
                  
                  {/* Status/Tag Filter */}
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Estado:</label>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: 'white',
                        cursor: 'pointer',
                        minWidth: '180px'
                      }}
                    >
                      <option value="">Todos os Estados</option>
                      <option value="available">Pedido Submetido</option>
                      <option value="in_progress">Em Processo</option>
                      <option value="completed">Proposta disponível</option>
                      <option value="cancelled">Cancelado</option>
                    </select>
                  </div>
                  
                  {/* Clear Filters Button */}
                  {(proposalSearchTerm || filterDay || filterMonth || filterYear || filterServiceType || filterStatus || filterSection) && (
                    <button
                      onClick={() => {
                        setProposalSearchTerm('');
                        setFilterDay('');
                        setFilterMonth('');
                        setFilterYear('');
                        setFilterServiceType('');
                        setFilterStatus('');
                        setFilterSection('');
                        setCurrentProposalPage(1);
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        marginLeft: 'auto'
                      }}
                    >
                      Limpar Filtros
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            <div className="modal-body" style={{
              padding: '1.5rem',
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column'
            }}>
              {loadingProposalRequests ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                  <WonderTradeLoader size={80} message="A carregar propostas..." />
                </div>
              ) : filteredProposalRequests.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '3rem',
                  color: '#6b7280'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '1rem' }}>📋</div>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '0.5rem', color: '#1f2937' }}>
                    Nenhuma proposta encontrada
                  </h3>
                  <p style={{ fontSize: '14px' }}>
                    Ainda não tem propostas submetidas nem pedidos partilhados consigo.
                  </p>
                </div>
              ) : (
                <div className="contracts-documents-container minhas-propostas-cards" style={{
                  flex: 1,
                  minHeight: 0
                }}>
                  {(() => {
                    const startIndex = (currentProposalPage - 1) * proposalsPerPage;
                    const endIndex = startIndex + proposalsPerPage;
                    const paginatedRequests = filteredProposalRequests.slice(startIndex, endIndex);
                    const displayRequests = paginatedRequests.slice(0, proposalsPerPage);
                    return displayRequests.map((request) => (
                      <div key={request.id} id={`proposal-${request.id}`}>
                        <RequestCard
                          request={request}
                          scrollableBody
                          expanded={expandedProposalCardId === String(request.id)}
                          onToggleExpand={() =>
                            setExpandedProposalCardId((prev) =>
                              prev === String(request.id) ? null : String(request.id)
                            )
                          }
                        />
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
            {filteredProposalRequests.length > proposalsPerPage && (
              <div className="modal-footer minhas-propostas-pagination" style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '20px',
                padding: '1rem 1.5rem',
                borderTop: '1px solid #e5e7eb',
                background: '#f9fafb',
                flexShrink: 0
              }}>
                      <button
                        className="pagination-arrow"
                        onClick={() => setCurrentProposalPage(prev => Math.max(1, prev - 1))}
                        disabled={currentProposalPage === 1}
                        style={{
                          padding: '12px 24px',
                          background: currentProposalPage === 1 ? '#f3f4f6' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                          color: currentProposalPage === 1 ? '#9ca3af' : 'white',
                          border: currentProposalPage === 1 ? '1px solid #e5e7eb' : 'none',
                          borderRadius: '8px',
                          cursor: currentProposalPage === 1 ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          transition: 'all 0.2s ease',
                          boxShadow: currentProposalPage === 1 ? 'none' : '0 2px 4px rgba(59, 130, 246, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          opacity: currentProposalPage === 1 ? 0.6 : 1,
                          minHeight: 'auto',
                          whiteSpace: 'nowrap',
                          minWidth: 'auto',
                          width: 'auto'
                        }}
                        onMouseEnter={(e) => {
                          if (currentProposalPage > 1) {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)';
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.3)';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (currentProposalPage > 1) {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.2)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }
                        }}
                      >
                        <span style={{ fontSize: '16px', lineHeight: '1' }}>←</span>
                        <span>Anterior</span>
                      </button>
                      <div className="pagination-info" style={{
                        fontSize: '14px',
                        color: '#374151',
                        fontWeight: '600',
                        padding: '12px 20px',
                        background: '#f9fafb',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        minWidth: 'auto',
                        textAlign: 'center',
                        minHeight: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        whiteSpace: 'nowrap'
                      }}>
                        Página {currentProposalPage} de {Math.ceil(filteredProposalRequests.length / proposalsPerPage)}
                      </div>
                      <button
                        className="pagination-arrow"
                        onClick={() => setCurrentProposalPage(prev => Math.min(Math.ceil(filteredProposalRequests.length / proposalsPerPage), prev + 1))}
                        disabled={currentProposalPage >= Math.ceil(filteredProposalRequests.length / proposalsPerPage)}
                        style={{
                          padding: '12px 24px',
                          background: currentProposalPage >= Math.ceil(filteredProposalRequests.length / proposalsPerPage) ? '#f3f4f6' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                          color: currentProposalPage >= Math.ceil(filteredProposalRequests.length / proposalsPerPage) ? '#9ca3af' : 'white',
                          border: currentProposalPage >= Math.ceil(filteredProposalRequests.length / proposalsPerPage) ? '1px solid #e5e7eb' : 'none',
                          borderRadius: '8px',
                          cursor: currentProposalPage >= Math.ceil(filteredProposalRequests.length / proposalsPerPage) ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          transition: 'all 0.2s ease',
                          boxShadow: currentProposalPage >= Math.ceil(filteredProposalRequests.length / proposalsPerPage) ? 'none' : '0 2px 4px rgba(59, 130, 246, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          opacity: currentProposalPage >= Math.ceil(filteredProposalRequests.length / proposalsPerPage) ? 0.6 : 1,
                          minHeight: 'auto',
                          whiteSpace: 'nowrap',
                          minWidth: 'auto',
                          width: 'auto'
                        }}
                        onMouseEnter={(e) => {
                          if (currentProposalPage < Math.ceil(filteredProposalRequests.length / proposalsPerPage)) {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)';
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.3)';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (currentProposalPage < Math.ceil(filteredProposalRequests.length / proposalsPerPage)) {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.2)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }
                        }}
                      >
                        <span>Seguinte</span>
                        <span style={{ fontSize: '16px', lineHeight: '1' }}>→</span>
                      </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Meus Contratos Modal */}
      {showMeusContratosModal && (
        <div className="modal active meus-contratos-modal" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
          <div className="modal-content meus-contratos-modal-content" style={{ 
            maxWidth: '1920px',
            width: 'min(95vw, 1920px)',
            maxHeight: 'calc(100dvh - 32px)',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden'
          }}>
            {/* Modal Header */}
            <div className="modal-header minhas-propostas-modal-header" style={{
              background: '#4b5563',
              color: 'white',
              padding: '1.5rem 2rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0
            }}>
              <div>
                <h2 className="minhas-propostas-title" style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>
                  Meus Contratos
                </h2>
                {!gestaoModalsOnly && (
                  <p className="minhas-propostas-subtitle" style={{ margin: '0.5rem 0 0 0', fontSize: '14px', opacity: 0.9 }}>
                    {isAdminOrGerenteComercial() && selectedSidebarUser
                      ? `Contratos submetidos por ${selectedSidebarUser}`
                      : 'Os seus contratos submetidos'}
                  </p>
                )}
                {gestaoModalsOnly && gestaoFilterUserLabel && gestaoFilterUserLabel !== 'Toda a Equipa' && (
                  <p className="minhas-propostas-subtitle" style={{ margin: '0.5rem 0 0 0', fontSize: '14px', opacity: 0.9 }}>
                    {`Contratos submetidos por ${gestaoFilterUserLabel}`}
                  </p>
                )}
              </div>
              <button
                onClick={closeMeusContratosModal}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '28px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Subheader with Search and Filters */}
            <div className="modal-subheader" style={{
              padding: '1.5rem',
              background: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Contract Type Tabs */}
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <button
                    onClick={() => {
                      setContractTypeTab('energia_gas');
                      setCurrentContractPage(1);
                    }}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: contractTypeTab === 'energia_gas' ? '#3b82f6' : 'white',
                      color: contractTypeTab === 'energia_gas' ? 'white' : '#374151',
                      border: '2px solid',
                      borderColor: contractTypeTab === 'energia_gas' ? '#3b82f6' : '#d1d5db',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'all 0.2s',
                      boxShadow: contractTypeTab === 'energia_gas' ? '0 2px 4px rgba(59, 130, 246, 0.2)' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (contractTypeTab !== 'energia_gas') {
                        e.currentTarget.style.borderColor = '#3b82f6';
                        e.currentTarget.style.color = '#3b82f6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (contractTypeTab !== 'energia_gas') {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.color = '#374151';
                      }
                    }}
                  >
                    Energia/Gás
                  </button>
                  <button
                    onClick={() => {
                      setContractTypeTab('solar');
                      setCurrentContractPage(1);
                    }}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: contractTypeTab === 'solar' ? '#10b981' : 'white',
                      color: contractTypeTab === 'solar' ? 'white' : '#374151',
                      border: '2px solid',
                      borderColor: contractTypeTab === 'solar' ? '#10b981' : '#d1d5db',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'all 0.2s',
                      boxShadow: contractTypeTab === 'solar' ? '0 2px 4px rgba(16, 185, 129, 0.2)' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (contractTypeTab !== 'solar') {
                        e.currentTarget.style.borderColor = '#10b981';
                        e.currentTarget.style.color = '#10b981';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (contractTypeTab !== 'solar') {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.color = '#374151';
                      }
                    }}
                  >
                    Solar
                  </button>
                </div>
                
                {/* Search Bar */}
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="Pesquisar por Empresa, NIF, CPE, Contacto Representante ou ID..."
                    value={contractSearchTerm}
                    onChange={(e) => setContractSearchTerm(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleContractSearch();
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                  <button
                    onClick={handleContractSearch}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: '#4b5563',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#374151';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#4b5563';
                    }}
                  >
                    🔍 Pesquisar
                  </button>
                </div>
                
                {/* Filters Row */}
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  {/* Date Filters */}
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Data:</label>
                    <select
                      value={contractFilterDay}
                      onChange={(e) => setContractFilterDay(e.target.value)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">Dia</option>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                        <option key={day} value={String(day).padStart(2, '0')}>{day}</option>
                      ))}
                    </select>
                    <select
                      value={contractFilterMonth}
                      onChange={(e) => setContractFilterMonth(e.target.value)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">Mês</option>
                      {[
                        { value: '01', label: 'Janeiro' },
                        { value: '02', label: 'Fevereiro' },
                        { value: '03', label: 'Março' },
                        { value: '04', label: 'Abril' },
                        { value: '05', label: 'Maio' },
                        { value: '06', label: 'Junho' },
                        { value: '07', label: 'Julho' },
                        { value: '08', label: 'Agosto' },
                        { value: '09', label: 'Setembro' },
                        { value: '10', label: 'Outubro' },
                        { value: '11', label: 'Novembro' },
                        { value: '12', label: 'Dezembro' }
                      ].map(month => (
                        <option key={month.value} value={month.value}>{month.label}</option>
                      ))}
                    </select>
                    <select
                      value={contractFilterYear}
                      onChange={(e) => setContractFilterYear(e.target.value)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">Ano</option>
                      {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                        <option key={year} value={String(year)}>{year}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Service Type Filter */}
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Tipo:</label>
                    <select
                      value={contractFilterServiceType}
                      onChange={(e) => setContractFilterServiceType(e.target.value)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: 'white',
                        cursor: 'pointer',
                        minWidth: '180px'
                      }}
                    >
                      <option value="">Todos os Tipos</option>
                      <option value="bairro-solar">Bairro Solar</option>
                      <option value="pap">Pay AS Produced (PAP)</option>
                      <option value="energia_gas">Energia/Gás</option>
                    </select>
                  </div>
                  
                  {/* Clear Filters Button */}
                  {(contractSearchTerm || contractFilterDay || contractFilterMonth || contractFilterYear || contractFilterServiceType) && (
                    <button
                      onClick={() => {
                        setContractSearchTerm('');
                        setContractFilterDay('');
                        setContractFilterMonth('');
                        setContractFilterYear('');
                        setContractFilterServiceType('');
                        setCurrentContractPage(1);
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        marginLeft: 'auto'
                      }}
                    >
                      Limpar Filtros
                    </button>
                  )}
                </div>
              </div>
            </div>

            {filteredContractSubmissions.length > 0 && (
              <div className="meus-contratos-pagination-bar meus-contratos-pagination" style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '1rem',
                padding: '1rem 1.5rem',
                borderBottom: '1px solid #e5e7eb',
                background: '#f9fafb',
                flexShrink: 0
              }}>
                      <button
                        className="pagination-arrow"
                        onClick={() => setCurrentContractPage(prev => Math.max(1, prev - 1))}
                        disabled={currentContractPage === 1}
                        style={{
                          padding: '12px 24px',
                          background: currentContractPage === 1 ? '#f3f4f6' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          color: currentContractPage === 1 ? '#9ca3af' : 'white',
                          border: currentContractPage === 1 ? '1px solid #e5e7eb' : 'none',
                          borderRadius: '8px',
                          cursor: currentContractPage === 1 ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          transition: 'all 0.2s ease',
                          boxShadow: currentContractPage === 1 ? 'none' : '0 2px 4px rgba(16, 185, 129, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          opacity: currentContractPage === 1 ? 0.6 : 1,
                          minHeight: 'auto',
                          whiteSpace: 'nowrap',
                          minWidth: 'auto',
                          width: 'auto'
                        }}
                        onMouseEnter={(e) => {
                          if (currentContractPage > 1) {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(16, 185, 129, 0.3)';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (currentContractPage > 1) {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.2)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }
                        }}
                      >
                        <span style={{ fontSize: '16px', lineHeight: '1' }}>←</span>
                        <span>Anterior</span>
                      </button>
                      <div className="pagination-info" style={{
                        fontSize: '14px',
                        color: '#374151',
                        fontWeight: '600',
                        padding: '12px 20px',
                        background: '#f9fafb',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        minWidth: 'auto',
                        textAlign: 'center',
                        minHeight: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        whiteSpace: 'nowrap'
                      }}>
                        Página {currentContractPage} de {Math.ceil(filteredContractSubmissions.length / contractsPerPage)}
                      </div>
                      <button
                        className="pagination-arrow"
                        onClick={() => setCurrentContractPage(prev => Math.min(Math.ceil(filteredContractSubmissions.length / contractsPerPage), prev + 1))}
                        disabled={currentContractPage >= Math.ceil(filteredContractSubmissions.length / contractsPerPage)}
                        style={{
                          padding: '12px 24px',
                          background: currentContractPage >= Math.ceil(filteredContractSubmissions.length / contractsPerPage) ? '#f3f4f6' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          color: currentContractPage >= Math.ceil(filteredContractSubmissions.length / contractsPerPage) ? '#9ca3af' : 'white',
                          border: currentContractPage >= Math.ceil(filteredContractSubmissions.length / contractsPerPage) ? '1px solid #e5e7eb' : 'none',
                          borderRadius: '8px',
                          cursor: currentContractPage >= Math.ceil(filteredContractSubmissions.length / contractsPerPage) ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          transition: 'all 0.2s ease',
                          boxShadow: currentContractPage >= Math.ceil(filteredContractSubmissions.length / contractsPerPage) ? 'none' : '0 2px 4px rgba(16, 185, 129, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          opacity: currentContractPage >= Math.ceil(filteredContractSubmissions.length / contractsPerPage) ? 0.6 : 1,
                          minHeight: 'auto',
                          whiteSpace: 'nowrap',
                          minWidth: 'auto',
                          width: 'auto'
                        }}
                        onMouseEnter={(e) => {
                          if (currentContractPage < Math.ceil(filteredContractSubmissions.length / contractsPerPage)) {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(16, 185, 129, 0.3)';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (currentContractPage < Math.ceil(filteredContractSubmissions.length / contractsPerPage)) {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.2)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }
                        }}
                      >
                        <span>Seguinte</span>
                        <span style={{ fontSize: '16px', lineHeight: '1' }}>→</span>
                      </button>
              </div>
            )}

            {/* Modal Body — scrolls below pagination so all cards on the page are reachable */}
            <div className="modal-body" style={{ 
              flex: 1, 
              minHeight: 0,
              padding: '2rem',
              background: '#f9fafb',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {loadingContractSubmissions ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                  <WonderTradeLoader />
                </div>
              ) : filteredContractSubmissions.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '3rem', 
                  color: '#6b7280',
                  fontSize: '16px'
                }}>
                  {contractSearchTerm || contractFilterDay || contractFilterMonth || contractFilterYear || contractFilterServiceType
                    ? 'Nenhum contrato encontrado com os filtros selecionados.'
                    : 'Nenhum contrato submetido ainda.'}
                </div>
              ) : (
                <div className="contracts-documents-container meus-contratos-cards" style={{
                  flex: '1 1 auto',
                  minHeight: 0,
                  maxWidth: '100%'
                }}>
                  {filteredContractSubmissions
                    .slice((currentContractPage - 1) * contractsPerPage, currentContractPage * contractsPerPage)
                    .map((contract) => (
                      <ContractCard
                        key={contract.id}
                        contract={contract}
                        onRefresh={fetchContractSubmissions}
                        scrollableBody
                        expanded={expandedContractCardId === String(contract.id)}
                        onToggleExpand={() =>
                          setExpandedContractCardId((prev) =>
                            prev === String(contract.id) ? null : String(contract.id)
                          )
                        }
                      />
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ContractCard component for displaying contract submissions
interface ContractCardProps {
  contract: any;
  onRefresh?: () => void;
  scrollableBody?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
}

const ContractCard: React.FC<ContractCardProps> = ({ contract, onRefresh, scrollableBody, expanded, onToggleExpand }) => {
  const [showResolutionModal, setShowResolutionModal] = React.useState(false);
  const [resolutionData, setResolutionData] = React.useState<Record<number, { description: string; files: File[] }>>({});
  const [isSubmittingResolution, setIsSubmittingResolution] = React.useState(false);
  const [resolvingRoundNumber, setResolvingRoundNumber] = React.useState<number | null>(null);
  const isCollapsible = Boolean(scrollableBody);
  const isControlledExpand = typeof expanded === 'boolean';
  const [localExpanded, setLocalExpanded] = React.useState<boolean>(!isCollapsible);
  const isExpanded = isControlledExpand ? Boolean(expanded) : localExpanded;

  const handleToggleExpand = React.useCallback(() => {
    if (isControlledExpand) {
      onToggleExpand?.();
      return;
    }
    setLocalExpanded((prev) => !prev);
  }, [isControlledExpand, onToggleExpand]);
  
  const contractData = contract.contract_data || {};
  const resolvedLeadId = Number(
    contract?.lead_id ??
    contractData?.lead_id ??
    contractData?.leadId ??
    contract?.marcacao?.lead_id ??
    contract?.marcacao?.form_data?.lead_id ??
    0
  ) || null;
  
  // Format timestamp for display
  const formatTimestamp = () => {
    const submittedDate = contract.submitted_at || contract.created_at || contract.updated_at;
    if (!submittedDate) return 'N/A';
    
    const date = new Date(submittedDate);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    
    return `${day}/${month}/${year} ${hour}:${minute}`;
  };
  
  const timestamp = formatTimestamp();
  
  // Get contract type display name
  const getContractTypeDisplay = () => {
    const tipoServico = contractData.tipoServico || contractData.tipo || '';
    if (tipoServico === 'bairro-solar') return 'Bairro Solar';
    if (tipoServico === 'pap') return 'Pay AS Produced (PAP)';
    if (tipoServico === 'energia_gas') return 'Energia/Gás';
    return tipoServico || 'N/A';
  };
  
  const contractType = getContractTypeDisplay();
  
  // Get empresa name from multiple possible sources
  // Priority: contractData.nomeEmpresa > contractData.empresa?.nomeEmpresa > marcação > lead
  const empresaName = 
    contractData.nomeEmpresa || 
    contractData.nome_empresa ||
    contractData.empresa?.nomeEmpresa ||
    contract.marcacao?.nome_empresa ||
    contract.marcacao?.empresa ||
    contract.marcacao?.form_data?.nomeEmpresa ||
    contract.lead?.company ||
    'N/A';
  
  const cardTitle = `${empresaName} - ${contractType}`;
  
  // Get status tag based on contract status
  const getStatusTag = () => {
    const contractStatus = contract.contract_status || 'available';
    const contractTipo = contractData.tipo || contractData.tipoServico || '';
    
    // EE/Gas contract statuses
    if (contractTipo === 'energia_gas') {
      if (contractStatus === 'cancelled') {
        return {
          text: 'Cancelado',
          color: '#ef4444',
          bgColor: '#fee2e2'
        };
      } else if (contractStatus === 'completed') {
        return {
          text: 'Concluído',
          color: '#10b981',
          bgColor: '#d1fae5'
        };
      } else if (contractStatus === 'objected') {
        // Check if all objection rounds are resolved
        const claimInfo = contract.contract_claim || {};
        const objectionRounds = claimInfo.objection_rounds || [];
        
        // If we have objection_rounds, check all rounds
        if (objectionRounds.length > 0) {
          const allRoundsResolved = objectionRounds.every((round: any) => {
            const roundResolution = round.objection_resolution;
            if (!roundResolution) return false;
            
            const resolutions = roundResolution.resolutions || [];
            const objectionReasons = round.objection_reasons || [];
            
            // All objections in this round must have resolutions with files
            return objectionReasons.length > 0 && objectionReasons.every((_: any, index: number) => {
              const resolution = resolutions.find((r: any) => r.objection_index === index);
              return resolution && resolution.files && resolution.files.length > 0;
            });
          });
          
          if (allRoundsResolved) {
            return {
              text: 'Objectado - Respondido',
              color: '#8b5cf6',
              bgColor: '#ede9fe'
            };
          }
        } else {
          // Fallback to old structure for backward compatibility
          const objectionReasons = claimInfo.objection_reasons || [];
          const objectionResolution = claimInfo.objection_resolution || {};
          const resolutions = objectionResolution?.resolutions || [];
          
          // Check if all objections have resolutions with files
          const allResolved = objectionReasons.length > 0 && objectionReasons.every((_: any, index: number) => {
            const resolution = resolutions.find((r: any) => r.objection_index === index);
            return resolution && resolution.files && resolution.files.length > 0;
          });
          
          if (allResolved) {
            return {
              text: 'Objectado - Respondido',
              color: '#8b5cf6',
              bgColor: '#ede9fe'
            };
          }
        }
        
        return {
          text: 'Objetado',
          color: '#f59e0b',
          bgColor: '#fef3c7'
        };
      } else if (contractStatus === 'pending') {
        return {
          text: 'Em Processo',
          color: '#f59e0b',
          bgColor: '#fef3c7'
        };
      } else {
        // Default: available
        return {
          text: 'Contrato Submetido',
          color: '#3b82f6',
          bgColor: '#dbeafe'
        };
      }
    } else {
      // Solar contract statuses
      if (contractStatus === 'cancelled') {
        return {
          text: 'Cancelado',
          color: '#ef4444',
          bgColor: '#fee2e2'
        };
      } else if (contractStatus === 'waiting_installation' || contractStatus === 'vt_realizada_ok' || contractStatus === 'vt_ok') {
        return {
          text: 'Aguardar Instalação',
          color: '#8b5cf6',
          bgColor: '#ede9fe'
        };
      } else if (contractStatus === 'installed') {
        return {
          text: 'Instalado',
          color: '#10b981',
          bgColor: '#d1fae5'
        };
      } else if (contractStatus === 'in_progress' || contractStatus === 'aguarda_agendamento_vt' || contractStatus === 'reformulacao') {
        return {
          text: 'Em Processo',
          color: '#f59e0b',
          bgColor: '#fef3c7'
        };
      } else {
        // Default: available
        return {
          text: 'Contrato Submetido',
          color: '#3b82f6',
          bgColor: '#dbeafe'
        };
      }
    }
  };
  
  const statusTag = getStatusTag();
  
  // Collect all documents
  const allDocuments: any[] = [];
  const documentos = contractData.documentos || {};
  
  Object.entries(documentos).forEach(([docType, files]: [string, any]) => {
    if (Array.isArray(files) && files.length > 0) {
      files.forEach((file: any) => {
        if (file && typeof file === 'object') {
          allDocuments.push({
            ...file,
            docType: docType
          });
        }
      });
    }
  });
  
  // Format file size helper
  const formatFileSize = (bytes: number) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };
  
  // Handle file download
  const handleFileDownload = async (file: any, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (!file) {
      alert('Erro: Ficheiro não encontrado');
      return;
    }
    
    try {
      
      // If file has base64 data, convert and download
      if (file.data) {
        try {
          const byteCharacters = atob(file.data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: file.type || 'application/octet-stream' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = file.name || file.file_name || 'file';
          document.body.appendChild(link);
          link.click();
          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }, 100);
          return;
        } catch (base64Error) {
          console.error('Error processing base64 file:', base64Error);
        }
      }
      
      // If file has a lead_file_id, use lead file download endpoint
      if (file.lead_file_id || file.leadFileId || file.file_id || file.id) {
        const leadId = file.lead_id || file.leadId || resolvedLeadId;
        const fileId = file.lead_file_id || file.leadFileId || file.file_id || file.id;
        
        if (leadId && fileId) {
          try {
            const response = await api.get(`/api/leads/${leadId}/files/${fileId}/download`, {
              responseType: 'blob'
            });
            const blob = new Blob([response.data], { type: file.type || 'application/octet-stream' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = file.name || file.file_name || 'file';
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            }, 100);
            return;
          } catch (error) {
            console.error('[ComercialManagement ContractCard] Error downloading file:', error);
          }
        }

        // Fallback for records that persisted only file ID (without lead_id)
        if (fileId) {
          try {
            const response = await api.get(`/api/leads/files/${fileId}/download`, {
              responseType: 'blob'
            });
            const blob = new Blob([response.data], { type: file.type || 'application/octet-stream' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = file.name || file.file_name || 'file';
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            }, 100);
            return;
          } catch (error) {
            console.error('[ComercialManagement ContractCard] Error downloading by file ID fallback:', error);
          }
        }
      }
      
      // If file has a public_id, use chat download endpoint
      if (file.public_id || file.publicId || file.cloudinary_id) {
        const publicId = file.public_id || file.publicId || file.cloudinary_id;
        
        try {
          const response = await api.get(`/api/chat/download/${encodeURIComponent(publicId)}`);
          
          if (response.data && response.data.success && response.data.download_url) {
            const link = document.createElement('a');
            link.href = response.data.download_url;
            link.download = file.name || file.file_name || 'file';
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
              document.body.removeChild(link);
            }, 100);
            return;
          }
        } catch (apiError) {
          console.error('Error downloading via API:', apiError);
          // Continue to next download method
        }
      }
      
      // If file has a URL, download directly
      if (file.url || file.file_url || file.download_url) {
        const url = file.url || file.file_url || file.download_url;
        const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
        const fullUrl = url.startsWith('http') 
          ? url 
          : `${backendUrl}${url.startsWith('/') ? '' : '/'}${url}`;
        
        const link = document.createElement('a');
        link.href = fullUrl;
        link.download = file.name || file.file_name || 'file';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
        return;
      }
      
      // If file has an id, try to fetch from lead files
      if (file.id && resolvedLeadId) {
        try {
          const response = await api.get(`/api/leads/${resolvedLeadId}/files/${file.id}/download`, {
            responseType: 'blob'
          });
          const blob = new Blob([response.data], { type: file.type || 'application/octet-stream' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = file.name || file.file_name || 'file';
          document.body.appendChild(link);
          link.click();
          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }, 100);
          return;
        } catch (leadError) {
          console.error('[ComercialManagement ContractCard] Error fetching from lead:', leadError);
        }
      }
      
      // If we only have metadata (name, size, type) but no actual file data
      if (file.name && file.size && file.type && !file.data && !file.url && !file.lead_file_id && !file.public_id) {
        console.error('File has only metadata, no actual file data available:', file);
        alert(`Não é possível descarregar o ficheiro "${file.name}". O ficheiro foi submetido apenas com metadados (nome, tamanho, tipo) mas os dados do ficheiro não foram guardados na base de dados. Por favor, contacte o suporte técnico.`);
        return;
      }
      
      console.error('No valid download method found for file:', file);
      alert(`Não foi possível descarregar o ficheiro "${file.name || file.file_name || 'desconhecido'}". Informações do ficheiro não disponíveis. Verifique a consola para mais detalhes.`);
    } catch (error) {
      console.error('Error downloading file:', error, file);
      alert(`Erro ao descarregar o ficheiro: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    }
  };
  
  // Handle download all files
  const handleDownloadAll = async (event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (allDocuments.length === 0) {
      alert('Não há ficheiros para descarregar');
      return;
    }
    
    for (let i = 0; i < allDocuments.length; i++) {
      const file = allDocuments[i];
      if (i > 0) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      await handleFileDownload(file);
    }
  };
  
  // Get file icon class
  const getFileIconClass = (type: string, name: string): string => {
    if (type.includes('pdf') || name.toLowerCase().endsWith('.pdf')) return 'file-icon-pdf';
    if (type.includes('image') || /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(name)) return 'file-icon-image';
    if (type.includes('word') || name.toLowerCase().endsWith('.doc') || name.toLowerCase().endsWith('.docx')) return 'file-icon-word';
    if (type.includes('excel') || name.toLowerCase().endsWith('.xls') || name.toLowerCase().endsWith('.xlsx')) return 'file-icon-excel';
    if (type.includes('zip') || /\.(zip|rar|7z|tar|gz)$/i.test(name)) return 'file-icon-zip';
    return 'file-icon-generic';
  };

  // Objectado section: computed outside JSX to avoid Babel parse issues with IIFE/braces
  const claimInfoObj = contract.contract_claim || {};
  const objectionRoundsArr = claimInfoObj.objection_rounds || [];
  const objectionReasonsArr = claimInfoObj.objection_reasons || [];
  const objectionResolutionObj = claimInfoObj.objection_resolution || {};
  const contractScdList = Array.isArray(claimInfoObj.service_completion_data)
    ? claimInfoObj.service_completion_data
    : [];
  const linkedProposalCompletions = Array.isArray(contract.linked_proposal_completions)
    ? contract.linked_proposal_completions
    : [];
  const vtCheckedBy = claimInfoObj.vt_checked_by as Record<string, unknown> | undefined;
  const hasContractCompletion =
    contractScdList.length > 0 ||
    Boolean(claimInfoObj.vt_checked_at || claimInfoObj.installed_at || claimInfoObj.completed_at) ||
    Boolean(claimInfoObj.completion_notes) ||
    linkedProposalCompletions.length > 0;

  const contractCompletionSection = hasContractCompletion ? (
    <div className="document-text-block" style={{ marginBottom: '20px' }}>
      <h3 style={{ color: 'var(--wondertrade-red)', marginBottom: '10px', fontSize: '16px', fontWeight: '600' }}>
        Conclusão BackOffice
      </h3>
      <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
        {claimInfoObj.vt_checked_at && (
          <div className="text-block-line">
            <strong>VT realizada:</strong> {formatContractDisplayDate(String(claimInfoObj.vt_checked_at))}
            {vtCheckedBy?.username || vtCheckedBy?.email
              ? ` — ${String(vtCheckedBy.username || vtCheckedBy.email)}`
              : ''}
          </div>
        )}
        {claimInfoObj.installed_at && (
          <div className="text-block-line">
            <strong>Instalação:</strong> {formatContractDisplayDate(String(claimInfoObj.installed_at))}
          </div>
        )}
        {claimInfoObj.completed_at && (
          <div className="text-block-line">
            <strong>Concluído:</strong> {formatContractDisplayDate(String(claimInfoObj.completed_at))}
          </div>
        )}
        {claimInfoObj.completion_notes && (
          <div className="text-block-line">
            <strong>Notas:</strong> {String(claimInfoObj.completion_notes)}
          </div>
        )}
        {contractScdList.map((row: any, index: number) => {
          const emailData = row?.emailData || {};
          const entries = Object.entries(emailData).filter(
            ([, v]) => v != null && String(v).trim() !== ''
          );
          if (!entries.length) return null;
          return (
            <div key={`contract-scd-${index}`} style={{ marginTop: index > 0 ? '12px' : 0 }}>
              <div className="text-block-line">
                <strong>Serviço {row?.service || row?.instanceId || index + 1}:</strong>{' '}
                {row?.status === 'cancelled' ? 'Cancelado' : 'Concluído'}
              </div>
              {entries.map(([key, value]) => (
                <div key={key} className="text-block-line">
                  <strong>{key}:</strong> {String(value)}
                </div>
              ))}
            </div>
          );
        })}
        {linkedProposalCompletions.map((linked: any) => (
          <div key={linked.propostaId} style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
            <div className="text-block-line">
              <strong>Proposta associada {linked.propostaId}:</strong>{' '}
              {linked.status === 'completed' ? 'Concluída' : linked.status === 'cancelled' ? 'Cancelada' : 'Em processo'}
            </div>
            {linked.completed_at && (
              <div className="text-block-line">
                <strong>Concluída em:</strong> {formatContractDisplayDate(String(linked.completed_at))}
              </div>
            )}
            {linked.completion_notes && (
              <div className="text-block-line">
                <strong>Notas:</strong> {String(linked.completion_notes)}
              </div>
            )}
            {(linked.service_completion_data || []).map((row: any, index: number) => {
              const emailData = row?.emailData || {};
              const entries = Object.entries(emailData).filter(
                ([, v]) => v != null && String(v).trim() !== ''
              );
              if (!entries.length) return null;
              return (
                <div key={`${linked.propostaId}-scd-${index}`} style={{ marginTop: '8px' }}>
                  {entries.map(([key, value]) => (
                    <div key={key} className="text-block-line">
                      <strong>{key}:</strong> {String(value)}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  ) : null;

  const objectadoSectionContent = (contractData.tipo === 'energia_gas' || contractData.tipoServico === 'energia_gas') ? (
    <div className="document-text-block" style={{ marginBottom: '20px' }}>
      <h3 style={{ color: 'var(--wondertrade-red)', marginBottom: '10px', fontSize: '16px', fontWeight: '600' }}>Objectado</h3>
      <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
        {objectionRoundsArr.length > 0 ? (
          objectionRoundsArr.map((round: any, roundIndex: number) => (
            <div key={roundIndex} style={{ marginBottom: roundIndex < objectionRoundsArr.length - 1 ? '16px' : 0 }}>
              {round.objection_reasons && round.objection_reasons.length > 0 && (
                <div className="text-block-line"><strong>Motivos (ronda {roundIndex + 1}):</strong> {round.objection_reasons.map((r: any) => r.reason || r).join(', ')}</div>
              )}
              {round.objection_resolution && (
                <div className="text-block-line"><strong>Resolução:</strong> {round.objection_resolution.resolutions?.length ? `${round.objection_resolution.resolutions.length} resoluções` : 'N/A'}</div>
              )}
            </div>
          ))
        ) : (
          <>
            {objectionReasonsArr.length > 0 && <div className="text-block-line"><strong>Motivos:</strong> {objectionReasonsArr.map((r: any) => r.reason || r).join(', ')}</div>}
            {objectionResolutionObj.resolutions && <div className="text-block-line"><strong>Resoluções:</strong> {objectionResolutionObj.resolutions.length}</div>}
          </>
        )}
      </div>
    </div>
  ) : null;
  
  return (
    <div 
      id={`contract-${contract.id}`}
      className="contract-document proposal-card" 
      style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}
    >
      <div
        className="document-header"
        style={{
          position: 'relative',
          padding: '1.5rem',
          background: '#f9fafb',
          borderBottom: '1px solid #e5e7eb',
          cursor: isCollapsible ? 'pointer' : 'default'
        }}
        onClick={isCollapsible ? handleToggleExpand : undefined}
      >
        {/* Timestamp stamp and Status Tag at the bottom right of the header */}
        <div className="contract-header-badges" style={{
          position: 'absolute',
          bottom: '0',
          right: '0',
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          zIndex: 10
        }}>
          {/* Timestamp stamp */}
          <div className="contract-timestamp-badge" style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '500',
            color: '#6b7280',
            backgroundColor: '#f3f4f6',
            border: '1px solid #e5e7eb',
            fontFamily: 'monospace'
          }}>
            {timestamp}
          </div>
          
          {/* Status Tag */}
          <div className="contract-status-badge" style={{
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            color: statusTag.color,
            backgroundColor: statusTag.bgColor,
            border: `1px solid ${statusTag.color}`
          }}>
            {statusTag.text}
          </div>
        </div>
        
        <h3 className="document-title" style={{ 
          margin: '0 0 0.5rem 0', 
          fontSize: '18px', 
          fontWeight: '600', 
          color: '#1f2937',
          lineHeight: '1.3'
        }}>
          {cardTitle}
        </h3>
      </div>

      {isExpanded && (
      <div className="document-body document-text-block" style={{ padding: '1.5rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div className="document-body-scroll">
        {/* Informações Gerais */}
        <div className="document-text-block" style={{ marginBottom: '20px' }}>
          <h3 style={{ color: 'var(--wondertrade-red)', marginBottom: '10px', fontSize: '16px', fontWeight: '600' }}>Informações Gerais</h3>
          <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
            <div className="text-block-line"><strong>Tipo de Contrato:</strong> {contractType}</div>
            {contractData.tipoServico && contractData.tipoServico !== contractData.tipo && (
              <div className="text-block-line"><strong>Tipo de Serviço:</strong> {contractData.tipoServico}</div>
            )}
            {contractData.numeroPaineis && (
              <div className="text-block-line"><strong>Número de Painéis:</strong> {contractData.numeroPaineis}</div>
            )}
          </div>
        </div>
        
        {/* Informações da Empresa (not for Energy/Gas contracts) */}
        {contractData.tipo !== 'energia_gas' && !(contractData.tipoServico === 'energia_gas') && (
          <div className="document-text-block" style={{ marginBottom: '20px' }}>
            <h3 style={{ color: 'var(--wondertrade-red)', marginBottom: '10px', fontSize: '16px', fontWeight: '600' }}>Informações da Empresa</h3>
            <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
              {contractData.nomeEmpresa && <div className="text-block-line"><strong>Nome da Empresa:</strong> {contractData.nomeEmpresa}</div>}
              {contractData.nifEmpresa && <div className="text-block-line"><strong>NIF:</strong> {contractData.nifEmpresa}</div>}
              {contractData.emailEmpresa && <div className="text-block-line"><strong>Email da Empresa:</strong> {contractData.emailEmpresa}</div>}
              {contractData.moradaInstalacao && <div className="text-block-line"><strong>Morada de Instalação:</strong> {contractData.moradaInstalacao}</div>}
              {contractData.distrito && <div className="text-block-line"><strong>Distrito:</strong> {contractData.distrito}</div>}
              {contractData.nomeRepresentante && <div className="text-block-line"><strong>Nome do Representante:</strong> {contractData.nomeRepresentante}</div>}
              {contractData.nifRepresentanteLegal && <div className="text-block-line"><strong>NIF do Representante Legal:</strong> {contractData.nifRepresentanteLegal}</div>}
              {contractData.contactoResponsavel && <div className="text-block-line"><strong>Contacto do Responsável:</strong> {contractData.contactoResponsavel}</div>}
              {contractData.emailRepresentanteLegal && <div className="text-block-line"><strong>Email do Representante Legal:</strong> {contractData.emailRepresentanteLegal}</div>}
            </div>
          </div>
        )}
        
        {/* Informações do Serviço (Solar contracts) */}
        {(contractData.tipoServico === 'bairro-solar' || contractData.tipoServico === 'pap' || contractData.tipo === 'bairro-solar' || contractData.tipo === 'pap') && (
          <div className="document-text-block" style={{ marginBottom: '20px' }}>
            <h3 style={{ color: 'var(--wondertrade-red)', marginBottom: '10px', fontSize: '16px', fontWeight: '600' }}>Informações do Serviço</h3>
            <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
              {contractData.cpe && <div className="text-block-line"><strong>CPE:</strong> {contractData.cpe}</div>}
              {contractData.potenciaContratada && <div className="text-block-line"><strong>Potência Contratada:</strong> {contractData.potenciaContratada}</div>}
              {contractData.idPropostaS2C && <div className="text-block-line"><strong>ID Proposta S2C:</strong> {contractData.idPropostaS2C}</div>}
              {contractData.valorProposta && <div className="text-block-line"><strong>Valor da Proposta:</strong> {contractData.valorProposta} €</div>}
              {contractData.codigoCertidaoPermanente && <div className="text-block-line"><strong>Código Certidão Permanente:</strong> {contractData.codigoCertidaoPermanente}</div>}
              {contractData.nomeComercial && <div className="text-block-line"><strong>Nome Comercial:</strong> {contractData.nomeComercial}</div>}
              {contractData.codigoComercial && <div className="text-block-line"><strong>Código Comercial:</strong> {contractData.codigoComercial}</div>}
              {contractData.vyn && <div className="text-block-line"><strong>VYN:</strong> {contractData.vyn}</div>}
            </div>
          </div>
        )}
        
        {/* Informações de Angariação (Energy/Gas contracts) */}
        {contractData.angariacao && (
          <div className="document-text-block" style={{ marginBottom: '20px' }}>
            <h3 style={{ color: 'var(--wondertrade-red)', marginBottom: '10px', fontSize: '16px', fontWeight: '600' }}>Informações de Angariação</h3>
            <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
              {contractData.angariacao.idProposta && <div className="text-block-line"><strong>ID Proposta:</strong> {contractData.angariacao.idProposta}</div>}
              {contractData.angariacao.dataInicioFornecimento && <div className="text-block-line"><strong>Data Início Fornecimento:</strong> {contractData.angariacao.dataInicioFornecimento}</div>}
              {contractData.angariacao.dataFimFornecimento && <div className="text-block-line"><strong>Data Fim Fornecimento:</strong> {contractData.angariacao.dataFimFornecimento}</div>}
              {contractData.angariacao.validaAteDia && <div className="text-block-line"><strong>Válida até Dia:</strong> {contractData.angariacao.validaAteDia}</div>}
              {contractData.angariacao.iban && <div className="text-block-line"><strong>IBAN:</strong> {contractData.angariacao.iban}</div>}
            </div>
          </div>
        )}
        
        {/* Objectado Section (Energia/Gás contracts only) - computed outside JSX to avoid parse issues */}
        {objectadoSectionContent}
        {contractCompletionSection}
        {/* Documents Section */}
        {allDocuments.length > 0 && (
          <div className="document-attachments" style={{ marginTop: 'auto' }}>
            <div className="attachments-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span className="attachments-count">{allDocuments.length} {allDocuments.length === 1 ? 'documento' : 'documentos'}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDownloadAll(e);
                }}
                style={{
                  padding: '6px 12px',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  transition: 'background 0.2s',
                  zIndex: 10,
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#059669';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#10b981';
                }}
                disabled={allDocuments.length === 0}
              >
                ⬇ Descarregar Todos ({allDocuments.length})
              </button>
            </div>
            <div className="attachments-grid">
              {allDocuments.map((file, index) => (
                <div 
                  key={index} 
                  className="attachment-icon-item"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    handleFileDownload(file, e);
                  }}
                  title="Clique para descarregar"
                >
                  <div className="attachment-icon-wrapper">
                    <div className={`attachment-icon-shape ${getFileIconClass(file.type || '', file.name || '')}`}>
                      <div className="file-icon-content"></div>
                    </div>
                    <div className="attachment-icon-label">
                      <div className="attachment-icon-envelope">✉</div>
                      <div className="attachment-icon-filename">{file.name || file.file_name || 'Documento sem nome'}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Flex spacer - fills remaining space; minHeight:1 prevents collapse at 75% zoom */}
        <div style={{ flex: '1 1 0%', minHeight: 1 }} aria-hidden="true" />
        </div>
      </div>
      )}
    </div>
  );
};

export default ComercialManagement;

